import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import api from '../utils/api';
import CodeBlock from './CodeBlock';
import { getAnimationClasses } from './AnimationControls';
import { Menu, X, ChevronDown, ExternalLink, ChevronLeft, ChevronRight, Search as SearchIcon, Quote, Check, ChevronUp, MapPin, Clock as ClockIcon, Download, Calendar as CalendarIcon } from 'lucide-react';
import PopupPageModal from './PopupPageModal';
import PlanRecommenderWizard from './PlanRecommenderWizard';
import AiAssistantWidget from './AiAssistantWidget';
import { SUPPORTED_LANGUAGES } from './LanguageSelector';
import { getButtonVariantStyle, getButtonSizeClass, getButtonHoverClass, renderButtonIcon, getFlatButtonVariantStyle, flatRadiusStyle, flatHoverClass, flatHasIcon, flatIconPos, flatIcon, getButtonRadius } from './buttonBuilderUtils';

// Shared typography scope for image+text blocks (stack/carousel/row).
// Without this, Tailwind's preflight reset flattens H1-H6 sizes and hard-return
// (margin) spacing inside the dangerouslySetInnerHTML content. Mirrors the
// .column-text-content rules used by text columns.
const slideContentTypographyCss = (getThemeValue) => {
  const tv = typeof getThemeValue === 'function' ? getThemeValue : () => undefined;
  return `
  .image-text-slide-content h1 { font-size: ${tv('h1_size') || 48}px; font-weight: 700; line-height: 1.2; margin: 0.5rem 0; color: inherit; }
  .image-text-slide-content h2 { font-size: ${tv('h2_size') || 36}px; font-weight: 700; line-height: 1.3; margin: 0.5rem 0; color: inherit; }
  .image-text-slide-content h3 { font-size: ${tv('h3_size') || 28}px; font-weight: 600; line-height: 1.3; margin: 0.5rem 0; color: inherit; }
  .image-text-slide-content h4 { font-size: ${tv('h4_size') || 22}px; font-weight: 600; line-height: 1.4; margin: 0.5rem 0; color: inherit; }
  .image-text-slide-content h5 { font-size: 1.125rem; font-weight: 600; line-height: 1.4; margin: 0.5rem 0; color: inherit; }
  .image-text-slide-content h6 { font-size: 1rem; font-weight: 600; line-height: 1.4; margin: 0.5rem 0; color: inherit; }
  .image-text-slide-content p { margin: 0.75rem 0; color: inherit; }
  .image-text-slide-content p:first-child { margin-top: 0; }
  .image-text-slide-content p:last-child { margin-bottom: 0; }
  .image-text-slide-content ul { list-style-type: disc; padding-left: 1.5rem; margin: 0.5rem 0; color: inherit; }
  .image-text-slide-content ol { list-style-type: decimal; padding-left: 1.5rem; margin: 0.5rem 0; color: inherit; }
  .image-text-slide-content li { margin: 0.25rem 0; display: list-item; color: inherit; }
  .image-text-slide-content ul ul { list-style-type: circle; }
  .image-text-slide-content ul ul ul { list-style-type: square; }
  .image-text-slide-content strong, .image-text-slide-content b { font-weight: 700; }
  .image-text-slide-content a { color: ${tv('link_color') || '#2563eb'}; text-decoration: underline; }
  .image-text-slide-content a:hover { color: ${tv('link_hover_color') || '#1d4ed8'}; }
`;
};


// Returns true when the renderer is being shown inside the admin Preview
// shell (URL starts with /preview/<token>). In that mode internal links
// should keep the user inside the preview shell by linking to
// `/preview/<page-token>`. On a real public site (custom domain or subdomain)
// we MUST link via the page's slug/full_path — `/preview/<token>` is not a
// public route and 404s on custom domains (the SubdomainRouter doesn't
// whitelist it). See bug report: nested nav links on horizonglobalacademy.com.
const isInPreviewMode = () => {
  try {
    return typeof window !== 'undefined' && window.location.pathname.startsWith('/preview/');
  } catch (e) {
    return false;
  }
};


/**
 * FeatureComparisonSection — wraps the comparison table + optional recommender
 * wizard. Lives outside the renderer switch so it can hold its own state
 * (the recommended plan id) and pulse-highlight the recommended column
 * when the wizard finishes.
 */
const FeatureComparisonSection = ({ block, content, plans, categories, accentColor, ts, renderValue, finalBlockStyle, wrapperClass, animationClass, index }) => {
  const [recommendedPlanId, setRecommendedPlanId] = useState(null);

  // When a recommendation lands, smooth-scroll the table into view so the
  // visitor sees the highlighted column.
  React.useEffect(() => {
    if (recommendedPlanId) {
      const el = document.querySelector(`[data-testid="feature-comparison-${block.id}"]`);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 250);
      }
    }
  }, [recommendedPlanId, block.id]);

  const isRecommended = (planId) => recommendedPlanId && planId === recommendedPlanId;
  const pulseStyle = (planId) => isRecommended(planId)
    ? { boxShadow: `0 0 0 3px ${accentColor}`, animation: 'fcPulse 2s ease-in-out 2' }
    : {};

  return (
    <section
      key={`block-${index}`}
      className={`py-8 px-4 md:py-16 md:px-6 ${wrapperClass} ${animationClass}`}
      style={finalBlockStyle}
    >
      <style>{`
        @keyframes fcPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.015); }
        }
      `}</style>
      <div className="max-w-6xl mx-auto">
        {content.title && (
          <h2
            className="mb-3"
            style={{
              fontSize: ts.fontSize ? `clamp(1.5rem, 4vw, ${parseInt(ts.fontSize, 10) / 16}rem)` : 'clamp(1.875rem, 4vw, 2.25rem)',
              fontFamily: ts.fontFamily && ts.fontFamily !== 'inherit' ? `${ts.fontFamily}, sans-serif` : 'inherit',
              fontWeight: ts.fontWeight || 'bold',
              color: ts.color || 'var(--site-heading-color)',
              textAlign: ts.align || 'center',
            }}
          >
            {content.title}
          </h2>
        )}
        {content.subtitle && (
          <p className="text-center mb-10 opacity-70" style={{ color: 'var(--site-body-color)' }}>
            {content.subtitle}
          </p>
        )}

        {/* Plan Recommender Wizard */}
        {content.enableRecommender && (content.recommenderQuestions || []).length > 0 && plans.length > 0 && (
          <PlanRecommenderWizard
            title={content.recommenderTitle}
            subtitle={content.recommenderSubtitle}
            questions={content.recommenderQuestions || []}
            plans={plans}
            accentColor={accentColor}
            recommendedPlanId={recommendedPlanId}
            onRecommendation={setRecommendedPlanId}
          />
        )}

        {/* Desktop: table layout */}
        <div className="hidden md:block overflow-x-auto rounded-2xl border border-slate-200 bg-white">
          <table className="w-full text-left" data-testid={`feature-comparison-${block.id}`}>
            <thead>
              <tr>
                <th className="p-4 align-bottom text-sm font-medium text-slate-500 w-1/4" />
                {plans.map((plan) => {
                  const isHL = plan.highlighted || isRecommended(plan.id);
                  return (
                  <th
                    key={plan.id}
                    className="p-4 align-bottom text-center transition-all"
                    style={{
                      ...(isHL ? { backgroundColor: `${accentColor}0F` } : {}),
                      ...pulseStyle(plan.id),
                    }}
                  >
                    {isRecommended(plan.id) && (
                      <span
                        className="inline-block mb-2 px-2 py-0.5 rounded-full text-[11px] font-semibold animate-pulse"
                        style={{ backgroundColor: accentColor, color: '#fff' }}
                      >
                        ★ Recommended for You
                      </span>
                    )}
                    {!isRecommended(plan.id) && plan.badge && (
                      <span
                        className="inline-block mb-2 px-2 py-0.5 rounded-full text-[11px] font-semibold"
                        style={{ backgroundColor: accentColor, color: '#fff' }}
                      >
                        {plan.badge}
                      </span>
                    )}
                    <div className="font-semibold text-lg" style={{ color: 'var(--site-heading-color)' }}>{plan.name}</div>
                    {(plan.price || plan.period) && (
                      <div className="mt-1">
                        <span className="text-2xl font-bold" style={{ color: 'var(--site-heading-color)' }}>{plan.price}</span>
                        {plan.period && <span className="text-sm opacity-60 ml-1">{plan.period}</span>}
                      </div>
                    )}
                    {plan.buttonText && (
                      <a
                        href={plan.buttonUrl || '#'}
                        className="inline-block mt-3 px-4 py-2 rounded-lg text-sm font-medium transition-opacity hover:opacity-90"
                        style={{
                          backgroundColor: isHL ? accentColor : 'transparent',
                          color: isHL ? '#fff' : accentColor,
                          border: `1px solid ${accentColor}`,
                        }}
                      >
                        {plan.buttonText}
                      </a>
                    )}
                  </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {categories.map((cat) => (
                <React.Fragment key={cat.id}>
                  {cat.name && (
                    <tr>
                      <td
                        colSpan={plans.length + 1}
                        className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 bg-slate-50 border-t border-slate-200"
                      >
                        {cat.name}
                      </td>
                    </tr>
                  )}
                  {(cat.features || []).map((f) => (
                    <tr key={f.id} className="border-t border-slate-100">
                      <td className="p-4 text-sm font-medium" style={{ color: 'var(--site-heading-color)' }}>{f.name}</td>
                      {plans.map((plan) => {
                        const isHL = plan.highlighted || isRecommended(plan.id);
                        return (
                        <td
                          key={plan.id}
                          className="p-4 text-center"
                          style={isHL ? { backgroundColor: `${accentColor}0A` } : {}}
                        >
                          {renderValue((f.values || {})[plan.id])}
                        </td>
                        );
                      })}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile: stacked plan cards */}
        <div className="md:hidden space-y-6">
          {plans.map((plan) => {
            const isHL = plan.highlighted || isRecommended(plan.id);
            return (
            <div
              key={plan.id}
              className="rounded-xl border bg-white overflow-hidden"
              style={isHL
                ? { borderColor: accentColor, borderWidth: 2, ...pulseStyle(plan.id) }
                : { borderColor: '#e2e8f0' }
              }
            >
              <div className="p-5 text-center border-b border-slate-100" style={isHL ? { backgroundColor: `${accentColor}0F` } : {}}>
                {isRecommended(plan.id) && (
                  <span className="inline-block mb-2 px-2 py-0.5 rounded-full text-[11px] font-semibold animate-pulse" style={{ backgroundColor: accentColor, color: '#fff' }}>
                    ★ Recommended for You
                  </span>
                )}
                {!isRecommended(plan.id) && plan.badge && (
                  <span className="inline-block mb-2 px-2 py-0.5 rounded-full text-[11px] font-semibold" style={{ backgroundColor: accentColor, color: '#fff' }}>
                    {plan.badge}
                  </span>
                )}
                <div className="font-semibold text-lg" style={{ color: 'var(--site-heading-color)' }}>{plan.name}</div>
                {(plan.price || plan.period) && (
                  <div className="mt-1">
                    <span className="text-2xl font-bold" style={{ color: 'var(--site-heading-color)' }}>{plan.price}</span>
                    {plan.period && <span className="text-sm opacity-60 ml-1">{plan.period}</span>}
                  </div>
                )}
                {plan.buttonText && (
                  <a
                    href={plan.buttonUrl || '#'}
                    className="inline-block mt-3 px-4 py-2 rounded-lg text-sm font-medium"
                    style={{
                      backgroundColor: isHL ? accentColor : 'transparent',
                      color: isHL ? '#fff' : accentColor,
                      border: `1px solid ${accentColor}`,
                    }}
                  >
                    {plan.buttonText}
                  </a>
                )}
              </div>
              <div className="p-4 space-y-3">
                {categories.map((cat) => (
                  <div key={cat.id}>
                    {cat.name && <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1">{cat.name}</p>}
                    <ul className="space-y-2">
                      {(cat.features || []).map((f) => (
                        <li key={f.id} className="flex items-center justify-between gap-3">
                          <span className="text-sm" style={{ color: 'var(--site-body-color)' }}>{f.name}</span>
                          <span>{renderValue((f.values || {})[plan.id])}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};


/**
 * derivePlanComparison — turns a Page Builder "pricing" block's items into a
 * clean comparison matrix (plans + ✓/✗ rows), mirroring the public landing
 * page logic. A feature is ✓ for a plan when its label appears in that plan's
 * feature list; rows that are ✗ for every plan are dropped as noise.
 */
export const derivePlanComparison = (pricingItems = []) => {
  const plans = (pricingItems || []).map((p) => ({
    id: p.id,
    name: p.name,
    price: p.price,
    period: p.period,
    highlighted: !!p.highlighted,
    badge: p.highlighted ? 'Most Popular' : '',
    buttonText: p.buttonText,
    buttonUrl: p.buttonUrl,
  }));
  const seen = new Map(); // normalized key -> display label
  (pricingItems || []).forEach((p) => {
    (p.features || []).forEach((f) => {
      const label = (f || '').trim();
      const key = label.toLowerCase();
      if (key && !seen.has(key)) seen.set(key, label);
    });
  });
  const includesFeature = (p, key) =>
    (p.features || []).some((f) => (f || '').trim().toLowerCase() === key);
  const rows = Array.from(seen.entries())
    .map(([key, label]) => ({
      id: `row-${key.replace(/[^a-z0-9]+/g, '-')}`,
      label,
      values: (pricingItems || []).reduce((acc, p) => {
        acc[p.id] = includesFeature(p, key) ? 'yes' : 'no';
        return acc;
      }, {}),
    }))
    .filter((row) => plans.some((p) => row.values[p.id] === 'yes'));
  return { plans, rows };
};

/**
 * PlanComparisonSection — cleaner comparison table (landing-page style) that
 * renders pre-computed `plans` + `rows`. Holds its own recommended-plan state
 * for the optional Plan Recommender wizard.
 */
const PlanComparisonSection = ({ block, content, plans, rows, accentColor, ts, finalBlockStyle, wrapperClass, animationClass, index, resolveUrl, getPopupClickHandler }) => {
  const [recommendedPlanId, setRecommendedPlanId] = useState(null);

  React.useEffect(() => {
    if (recommendedPlanId) {
      const el = document.querySelector(`[data-testid="plan-comparison-${block.id}"]`);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 250);
    }
  }, [recommendedPlanId, block.id]);

  const isRecommended = (planId) => recommendedPlanId && planId === recommendedPlanId;
  const pulseStyle = (planId) => isRecommended(planId)
    ? { boxShadow: `0 0 0 3px ${accentColor}`, animation: 'pcPulse 2s ease-in-out 2' }
    : {};

  const renderCell = (raw) => {
    const v = (raw == null ? '' : String(raw)).trim();
    const low = v.toLowerCase();
    if (['yes', 'true', '✓', 'included', 'y', '1'].includes(low)) {
      return (
        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full" style={{ backgroundColor: `${accentColor}1A`, color: accentColor }} aria-label="Included">
          <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="4 11 8 15 16 6" /></svg>
        </span>
      );
    }
    if (['no', 'false', '-', '', 'x', '✗', 'n', '0'].includes(low)) {
      return (
        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-slate-100 text-slate-400" aria-label="Not included">
          <svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="5" y1="5" x2="15" y2="15" /><line x1="15" y1="5" x2="5" y2="15" /></svg>
        </span>
      );
    }
    return <span className="text-sm font-medium" style={{ color: 'var(--site-body-color)' }}>{v}</span>;
  };

  const renderPlanButton = (plan, isHL) => {
    if (!plan.buttonText) return null;
    const href = resolveUrl ? resolveUrl(plan.buttonUrl) : (plan.buttonUrl || '#');
    const onClick = (getPopupClickHandler && plan.buttonUrl) ? getPopupClickHandler(plan.buttonUrl) : undefined;
    return (
      <a
        href={href}
        onClick={onClick}
        className="inline-block mt-3 px-4 py-2 rounded-lg text-sm font-medium transition-opacity hover:opacity-90"
        style={{
          backgroundColor: isHL ? accentColor : 'transparent',
          color: isHL ? '#fff' : accentColor,
          border: `1px solid ${accentColor}`,
        }}
      >
        {plan.buttonText}
      </a>
    );
  };

  if (!plans || plans.length === 0) {
    return (
      <section key={`block-${index}`} className={`py-8 px-4 md:py-16 md:px-6 ${wrapperClass} ${animationClass}`} style={finalBlockStyle}>
        <div className="max-w-3xl mx-auto p-8 bg-slate-50 border-2 border-dashed border-slate-200 rounded-lg text-center">
          <p className="text-slate-400">Add a Pricing block to this page (Auto mode), or switch this block to Custom mode to add plans.</p>
        </div>
      </section>
    );
  }

  return (
    <section key={`block-${index}`} className={`py-8 px-4 md:py-16 md:px-6 ${wrapperClass} ${animationClass}`} style={finalBlockStyle}>
      <style>{`@keyframes pcPulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.015); } }`}</style>
      <div className="max-w-6xl mx-auto">
        {content.title && (
          <h2
            className="mb-3"
            style={{
              fontSize: ts.fontSize ? `clamp(1.5rem, 4vw, ${parseInt(ts.fontSize, 10) / 16}rem)` : 'clamp(1.875rem, 4vw, 2.25rem)',
              fontFamily: ts.fontFamily && ts.fontFamily !== 'inherit' ? `${ts.fontFamily}, sans-serif` : 'inherit',
              fontWeight: ts.fontWeight || 'bold',
              color: ts.color || 'var(--site-heading-color)',
              textAlign: ts.align || 'center',
            }}
          >
            {content.title}
          </h2>
        )}
        {content.subtitle && (
          <p className="text-center mb-10 opacity-70" style={{ color: 'var(--site-body-color)' }}>{content.subtitle}</p>
        )}

        {/* Plan Recommender Wizard */}
        {content.enableRecommender && (content.recommenderQuestions || []).length > 0 && plans.length > 0 && (
          <PlanRecommenderWizard
            title={content.recommenderTitle}
            subtitle={content.recommenderSubtitle}
            questions={content.recommenderQuestions || []}
            plans={plans}
            accentColor={accentColor}
            recommendedPlanId={recommendedPlanId}
            onRecommendation={setRecommendedPlanId}
          />
        )}

        {/* Desktop: table layout */}
        <div className="hidden md:block overflow-x-auto rounded-2xl border border-slate-200 bg-white">
          <table className="w-full text-left" data-testid={`plan-comparison-${block.id}`}>
            <thead>
              <tr className="border-b border-slate-200">
                <th className="p-4 align-bottom text-sm font-semibold text-slate-500 w-1/3">Feature</th>
                {plans.map((plan) => {
                  const isHL = plan.highlighted || isRecommended(plan.id);
                  return (
                    <th key={plan.id} className="p-4 align-bottom text-center transition-all" style={{ ...(isHL ? { backgroundColor: `${accentColor}0F` } : {}), ...pulseStyle(plan.id) }}>
                      {isRecommended(plan.id) && (
                        <span className="inline-block mb-2 px-2 py-0.5 rounded-full text-[11px] font-semibold animate-pulse" style={{ backgroundColor: accentColor, color: '#fff' }}>★ Recommended for You</span>
                      )}
                      {!isRecommended(plan.id) && plan.badge && (
                        <span className="inline-block mb-2 px-2 py-0.5 rounded-full text-[11px] font-semibold" style={{ backgroundColor: accentColor, color: '#fff' }}>{plan.badge}</span>
                      )}
                      <div className="font-semibold text-lg" style={{ color: 'var(--site-heading-color)' }}>{plan.name}</div>
                      {(plan.price || plan.period) && (
                        <div className="mt-1">
                          <span className="text-2xl font-bold" style={{ color: 'var(--site-heading-color)' }}>{plan.price}</span>
                          {plan.period && <span className="text-sm opacity-60 ml-1">{plan.period}</span>}
                        </div>
                      )}
                      {renderPlanButton(plan, isHL)}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIdx) => (
                <tr key={row.id || `${row.label}-${rowIdx}`} className={rowIdx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                  <td className="p-4 text-sm font-medium" style={{ color: 'var(--site-heading-color)' }}>{row.label}</td>
                  {plans.map((plan) => {
                    const isHL = plan.highlighted || isRecommended(plan.id);
                    return (
                      <td key={plan.id} className="p-4 text-center" style={isHL ? { backgroundColor: `${accentColor}0A` } : {}}>
                        {renderCell((row.values || {})[plan.id])}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile: stacked plan cards */}
        <div className="md:hidden space-y-6">
          {plans.map((plan) => {
            const isHL = plan.highlighted || isRecommended(plan.id);
            return (
              <div key={plan.id} className="rounded-xl border bg-white overflow-hidden" style={isHL ? { borderColor: accentColor, borderWidth: 2, ...pulseStyle(plan.id) } : { borderColor: '#e2e8f0' }}>
                <div className="p-5 text-center border-b border-slate-100" style={isHL ? { backgroundColor: `${accentColor}0F` } : {}}>
                  {isRecommended(plan.id) && (
                    <span className="inline-block mb-2 px-2 py-0.5 rounded-full text-[11px] font-semibold animate-pulse" style={{ backgroundColor: accentColor, color: '#fff' }}>★ Recommended for You</span>
                  )}
                  {!isRecommended(plan.id) && plan.badge && (
                    <span className="inline-block mb-2 px-2 py-0.5 rounded-full text-[11px] font-semibold" style={{ backgroundColor: accentColor, color: '#fff' }}>{plan.badge}</span>
                  )}
                  <div className="font-semibold text-lg" style={{ color: 'var(--site-heading-color)' }}>{plan.name}</div>
                  {(plan.price || plan.period) && (
                    <div className="mt-1">
                      <span className="text-2xl font-bold" style={{ color: 'var(--site-heading-color)' }}>{plan.price}</span>
                      {plan.period && <span className="text-sm opacity-60 ml-1">{plan.period}</span>}
                    </div>
                  )}
                  {renderPlanButton(plan, isHL)}
                </div>
                <ul className="p-4 space-y-2">
                  {rows.map((row) => (
                    <li key={row.id || row.label} className="flex items-center justify-between gap-3">
                      <span className="text-sm" style={{ color: 'var(--site-body-color)' }}>{row.label}</span>
                      <span>{renderCell((row.values || {})[plan.id])}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};



const API_URL = process.env.REACT_APP_BACKEND_URL;

// FAQ Accordion Block Component (own state for expanded item)
const FAQAccordion = ({ items = [], theme, accentColor, cardBg, bodyColor }) => {
  const [expanded, setExpanded] = useState(null);
  if (items.length === 0) {
    return (
      <div className="p-8 bg-slate-50 border-2 border-dashed border-slate-200 rounded-lg text-center">
        <p className="text-slate-400">No questions yet</p>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      {items.map((item) => {
        const isOpen = expanded === item.id;
        return (
          <div
            key={item.id}
            className="border rounded-lg overflow-hidden transition-colors"
            style={{
              backgroundColor: cardBg,
              borderColor: isOpen ? accentColor : '#e2e8f0',
            }}
          >
            <button
              type="button"
              className="w-full px-6 py-4 flex items-center justify-between text-left font-medium"
              style={{ color: bodyColor }}
              onClick={() => setExpanded(isOpen ? null : item.id)}
            >
              <span>{item.question}</span>
              {isOpen ? <ChevronUp size={20} style={{ color: accentColor }} /> : <ChevronDown size={20} style={{ color: accentColor }} />}
            </button>
            {isOpen && item.answer && (
              <div className="px-6 pb-4 opacity-80" style={{ color: bodyColor }}>
                {item.answer}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// Site Search Block Component
const SiteSearchBlock = ({ content, siteTheme, subdomain, wrapperClass, animationClass, blockStyle }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  
  const placeholder = content?.placeholder || 'Search pages and articles...';
  const buttonText = content?.buttonText || 'Search';
  const searchPages = content?.searchPages !== false;
  const searchArticles = content?.searchArticles !== false;
  
  const handleSearch = async () => {
    if (!query.trim() || !subdomain) return;
    
    setIsSearching(true);
    setHasSearched(true);
    
    const types = [];
    if (searchPages) types.push('pages');
    if (searchArticles) types.push('articles');
    
    try {
      const response = await fetch(`/api/public/tenant/${subdomain}/search?q=${encodeURIComponent(query)}&types=${types.join(',')}`);
      if (response.ok) {
        const data = await response.json();
        setResults(data.results || []);
      }
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsSearching(false);
    }
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };
  
  const primaryColor = siteTheme?.primary_color || '#4f46e5';
  
  return (
    <div className={`${wrapperClass} ${animationClass}`} style={blockStyle}>
      <div className="py-8">
        <div className="max-w-2xl mx-auto">
          {/* Search Input */}
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <SearchIcon 
                size={18} 
                className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" 
              />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                className="w-full pl-12 pr-4 py-3 border border-slate-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={handleSearch}
              disabled={isSearching}
              style={{ backgroundColor: primaryColor }}
              className="px-6 py-3 text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {isSearching ? 'Searching...' : buttonText}
            </button>
          </div>
          
          {/* Search Results */}
          {hasSearched && (
            <div className="mt-6">
              {results.length === 0 ? (
                <p className="text-center text-slate-500 py-8">
                  No results found for "{query}"
                </p>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-slate-500">{results.length} result{results.length !== 1 ? 's' : ''} found</p>
                  {results.map((result, idx) => (
                    <a
                      key={idx}
                      href={result.url}
                      className="block p-4 bg-white border border-slate-200 rounded-lg hover:border-indigo-300 hover:shadow-md transition-all"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span 
                          className="text-xs px-2 py-0.5 rounded-full font-medium"
                          style={{ 
                            backgroundColor: result.type === 'page' ? '#eff6ff' : '#fef3c7',
                            color: result.type === 'page' ? '#3b82f6' : '#d97706'
                          }}
                        >
                          {result.type === 'page' ? 'Page' : 'Article'}
                        </span>
                      </div>
                      <h3 className="font-semibold text-slate-800">{result.title}</h3>
                      {result.snippet && (
                        <p className="text-sm text-slate-500 mt-1 line-clamp-2">{result.snippet}</p>
                      )}
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Social Media Icons Component
const SocialIcon = ({ platform, size = 20, color = 'currentColor' }) => {
  const icons = {
    facebook: (
      <svg viewBox="0 0 24 24" width={size} height={size} fill={color}>
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>
    ),
    twitter: (
      <svg viewBox="0 0 24 24" width={size} height={size} fill={color}>
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
      </svg>
    ),
    instagram: (
      <svg viewBox="0 0 24 24" width={size} height={size} fill={color}>
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
      </svg>
    ),
    linkedin: (
      <svg viewBox="0 0 24 24" width={size} height={size} fill={color}>
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
      </svg>
    ),
    youtube: (
      <svg viewBox="0 0 24 24" width={size} height={size} fill={color}>
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
      </svg>
    ),
    bluesky: (
      <svg viewBox="0 0 600 530" width={size} height={size} fill={color}>
        <path d="M135.72 44.03C202.216 93.951 273.74 195.17 300 249.49c26.262-54.316 97.782-155.54 164.28-205.46C512.26 8.009 590-19.862 590 68.825c0 17.712-10.155 148.79-16.111 170.07-20.703 73.984-96.144 92.854-163.25 81.433 117.3 19.964 147.14 86.092 82.697 152.22-122.39 125.59-175.91-31.511-189.63-71.766-2.514-7.38-3.69-10.832-3.708-7.896-.017-2.936-1.193.516-3.707 7.896-13.714 40.255-67.233 197.36-189.63 71.766-64.444-66.128-34.605-132.26 82.697-152.22-67.108 11.421-142.55-7.449-163.25-81.433C20.15 217.613 10 86.536 10 68.824c0-88.687 77.742-60.816 125.72-24.795z"/>
      </svg>
    ),
    tiktok: (
      <svg viewBox="0 0 24 24" width={size} height={size} fill={color}>
        <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
      </svg>
    ),
    pinterest: (
      <svg viewBox="0 0 24 24" width={size} height={size} fill={color}>
        <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.401.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.354-.629-2.758-1.379l-.749 2.848c-.269 1.045-1.004 2.352-1.498 3.146 1.123.345 2.306.535 3.55.535 6.607 0 11.985-5.365 11.985-11.987C23.97 5.39 18.592.026 11.985.026L12.017 0z"/>
      </svg>
    ),
    github: (
      <svg viewBox="0 0 24 24" width={size} height={size} fill={color}>
        <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"/>
      </svg>
    ),
    email: (
      <svg viewBox="0 0 24 24" width={size} height={size} fill={color}>
        <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
      </svg>
    ),
    website: (
      <svg viewBox="0 0 24 24" width={size} height={size} fill={color}>
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
      </svg>
    )
  };
  
  return icons[platform] || icons.website;
};

// Helper functions for download links
const isDownloadLink = (url) => url && url.startsWith('download:');
const getDownloadUrl = (url) => url ? url.replace(/^download:/, '') : '';
const getFilenameFromUrl = (url) => {
  try {
    const cleanUrl = getDownloadUrl(url);
    const pathname = new URL(cleanUrl).pathname;
    return pathname.split('/').pop() || 'download';
  } catch {
    return 'download';
  }
};

// Helper function to generate anchor ID from block content (same as BlockEditor)
const generateAnchorId = (block) => {
  let text = '';
  
  if (block.type === 'heading') {
    text = block.content?.html?.replace(/<[^>]*>/g, '') || '';
  } else if (block.type === 'hero') {
    text = block.content?.headline || '';
  } else if (block.type === 'paragraph') {
    text = block.content?.html?.replace(/<[^>]*>/g, '').substring(0, 50) || '';
  } else if (block.type === 'quote') {
    text = block.content?.html?.replace(/<[^>]*>/g, '').substring(0, 50) || '';
  } else if (block.type === 'form') {
    text = block.content?.formName || 'form';
  } else if (block.type === 'gallery') {
    text = 'gallery';
  } else if (block.type === 'article-feed') {
    text = block.content?.heading || 'articles';
  }
  
  if (text) {
    return text.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .substring(0, 50) || block.type;
  }
  
  return `${block.type}-${block.id?.split('-').pop() || 'block'}`;
};

// Handle anchor link clicks for smooth scrolling
const handleAnchorClick = (e, href, currentPath = '') => {
  if (!href || !href.includes('#')) return false;
  
  // Parse the href to get page path and anchor
  const hashIndex = href.indexOf('#');
  const pagePath = href.substring(0, hashIndex);
  const anchorId = href.substring(hashIndex + 1);
  
  if (!anchorId) return false;
  
  // Get current page path (normalize to handle /home vs /)
  const currentPagePath = currentPath || window.location.pathname;
  const normalizedCurrent = currentPagePath === '/' ? '/home' : currentPagePath;
  const normalizedTarget = pagePath === '/' ? '/home' : (pagePath || '/home');
  
  // Check if we're on the same page or target is home page and we're on root
  const isSamePage = normalizedCurrent === normalizedTarget || 
                     (pagePath === '' && currentPagePath.endsWith(normalizedCurrent.split('/').pop())) ||
                     (pagePath === '/home' && (currentPagePath === '/' || currentPagePath === '/home'));
  
  if (isSamePage || pagePath === '' || href.startsWith('#')) {
    e.preventDefault();
    const element = document.getElementById(anchorId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Update URL hash without jumping
      window.history.pushState(null, '', `#${anchorId}`);
      return true;
    }
  }
  
  // If different page, let the browser navigate but add hash
  return false;
};

// Utility to strip inline color styles from HTML when using site theme
// Helper functions to extract video IDs
const getYouTubeVideoId = (url) => {
  if (!url) return '';
  // Handle youtube-nocookie.com embed URLs
  if (url.includes('youtube-nocookie.com')) {
    const match = url.match(/embed\/([^?&#]+)/);
    return match ? match[1] : '';
  }
  // Handle regular youtube.com and youtu.be URLs
  const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[7].length === 11) ? match[7] : '';
};

const getVimeoVideoId = (url) => {
  if (!url) return '';
  const regExp = /vimeo.*\/(\d+)/i;
  const match = url.match(regExp);
  return match ? match[1] : '';
};

const stripInlineColorStyles = (html, shouldStrip) => {
  if (!shouldStrip || !html) return html;
  
  // First, preserve mark tags with their background colors by replacing them temporarily
  const markTags = [];
  let preservedHtml = html.replace(/<mark([^>]*)style="([^"]*)"([^>]*)>/gi, (match, before, styleContent, after) => {
    const placeholder = `__MARK_PLACEHOLDER_${markTags.length}__`;
    markTags.push(match);
    return placeholder;
  });
  
  // Also preserve span tags with color styles (user-set text colors from the editor)
  const spanColorTags = [];
  preservedHtml = preservedHtml.replace(/<span([^>]*)style="([^"]*color[^"]*)"([^>]*)>/gi, (match, before, styleContent, after) => {
    const placeholder = `__SPAN_COLOR_PLACEHOLDER_${spanColorTags.length}__`;
    spanColorTags.push(match);
    return placeholder;
  });
  
  // Remove color and background-color from inline styles on other elements (like p, div, etc.)
  // This allows site theme to control default text color while preserving user-set colors on spans
  preservedHtml = preservedHtml.replace(/style="([^"]*)"/gi, (match, styleContent) => {
    // Remove color: and background-color: properties from non-span elements
    const cleanedStyle = styleContent
      .split(';')
      .filter(prop => {
        const trimmed = prop.trim().toLowerCase();
        // Keep the property if it's NOT a color property
        return !trimmed.startsWith('color:') && 
               !trimmed.startsWith('background-color:') &&
               !trimmed.startsWith('background:');
      })
      .join(';')
      .trim();
    
    // If no styles left, remove the style attribute entirely
    if (!cleanedStyle) return '';
    return `style="${cleanedStyle}"`;
  });
  
  // Restore mark tags with their original styles
  markTags.forEach((markTag, index) => {
    preservedHtml = preservedHtml.replace(`__MARK_PLACEHOLDER_${index}__`, markTag);
  });
  
  // Restore span color tags with their original styles
  spanColorTags.forEach((spanTag, index) => {
    preservedHtml = preservedHtml.replace(`__SPAN_COLOR_PLACEHOLDER_${index}__`, spanTag);
  });
  
  return preservedHtml;
};

// ============================================================================
// Responsive font-size helper
// Wraps a fixed pixel size in CSS clamp() so it gracefully shrinks on phones.
// • At ≥1280px viewport → returns the user's chosen size unchanged.
// • At 360px viewport → shrinks to ~45% of the chosen size, never below 14px
//   and never below floor (so a 24px stays 24px, but an 80px becomes ~36px).
// • Returns `undefined` for invalid input so the caller can decide a fallback.
// ============================================================================
const responsiveFontSize = (size) => {
  const n = parseFloat(size);
  if (!Number.isFinite(n) || n <= 0) return undefined;
  // Small sizes (<= 20px) don't need responsive shrinking — they're already
  // appropriately mobile-sized. Pass through unchanged.
  if (n <= 20) return `${n}px`;
  // Floor: never smaller than 14px (or 45% of original, whichever is bigger).
  const minPx = Math.max(14, Math.round(n * 0.45));
  // vw rate: how many vw equals the original size at a 1280px viewport.
  // e.g. 80px / 12.8 = 6.25vw — at 1280px that's exactly 80px, at 360px = 22.5px
  // which gets clamped to the minPx floor.
  const vwRate = (n / 12.8).toFixed(2);
  return `clamp(${minPx}px, ${vwRate}vw, ${n}px)`;
};

// ============================================================================
// clampInlineFontSizes — rewrites any inline `font-size: NNpx` style inside an
// HTML string to use a responsive `clamp()` formula. The WYSIWYG editor
// (TipTap) lets users set explicit pixel sizes via the font-size dropdown;
// without this post-processor those pixel values render at their full size on
// mobile and overflow narrow blocks (e.g. an image+text carousel slide where
// the text column shrinks to ~150px on a 360px-wide phone).
//
// Why a regex instead of a proper parser? The HTML is short, controlled by
// our editor, and we only care about one CSS property. A DOM-based rewrite
// would require parsing in a virtual document and re-serialising — far more
// expensive on every render.
//
// For NARROW container scenarios (e.g. carousel slides where the column is
// ~half the viewport), pass `tighter: true` to shrink the floor aggressively
// so 36px doesn't crash-land at 18px in a 150px-wide column — we want it
// closer to 13–15px on phones, scaling up smoothly to the original size on
// desktop.
// ============================================================================
const clampInlineFontSizes = (html, opts = {}) => {
  if (!html || typeof html !== 'string') return html;
  const tighter = Boolean(opts.tighter);
  return html.replace(/font-size\s*:\s*(\d+(?:\.\d+)?)\s*px/gi, (match, sizeStr) => {
    const n = parseFloat(sizeStr);
    if (!Number.isFinite(n) || n <= 16) return match; // leave small sizes alone
    // Floor: 14px is the WCAG-friendly minimum for body text on mobile. We
    // keep it stable in both modes so 24px body never crashes to 12px.
    const floorRatio = tighter ? 0.34 : 0.45;
    const minFloor = 14;
    const minPx = Math.max(minFloor, Math.round(n * floorRatio));
    // Reference viewport: 1280px desktop = original size in standard mode.
    // Tighter mode uses 960px so narrow columns (half of a 1920px viewport)
    // hit the desktop ceiling sooner and shrink faster on phones.
    const refViewport = tighter ? 9.6 : 12.8;
    const vwRate = (n / refViewport).toFixed(2);
    return `font-size: clamp(${minPx}px, ${vwRate}vw, ${n}px)`;
  });
};

// ============================================================================
// useSwipe — tiny touch-swipe hook for mobile carousel navigation.
// Returns event handlers to spread onto a touchable container. Triggers
// `onSwipeLeft` after a swipe from right→left that exceeds `threshold` (px),
// and `onSwipeRight` after a swipe from left→right.
//
// Used by HeroCarousel + image-text-carousel. Side chevrons are hidden on
// mobile (see md:flex), so swipe is the primary navigation gesture there.
// Vertical scrolls are detected and ignored so we never hijack the page's
// natural scroll direction.
// ============================================================================
const useSwipe = (onSwipeLeft, onSwipeRight, threshold = 50) => {
  const startRef = React.useRef({ x: 0, y: 0, t: 0 });
  return {
    onTouchStart: (e) => {
      const t = e.touches[0];
      startRef.current = { x: t.clientX, y: t.clientY, t: Date.now() };
    },
    onTouchEnd: (e) => {
      const c = e.changedTouches[0];
      const dx = c.clientX - startRef.current.x;
      const dy = c.clientY - startRef.current.y;
      const dt = Date.now() - startRef.current.t;
      // Ignore taps, slow drags, and primarily-vertical swipes (= page scroll).
      if (dt > 600) return;
      if (Math.abs(dx) < threshold) return;
      if (Math.abs(dy) > Math.abs(dx)) return;
      if (dx < 0 && onSwipeLeft) onSwipeLeft();
      else if (dx > 0 && onSwipeRight) onSwipeRight();
    },
  };
};

const HeroCarousel = ({ 
  slides, overlay, overlayStyle, textColorClass, defaultTextColor, textPositionH, textPositionV,
  height, widthStyle, isFullWidth, autoPlay, autoPlaySpeed, transitionEffect,
  wrapperClass, blockStyle, pages = []
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  
  // Helper to resolve any URL - handles internal page links in both preview and live mode
  const resolveUrl = (url) => {
    if (!url || url === '#') return '#';
    // Handle download links - strip prefix and return raw URL
    if (url.startsWith('download:')) return url.substring(9);
    // mailto: and tel: are native browser protocols — pass through untouched.
    if (url.startsWith('mailto:') || url.startsWith('tel:')) return url;
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    if (url.startsWith('#')) return url;
    
    // Handle anchor links with page slug (e.g., /page-slug#anchor or page-slug#anchor)
    if (url.includes('#')) {
      const [pathPart, anchorPart] = url.split('#');
      const slug = pathPart.startsWith('/') ? pathPart.substring(1) : pathPart;
      
      if (slug && pages && pages.length > 0) {
        const page = pages.find(p => p.slug === slug);
        if (page && page.preview_token && isInPreviewMode()) {
          return `/preview/${page.preview_token}#${anchorPart}`;
        }
      }
      // Fallback - return with proper formatting
      return pathPart.startsWith('/') ? url : `/${url}`;
    }
    
    // Internal page links - handle both /slug and slug formats
    const slug = url.startsWith('/') ? url.substring(1) : url;
    if (pages && pages.length > 0) {
      const page = pages.find(p => p.slug === slug);
      if (page && page.preview_token && isInPreviewMode()) return `/preview/${page.preview_token}`;
    }
    // Return with leading slash for proper routing
    return url.startsWith('/') ? url : `/${url}`;
  };
  
  // Auto-play functionality
  React.useEffect(() => {
    if (!autoPlay || slides.length <= 1) return;
    
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setActiveIndex(prev => (prev + 1) % slides.length);
        setIsAnimating(false);
      }, 300);
    }, autoPlaySpeed);
    
    return () => clearInterval(interval);
  }, [autoPlay, autoPlaySpeed, slides.length]);
  
  const goToSlide = (index) => {
    if (index === activeIndex) return;
    setIsAnimating(true);
    setTimeout(() => {
      setActiveIndex(index);
      setIsAnimating(false);
    }, 300);
  };
  
  const goToPrev = () => goToSlide(activeIndex > 0 ? activeIndex - 1 : slides.length - 1);
  const goToNext = () => goToSlide((activeIndex + 1) % slides.length);
  // Touch swipe — primary navigation on mobile (side chevrons hidden < md).
  const heroSwipeHandlers = useSwipe(goToNext, goToPrev);
  
  const currentSlide = slides[activeIndex];

  // Resolve the text colour for the current slide.
  // Priority: slide.textColor (hex) → defaultTextColor (hex/legacy) → tailwind class.
  const isHex = (v) => typeof v === 'string' && /^#[0-9a-fA-F]{3,8}$/.test(v.trim());
  const resolvedSlideColor = (currentSlide && isHex(currentSlide.textColor))
    ? currentSlide.textColor
    : (isHex(defaultTextColor) ? defaultTextColor : null);
  // If we have a hex colour, drop the legacy tailwind class and use inline style instead.
  const effectiveTextColorClass = resolvedSlideColor ? '' : textColorClass;
  const slideColorStyle = resolvedSlideColor ? { color: resolvedSlideColor } : {};
  
  const getTextAnimClass = (anim) => {
    if (!anim || anim.entrance === 'none') return '';
    return `animate animate-${anim.entrance} animate-${anim.speed || 'normal'} ${anim.delay !== '0' ? `animate-delay-${anim.delay}` : ''}`;
  };
  
  const getTransitionClass = () => {
    if (!isAnimating) return 'opacity-100';
    switch (transitionEffect) {
      case 'fade': return 'opacity-0';
      case 'slide': return 'opacity-0 -translate-x-full';
      case 'zoom': return 'opacity-0 scale-95';
      default: return 'opacity-0';
    }
  };
  
  return (
    <div 
      className={`relative overflow-hidden ${isFullWidth ? '' : 'mb-8'} ${wrapperClass} hero-responsive`}
      style={{ ...blockStyle, borderRadius: blockStyle?.borderRadius || (isFullWidth ? undefined : '12px'), overflow: 'hidden', minHeight: `${height}px`, ...widthStyle, touchAction: 'pan-y' }}
      {...heroSwipeHandlers}
    >
      {/* Background - Video, Color or Image with Transition */}
      <div className={`absolute inset-0 transition-all duration-500 ${getTransitionClass()} overflow-hidden`}>
        {currentSlide?.backgroundType === 'video' && currentSlide?.videoUrl ? (
          <>
            {currentSlide.videoUrl.includes('youtube.com') || currentSlide.videoUrl.includes('youtu.be') || currentSlide.videoUrl.includes('youtube-nocookie.com') ? (
              <div className="absolute inset-0 overflow-hidden">
                <iframe
                  src={`https://www.youtube.com/embed/${getYouTubeVideoId(currentSlide.videoUrl)}?autoplay=1&mute=${currentSlide.videoMuted !== false ? 1 : 0}&loop=1&playlist=${getYouTubeVideoId(currentSlide.videoUrl)}&controls=1&showinfo=0&modestbranding=1&rel=0`}
                  className="absolute inset-0 w-full h-full"
                  allow="autoplay; encrypted-media; fullscreen"
                  allowFullScreen
                  title="Video"
                />
              </div>
            ) : currentSlide.videoUrl.includes('vimeo.com') ? (
              <div className="absolute inset-0 overflow-hidden">
                <iframe
                  src={`https://player.vimeo.com/video/${getVimeoVideoId(currentSlide.videoUrl)}?autoplay=1&muted=${currentSlide.videoMuted !== false ? 1 : 0}&loop=1`}
                  className="absolute inset-0 w-full h-full"
                  allow="autoplay; fullscreen"
                  allowFullScreen
                  title="Video"
                />
              </div>
            ) : (
              <video
                src={currentSlide.videoUrl}
                autoPlay
                muted={currentSlide.videoMuted !== false}
                loop={currentSlide.videoLoop !== false}
                playsInline
                className="absolute inset-0 w-full h-full object-cover"
              />
            )}
          </>
        ) : currentSlide?.useBackgroundColor ? (
          <div 
            className="absolute inset-0"
            style={{ backgroundColor: currentSlide.backgroundColor || '#6366f1' }}
          />
        ) : currentSlide?.imageUrl ? (
          <img 
            src={currentSlide.imageUrl} 
            alt="Hero background"
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 to-purple-700" />
        )}
      </div>
      
      {/* Overlay */}
      {overlay !== 'none' && (
        <div className="absolute inset-0" style={overlayStyle} />
      )}
      
      {/* Content with Animations */}
      <div 
        key={activeIndex}
        className={`
          absolute inset-0 px-6 pt-8 pb-20 md:p-12 flex flex-col transition-opacity duration-300
          ${textPositionH === 'left' ? 'items-start text-left' : ''}
          ${textPositionH === 'center' ? 'items-center text-center' : ''}
          ${textPositionH === 'right' ? 'items-end text-right' : ''}
          ${textPositionV === 'top' ? 'justify-start' : ''}
          ${textPositionV === 'middle' ? 'justify-center' : ''}
          ${textPositionV === 'bottom' ? 'justify-end' : ''}
          ${effectiveTextColorClass}
          ${isAnimating ? 'opacity-0' : 'opacity-100'}
        `}
        style={slideColorStyle}
      >
        {currentSlide?.headline && (
          <h2 
            className={`text-4xl md:text-5xl lg:text-6xl font-bold mb-4 w-full max-w-3xl ${getTextAnimClass(currentSlide.headlineAnimation)}`}
            style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.3)', ...(currentSlide.headlineStyle || {}) }}
          >
            {currentSlide.headline}
          </h2>
        )}
        {currentSlide?.subheadline && (
          <p 
            className={`text-xl md:text-2xl mb-8 w-full max-w-2xl opacity-90 ${getTextAnimClass(currentSlide.subheadlineAnimation)}`}
            style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.3)', ...(currentSlide.subheadlineStyle || {}) }}
          >
            {currentSlide.subheadline}
          </p>
        )}
        {currentSlide?.buttonText && (
          <a 
            href={resolveUrl(currentSlide.buttonUrl) || '#'}
            download={isDownloadLink(currentSlide.buttonUrl) ? getFilenameFromUrl(currentSlide.buttonUrl) : undefined}
            target={currentSlide.buttonOpenInNewTab ? '_blank' : undefined}
            rel={currentSlide.buttonOpenInNewTab ? 'noopener noreferrer' : undefined}
            className={`inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-slate-900 rounded-lg font-semibold text-lg hover:bg-slate-100 transition-all transform hover:scale-105 shadow-lg ${getTextAnimClass(currentSlide.buttonAnimation)}`}
            style={flatRadiusStyle(currentSlide)}
          >
            {flatHasIcon(currentSlide) && flatIconPos(currentSlide) === 'left' && flatIcon(currentSlide, 20)}
            {currentSlide.buttonText}
            {flatHasIcon(currentSlide) && flatIconPos(currentSlide) === 'right' && flatIcon(currentSlide, 20)}
          </a>
        )}
      </div>
      
      {/* Navigation Arrows — hidden on mobile (rely on swipe + dot indicators
          below). Bumped z-index to 20 so they always sit above slide content
          like CTA buttons on tablet/desktop. Reported on horizonglobalacademy.com:
          left/right chevrons overlapped the centered "Apply Now" CTA on phones. */}
      <button 
        onClick={goToPrev}
        className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/30 hover:bg-black/50 rounded-full items-center justify-center text-white transition-all z-20"
        aria-label="Previous slide"
      >
        <ChevronLeft size={24} />
      </button>
      <button 
        onClick={goToNext}
        className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/30 hover:bg-black/50 rounded-full items-center justify-center text-white transition-all z-20"
        aria-label="Next slide"
      >
        <ChevronRight size={24} />
      </button>
      
      {/* Slide Indicators — smaller dots + tighter gap on mobile.
          z-30 so they always render above the slide CTA (which can be a
          `<a>` with inline transitions that creates its own stacking context).
          Earlier inconsistency: active dot was wider so it visually overlapped
          the CTA while smaller dots didn't — reported as "Apply Now sits
          behind the active dot but in front of the others". */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1.5 md:gap-2 z-30">
        {slides.map((_, idx) => (
          <button
            key={idx}
            onClick={() => goToSlide(idx)}
            aria-label={`Go to slide ${idx + 1}`}
            className={`h-1.5 md:h-2 rounded-full transition-all ${
              idx === activeIndex 
                ? 'w-6 md:w-8 bg-white' 
                : 'w-1.5 md:w-2 bg-white/50 hover:bg-white/70'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

// Image+Text Carousel Component
const ImageTextCarousel = ({ 
  slides, autoPlay, autoPlaySpeed, showDots, showArrows, 
  wrapperClass, animationClass, blockStyle, processHtml, blockWidth, pages = [],
  isQuickPreview = false
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  
  // Helper to resolve any URL - handles internal page links in both preview and live mode
  const resolveUrl = (url) => {
    if (!url || url === '#') return '#';
    // Handle download links - strip prefix and return raw URL
    if (url.startsWith('download:')) return url.substring(9);
    // mailto: and tel: are native browser protocols — pass through untouched.
    if (url.startsWith('mailto:') || url.startsWith('tel:')) return url;
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    if (url.startsWith('#')) return url;
    
    // Handle anchor links with page slug (e.g., /page-slug#anchor or page-slug#anchor)
    if (url.includes('#')) {
      const [pathPart, anchorPart] = url.split('#');
      const slug = pathPart.startsWith('/') ? pathPart.substring(1) : pathPart;
      
      if (slug && pages && pages.length > 0) {
        const page = pages.find(p => p.slug === slug);
        if (page && page.preview_token && isInPreviewMode()) {
          return `/preview/${page.preview_token}#${anchorPart}`;
        }
      }
      // Fallback - return with proper formatting
      return pathPart.startsWith('/') ? url : `/${url}`;
    }
    
    // Internal page links - handle both /slug and slug formats
    const slug = url.startsWith('/') ? url.substring(1) : url;
    if (pages && pages.length > 0) {
      const page = pages.find(p => p.slug === slug);
      if (page && page.preview_token && isInPreviewMode()) return `/preview/${page.preview_token}`;
    }
    // Return with leading slash for proper routing
    return url.startsWith('/') ? url : `/${url}`;
  };
  
  // Block width styling
  const isFullWidth = blockWidth === 'full';
  const getBlockWidthStyle = () => {
    switch (blockWidth) {
      case 'full': return isQuickPreview ? { width: '100%', maxWidth: 'none' } : { 
        width: '100vw', 
        maxWidth: 'none',
        marginLeft: 'calc(-50vw + 50%)',
        marginRight: 'calc(-50vw + 50%)',
        paddingLeft: '1rem',
        paddingRight: '1rem',
      };
      case 'large': return { width: '100%', maxWidth: '1200px', margin: '0 auto' };
      case 'medium': return { width: '100%', maxWidth: '900px', margin: '0 auto' };
      case 'small': return { width: '100%', maxWidth: '600px', margin: '0 auto' };
      default: return { width: '100%', maxWidth: 'none' }; // container
    }
  };
  const widthStyle = getBlockWidthStyle();
  
  // Auto-play functionality
  React.useEffect(() => {
    if (!autoPlay || slides.length <= 1) return;
    
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setActiveIndex(prev => (prev + 1) % slides.length);
        setIsAnimating(false);
      }, 300);
    }, autoPlaySpeed);
    
    return () => clearInterval(interval);
  }, [autoPlay, autoPlaySpeed, slides.length]);
  
  const goToSlide = (index) => {
    if (index === activeIndex) return;
    setIsAnimating(true);
    setTimeout(() => {
      setActiveIndex(index);
      setIsAnimating(false);
    }, 300);
  };
  
  const goToPrev = () => goToSlide(activeIndex > 0 ? activeIndex - 1 : slides.length - 1);
  const goToNext = () => goToSlide((activeIndex + 1) % slides.length);
  // Touch swipe — primary navigation on mobile (chevrons hidden < md).
  const carouselSwipeHandlers = useSwipe(goToNext, goToPrev);
  
  const currentSlide = slides[activeIndex];
  const imageOnLeft = currentSlide.imagePosition === 'left';
  
  // Get image container style for current slide
  const getImageContainerStyle = (slide) => {
    const shapeMask = slide.shapeMask || 'none';
    const shapeSize = slide.shapeSize || 250;
    const imageCorners = slide.imageCorners || 'rounded';
    
    // Determine border radius based on corners setting (only applies when no shape mask)
    const getCornerRadius = () => {
      if (shapeMask === 'circle' || shapeMask === 'ellipse') return '50%';
      if (shapeMask === 'rounded') return '20px';
      if (shapeMask !== 'none') return '0';
      // No shape mask - use corners setting
      switch (imageCorners) {
        case 'square': return '0';
        case 'pill': return '9999px';
        case 'rounded':
        default: return '8px';
      }
    };
    
    return {
      width: shapeMask !== 'none'
        ? `${shapeSize}px`
        : (slide.imageSizeMode === 'custom' ? `${slide.imageWidth || 300}px` : 
           slide.imageSizeMode === 'height' ? 'auto' : `${slide.imageWidth || 300}px`),
      height: shapeMask !== 'none'
        ? (shapeMask === 'ellipse' ? `${shapeSize * 1.25}px` : `${shapeSize}px`)
        : (slide.imageSizeMode === 'custom' ? `${slide.imageHeight || 200}px` :
           slide.imageSizeMode === 'height' ? `${slide.imageHeight || 200}px` : 'auto'),
      maxWidth: '100%',
      borderRadius: getCornerRadius(),
      clipPath: shapeMask === 'diamond' ? 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)' :
               shapeMask === 'hexagon' ? 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)' :
               shapeMask === 'octagon' ? 'polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%)' :
               shapeMask === 'star' ? 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)' :
               shapeMask === 'arch' ? 'ellipse(50% 100% at 50% 100%)' : 'none',
      border: slide.shapeBorderWidth && shapeMask !== 'none'
        ? `${slide.shapeBorderWidth}px solid ${slide.shapeBorderColor || '#ffffff'}` : 'none',
      overflow: 'hidden',
      ...(slide.paddingSize ? {
        paddingTop: (slide.paddingSide === 'all' || slide.paddingSide === 'top' || !slide.paddingSide) ? `${slide.paddingSize}px` : '0',
        paddingRight: (slide.paddingSide === 'all' || slide.paddingSide === 'right' || !slide.paddingSide) ? `${slide.paddingSize}px` : '0',
        paddingBottom: (slide.paddingSide === 'all' || slide.paddingSide === 'bottom' || !slide.paddingSide) ? `${slide.paddingSize}px` : '0',
        paddingLeft: (slide.paddingSide === 'all' || slide.paddingSide === 'left' || !slide.paddingSide) ? `${slide.paddingSize}px` : '0',
      } : {}),
    };
  };
  
  const renderSlideImage = (slide) => {
    const containerStyle = getImageContainerStyle(slide);
    return (
      <div style={{ textAlign: slide.imageAlign || 'center', minWidth: 0, maxWidth: '100%' }}>
        <div className="inline-block" style={{ ...containerStyle, maxWidth: '100%' }}>
          <img 
            src={slide.imageUrl} 
            alt={slide.imageAlt || ''} 
            className="w-full h-full object-cover"
            style={{ borderRadius: containerStyle.borderRadius || '8px', maxWidth: '100%' }}
          />
        </div>
      </div>
    );
  };
  
  return (
    <div
      className={`relative mb-8 ${wrapperClass} ${animationClass} overflow-hidden`}
      style={{ ...blockStyle, ...widthStyle, touchAction: 'pan-y' }}
      {...carouselSwipeHandlers}
    >
      {/* Carousel Content */}
      <div className={`grid grid-cols-1 md:grid-cols-2 gap-8 items-center transition-opacity duration-300 ${isAnimating ? 'opacity-0' : 'opacity-100'}`}>
        {imageOnLeft && currentSlide.imageUrl && renderSlideImage(currentSlide)}
        <div className="space-y-4 min-w-0">
          <div
            dangerouslySetInnerHTML={{ __html: clampInlineFontSizes(processHtml(currentSlide.textHtml || '<p>Text content</p>'), { tighter: true }) }}
            className="leading-relaxed image-text-slide-content"
            style={{
              textAlign: currentSlide.textAlign || 'left',
              color: currentSlide.textColor || '#475569',
              fontSize: responsiveFontSize(currentSlide.textSize || 18),
              fontFamily: currentSlide.fontFamily || 'inherit',
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
              minWidth: 0,
            }}
          />
          <style>{slideContentTypographyCss()}</style>
          {/* Slide Button */}
          {currentSlide.hasButton && (
            <div style={{ textAlign: currentSlide.buttonAlign || 'left', marginTop: '1rem' }}>
              <a
                href={resolveUrl(currentSlide.buttonUrl) || '#'}
                download={isDownloadLink(currentSlide.buttonUrl) ? getFilenameFromUrl(currentSlide.buttonUrl) : undefined}
                target={currentSlide.buttonOpenInNewTab ? '_blank' : undefined}
                rel={currentSlide.buttonOpenInNewTab ? 'noopener noreferrer' : undefined}
                className={`inline-flex items-center justify-center gap-2 ${currentSlide.buttonSize === 'sm' ? 'px-3 py-1 text-sm' : currentSlide.buttonSize === 'lg' ? 'px-6 py-3 text-lg' : 'px-4 py-2'} ${currentSlide.buttonFullWidth ? 'w-full' : ''} font-medium transition-all ${getButtonHoverClass(currentSlide.buttonHover || 'fade')}`}
                style={getFlatButtonVariantStyle(currentSlide)}
              >
                {flatHasIcon(currentSlide) && flatIconPos(currentSlide) === 'left' && flatIcon(currentSlide, currentSlide.buttonSize === 'lg' ? 20 : 16)}
                {currentSlide.buttonText || 'Learn More'}
                {flatHasIcon(currentSlide) && flatIconPos(currentSlide) === 'right' && flatIcon(currentSlide, currentSlide.buttonSize === 'lg' ? 20 : 16)}
              </a>
            </div>
          )}
        </div>
        {!imageOnLeft && currentSlide.imageUrl && renderSlideImage(currentSlide)}
      </div>
      
      {/* Navigation Arrows — hidden on mobile (rely on swipe + dot indicators
          to avoid overlapping centered slide content / CTAs on narrow viewports). */}
      {showArrows && slides.length > 1 && (
        <>
          <button 
            onClick={goToPrev}
            className="hidden md:flex absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-slate-100 hover:bg-slate-200 rounded-full items-center justify-center text-slate-600 transition-all z-20 shadow-md"
            aria-label="Previous slide"
          >
            <ChevronLeft size={20} />
          </button>
          <button 
            onClick={goToNext}
            className="hidden md:flex absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-slate-100 hover:bg-slate-200 rounded-full items-center justify-center text-slate-600 transition-all z-20 shadow-md"
            aria-label="Next slide"
          >
            <ChevronRight size={20} />
          </button>
        </>
      )}
      
      {/* Slide Indicators */}
      {showDots && slides.length > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          {slides.map((_, idx) => (
            <button
              key={idx}
              onClick={() => goToSlide(idx)}
              className={`h-2 rounded-full transition-all ${
                idx === activeIndex 
                  ? 'w-8 bg-indigo-600' 
                  : 'w-2 bg-slate-300 hover:bg-slate-400'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Gallery Lightbox Component
const GalleryWithLightbox = ({ images, enableLightbox, blockStyle, wrapperClass, renderGallery }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  
  const openLightbox = (index) => {
    if (enableLightbox) {
      setLightboxIndex(index);
      setLightboxOpen(true);
    }
  };
  
  const closeLightbox = () => setLightboxOpen(false);
  
  const goToPrev = React.useCallback(() => setLightboxIndex(prev => (prev > 0 ? prev - 1 : images.length - 1)), [images.length]);
  const goToNext = React.useCallback(() => setLightboxIndex(prev => (prev < images.length - 1 ? prev + 1 : 0)), [images.length]);
  
  // Handle keyboard navigation
  React.useEffect(() => {
    if (!lightboxOpen) return;
    
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') goToPrev();
      if (e.key === 'ArrowRight') goToNext();
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, goToPrev, goToNext]);
  
  return (
    <>
      <div className={`mb-8 ${wrapperClass}`} style={blockStyle}>
        {renderGallery(openLightbox)}
      </div>
      
      {/* Lightbox Modal */}
      {lightboxOpen && (
        <div 
          className="fixed inset-0 z-[70] bg-black/90 flex items-center justify-center"
          onClick={closeLightbox}
        >
          {/* Close Button */}
          <button 
            className="absolute top-4 right-4 text-white hover:text-gray-300 z-[70]"
            onClick={closeLightbox}
          >
            <X size={32} />
          </button>
          
          {/* Navigation Arrows */}
          {images.length > 1 && (
            <>
              <button 
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/20 hover:bg-white/40 rounded-full flex items-center justify-center text-white transition-all"
                onClick={(e) => { e.stopPropagation(); goToPrev(); }}
              >
                <ChevronLeft size={28} />
              </button>
              <button 
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/20 hover:bg-white/40 rounded-full flex items-center justify-center text-white transition-all"
                onClick={(e) => { e.stopPropagation(); goToNext(); }}
              >
                <ChevronRight size={28} />
              </button>
            </>
          )}
          
          {/* Main Image */}
          <div 
            className="max-w-[90vw] max-h-[85vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img 
              src={images[lightboxIndex]?.url} 
              alt={images[lightboxIndex]?.alt || ''} 
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
            />
          </div>
          
          {/* Image Counter */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm bg-black/50 px-4 py-2 rounded-full">
            {lightboxIndex + 1} / {images.length}
          </div>
          
          {/* Caption if available */}
          {images[lightboxIndex]?.caption && (
            <div className="absolute bottom-16 left-1/2 -translate-x-1/2 text-white text-center max-w-2xl px-4">
              {images[lightboxIndex].caption}
            </div>
          )}
        </div>
      )}
    </>
  );
};

// Featured Gallery Component with interactive thumbnails
const FeaturedGallery = ({ images, gap, radius, thumbnailPosition, blockStyle, wrapperClass, animationClass, enableLightbox = true }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [thumbStart, setThumbStart] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  
  const isBottom = thumbnailPosition === 'bottom';
  const visibleThumbs = isBottom ? 6 : 4;
  const maxThumbStart = Math.max(0, images.length - visibleThumbs);
  
  const activeImage = images[activeIndex] || images[0];
  
  const openLightbox = () => {
    if (enableLightbox) setLightboxOpen(true);
  };
  
  const closeLightbox = () => setLightboxOpen(false);
  
  const goToPrev = React.useCallback(() => setActiveIndex(prev => (prev > 0 ? prev - 1 : images.length - 1)), [images.length]);
  const goToNext = React.useCallback(() => setActiveIndex(prev => (prev < images.length - 1 ? prev + 1 : 0)), [images.length]);
  
  // Handle keyboard navigation in lightbox
  React.useEffect(() => {
    if (!lightboxOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') goToPrev();
      if (e.key === 'ArrowRight') goToNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, goToPrev, goToNext]);
  
  const handlePrevThumbs = () => {
    setThumbStart(prev => Math.max(0, prev - 1));
  };
  
  const handleNextThumbs = () => {
    setThumbStart(prev => Math.min(maxThumbStart, prev + 1));
  };
  
  const visibleThumbnails = images.slice(thumbStart, thumbStart + visibleThumbs);
  const canGoPrev = thumbStart > 0;
  const canGoNext = thumbStart < maxThumbStart;
  
  // Lightbox modal for featured gallery
  const renderLightbox = () => lightboxOpen && (
    <div 
      className="fixed inset-0 z-[70] bg-black/90 flex items-center justify-center"
      onClick={closeLightbox}
    >
      <button className="absolute top-4 right-4 text-white hover:text-gray-300 z-[70]" onClick={closeLightbox}>
        <X size={32} />
      </button>
      {images.length > 1 && (
        <>
          <button 
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/20 hover:bg-white/40 rounded-full flex items-center justify-center text-white"
            onClick={(e) => { e.stopPropagation(); goToPrev(); }}
          >
            <ChevronLeft size={28} />
          </button>
          <button 
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/20 hover:bg-white/40 rounded-full flex items-center justify-center text-white"
            onClick={(e) => { e.stopPropagation(); goToNext(); }}
          >
            <ChevronRight size={28} />
          </button>
        </>
      )}
      <img 
        src={activeImage?.url} 
        alt={activeImage?.alt || ''} 
        className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg"
        onClick={(e) => e.stopPropagation()}
      />
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm bg-black/50 px-4 py-2 rounded-full">
        {activeIndex + 1} / {images.length}
      </div>
    </div>
  );
  
  if (isBottom) {
    // Bottom layout
    return (
      <>
        <div className={`mb-8 ${wrapperClass} ${animationClass}`} style={blockStyle}>
          {/* Main Image - Click to enlarge */}
          <div 
            style={{ borderRadius: `${radius}px`, overflow: 'hidden' }} 
            className="mb-3 cursor-pointer hover:opacity-95 transition-opacity"
            onClick={openLightbox}
          >
            <img 
              src={activeImage?.url} 
              alt={activeImage?.alt || ''} 
              className="w-full h-96 object-cover transition-all duration-300"
            />
          </div>
        
        {/* Thumbnails Row with Navigation */}
        <div className="relative">
          {canGoPrev && (
            <button 
              onClick={handlePrevThumbs}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white/90 hover:bg-white shadow-md rounded-full flex items-center justify-center transition-all"
            >
              <ChevronLeft size={18} className="text-slate-700" />
            </button>
          )}
          
          <div className="flex gap-2 justify-center px-10" style={{ gap: `${gap}px` }}>
            {visibleThumbnails.map((img, idx) => {
              const realIndex = thumbStart + idx;
              return (
                <button
                  key={img.id || idx}
                  onClick={() => setActiveIndex(realIndex)}
                  className={`flex-shrink-0 transition-all duration-200 ${
                    activeIndex === realIndex 
                      ? 'ring-2 ring-indigo-500 ring-offset-2 scale-105' 
                      : 'opacity-70 hover:opacity-100'
                  }`}
                  style={{ borderRadius: `${radius}px`, overflow: 'hidden' }}
                >
                  <img 
                    src={img.url} 
                    alt={img.alt || ''} 
                    className="w-20 h-16 object-cover"
                  />
                </button>
              );
            })}
          </div>
          
          {canGoNext && (
            <button 
              onClick={handleNextThumbs}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white/90 hover:bg-white shadow-md rounded-full flex items-center justify-center transition-all"
            >
              <ChevronRight size={18} className="text-slate-700" />
            </button>
          )}
        </div>
        
        {/* Image counter */}
        <div className="text-center mt-2 text-sm text-slate-500">
          {activeIndex + 1} / {images.length}
        </div>
        </div>
        {renderLightbox()}
      </>
    );
  }
  
  // Side layout (default)
  return (
    <>
      <div className={`mb-8 ${wrapperClass} ${animationClass}`} style={blockStyle}>
        <div className="grid grid-cols-4 gap-4" style={{ gap: `${gap}px` }}>
          {/* Main Image - Click to enlarge */}
          <div 
            className="col-span-3 cursor-pointer hover:opacity-95 transition-opacity" 
            style={{ borderRadius: `${radius}px`, overflow: 'hidden' }}
            onClick={openLightbox}
          >
            <img 
            src={activeImage?.url} 
            alt={activeImage?.alt || ''} 
            className="w-full h-96 object-cover transition-all duration-300"
          />
        </div>
        
        {/* Thumbnails Column with Navigation */}
        <div className="col-span-1 relative">
          {canGoPrev && (
            <button 
              onClick={handlePrevThumbs}
              className="absolute -top-2 left-1/2 -translate-x-1/2 z-10 w-7 h-7 bg-white/90 hover:bg-white shadow-md rounded-full flex items-center justify-center transition-all"
            >
              <ChevronLeft size={16} className="text-slate-700 rotate-90" />
            </button>
          )}
          
          <div className="flex flex-col gap-2 py-2" style={{ gap: `${gap}px` }}>
            {visibleThumbnails.map((img, idx) => {
              const realIndex = thumbStart + idx;
              return (
                <button
                  key={img.id || idx}
                  onClick={() => setActiveIndex(realIndex)}
                  className={`transition-all duration-200 ${
                    activeIndex === realIndex 
                      ? 'ring-2 ring-indigo-500 ring-offset-2 scale-105' 
                      : 'opacity-70 hover:opacity-100'
                  }`}
                  style={{ borderRadius: `${radius}px`, overflow: 'hidden' }}
                >
                  <img 
                    src={img.url} 
                    alt={img.alt || ''} 
                    className="w-full h-20 object-cover"
                  />
                </button>
              );
            })}
          </div>
          
          {canGoNext && (
            <button 
              onClick={handleNextThumbs}
              className="absolute -bottom-2 left-1/2 -translate-x-1/2 z-10 w-7 h-7 bg-white/90 hover:bg-white shadow-md rounded-full flex items-center justify-center transition-all"
            >
              <ChevronRight size={16} className="text-slate-700 rotate-90" />
            </button>
          )}
        </div>
      </div>
      
      {/* Image counter */}
      <div className="text-center mt-2 text-sm text-slate-500">
        {activeIndex + 1} / {images.length}
      </div>
      </div>
      {renderLightbox()}
    </>
  );
};

const OVERLAY_STYLES = {
  'none': {},
  'dark': { background: 'rgba(0,0,0,0.5)' },
  'darker': { background: 'rgba(0,0,0,0.7)' },
  'light': { background: 'rgba(255,255,255,0.5)' },
  'gradient-dark': { background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)' },
  'gradient-primary': { background: 'linear-gradient(135deg, rgba(99,102,241,0.8), rgba(168,85,247,0.8))' },
  'gradient-warm': { background: 'linear-gradient(135deg, rgba(251,146,60,0.8), rgba(251,113,133,0.8))' },
  'gradient-cool': { background: 'linear-gradient(135deg, rgba(56,189,248,0.8), rgba(99,102,241,0.8))' },
  'custom': {}, // Custom will be calculated dynamically
};

// Helper function to get custom overlay style
const getCustomOverlayStyle = (content) => {
  if (content.overlay !== 'custom') {
    return OVERLAY_STYLES[content.overlay] || OVERLAY_STYLES['dark'];
  }
  const color = content.overlayColor || '#000000';
  const opacity = content.overlayOpacity !== undefined ? content.overlayOpacity : 50;
  // Convert hex to rgba
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return { background: `rgba(${r}, ${g}, ${b}, ${opacity / 100})` };
};

// Navigation Menu Component for Preview
const NavigationPreview = ({ menu, location, siteTheme, previewToken, pages, onPopupOpen, subdomain, currentLanguage, defaultLanguage = 'en', tenantId }) => {
  const [openDropdowns, setOpenDropdowns] = useState({});
  const [mobileOpen, setMobileOpen] = useState(false);
  const navRef = React.useRef(null);
  const hoverTimeoutRef = React.useRef({});
  
  // Translation state for menu labels
  const [translatedLabels, setTranslatedLabels] = useState({});
  
  // Translate menu item labels when language changes
  React.useEffect(() => {
    if (!currentLanguage || currentLanguage === defaultLanguage || !menu?.items?.length) {
      setTranslatedLabels({});
      return;
    }
    
    const translateMenuLabels = async () => {
      // Collect all labels (including children)
      const labelsMap = {};
      const collectLabels = (items) => {
        items.forEach(item => {
          if (item.label) labelsMap[item.item_id] = item.label;
          if (item.children?.length) collectLabels(item.children);
        });
      };
      collectLabels(menu.items);
      
      // Also collect footer section titles and link labels
      if (menu.footer_settings?.sections) {
        menu.footer_settings.sections.forEach((section, idx) => {
          if (section.title) labelsMap[`footer_section_${idx}`] = section.title;
          if (section.links) {
            section.links.forEach(link => {
              if (link.label) labelsMap[link.link_id] = link.label;
            });
          }
        });
      }
      
      const ids = Object.keys(labelsMap);
      const texts = Object.values(labelsMap);
      
      if (texts.length === 0) return;
      
      try {
        const response = await fetch(`${API_URL}/api/translation/translate-texts`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            texts,
            target_language: currentLanguage,
            tenant_id: tenantId,
            source_language: defaultLanguage
          })
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.translated_texts) {
            const newLabels = {};
            ids.forEach((id, idx) => {
              newLabels[id] = data.translated_texts[idx] || labelsMap[id];
            });
            setTranslatedLabels(newLabels);
          }
        }
      } catch (err) {
        console.error('[NavigationPreview] Translation error:', err);
      }
    };
    
    translateMenuLabels();
  }, [currentLanguage, defaultLanguage, menu, tenantId]);
  
  // Helper to get translated label
  const getLabel = (item) => translatedLabels[item.item_id] || item.label;
  const getFooterSectionTitle = (section, idx) => translatedLabels[`footer_section_${idx}`] || section.title;
  const getLinkLabel = (link) => translatedLabels[link.link_id] || link.label;
  
  // Search state for header navigation
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const searchInputRef = React.useRef(null);
  
  // Focus search input when opened
  React.useEffect(() => {
    if (showSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showSearch]);
  
  // Close search results when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSearch && navRef.current && !navRef.current.contains(event.target)) {
        setShowSearch(false);
        setHasSearched(false);
        setSearchResults([]);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSearch]);
  
  // Handle search
  const handleSearch = async () => {
    if (!searchQuery.trim() || !subdomain) return;
    
    setIsSearching(true);
    setHasSearched(true);
    
    try {
      const response = await fetch(`/api/public/tenant/${subdomain}/search?q=${encodeURIComponent(searchQuery)}&types=pages,articles`);
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.results || []);
      }
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsSearching(false);
    }
  };
  
  // Handle search input keydown
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    } else if (e.key === 'Escape') {
      setShowSearch(false);
      setHasSearched(false);
      setSearchResults([]);
      setSearchQuery('');
    }
  };
  
  // Toggle search visibility
  const toggleSearch = () => {
    if (showSearch) {
      setShowSearch(false);
      setHasSearched(false);
      setSearchResults([]);
      setSearchQuery('');
    } else {
      setShowSearch(true);
    }
  };

  // Dropdown trigger mode: 'click' or 'hover'
  const dropdownTrigger = menu.dropdown_trigger || 'click';

  // Hamburger mode — when the menu is configured as a hamburger menu in
  // the CMS Navigation editor, the toggle button must show on ALL screen
  // sizes (not just mobile) and the desktop nav strip must be hidden.
  const isHamburger = menu.style === 'hamburger';
  // Classes for the desktop nav strip: hidden entirely when hamburger.
  const desktopNavClass = isHamburger ? 'hidden' : 'hidden md:flex';
  // Classes for the toggle button: visible on all sizes when hamburger.
  const toggleBtnClass = isHamburger ? '' : 'md:hidden';
  // Classes for the mobile/expanded menu container: drop md:hidden when
  // hamburger so the dropdown also works on desktop.
  const expandedMenuClass = isHamburger ? '' : 'md:hidden';
  // The mobile drawer only ever contains menu links and (optionally) the
  // search box. If there are neither, the hamburger would open an empty
  // panel — so hide the toggle entirely in that case.
  const hasMobileContent = (menu.items?.length > 0) || (subdomain && menu.show_search);

  // Check if a link points to a popup page
  const checkIfPopupPage = (item) => {
    if (!item || !pages || pages.length === 0) return null;
    
    // For internal links, check if the target page is a popup
    if (item.link_type === 'internal' && item.link_value) {
      const page = pages.find(p => p.page_id === item.link_value);
      if (page && page.isPopup) {
        return page;
      }
    }
    
    // Check by URL/slug
    const url = item.url || item.link_value;
    if (url && !url.startsWith('http') && !url.startsWith('#')) {
      const slug = url.startsWith('/') ? url.substring(1) : url;
      const page = pages.find(p => p.slug === slug);
      if (page && page.isPopup) {
        return page;
      }
    }
    
    return null;
  };

  // Helper to generate the correct link for an item
  // In PREVIEW mode (URL is /preview/...) we link via /preview/<token> so the
  // viewer stays in the admin preview shell. In PUBLIC mode we MUST link via
  // the page's public slug/full_path — `/preview/<token>` is not routed on
  // custom domains and would 404. This bug was reported on
  // www.horizonglobalacademy.com where nested nav links 404'd.
  const previewMode = isInPreviewMode();
  const getItemLink = (item) => {
    if (!item) return null;
    
    // External links
    if (item.link_type === 'external') {
      return item.link_value || item.url || null;
    }
    
    // Anchor links - can be "anchorId" or "pageSlug#anchorId"
    if (item.link_type === 'anchor') {
      const anchorValue = item.link_value || '';
      if (!anchorValue) return null;
      
      // Check if it includes a page slug (format: "pageSlug#anchorId")
      if (anchorValue.includes('#')) {
        const [pageSlug, anchorId] = anchorValue.split('#');
        
        // In preview mode, try to find the page's preview token
        if (previewMode && pages && pages.length > 0) {
          const page = pages.find(p => p.slug === pageSlug);
          if (page && page.preview_token) {
            return `/preview/${page.preview_token}#${anchorId}`;
          }
        }
        
        // Fallback to public URL
        return `/${pageSlug}#${anchorId}`;
      }
      
      // Just an anchor ID - prepend # for current page
      return `#${anchorValue}`;
    }
    
    // Internal page links
    if (item.link_type === 'internal' && item.link_value) {
      // If we have pages array, try to find the page
      if (pages && pages.length > 0) {
        // First try matching by page_id
        let page = pages.find(p => p.page_id === item.link_value);
        
        // Fallback: try matching by slug (handles cases where page was recreated with new ID)
        if (!page) {
          page = pages.find(p => p.slug === item.link_value);
        }
        
        if (page) {
          // Preview mode only: link via /preview/<token> so the admin preview
          // navigation stays inside the preview shell.
          if (previewMode && page.preview_token) {
            return `/preview/${page.preview_token}`;
          }
          // Public mode: prefer full_path (nested pages) → slug
          if (page.full_path) {
            return `/${page.full_path}`;
          }
          if (page.slug) {
            return `/${page.slug}`;
          }
        }
      }
      
      // If link_value looks like a slug (no special chars, not a UUID), use it as path
      if (item.link_value && /^[a-z0-9-]+$/i.test(item.link_value) && !item.link_value.match(/^[0-9a-f]{8}-[0-9a-f]{4}/i)) {
        return `/${item.link_value}`;
      }
      
      return null;
    }
    
    // Check for direct url field (legacy or simple links)
    if (item.url) {
      // If it's already a full URL or starts with / or #, use it
      if (item.url.startsWith('http') || item.url.startsWith('/') || item.url.startsWith('#')) {
        return item.url;
      }
      // Otherwise treat as a slug
      return `/${item.url}`;
    }
    
    return null;
  };

  // Helper to resolve any URL - handles internal page links in both preview and live mode
  const resolveUrl = (url) => {
    if (!url || url === '#') return '#';
    // Handle download links - strip prefix and return raw URL
    if (url.startsWith('download:')) return url.substring(9);
    // mailto: and tel: are native browser protocols — pass through untouched.
    if (url.startsWith('mailto:') || url.startsWith('tel:')) return url;
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    if (url.startsWith('#')) return url;
    
    // Handle anchor links with page slug (e.g., /page-slug#anchor or page-slug#anchor)
    if (url.includes('#')) {
      const [pathPart, anchorPart] = url.split('#');
      const slug = pathPart.startsWith('/') ? pathPart.substring(1) : pathPart;
      
      if (slug && pages && pages.length > 0) {
        const page = pages.find(p => p.slug === slug);
        if (page && page.preview_token && isInPreviewMode()) {
          return `/preview/${page.preview_token}#${anchorPart}`;
        }
      }
      // Fallback - return with proper formatting
      return pathPart.startsWith('/') ? url : `/${url}`;
    }
    
    // Internal page links - handle both /slug and slug formats
    const slug = url.startsWith('/') ? url.substring(1) : url;
    if (pages && pages.length > 0) {
      const page = pages.find(p => p.slug === slug);
      if (page && page.preview_token && isInPreviewMode()) return `/preview/${page.preview_token}`;
    }
    // Return with leading slash for proper routing
    return url.startsWith('/') ? url : `/${url}`;
  };

  // Close dropdowns when clicking outside (only for click mode)
  React.useEffect(() => {
    if (dropdownTrigger !== 'click') return;
    
    const handleClickOutside = (event) => {
      // Close dropdowns if clicking outside nav
      if (navRef.current && !navRef.current.contains(event.target)) {
        setOpenDropdowns({});
      }
    };
    
    // Also close on Escape key
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        setOpenDropdowns({});
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [dropdownTrigger]);

  // Cleanup hover timeouts on unmount
  React.useEffect(() => {
    return () => {
      Object.values(hoverTimeoutRef.current).forEach(timeout => clearTimeout(timeout));
    };
  }, []);

  // For header menus, render if there are items OR a logo is configured
  // (a logo-only header should still show the logo bar)
  // For footer menus, allow rendering even without items (footer can have sections, social links, copyright)
  if (!menu) return null;
  const headerHasLogo = !!(menu.logo && menu.logo.url);
  if (location === 'header' && (!menu.items || menu.items.length === 0) && !headerHasLogo) return null;
  // For footer, check if there's ANY content to show
  if (location === 'footer') {
    const footerSettings = menu.footer_settings || {};
    const hasItems = menu.items && menu.items.length > 0;
    const hasSections = footerSettings.sections && footerSettings.sections.length > 0;
    const hasSocialLinks = footerSettings.social_links && footerSettings.social_links.length > 0;
    const showCopyright = footerSettings.show_copyright !== false;
    // If footer has no content at all, don't render
    if (!hasItems && !hasSections && !hasSocialLinks && !showCopyright) return null;
  }

  // Check if menu has custom theme override (null/undefined/empty means use site theme)
  const hasCustomTheme = menu.theme_override && Object.keys(menu.theme_override).length > 0;
  
  // Navigation should match the site theme background color for consistency
  const bgColor = hasCustomTheme ? menu.theme_override.backgroundColor : (siteTheme?.background_color || siteTheme?.primary_color || '#1e293b');
  // Text color should contrast with the background - use primary_color or heading_color for visibility
  const textColor = hasCustomTheme ? menu.theme_override.textColor : (siteTheme?.heading_color || siteTheme?.primary_color || '#ffffff');
  const accentColor = hasCustomTheme ? menu.theme_override.accentColor : (siteTheme?.accent_color || siteTheme?.primary_color || '#6366f1');

  // Logo settings
  const logo = menu.logo || {};
  const logoUrl = logo.url;
  const logoPosition = logo.position || 'left';
  const logoHeight = logo.height || 40;
  const logoMaxWidth = logo.maxWidth || logo.width || null;
  const logoLink = logo.link || '/';
  const logoAlt = logo.alt || 'Logo';

  // Click handler for dropdown toggle
  const handleDropdownClick = (id) => {
    if (dropdownTrigger !== 'click') return;
    
    setOpenDropdowns(prev => {
      // If clicking on an open dropdown, close it
      if (prev[id]) {
        return { ...prev, [id]: false };
      }
      // Otherwise, close all dropdowns and open the clicked one
      const newState = {};
      Object.keys(prev).forEach(key => {
        newState[key] = false;
      });
      newState[id] = true;
      return newState;
    });
  };

  // Hover handlers for dropdown
  const handleDropdownMouseEnter = (id) => {
    if (dropdownTrigger !== 'hover') return;
    
    // Clear any pending close timeout for this dropdown
    if (hoverTimeoutRef.current[id]) {
      clearTimeout(hoverTimeoutRef.current[id]);
      delete hoverTimeoutRef.current[id];
    }
    
    // Close all other dropdowns and open this one
    setOpenDropdowns(prev => {
      const newState = {};
      Object.keys(prev).forEach(key => {
        newState[key] = false;
      });
      newState[id] = true;
      return newState;
    });
  };

  const handleDropdownMouseLeave = (id) => {
    if (dropdownTrigger !== 'hover') return;
    
    // Delay closing to allow moving to dropdown content
    hoverTimeoutRef.current[id] = setTimeout(() => {
      setOpenDropdowns(prev => ({ ...prev, [id]: false }));
      delete hoverTimeoutRef.current[id];
    }, 150);
  };
  
  // Render logo component
  const renderLogo = () => {
    if (!logoUrl) return null;
    
    const logoElement = (
      <img 
        src={logoUrl} 
        alt={logoAlt}
        style={{ 
          height: `${logoHeight}px`, 
          width: 'auto',
          ...(logoMaxWidth ? { maxWidth: `${logoMaxWidth}px` } : {}),
          objectFit: 'contain' 
        }}
        className="flex-shrink-0 max-w-[65vw] md:max-w-none"
      />
    );
    
    if (logoLink) {
      return (
        <a href={logoLink} className="flex items-center hover:opacity-80 transition-opacity">
          {logoElement}
        </a>
      );
    }
    return logoElement;
  };

  const renderMenuItem = (item, depth = 0) => {
    const hasChildren = item.children?.length > 0;
    const isOpen = openDropdowns[item.item_id];
    const popupPage = checkIfPopupPage(item);

    // Hover handlers for the menu item wrapper
    const itemHoverProps = dropdownTrigger === 'hover' && hasChildren ? {
      onMouseEnter: () => handleDropdownMouseEnter(item.item_id),
      onMouseLeave: () => handleDropdownMouseLeave(item.item_id),
    } : {};

    const itemLink = popupPage ? null : getItemLink(item);

    // Handle click on the main label - navigate if has link, otherwise toggle dropdown
    const handleLabelClick = (e) => {
      if (popupPage && onPopupOpen) {
        e.preventDefault();
        onPopupOpen(popupPage.slug, popupPage.preview_token);
      } else if (itemLink) {
        // Check if it's an anchor link (contains #)
        if (itemLink.includes('#')) {
          const handled = handleAnchorClick(e, itemLink);
          if (handled) return;
        }
        // For other links, allow default navigation
        return;
      } else if (hasChildren) {
        e.preventDefault();
        handleDropdownClick(item.item_id);
      } else {
        e.preventDefault();
      }
    };

    // Handle click on dropdown arrow - always toggle dropdown
    const handleDropdownToggle = (e) => {
      e.preventDefault();
      e.stopPropagation();
      handleDropdownClick(item.item_id);
    };

    return (
      <div key={item.item_id} className="relative group" {...itemHoverProps}>
        <div className="flex items-center">
          {item.is_button ? (
            // Render as a styled CTA button
            (() => {
              const btnBg = item.button_bg_color || accentColor;
              const btnText = item.button_text_color || '#ffffff';
              const btnStyle = item.button_style || 'solid';
              const btnRadius = item.button_border_radius === 'pill' ? '9999px' : item.button_border_radius === 'square' ? '2px' : '6px';
              let btnInlineStyle;
              if (btnStyle === 'outline') {
                btnInlineStyle = { background: 'transparent', color: btnBg, border: `1.5px solid ${btnBg}`, borderRadius: btnRadius };
              } else if (btnStyle === 'ghost') {
                btnInlineStyle = { background: `${btnBg}18`, color: btnBg, borderRadius: btnRadius };
              } else {
                btnInlineStyle = { backgroundColor: btnBg, color: btnText, borderRadius: btnRadius };
              }
              return (
                <a
                  href={itemLink || undefined}
                  target={item.target}
                  onClick={handleLabelClick}
                  className="inline-flex items-center gap-1 px-4 py-1.5 text-sm font-semibold transition-all hover:opacity-80 active:scale-95"
                  style={btnInlineStyle}
                  data-testid={`nav-button-${item.item_id}`}
                >
                  {getLabel(item)}
                </a>
              );
            })()
          ) : (
            <a
              href={itemLink || undefined}
              target={item.target}
              onClick={handleLabelClick}
              className="flex items-center gap-1 px-4 py-2 rounded-md transition-colors hover:bg-black/5 text-sm font-medium"
              style={{ color: textColor, cursor: (itemLink || popupPage || hasChildren) ? 'pointer' : 'default' }}
            >
              <span>{getLabel(item)}</span>
              {item.link_type === 'external' && <ExternalLink size={12} className="ml-1 opacity-50" />}
            </a>
          )}
          {hasChildren && (
            <button
              onClick={handleDropdownToggle}
              className="p-1 rounded hover:bg-black/5 transition-colors -ml-2"
              style={{ color: textColor }}
            >
              <ChevronDown size={14} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>
          )}
        </div>
        {hasChildren && isOpen && (
          <div 
            className="absolute top-full left-0 mt-1 py-2 min-w-[180px] rounded-lg shadow-lg border bg-white z-50"
            onMouseEnter={() => dropdownTrigger === 'hover' && handleDropdownMouseEnter(item.item_id)}
            onMouseLeave={() => dropdownTrigger === 'hover' && handleDropdownMouseLeave(item.item_id)}
          >
            {item.children.map(child => {
              const childPopupPage = checkIfPopupPage(child);
              const childLink = childPopupPage ? null : getItemLink(child);
              
              const handleChildClick = (e) => {
                if (childPopupPage && onPopupOpen) {
                  e.preventDefault();
                  onPopupOpen(childPopupPage.slug, childPopupPage.preview_token);
                } else if (childLink && childLink.includes('#')) {
                  const handled = handleAnchorClick(e, childLink);
                  if (handled) return;
                } else if (!childLink) {
                  e.preventDefault();
                }
              };
              
              return (
                <a
                  key={child.item_id}
                  href={childLink || undefined}
                  target={child.target}
                  onClick={handleChildClick}
                  className="block px-4 py-2 text-sm hover:bg-slate-50 transition-colors"
                  style={{ color: '#1e293b', cursor: (childLink || childPopupPage) ? 'pointer' : 'default' }}
                >
                  {getLabel(child)}
                </a>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  if (location === 'header') {
    // Split menu items for center logo position
    const midpoint = Math.ceil(menu.items.length / 2);
    const leftItems = menu.items.slice(0, midpoint);
    const rightItems = menu.items.slice(midpoint);
    
    // Calculate padding based on logo height - smaller logos get more padding, larger ones less
    // Base padding of 12px (py-3), adjust for logos larger than 40px
    const verticalPadding = Math.max(8, Math.min(16, 20 - (logoHeight - 40) / 5));

    // Header content width + horizontal edge padding (controls how far in the logo/nav sits)
    const contentWidth = menu.content_width || 'contained';
    const widthClass = contentWidth === 'full'
      ? 'w-full'
      : contentWidth === 'wide'
        ? 'max-w-screen-2xl mx-auto'
        : 'max-w-7xl mx-auto';
    const edgePadding = (menu.edge_padding !== undefined && menu.edge_padding !== null)
      ? menu.edge_padding
      : 16;
    
    return (
      <header 
        ref={navRef}
        className="sticky top-0 z-40 border-b shadow-sm relative"
        style={{ backgroundColor: bgColor }}
      >
        <div 
          className={`${widthClass} flex items-center justify-between`}
          style={{ paddingTop: `${verticalPadding}px`, paddingBottom: `${verticalPadding}px`, paddingLeft: `${edgePadding}px`, paddingRight: `${edgePadding}px` }}
        >
          {/* Left section */}
          <div className="flex items-center gap-4">
            {logoPosition === 'left' && renderLogo()}
            {logoPosition === 'left' && !logoUrl && (
              <div className="font-bold text-lg" style={{ color: accentColor }}>
                {menu.name || 'Site Name'}
              </div>
            )}
            {logoPosition === 'center' && (
              <nav className={`${desktopNavClass} items-center gap-1`}>
                {leftItems.map(item => renderMenuItem(item))}
              </nav>
            )}
          </div>
          
          {/* Center section - for center logo */}
          {logoPosition === 'center' && (
            <div className="flex items-center">
              {renderLogo()}
              {!logoUrl && (
                <div className="font-bold text-lg" style={{ color: accentColor }}>
                  {menu.name || 'Site Name'}
                </div>
              )}
            </div>
          )}
          
          {/* Right section */}
          <div className="flex items-center gap-4">
            {/* Desktop Menu - all items if not center, right items if center */}
            <nav className={`${desktopNavClass} items-center gap-1`}>
              {logoPosition === 'center' 
                ? rightItems.map(item => renderMenuItem(item))
                : (logoPosition !== 'right' && menu.items.map(item => renderMenuItem(item)))
              }
            </nav>
            
            {logoPosition === 'right' && (
              <nav className={`${desktopNavClass} items-center gap-1`}>
                {menu.items.map(item => renderMenuItem(item))}
              </nav>
            )}
            
            {logoPosition === 'right' && renderLogo()}
            {logoPosition === 'right' && !logoUrl && (
              <div className="font-bold text-lg" style={{ color: accentColor }}>
                {menu.name || 'Site Name'}
              </div>
            )}
            
            {/* Search Icon & Expandable Search */}
            {subdomain && menu.show_search && (
              <div className="relative flex items-center">
                {showSearch ? (
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <input
                        ref={searchInputRef}
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={handleSearchKeyDown}
                        placeholder="Search..."
                        className="w-48 md:w-64 pl-3 pr-10 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                        style={{ 
                          backgroundColor: 'white',
                          color: '#334155',
                          borderColor: '#e2e8f0'
                        }}
                        data-testid="nav-search-input"
                      />
                      <button
                        onClick={handleSearch}
                        disabled={isSearching}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600"
                        data-testid="nav-search-submit"
                      >
                        <SearchIcon size={16} />
                      </button>
                    </div>
                    <button
                      onClick={toggleSearch}
                      className="p-2 rounded-lg hover:bg-black/5 transition-colors"
                      style={{ color: textColor }}
                      title="Close search"
                      data-testid="nav-search-close"
                    >
                      <X size={18} />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={toggleSearch}
                    className="p-2 rounded-lg hover:bg-black/5 transition-colors"
                    style={{ color: textColor }}
                    title="Search"
                    data-testid="nav-search-toggle"
                  >
                    <SearchIcon size={20} />
                  </button>
                )}
                
                {/* Search Results Dropdown */}
                {showSearch && hasSearched && (
                  <div 
                    className="absolute top-full right-0 mt-2 w-80 max-h-96 overflow-y-auto bg-white rounded-lg shadow-xl border border-slate-200 z-50"
                    data-testid="nav-search-results"
                  >
                    {isSearching ? (
                      <div className="p-4 text-center text-slate-500">
                        Searching...
                      </div>
                    ) : searchResults.length === 0 ? (
                      <div className="p-4 text-center text-slate-500">
                        No results found for "{searchQuery}"
                      </div>
                    ) : (
                      <div className="py-2">
                        <div className="px-3 py-1 text-xs text-slate-400 uppercase tracking-wide">
                          {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}
                        </div>
                        {searchResults.map((result, idx) => (
                          <a
                            key={idx}
                            href={result.url}
                            className="block px-3 py-2 hover:bg-slate-50 transition-colors"
                            onClick={() => {
                              setShowSearch(false);
                              setHasSearched(false);
                              setSearchResults([]);
                            }}
                          >
                            <div className="flex items-center gap-2 mb-0.5">
                              <span 
                                className="text-[10px] px-1.5 py-0.5 rounded font-medium"
                                style={{ 
                                  backgroundColor: result.type === 'page' ? '#eff6ff' : '#fef3c7',
                                  color: result.type === 'page' ? '#3b82f6' : '#d97706'
                                }}
                              >
                                {result.type === 'page' ? 'Page' : 'Article'}
                              </span>
                            </div>
                            <div className="font-medium text-sm text-slate-800">{result.title}</div>
                            {result.snippet && (
                              <p className="text-xs text-slate-500 line-clamp-1 mt-0.5">{result.snippet}</p>
                            )}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {/* Toggle button — full hamburger mode shows on all sizes,
                otherwise only on mobile. Hidden entirely when the drawer
                would be empty (no menu items and no search). */}
            {hasMobileContent && (
              <button 
                className={`${toggleBtnClass} p-2 rounded-lg hover:bg-black/5`}
                onClick={() => setMobileOpen(!mobileOpen)}
                style={{ color: textColor }}
                aria-label="Toggle menu"
              >
                {mobileOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            )}
          </div>
        </div>
        
        {/* Expanded Menu (Mobile + Hamburger Desktop)
            On desktop-hamburger mode this renders as a floating card anchored
            under the toggle button. On mobile-only mode it stays as a
            full-width drawer (the old behaviour) so we don't change the
            mobile UX for non-hamburger menus. */}
        {mobileOpen && (() => {
          const align = menu.hamburger_align || 'left';
          const itemJustifyClass =
            align === 'right' ? 'justify-end' :
            align === 'center' ? 'justify-center' : 'justify-start';
          const itemTextAlign =
            align === 'right' ? 'right' :
            align === 'center' ? 'center' : 'left';

          // Floating-card mode (desktop hamburger).
          // Position the panel against the same edge as the hamburger button
          // with breathing room, and cap its width so it doesn't sprawl.
          const panelWrapperBase = isHamburger
            ? 'absolute z-40 mt-2 px-4'
            : `${expandedMenuClass} border-t py-2 px-4`;
          const panelWrapperPos = isHamburger
            ? (align === 'right' ? 'right-0' : align === 'center' ? 'left-1/2 -translate-x-1/2' : 'left-0')
            : '';
          const panelCardClass = isHamburger
            ? 'rounded-xl shadow-2xl border min-w-[240px] max-w-[320px] p-3'
            : '';
          const panelCardStyle = isHamburger
            ? { backgroundColor: bgColor, borderColor: `${textColor}1A` }
            : { backgroundColor: bgColor };

          // Backdrop for desktop hamburger so clicking outside closes it.
          const backdrop = isHamburger ? (
            <div
              className="fixed inset-0 z-30 bg-black/10"
              onClick={() => setMobileOpen(false)}
              aria-hidden="true"
            />
          ) : null;
          
          return (
          <>
            {backdrop}
            <div
              className={`${panelWrapperBase} ${panelWrapperPos}`}
              style={isHamburger ? {} : panelCardStyle}
            >
            <div className={panelCardClass} style={isHamburger ? panelCardStyle : {}}>
            {/* Mobile Search */}
            {subdomain && menu.show_search && (
              <div className="py-2 mb-2 border-b" style={{ borderColor: `${textColor}20` }}>
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    placeholder="Search..."
                    className="w-full pl-3 pr-10 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    style={{ 
                      backgroundColor: 'white',
                      color: '#334155',
                      borderColor: '#e2e8f0'
                    }}
                    data-testid="nav-mobile-search-input"
                  />
                  <button
                    onClick={handleSearch}
                    disabled={isSearching}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600"
                    data-testid="nav-mobile-search-submit"
                  >
                    <SearchIcon size={16} />
                  </button>
                </div>
                {/* Mobile Search Results */}
                {hasSearched && (
                  <div className="mt-2 max-h-48 overflow-y-auto bg-white rounded-lg border border-slate-200">
                    {isSearching ? (
                      <div className="p-3 text-center text-slate-500 text-sm">Searching...</div>
                    ) : searchResults.length === 0 ? (
                      <div className="p-3 text-center text-slate-500 text-sm">No results found</div>
                    ) : (
                      searchResults.map((result, idx) => (
                        <a
                          key={idx}
                          href={result.url}
                          className="block px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-b-0"
                          onClick={() => setMobileOpen(false)}
                        >
                          <div className="font-medium text-sm text-slate-800">{result.title}</div>
                        </a>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
            
            {menu.items.map(item => {
              const hasChildren = item.children?.length > 0;
              const mobileLink = getItemLink(item);
              const mobilePopupPage = checkIfPopupPage(item);
              
              const handleMobileClick = (e) => {
                if (hasChildren) {
                  e.preventDefault();
                  setOpenDropdowns(prev => ({ ...prev, [item.item_id]: !prev[item.item_id] }));
                  return;
                }
                if (mobilePopupPage && onPopupOpen) {
                  e.preventDefault();
                  onPopupOpen(mobilePopupPage.slug, mobilePopupPage.preview_token);
                  setMobileOpen(false);
                } else if (mobileLink && mobileLink.includes('#')) {
                  const handled = handleAnchorClick(e, mobileLink);
                  if (handled) {
                    setMobileOpen(false);
                  }
                }
              };
              
              return (
                <div key={item.item_id} style={{ textAlign: itemTextAlign }}>
                  {item.is_button ? (
                    (() => {
                      const btnBg = item.button_bg_color || accentColor;
                      const btnText = item.button_text_color || '#ffffff';
                      const btnStyle = item.button_style || 'solid';
                      const btnRadius = item.button_border_radius === 'pill' ? '9999px' : item.button_border_radius === 'square' ? '2px' : '6px';
                      let btnInlineStyle;
                      if (btnStyle === 'outline') {
                        btnInlineStyle = { background: 'transparent', color: btnBg, border: `1.5px solid ${btnBg}`, borderRadius: btnRadius };
                      } else if (btnStyle === 'ghost') {
                        btnInlineStyle = { background: `${btnBg}18`, color: btnBg, borderRadius: btnRadius };
                      } else {
                        btnInlineStyle = { backgroundColor: btnBg, color: btnText, borderRadius: btnRadius };
                      }
                      return (
                        <a
                          href={mobileLink || '#'}
                          onClick={(e) => { handleMobileClick(e); setMobileOpen(false); }}
                          className={`inline-flex items-center gap-2 mx-3 my-1 px-4 py-2 text-sm font-semibold transition-all hover:opacity-80`}
                          style={btnInlineStyle}
                        >
                          {getLabel(item)}
                        </a>
                      );
                    })()
                  ) : (
                  <a
                    href={mobileLink || '#'}
                    onClick={handleMobileClick}
                    className={`flex items-center ${itemJustifyClass} gap-2 px-3 py-2 text-sm font-medium rounded-lg hover:bg-black/5 transition-colors`}
                    style={{ color: textColor }}
                  >
                    <span>{getLabel(item)}</span>
                    {hasChildren && (
                      <ChevronDown size={16} className={`transition-transform ${openDropdowns[item.item_id] ? 'rotate-180' : ''}`} style={{ color: textColor, opacity: 0.6 }} />
                    )}
                  </a>
                  )}
                  {hasChildren && openDropdowns[item.item_id] && (
                    <div
                      className={`pb-2 space-y-1 mt-1 ${align === 'right' ? 'pr-3 border-r-2 mr-1' : align === 'center' ? '' : 'pl-3 border-l-2 ml-1'}`}
                      style={{ borderColor: `${textColor}20` }}
                    >
                      {item.children.map(child => {
                        const childLink = getItemLink(child);
                        const childPopupPage = checkIfPopupPage(child);
                        
                        const handleChildMobileClick = (e) => {
                          if (childPopupPage && onPopupOpen) {
                            e.preventDefault();
                            onPopupOpen(childPopupPage.slug, childPopupPage.preview_token);
                            setMobileOpen(false);
                          } else if (childLink && childLink.includes('#')) {
                            const handled = handleAnchorClick(e, childLink);
                            if (handled) setMobileOpen(false);
                          } else if (childLink) {
                            setMobileOpen(false);
                          }
                        };
                        
                        return (
                          <a
                            key={child.item_id}
                            href={childLink || '#'}
                            onClick={handleChildMobileClick}
                            className="block px-3 py-1.5 text-sm rounded-md hover:bg-black/5 transition-colors"
                            style={{ color: textColor, opacity: 0.85, textAlign: itemTextAlign }}
                          >
                            {getLabel(child)}
                          </a>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
            </div>
            </div>
          </>
          );
        })()}
      </header>
    );
  }

  if (location === 'footer') {
    const footerSettings = menu.footer_settings || {};
    const layout = footerSettings.layout || 'simple';
    const sections = footerSettings.sections || [];
    const showCopyright = footerSettings.show_copyright !== false;
    const copyrightText = footerSettings.copyright_text || `© ${new Date().getFullYear()} All rights reserved`;
    const paddingTop = footerSettings.padding_top || 48;
    const paddingBottom = footerSettings.padding_bottom || 32;
    const marginTopValue = footerSettings.margin_top ?? 48;
    const sectionTitleColor = footerSettings.section_title_color || textColor;
    const copyrightColor = footerSettings.copyright_color || textColor;
    const linkColor = footerSettings.link_color || textColor;
    const contentAlign = footerSettings.content_align || (layout === 'columns' ? 'left' : 'center');
    const alignTextClass = contentAlign === 'right' ? 'text-right' : contentAlign === 'left' ? 'text-left' : 'text-center';
    const alignItemsClass = contentAlign === 'right' ? 'items-end' : contentAlign === 'left' ? 'items-start' : 'items-center';
    const alignJustifyClass = contentAlign === 'right' ? 'justify-end' : contentAlign === 'left' ? 'justify-start' : 'justify-center';
    const proseAlignClass = contentAlign === 'center' ? 'mx-auto' : '';

    return (
      <footer 
        className="border-t"
        style={{ 
          backgroundColor: bgColor,
          paddingTop: `${paddingTop}px`,
          paddingBottom: `${paddingBottom}px`,
          marginTop: `${marginTopValue}px`,
        }}
      >
        <div className="max-w-7xl mx-auto px-4">
          {/* Columns Layout */}
          {layout === 'columns' && sections.length > 0 && (
            <div className={`grid gap-8 mb-8 ${
              sections.length === 1 ? 'grid-cols-1 md:grid-cols-2' :
              sections.length === 2 ? 'grid-cols-1 md:grid-cols-3' :
              sections.length >= 3 ? 'grid-cols-1 md:grid-cols-4' : 'grid-cols-1'
            }`}>
              {/* Logo Column */}
              {logoUrl && (
                <div className="space-y-4">
                  {renderLogo()}
                </div>
              )}
              
              {/* Content Sections */}
              {sections.map((section, sectionIdx) => (
                <div key={section.section_id} className={`space-y-3 ${alignTextClass}`}>
                  {section.title && (
                    <h4 className="font-semibold text-sm uppercase tracking-wide" style={{ color: sectionTitleColor }}>
                      {getFooterSectionTitle(section, sectionIdx)}
                    </h4>
                  )}
                  {section.content_html && (
                    <div 
                      className="text-sm space-y-1 prose prose-sm max-w-none"
                      style={{ color: textColor, opacity: 0.9 }}
                      dangerouslySetInnerHTML={{ __html: section.content_html }}
                    />
                  )}
                  {/* Section Links */}
                  {section.links && section.links.length > 0 && (
                    <nav className={`flex flex-col gap-1.5 mt-2 ${alignItemsClass}`}>
                      {section.links.map(link => (
                        <a
                          key={link.link_id}
                          href={link.link_type === 'internal' && link.page_id 
                            ? (() => {
                                const pg = pages?.find(p => p.page_id === link.page_id);
                                if (!pg) return link.url || '#';
                                if (pg.preview_token && isInPreviewMode()) return `/preview/${pg.preview_token}`;
                                return pg.full_path ? `/${pg.full_path}` : (pg.slug ? `/${pg.slug}` : (link.url || '#'));
                              })()
                            : link.url || '#'}
                          target={link.link_type === 'external' ? '_blank' : undefined}
                          rel={link.link_type === 'external' ? 'noopener noreferrer' : undefined}
                          className="text-sm hover:underline transition-opacity hover:opacity-80"
                          style={{ color: linkColor, opacity: footerSettings.link_color ? 1 : 0.9 }}
                        >
                          {getLinkLabel(link)}
                        </a>
                      ))}
                    </nav>
                  )}
                </div>
              ))}
              
              {/* Links Column */}
              {menu.items.length > 0 && (
                <div className={`space-y-3 ${alignTextClass}`}>
                  <h4 className="font-semibold text-sm uppercase tracking-wide" style={{ color: sectionTitleColor }}>
                    Quick Links
                  </h4>
                  <nav className={`flex flex-col gap-2 ${alignItemsClass}`}>
                    {menu.items.map(item => {
                      const itemHref = getItemLink(item) || '#';
                      const isExternal = item.link_type === 'external';
                      return (
                        <a
                          key={item.item_id}
                          href={itemHref}
                          target={item.target || (isExternal ? '_blank' : undefined)}
                          rel={isExternal ? 'noopener noreferrer' : undefined}
                          className="text-sm hover:underline"
                          style={{ color: linkColor, opacity: footerSettings.link_color ? 1 : 0.9 }}
                        >
                          {getLabel(item)}
                        </a>
                      );
                    })}
                  </nav>
                </div>
              )}
            </div>
          )}

          {/* Stacked Layout */}
          {layout === 'stacked' && sections.length > 0 && (
            <div className="mb-8 space-y-6">
              {/* Logo */}
              {logoUrl && (
                <div className="flex justify-center">
                  {renderLogo()}
                </div>
              )}
              
              {/* Content Sections */}
              <div className={`grid gap-6 ${
                sections.length === 1 ? 'grid-cols-1' :
                sections.length === 2 ? 'grid-cols-1 md:grid-cols-2' :
                'grid-cols-1 md:grid-cols-3'
              }`}>
                {sections.map((section, sectionIdx) => (
                  <div key={section.section_id} className={alignTextClass}>
                    {section.title && (
                      <h4 className="font-semibold text-sm uppercase tracking-wide mb-2" style={{ color: sectionTitleColor }}>
                        {getFooterSectionTitle(section, sectionIdx)}
                      </h4>
                    )}
                    {section.content_html && (
                      <div 
                        className={`text-sm prose prose-sm max-w-none ${proseAlignClass}`}
                        style={{ color: textColor, opacity: 0.9 }}
                        dangerouslySetInnerHTML={{ __html: section.content_html }}
                      />
                    )}
                    {/* Section Links */}
                    {section.links && section.links.length > 0 && (
                      <nav className={`flex flex-col gap-1.5 mt-2 ${alignItemsClass}`}>
                        {section.links.map(link => (
                          <a
                            key={link.link_id}
                            href={link.link_type === 'internal' && link.page_id 
                              ? (() => {
                                  const pg = pages?.find(p => p.page_id === link.page_id);
                                  if (!pg) return link.url || '#';
                                  if (pg.preview_token && isInPreviewMode()) return `/preview/${pg.preview_token}`;
                                  return pg.full_path ? `/${pg.full_path}` : (pg.slug ? `/${pg.slug}` : (link.url || '#'));
                                })()
                              : link.url || '#'}
                            target={link.link_type === 'external' ? '_blank' : undefined}
                            rel={link.link_type === 'external' ? 'noopener noreferrer' : undefined}
                            className="text-sm hover:underline transition-opacity hover:opacity-80"
                            style={{ color: linkColor, opacity: footerSettings.link_color ? 1 : 0.9 }}
                          >
                            {getLinkLabel(link)}
                          </a>
                        ))}
                      </nav>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Simple Layout - Logo and Links */}
          {(layout === 'simple' || sections.length === 0) && (
            <>
              {logoUrl && (
                <div className="flex justify-center mb-6">
                  {renderLogo()}
                </div>
              )}
            </>
          )}

          {/* Content Sections — also rendered on Simple layout so users who
              add sections without changing the layout still see them.
              The 'columns' and 'stacked' branches above already render their
              own (richer) version, so skip here to avoid duplication. */}
          {layout === 'simple' && sections.length > 0 && (
            <div className={`grid gap-8 mb-8 ${alignTextClass} ${
              sections.length === 1 ? 'grid-cols-1' :
              sections.length === 2 ? 'grid-cols-1 md:grid-cols-2' :
              'grid-cols-1 md:grid-cols-3'
            }`}>
              {sections.map((section, sectionIdx) => (
                <div key={section.section_id} className={`space-y-3 ${alignTextClass}`}>
                  {section.title && (
                    <h4 className="font-semibold text-sm uppercase tracking-wide" style={{ color: sectionTitleColor }}>
                      {getFooterSectionTitle(section, sectionIdx)}
                    </h4>
                  )}
                  {section.content_html && (
                    <div
                      className={`text-sm prose prose-sm max-w-none ${proseAlignClass}`}
                      style={{ color: textColor, opacity: 0.9 }}
                      dangerouslySetInnerHTML={{ __html: section.content_html }}
                    />
                  )}
                  {section.links && section.links.length > 0 && (
                    <nav className={`flex flex-col gap-1.5 mt-2 ${alignItemsClass}`}>
                      {section.links.map(link => (
                        <a
                          key={link.link_id}
                          href={link.link_type === 'internal' && link.page_id
                            ? (() => {
                                const pg = pages?.find(p => p.page_id === link.page_id);
                                if (!pg) return link.url || '#';
                                if (pg.preview_token && isInPreviewMode()) return `/preview/${pg.preview_token}`;
                                return pg.full_path ? `/${pg.full_path}` : (pg.slug ? `/${pg.slug}` : (link.url || '#'));
                              })()
                            : link.url || '#'}
                          target={link.link_type === 'external' ? '_blank' : undefined}
                          rel={link.link_type === 'external' ? 'noopener noreferrer' : undefined}
                          className="text-sm hover:underline transition-opacity hover:opacity-80"
                          style={{ color: linkColor, opacity: footerSettings.link_color ? 1 : 0.9 }}
                        >
                          {getLinkLabel(link)}
                        </a>
                      ))}
                    </nav>
                  )}
                </div>
              ))}
            </div>
          )}
          
          {/* Navigation Links (for simple/stacked layouts) */}
          {layout !== 'columns' && menu.items.length > 0 && (
                        <nav className={`flex flex-wrap gap-6 mb-4 ${alignJustifyClass}`}>
              {menu.items.map(item => {
                const itemHref = getItemLink(item) || '#';
                const isExternal = item.link_type === 'external';
                return (
                  <a
                    key={item.item_id}
                    href={itemHref}
                    target={item.target || (isExternal ? '_blank' : undefined)}
                    rel={isExternal ? 'noopener noreferrer' : undefined}
                    className="text-sm hover:underline"
                    style={{ color: linkColor }}
                  >
                    {getLabel(item)}
                  </a>
                );
              })}
            </nav>
          )}

          {/* Social Links */}
          {footerSettings.social_links?.length > 0 && (
            <div className="flex justify-center gap-4 mb-4">
              {footerSettings.social_links.map((link) => (
                <a
                  key={link.id}
                  href={link.url || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:opacity-70 transition-opacity p-2 rounded-full hover:bg-black/5"
                  style={{ color: textColor }}
                  title={link.platform.charAt(0).toUpperCase() + link.platform.slice(1)}
                >
                  <SocialIcon platform={link.platform} size={22} color={textColor} />
                </a>
              ))}
            </div>
          )}

          {/* Copyright */}
          {showCopyright && (
            <p className="text-center text-xs mt-6 pt-4 border-t border-current/10" style={{ color: copyrightColor, opacity: footerSettings.copyright_color ? 1 : 0.6 }}>
              {copyrightText}
            </p>
          )}
        </div>
      </footer>
    );
  }

  return null;
};

// Form Preview Block - Renders the actual form for submissions
const FormPreviewBlock = ({ formId, formName, blockStyle, wrapperClass, animationClass, customStyle = {}, enabledLanguages = null, defaultLanguage = 'en' }) => {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [formLoadTime] = useState(Date.now());

  const fetchForm = useCallback(async () => {
    try {
      const response = await api.get(`/public/forms/${formId}`);
      setForm(response.data);
      if (response.data?.collect_language_preference) {
        setFormData((prev) => ({ ...prev, preferred_language: prev.preferred_language || defaultLanguage || 'en' }));
      }
    } catch (error) {
      console.error('Failed to fetch form:', error);
    } finally {
      setLoading(false);
    }
  }, [formId, defaultLanguage]);

  useEffect(() => {
    if (formId) {
      fetchForm();
    }
  }, [formId, fetchForm]);

  const handleInputChange = (fieldId, value) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
    if (errors[fieldId]) {
      setErrors(prev => ({ ...prev, [fieldId]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Honeypot check - if hidden field is filled, silently reject (bot detected)
    if (formData['_hp_website_url']) {
      // Fake success to not alert the bot
      setSubmitted(true);
      return;
    }
    
    // Timing check - reject instant submissions (< 2 seconds)
    const elapsed = Date.now() - formLoadTime;
    if (elapsed < 2000) {
      setSubmitted(true); // Fake success
      return;
    }
    
    // Validate required fields
    const newErrors = {};
    (form?.fields || []).forEach(field => {
      if (field.required && !formData[field.field_id]) {
        newErrors[field.field_id] = `${field.label} is required`;
      }
    });
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSubmitting(true);
    try {
      // Remove honeypot field before sending
      const cleanData = { ...formData };
      delete cleanData['_hp_website_url'];
      
      const response = await api.post(`/public/forms/${formId}/submit`, {
        form_id: formId,
        data: cleanData,
        _t: formLoadTime
      });
      setSubmitted(true);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Submission failed. Please try again.';
      setErrors({ submit: errorMsg });
    } finally {
      setSubmitting(false);
    }
  };

  if (!formId) {
    return (
      <div className={`mb-8 p-6 bg-slate-50 border border-dashed border-slate-300 rounded-lg text-center ${wrapperClass} ${animationClass}`} style={blockStyle}>
        <p className="text-slate-500">No form selected</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`mb-8 p-6 bg-white border border-slate-200 rounded-lg ${wrapperClass} ${animationClass}`} style={blockStyle}>
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-slate-200 rounded w-1/4"></div>
          <div className="h-10 bg-slate-200 rounded"></div>
          <div className="h-10 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (!form) {
    return (
      <div className={`mb-8 p-6 bg-red-50 border border-red-200 rounded-lg text-center ${wrapperClass} ${animationClass}`} style={blockStyle}>
        <p className="text-red-600">Form not found or inactive</p>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className={`mb-8 p-8 bg-green-50 border border-green-200 rounded-lg text-center ${wrapperClass} ${animationClass}`} style={blockStyle}>
        <div className="text-4xl mb-4">✅</div>
        <p className="text-green-800 text-lg font-medium">{form.success_message}</p>
      </div>
    );
  }

  // Get form's saved styles (from Form Builder)
  const savedFormStyles = form?.styles?.use_site_theme === false ? form.styles : {};
  
  // Merge: Page Builder customStyle overrides Form Builder savedFormStyles, with defaults as fallback
  const layout = customStyle.layout || savedFormStyles.layout || 'stacked';
  const formWidth = customStyle.formWidth || savedFormStyles.formWidth || '100';
  const fieldWidth = customStyle.fieldWidth || savedFormStyles.fieldWidth || '100';
  const fieldRadius = customStyle.fieldRadius || savedFormStyles.fieldRadius || 'rounded';
  const fieldSize = customStyle.fieldSize || savedFormStyles.fieldSize || 'md';
  const buttonWidth = customStyle.buttonWidth || savedFormStyles.buttonWidth || 'auto';
  const buttonStyle = customStyle.buttonStyle || savedFormStyles.buttonStyle || 'solid';
  const buttonRadius = customStyle.buttonRadius || savedFormStyles.buttonRadius || 'rounded';
  const buttonColor = customStyle.buttonColor || savedFormStyles.button_color || '#6366f1';
  const buttonTextColor = customStyle.buttonTextColor || savedFormStyles.button_text_color || '#ffffff';
  const buttonAlign = customStyle.buttonAlign || savedFormStyles.buttonAlign || 'left';
  const buttonText = customStyle.buttonText;
  const labelColor = customStyle.labelColor || savedFormStyles.label_color || '#374151';
  const labelSize = customStyle.labelSize || savedFormStyles.labelSize || 'sm';
  const labelWeight = customStyle.labelWeight || savedFormStyles.labelWeight || 'medium';
  const fieldGap = customStyle.fieldGap || savedFormStyles.fieldGap || '16';
  const formPadding = customStyle.formPadding || savedFormStyles.formPadding || '0';
  const backgroundColor = customStyle.backgroundColor || savedFormStyles.background_color || '#ffffff';
  const transparentBg = customStyle.transparentBg || savedFormStyles.transparentBg || false;

  // Colors from form styles
  const formStyles = {
    background_color: backgroundColor,
    text_color: savedFormStyles.text_color || '#1e293b',
    label_color: labelColor,
    input_background: savedFormStyles.input_background || '#ffffff',
    input_border_color: savedFormStyles.input_border_color || '#d1d5db',
    input_text_color: savedFormStyles.input_text_color || '#1e293b',
    button_color: buttonColor,
    button_text_color: buttonTextColor,
    border_radius: savedFormStyles.border_radius || '8px',
    padding: savedFormStyles.padding || '24px'
  };
  
  // Field radius helper
  const getFieldRadius = () => {
    switch (fieldRadius) {
      case 'none': return '0';
      case 'pill': return '9999px';
      case 'rounded':
      default: return '8px';
    }
  };
  
  // Button radius helper
  const getButtonRadius = () => {
    switch (buttonRadius) {
      case 'none': return '0';
      case 'pill': return '9999px';
      case 'rounded':
      default: return '8px';
    }
  };
  
  // Field size helper (padding)
  const getFieldPadding = () => {
    switch (fieldSize) {
      case 'sm': return '0.375rem 0.75rem';
      case 'lg': return '0.75rem 1rem';
      case 'md':
      default: return '0.5rem 1rem';
    }
  };
  
  // Label size helper
  const getLabelSize = () => {
    switch (labelSize) {
      case 'xs': return '0.75rem';
      case 'md': return '1rem';
      case 'lg': return '1.125rem';
      case 'sm':
      default: return '0.875rem';
    }
  };
  
  // Label weight helper
  const getLabelWeight = () => {
    switch (labelWeight) {
      case 'normal': return '400';
      case 'semibold': return '600';
      case 'bold': return '700';
      case 'medium':
      default: return '500';
    }
  };
  
  // Computed styles
  const computedFieldRadius = getFieldRadius();
  const computedButtonRadius = getButtonRadius();
  const computedFieldPadding = getFieldPadding();
  const computedLabelSize = getLabelSize();
  const computedLabelWeight = getLabelWeight();

  const renderField = (field) => {
    const hasError = errors[field.field_id];
    const inputStyle = {
      backgroundColor: formStyles.input_background,
      borderColor: hasError ? '#ef4444' : formStyles.input_border_color,
      color: formStyles.input_text_color,
      borderRadius: computedFieldRadius,
      padding: computedFieldPadding,
      width: '100%',
    };
    
    switch (field.type) {
      case 'textarea':
        return (
          <textarea
            value={formData[field.field_id] || ''}
            onChange={(e) => handleInputChange(field.field_id, e.target.value)}
            placeholder={field.placeholder}
            className="border focus:outline-none focus:ring-2 focus:ring-indigo-500 min-h-[100px]"
            style={inputStyle}
          />
        );
      case 'select':
        return (
          <select
            value={formData[field.field_id] || ''}
            onChange={(e) => handleInputChange(field.field_id, e.target.value)}
            className="border focus:outline-none focus:ring-2 focus:ring-indigo-500"
            style={inputStyle}
          >
            <option value="">{field.placeholder || 'Select...'}</option>
            {(field.options || []).map((opt, i) => (
              <option key={i} value={opt}>{opt}</option>
            ))}
          </select>
        );
      case 'recipient_select':
        return (
          <select
            value={formData[field.field_id] || ''}
            onChange={(e) => handleInputChange(field.field_id, e.target.value)}
            className="border focus:outline-none focus:ring-2 focus:ring-indigo-500"
            style={inputStyle}
          >
            <option value="">{field.placeholder || 'Select recipient...'}</option>
            {(form?.recipient_names || []).map((name, i) => (
              <option key={i} value={name}>{name}</option>
            ))}
          </select>
        );
      case 'radio':
        return (
          <div className="space-y-2">
            {(field.options || []).map((opt, i) => (
              <label key={i} className="flex items-center gap-2 cursor-pointer" style={{ color: formStyles.text_color }}>
                <input
                  type="radio"
                  name={field.field_id}
                  value={opt}
                  checked={formData[field.field_id] === opt}
                  onChange={(e) => handleInputChange(field.field_id, e.target.value)}
                  className="w-4 h-4"
                  style={{ accentColor: buttonColor }}
                />
                <span>{opt}</span>
              </label>
            ))}
          </div>
        );
      case 'checkbox':
        return (
          <div className="space-y-2">
            {(field.options || []).map((opt, i) => (
              <label key={i} className="flex items-center gap-2 cursor-pointer" style={{ color: formStyles.text_color }}>
                <input
                  type="checkbox"
                  checked={(formData[field.field_id] || []).includes(opt)}
                  onChange={(e) => {
                    const current = formData[field.field_id] || [];
                    const newValue = e.target.checked
                      ? [...current, opt]
                      : current.filter(v => v !== opt);
                    handleInputChange(field.field_id, newValue);
                  }}
                  className="w-4 h-4 rounded"
                  style={{ accentColor: buttonColor }}
                />
                <span>{opt}</span>
              </label>
            ))}
          </div>
        );
      default:
        return (
          <input
            type={field.type}
            value={formData[field.field_id] || ''}
            onChange={(e) => handleInputChange(field.field_id, e.target.value)}
            placeholder={field.placeholder}
            className="border focus:outline-none focus:ring-2 focus:ring-indigo-500"
            style={inputStyle}
          />
        );
    }
  };

  // Form container style - uses page builder overrides
  const formContainerStyle = {
    backgroundColor: transparentBg ? 'transparent' : backgroundColor,
    borderRadius: computedFieldRadius,
    padding: formPadding !== '0' ? `${formPadding}px` : formStyles.padding,
    width: `${formWidth}%`,
    margin: formWidth !== '100' ? '0 auto' : undefined,
  };

  // Button style with page builder overrides
  const computedButtonStyle = {
    backgroundColor: buttonStyle === 'solid' ? buttonColor : 'transparent',
    color: buttonStyle === 'solid' ? buttonTextColor : buttonColor,
    borderRadius: computedButtonRadius,
    border: buttonStyle === 'outline' ? `2px solid ${buttonColor}` : 'none',
    width: buttonWidth === 'auto' ? 'auto' : `${buttonWidth}%`,
    padding: '0.75rem 1.5rem',
    fontFamily: 'inherit',
  };
  
  // Button alignment style
  const buttonContainerStyle = {
    textAlign: buttonAlign,
    marginTop: '1.5rem',
  };
  
  // Layout class based on layout option
  const getLayoutClass = () => {
    switch (layout) {
      case 'inline':
        // 'form-inline-layout' below hooks a mobile media query that
        // forces each field back to 100% width so 3-across forms don't
        // squash on narrow screens.
        return 'flex flex-wrap items-end form-inline-layout';
      case 'two-column':
        return 'grid grid-cols-1 md:grid-cols-2';
      case 'three-column':
        return 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3';
      case 'stacked':
      default:
        return 'flex flex-col';
    }
  };
  
  // Field wrapper style based on layout and field width
  const getFieldWrapperStyle = (field) => {
    // For grid layouts (two-column, three-column), fields take full cell width
    if (layout === 'two-column' || layout === 'three-column') {
      return { width: '100%' };
    }
    // For inline, use fieldWidth
    if (layout === 'inline') {
      return {
        width: `${fieldWidth}%`,
        paddingRight: '0.5rem',
        flexShrink: 0,
      };
    }
    // For stacked, apply fieldWidth and center if not 100%
    return {
      width: `${fieldWidth}%`,
      margin: fieldWidth !== '100' ? '0 auto' : undefined,
    };
  };
  
  // Label style with page builder overrides
  const labelStyle = {
    color: labelColor,
    fontSize: computedLabelSize,
    fontWeight: computedLabelWeight,
    marginBottom: '0.25rem',
    display: 'block',
  };

  return (
    <div className={`mb-8 ${wrapperClass} ${animationClass}`} style={blockStyle}>
      {/* Mobile override: inline-layout forms (which use an explicit
          width:N% on each field wrapper) collapse each field to full width
          on narrow screens so 3-across name/name/email doesn't squash. */}
      <style>{`
        @media (max-width: 639px) {
          .form-inline-layout > div {
            width: 100% !important;
            padding-right: 0 !important;
            margin-right: 0 !important;
          }
        }
      `}</style>
      <form 
        onSubmit={handleSubmit} 
        className={transparentBg ? '' : 'border border-slate-200 shadow-sm'} 
        style={formContainerStyle}
      >
        {/* Honeypot field - hidden from users, bots will fill it */}
        <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
          <label htmlFor="_hp_website_url">Website URL</label>
          <input
            type="text"
            id="_hp_website_url"
            name="_hp_website_url"
            tabIndex={-1}
            autoComplete="off"
            value={formData['_hp_website_url'] || ''}
            onChange={(e) => setFormData(prev => ({ ...prev, '_hp_website_url': e.target.value }))}
          />
        </div>
        {form.name && (
          <h3 className="text-xl font-semibold mb-2" style={{ color: formStyles.text_color }}>{form.name}</h3>
        )}
        {form.description && (
          <p className="mb-6" style={{ color: labelColor }}>{form.description}</p>
        )}
        
        <div className={getLayoutClass()} style={{ gap: `${fieldGap}px` }}>
          {(form.fields || []).map((field) => (
            <div key={field.field_id} style={getFieldWrapperStyle(field)}>
              <label style={labelStyle}>
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </label>
              {renderField(field)}
              {errors[field.field_id] && (
                <p className="text-sm text-red-500 mt-1">{errors[field.field_id]}</p>
              )}
            </div>
          ))}

          {/* Preferred language picker (shown when the form collects language preference) */}
          {form.collect_language_preference && (() => {
            const langCodes = (Array.isArray(enabledLanguages) && enabledLanguages.length > 0)
              ? enabledLanguages.filter((c) => SUPPORTED_LANGUAGES[c])
              : Object.keys(SUPPORTED_LANGUAGES);
            // Always ensure the default language is selectable
            if (defaultLanguage && SUPPORTED_LANGUAGES[defaultLanguage] && !langCodes.includes(defaultLanguage)) {
              langCodes.unshift(defaultLanguage);
            }
            return (
              <div style={{ width: '100%' }} data-testid="form-language-field">
                <label style={labelStyle}>Preferred language</label>
                <select
                  value={formData['preferred_language'] || defaultLanguage || 'en'}
                  onChange={(e) => handleInputChange('preferred_language', e.target.value)}
                  className="border focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  style={{
                    backgroundColor: formStyles.input_background,
                    borderColor: formStyles.input_border_color,
                    color: formStyles.input_text_color,
                    borderRadius: computedFieldRadius,
                    padding: computedFieldPadding,
                    width: '100%',
                  }}
                  data-testid="form-language-select"
                >
                  {langCodes.map((code) => (
                    <option key={code} value={code}>
                      {SUPPORTED_LANGUAGES[code]?.flag ? `${SUPPORTED_LANGUAGES[code].flag} ` : ''}{SUPPORTED_LANGUAGES[code]?.name || code}
                    </option>
                  ))}
                </select>
                <p className="text-xs mt-1" style={{ color: labelColor, opacity: 0.7 }}>
                  We'll send you newsletters in this language.
                </p>
              </div>
            );
          })()}
          
          {/* Inline layout: button in same row */}
          {layout === 'inline' && (
            <div style={{ flexShrink: 0 }}>
              <button
                type="submit"
                disabled={submitting}
                className="font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors hover:opacity-90"
                style={computedButtonStyle}
              >
                {submitting ? 'Submitting...' : (buttonText || form.submit_button_text)}
              </button>
            </div>
          )}
        </div>

        {errors.submit && (
          <p className="text-sm text-red-500 mt-4">{errors.submit}</p>
        )}

        {/* Stacked/Two-column layout: button below fields */}
        {layout !== 'inline' && (
          <div style={buttonContainerStyle}>
            <button
              type="submit"
              disabled={submitting}
              className="font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors hover:opacity-90"
              style={computedButtonStyle}
            >
              {submitting ? 'Submitting...' : (buttonText || form.submit_button_text)}
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

// Ticker Tape Step Animation Component
const TickerStepAnimation = ({ items, direction, stepDuration, pauseOnHover, spacing, renderItem }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  
  React.useEffect(() => {
    if (isPaused || items.length <= 1) return;
    
    const interval = setInterval(() => {
      setActiveIndex(prev => {
        if (direction === 'left') {
          return (prev + 1) % items.length;
        } else {
          return prev === 0 ? items.length - 1 : prev - 1;
        }
      });
    }, stepDuration);
    
    return () => clearInterval(interval);
  }, [direction, stepDuration, items.length, isPaused]);
  
  // Calculate transform based on active index
  const getTransform = () => {
    const itemWidth = 150 + spacing; // Approximate item width + spacing
    return `translateX(-${activeIndex * itemWidth}px)`;
  };
  
  return (
    <div 
      className="relative overflow-hidden"
      onMouseEnter={() => pauseOnHover && setIsPaused(true)}
      onMouseLeave={() => pauseOnHover && setIsPaused(false)}
    >
      <div 
        className="flex items-center transition-transform duration-500 ease-in-out"
        style={{ 
          gap: `${spacing}px`,
          transform: getTransform(),
        }}
      >
        {items.map((item, idx) => renderItem(item, idx))}
      </div>
    </div>
  );
};

// Article Feed Preview Component
// Note: fontSize is intentionally applied via ts.fontSize when set so the
// editor's "Font size" input on the block title actually takes effect.
// Callers can pass their own default fontSize AFTER spreading heroHead(ts);
// prefer the reverse order — spread heroHead LAST — so user overrides win.
const heroHead = (ts) => ({
  fontFamily: ts.fontFamily && ts.fontFamily !== 'inherit' ? ts.fontFamily : 'var(--site-heading-font)',
  color: ts.color || 'var(--site-heading-color)',
  fontWeight: ts.fontWeight || 800,
  textAlign: ts.align || undefined,
  ...(ts.fontSize ? { fontSize: responsiveFontSize(ts.fontSize) } : {}),
});

const heroHref = (u) => (!u ? '#' : (/^(https?:|\/|#|mailto:|tel:)/i.test(u) ? u : `https://${u}`));

// Shared width/height layout for the extra Hero blocks (mirrors the main Hero block's Layout & Style)
const heroWidthStyle = (w) => {
  switch (w) {
    case 'full': return { width: '100%' };
    case 'large': return { width: '100%', maxWidth: '1200px', marginLeft: 'auto', marginRight: 'auto' };
    case 'medium': return { width: '100%', maxWidth: '900px', marginLeft: 'auto', marginRight: 'auto' };
    case 'small': return { width: '100%', maxWidth: '600px', marginLeft: 'auto', marginRight: 'auto' };
    case 'container': return { width: '100%', maxWidth: '1280px', marginLeft: 'auto', marginRight: 'auto' };
    default: return {};
  }
};
const heroHeightStyle = (h) => (h && parseInt(h) > 0 ? { minHeight: `${parseInt(h)}px`, display: 'flex', flexDirection: 'column', justifyContent: 'center' } : {});
const heroLayoutStyle = (content) => ({ ...heroWidthStyle(content.width), ...heroHeightStyle(content.height) });

// Horizontal + Vertical text positioning (from Layout & Style panel).
// Returns Tailwind class strings compatible with a flex-col container.
const heroPositionClasses = (content) => {
  const h = content.textPositionH || 'center';
  const v = content.textPositionV || 'middle';
  const items = h === 'left' ? 'items-start' : h === 'right' ? 'items-end' : 'items-center';
  const text = h === 'left' ? 'text-left' : h === 'right' ? 'text-right' : 'text-center';
  const justify = v === 'top' ? 'justify-start' : v === 'bottom' ? 'justify-end' : 'justify-center';
  return { items, text, justify, alignFlex: h === 'left' ? 'flex-start' : h === 'right' ? 'flex-end' : 'center' };
};

// Independent typography styles for headline / subheadline (from
// Typography panel on the new hero variants). Falls back to legacy
// content.titleStyle for backwards compatibility.
const heroHeadlineStyle = (content, fallbackColor) => {
  const s = content.headlineStyle || {};
  const legacy = content.titleStyle || {};
  return {
    ...(s.fontFamily && s.fontFamily !== 'inherit' ? { fontFamily: s.fontFamily } : (legacy.fontFamily && legacy.fontFamily !== 'inherit' ? { fontFamily: legacy.fontFamily } : {})),
    ...(s.fontWeight ? { fontWeight: s.fontWeight } : (legacy.fontWeight ? { fontWeight: legacy.fontWeight } : {})),
    ...(s.fontSize ? { fontSize: responsiveFontSize(s.fontSize) } : {}),
    ...(s.color ? { color: s.color } : (legacy.color ? { color: legacy.color } : (fallbackColor ? { color: fallbackColor } : {}))),
  };
};
const heroSubheadlineStyle = (content, fallbackColor) => {
  const s = content.subheadlineStyle || {};
  return {
    ...(s.fontFamily && s.fontFamily !== 'inherit' ? { fontFamily: s.fontFamily } : {}),
    ...(s.fontWeight ? { fontWeight: s.fontWeight } : {}),
    ...(s.fontSize ? { fontSize: responsiveFontSize(s.fontSize) } : {}),
    ...(s.color ? { color: s.color } : (fallbackColor ? { color: fallbackColor } : {})),
  };
};

const HeroTabs = ({ content, accent, headStyle, subColor, finalBlockStyle, wrapperClass, animationClass }) => {
  const tabs = (content.tabs || []).filter((t) => t);
  const [active, setActive] = useState(0);
  const cur = tabs[active] || tabs[0];
  const tabColor = content.tabColor || accent;
  const tabRadius = content.tabShape === 'rectangle' ? '8px' : '9999px';
  return (
    <section className={`py-10 px-4 md:py-20 md:px-6 ${wrapperClass} ${animationClass}`} style={{ ...finalBlockStyle, ...heroLayoutStyle(content) }}>
      <div className="max-w-4xl mx-auto text-center">
        {content.eyebrow && <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold mb-5" style={{ backgroundColor: accent, color: '#fff' }}>{content.eyebrow}</span>}
        {content.headline && <h1 style={{ ...headStyle, fontSize: responsiveFontSize(52), lineHeight: 1.1, ...heroHeadlineStyle(content) }}>{content.headline}</h1>}
        {content.subtitle && <p className="mt-5 max-w-2xl mx-auto" style={{ color: subColor, fontSize: responsiveFontSize(18), lineHeight: 1.6, ...heroSubheadlineStyle(content) }}>{content.subtitle}</p>}
        {content.ctaText && <a href={heroHref(content.ctaUrl)} target={content.ctaOpenInNewTab ? '_blank' : '_self'} rel={content.ctaOpenInNewTab ? 'noopener noreferrer' : undefined} className={`inline-flex items-center justify-center gap-2 mt-7 px-7 py-3 font-semibold transition-all hover:-translate-y-0.5 ${flatHoverClass(content)}`} style={{ backgroundColor: accent, color: content.buttonTextColor || '#fff', borderRadius: getButtonRadius(content.buttonRadius) }}>{flatHasIcon(content) && flatIconPos(content) === 'left' && flatIcon(content, 18)}{content.ctaText}{flatHasIcon(content) && flatIconPos(content) === 'right' && flatIcon(content, 18)}</a>}
        {tabs.length > 0 && (
          <>
            <div className="flex flex-wrap justify-center gap-2 mt-12">
              {tabs.map((t, i) => (
                <button key={t.id || i} onClick={() => setActive(i)} className="px-4 py-2 text-sm font-medium transition-colors" style={i === active ? { backgroundColor: tabColor, color: '#fff', borderRadius: tabRadius, ...(t.labelStyle || {}) } : { backgroundColor: 'transparent', color: subColor, border: `1px solid ${tabColor}55`, borderRadius: tabRadius, ...(t.labelStyle || {}) }}>
                  {t.label || `Tab ${i + 1}`}
                </button>
              ))}
            </div>
            <div className="mt-8 relative rounded-2xl overflow-hidden mx-auto" style={{ maxWidth: '900px', border: `1px solid ${accent}33`, boxShadow: `0 24px 70px -24px ${accent}66` }}>
              {cur?.imageUrl ? <img src={cur.imageUrl} alt={cur.label || ''} className="w-full block" /> : <div className="flex items-center justify-center bg-slate-100 text-slate-400" style={{ aspectRatio: '16 / 9' }}>Add an image for this tab</div>}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

// 21st.dev-inspired: Expandable Tabs (icon-only -> expands to icon+label)
const EXP_TAB_ICONS = {
  home: 'M3 12l9-9 9 9M5 10v10h14V10',
  bell: 'M6 8a6 6 0 0112 0c0 7 3 9 3 9H3s3-2 3-9M10.3 21a1.94 1.94 0 003.4 0',
  settings: 'M12 15a3 3 0 100-6 3 3 0 000 6zM19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 110-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 114 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1z',
  help: 'M12 22a10 10 0 100-20 10 10 0 000 20zM9.1 9a3 3 0 015.8 1c0 2-3 3-3 3M12 17h.01',
  shield: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z',
  mail: 'M4 4h16v16H4zM4 6l8 6 8-6',
  user: 'M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z',
  file: 'M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8zM14 2v6h6',
  lock: 'M5 11h14v10H5zM8 11V7a4 4 0 018 0v4',
  star: 'M12 2l3 7 7 .5-5.5 4.5 2 7L12 17l-6.5 4 2-7L2 9.5 9 9z',
  heart: 'M20.8 5.6a5.5 5.5 0 00-7.8 0L12 6.6l-1-1a5.5 5.5 0 00-7.8 7.8l1 1L12 22l7.8-7.6 1-1a5.5 5.5 0 000-7.8z',
  globe: 'M12 22a10 10 0 100-20 10 10 0 000 20zM2 12h20M12 2a15 15 0 010 20 15 15 0 010-20z',
  phone: 'M22 16.9v3a2 2 0 01-2.2 2 19.8 19.8 0 01-8.6-3 19.5 19.5 0 01-6-6 19.8 19.8 0 01-3-8.6A2 2 0 014.1 2h3a2 2 0 012 1.7c.1.9.3 1.8.6 2.7a2 2 0 01-.5 2.1L8 9.8a16 16 0 006 6l1.3-1.3a2 2 0 012.1-.4c.9.3 1.8.5 2.7.6a2 2 0 011.7 2z',
  calendar: 'M3 4h18v18H3zM3 10h18M8 2v4M16 2v4',
  cart: 'M6 6h15l-1.5 9h-12zM6 6L5 2H2M9 21a1 1 0 100-2 1 1 0 000 2zM18 21a1 1 0 100-2 1 1 0 000 2z',
  zap: 'M13 2L3 14h7l-1 8 10-12h-7z',
};
const ExpandableTabsBlock = ({ content, accent, headStyle, subColor, finalBlockStyle, wrapperClass, animationClass }) => {
  const tabs = (content.tabs || []).filter((t) => t);
  const tabColor = content.tabColor || accent;
  const [active, setActive] = useState(null);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setActive(null); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  const Icon = ({ name }) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={EXP_TAB_ICONS[name] || EXP_TAB_ICONS.star} /></svg>
  );
  return (
    <section className={`py-14 px-6 ${wrapperClass} ${animationClass}`} style={finalBlockStyle}>
      <div className="max-w-3xl mx-auto text-center">
        {content.heading && <h2 className="mb-3" style={{ fontSize: responsiveFontSize(34), lineHeight: 1.2, ...headStyle }}>{content.heading}</h2>}
        {content.subheading && <p className="mb-8 max-w-xl mx-auto" style={{ color: subColor, fontSize: responsiveFontSize(16) }}>{content.subheading}</p>}
        <div ref={ref} className="inline-flex flex-wrap items-center gap-2 justify-center rounded-2xl p-1.5" style={{ border: `1px solid ${tabColor}33`, background: 'var(--site-card-bg,#fff)' }}>
          {tabs.map((t, i) => {
            if (t.type === 'separator') return <span key={i} className="mx-1.5 self-stretch my-1" style={{ width: '2px', minHeight: '1.75rem', backgroundColor: `${tabColor}66`, borderRadius: '2px' }} />;
            const isActive = active === i;
            const inner = (
              <span className="flex items-center overflow-hidden whitespace-nowrap" style={{ color: isActive ? tabColor : 'var(--site-body-color)' }}>
                <Icon name={t.icon} />
                <span style={{ maxWidth: isActive ? '160px' : '0px', marginLeft: isActive ? '8px' : '0px', transition: 'max-width .35s ease, margin-left .35s ease', fontSize: responsiveFontSize(14), fontWeight: 600 }}>{t.label}</span>
              </span>
            );
            const cls = 'flex items-center rounded-xl px-3 py-2 transition-colors';
            const stl = { backgroundColor: isActive ? `${tabColor}1a` : 'transparent', cursor: 'pointer' };
            return t.url
              ? <a key={t.id || i} href={heroHref(t.url)} className={cls} style={stl} onMouseEnter={() => setActive(i)} onClick={() => setActive(i)}>{inner}</a>
              : <button key={t.id || i} type="button" className={cls} style={stl} onClick={() => setActive(isActive ? null : i)} onMouseEnter={() => setActive(i)}>{inner}</button>;
          })}
          {tabs.length === 0 && <span className="px-3 py-2 text-sm" style={{ color: subColor }}>Add tabs in the editor</span>}
        </div>
      </div>
    </section>
  );
};

// 21st.dev-inspired (Aceternity): Sparkles hero (canvas particle field, no deps)
const SparklesBlock = ({ content, accent, headStyle, subColor, finalBlockStyle, wrapperClass, animationClass }) => {
  const canvasRef = useRef(null);
  const bg = content.backgroundColor || '#020617';
  const particleColor = content.particleColor || '#FFFFFF';
  const density = Math.max(20, Math.min(400, parseInt(content.density) || 120));
  const height = parseInt(content.height) || 460;
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let raf, particles = [], w = 0, h = 0;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      w = rect.width; h = rect.height;
      canvas.width = w * dpr; canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const count = Math.floor((w * h) / 8000 * (density / 120));
      particles = Array.from({ length: count }, () => ({
        x: Math.random() * w, y: Math.random() * h,
        r: Math.random() * 1.1 + 0.3,
        a: Math.random(), s: Math.random() * 0.02 + 0.005,
      }));
    };
    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      ctx.fillStyle = particleColor;
      particles.forEach((p) => {
        p.a += p.s; if (p.a > 1 || p.a < 0) p.s = -p.s;
        ctx.globalAlpha = Math.abs(p.a);
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2); ctx.fill();
      });
      ctx.globalAlpha = 1;
      raf = requestAnimationFrame(draw);
    };
    resize(); draw();
    window.addEventListener('resize', resize);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', resize); };
  }, [density, particleColor]);
  return (
    <section className={`relative overflow-hidden ${wrapperClass} ${animationClass}`} style={{ ...finalBlockStyle, backgroundColor: bg, minHeight: `${height}px` }}>
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" style={{ width: '100%', height: '100%' }} />
      <div className="relative z-10 flex flex-col items-center justify-center text-center px-6" style={{ minHeight: `${height}px` }}>
        {content.headline && <h1 style={{ ...headStyle, color: headStyle?.color && headStyle.color !== 'var(--site-heading-color)' ? headStyle.color : '#fff', fontSize: responsiveFontSize(64), lineHeight: 1.05 }}>{content.headline}</h1>}
        <div className="relative my-5" style={{ width: 'min(38rem,90%)', height: '2px' }}>
          <div className="absolute inset-x-[15%] top-0 h-[2px] blur-sm" style={{ background: `linear-gradient(to right, transparent, ${accent}, transparent)` }} />
          <div className="absolute inset-x-[15%] top-0 h-px" style={{ background: `linear-gradient(to right, transparent, ${accent}, transparent)` }} />
        </div>
        {content.subheadline && <p className="max-w-xl" style={{ color: content.subtitleColor || 'rgba(255,255,255,0.7)', fontSize: responsiveFontSize(18), lineHeight: 1.6 }}>{content.subheadline}</p>}
        {content.buttonText && <a href={heroHref(content.buttonUrl)} target={content.buttonOpenInNewTab ? '_blank' : '_self'} rel={content.buttonOpenInNewTab ? 'noopener noreferrer' : undefined} className={`inline-flex items-center justify-center gap-2 mt-7 px-7 py-3 font-semibold transition-all hover:-translate-y-0.5 ${flatHoverClass(content)}`} style={{ backgroundColor: accent, color: content.buttonTextColor || '#fff', borderRadius: getButtonRadius(content.buttonRadius) }}>{flatHasIcon(content) && flatIconPos(content) === 'left' && flatIcon(content, 18)}{content.buttonText}{flatHasIcon(content) && flatIconPos(content) === 'right' && flatIcon(content, 18)}</a>}
      </div>
    </section>
  );
};

// 21st.dev-inspired (shadcnblocks): Gallery carousel of tall image cards
const GalleryCarouselBlock = ({ content, accent, headStyle, subColor, finalBlockStyle, wrapperClass, animationClass }) => {
  const items = (content.items || []).filter((i) => i);
  const scrollRef = useRef(null);
  const scrollBy = (dir) => {
    const el = scrollRef.current;
    if (el) el.scrollBy({ left: dir * Math.min(el.clientWidth * 0.8, 420), behavior: 'smooth' });
  };
  return (
    <section className={`py-20 ${wrapperClass} ${animationClass}`} style={finalBlockStyle}>
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex items-end justify-between gap-4 mb-8">
          <div>
            {content.title && <h2 style={{ fontSize: responsiveFontSize(40), lineHeight: 1.15, ...headStyle }}>{content.title}</h2>}
            {content.description && <p className="mt-3 max-w-xl" style={{ color: subColor, fontSize: responsiveFontSize(16), lineHeight: 1.6 }}>{content.description}</p>}
          </div>
          <div className="hidden sm:flex gap-2 shrink-0">
            <button type="button" onClick={() => scrollBy(-1)} aria-label="Previous" className="h-10 w-10 rounded-full flex items-center justify-center transition-colors" style={{ border: `1px solid ${accent}55`, color: accent }}><ChevronLeft size={18} /></button>
            <button type="button" onClick={() => scrollBy(1)} aria-label="Next" className="h-10 w-10 rounded-full flex items-center justify-center transition-colors" style={{ border: `1px solid ${accent}55`, color: accent }}><ChevronRight size={18} /></button>
          </div>
        </div>
      </div>
      <div ref={scrollRef} className="flex gap-6 overflow-x-auto pb-4 px-6 max-w-6xl mx-auto" style={{ scrollSnapType: 'x mandatory', scrollbarWidth: 'none' }}>
        {items.map((it, i) => {
          const cardText = content.cardTextColor || '#ffffff';
          return (
          <a key={it.id || i} href={heroHref(it.href)} className="group relative shrink-0 rounded-2xl overflow-hidden block" style={{ width: 'min(20rem,80vw)', height: '24rem', scrollSnapAlign: 'start', boxShadow: `0 20px 60px -28px ${accent}88` }}>
            {it.imageUrl ? <img src={it.imageUrl} alt={it.title || ''} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" /> : <div className="absolute inset-0 bg-slate-200" />}
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,.85), rgba(0,0,0,.1) 55%)' }} />
            <div className="absolute bottom-0 left-0 right-0 p-6" style={{ color: cardText }}>
              {it.title && <h3 className="font-semibold mb-1" style={{ fontSize: responsiveFontSize(20), lineHeight: 1.25 }}>{it.title}</h3>}
              {it.description && <p className="opacity-85 line-clamp-3" style={{ fontSize: responsiveFontSize(14), lineHeight: 1.5 }}>{it.description}</p>}
              <span className="inline-flex items-center gap-1 mt-3 text-sm font-medium">Read more <ChevronRight size={14} /></span>
            </div>
          </a>
          );
        })}
        {items.length === 0 && <div className="shrink-0 rounded-2xl bg-slate-100 border-2 border-dashed border-slate-200 flex items-center justify-center text-slate-400" style={{ width: '20rem', height: '24rem' }}>Add gallery items</div>}
      </div>
    </section>
  );
};

// Shared: star rating row
const StarRow = ({ n = 5, color }) => (
  <div className="flex gap-0.5">
    {Array.from({ length: 5 }).map((_, i) => (
      <svg key={i} width="15" height="15" viewBox="0 0 24 24" fill={i < n ? color : 'none'} stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l3 7 7 .5-5.5 4.5 2 7L12 17l-6.5 4 2-7L2 9.5 9 9z"/></svg>
    ))}
  </div>
);

// 21st.dev-inspired (vaib215/testimonial-cards): rotating stacked testimonial deck
const TestimonialDeckBlock = ({ content, accent, headStyle, subColor, finalBlockStyle, wrapperClass, animationClass }) => {
  const items = (content.items || []).filter((t) => t);
  const [active, setActive] = useState(0);
  const len = items.length;
  useEffect(() => {
    if (len < 2 || content.autoplay === false) return;
    const ms = Math.max(1, parseInt(content.autoplaySeconds) || 5) * 1000;
    const id = setInterval(() => setActive((a) => (a + 1) % len), ms);
    return () => clearInterval(id);
  }, [len, content.autoplay, content.autoplaySeconds]);
  const maxChars = parseInt(content.maxChars) || 0;
  const clip = (q) => (maxChars > 0 && q && q.length > maxChars ? q.slice(0, maxChars).trim() + '…' : q);
  const goDeck = (d) => setActive((a) => (a + d + len) % len);
  return (
    <section className={`py-10 px-4 md:py-20 md:px-6 ${wrapperClass} ${animationClass}`} style={finalBlockStyle}>
      <div className="max-w-3xl mx-auto text-center">
        {content.heading && <h2 className="mb-3" style={{ fontSize: responsiveFontSize(38), lineHeight: 1.15, ...headStyle }}>{content.heading}</h2>}
        {content.subheading && <p className="mb-12 max-w-2xl mx-auto" style={{ color: subColor, fontSize: responsiveFontSize(17) }}>{content.subheading}</p>}
        <div className="relative mx-auto" style={{ maxWidth: '34rem', height: '20rem' }}>
          {items.map((t, i) => {
            const offset = (i - active + len) % len;
            const visible = offset <= 2;
            return (
              <button type="button" key={t.id || i} onClick={() => setActive(offset === 0 ? (i + 1) % len : i)} aria-label={`Testimonial ${i + 1}`}
                className="absolute inset-0 text-left rounded-3xl p-8 flex flex-col"
                style={{
                  background: 'var(--site-card-bg,#fff)',
                  border: `1px solid ${accent}33`,
                  boxShadow: `0 30px 70px -30px ${accent}66`,
                  transform: `translateY(${offset * 14}px) scale(${1 - offset * 0.05}) rotate(${offset === 0 ? 0 : (i % 2 ? 1.5 : -1.5)}deg)`,
                  zIndex: len - offset,
                  opacity: visible ? (offset === 0 ? 1 : 0.55) : 0,
                  transition: 'transform .5s cubic-bezier(.2,.8,.2,1), opacity .5s',
                  cursor: 'pointer',
                }}>
                <StarRow n={t.rating || 5} color={accent} />
                <p className="mt-4 flex-1 overflow-hidden" style={{ color: 'var(--site-heading-color)', fontSize: responsiveFontSize(18), lineHeight: 1.5 }}>“{clip(t.quote)}”</p>
                <div className="flex items-center gap-3 mt-5">
                  {t.avatarUrl ? <img src={t.avatarUrl} alt={t.name || ''} className="h-11 w-11 rounded-full object-cover" /> : <span className="h-11 w-11 rounded-full flex items-center justify-center font-bold text-white" style={{ backgroundColor: accent }}>{(t.name || '?').charAt(0)}</span>}
                  <div>
                    <div className="font-semibold" style={{ color: 'var(--site-heading-color)', fontSize: responsiveFontSize(15) }}>{t.name}</div>
                    {t.role && <div style={{ color: subColor, fontSize: responsiveFontSize(13) }}>{t.role}</div>}
                  </div>
                </div>
              </button>
            );
          })}
          {len === 0 && <div className="absolute inset-0 rounded-3xl bg-slate-100 border-2 border-dashed border-slate-200 flex items-center justify-center text-slate-400">Add testimonials</div>}
        </div>
        {len > 1 && (
          <div className="flex items-center justify-center gap-4 mt-8 px-4">
            <button type="button" onClick={() => goDeck(-1)} aria-label="Previous" className="h-10 w-10 rounded-full flex items-center justify-center transition-transform hover:-translate-y-0.5 shrink-0" style={{ backgroundColor: accent, color: '#fff' }}><ChevronLeft size={18} /></button>
            <div className="flex flex-wrap justify-center gap-2">
              {items.map((_, i) => (
                <button key={i} type="button" onClick={() => setActive(i)} aria-label={`Go to ${i + 1}`} className="h-2 rounded-full transition-all" style={{ width: i === active ? 22 : 8, backgroundColor: i === active ? accent : `${accent}66` }} />
              ))}
            </div>
            <button type="button" onClick={() => goDeck(1)} aria-label="Next" className="h-10 w-10 rounded-full flex items-center justify-center transition-transform hover:-translate-y-0.5 shrink-0" style={{ backgroundColor: accent, color: '#fff' }}><ChevronRight size={18} /></button>
          </div>
        )}
      </div>
    </section>
  );
};

// 21st.dev-inspired (suraj-xd/liquid-glass): Apple-style frosted glass CTA card
const LiquidGlassBlock = ({ content, accent, headStyle, finalBlockStyle, wrapperClass, animationClass }) => {
  const dark = content.glassTint === 'dark';
  const glassBg = dark ? 'rgba(15,23,42,0.35)' : 'rgba(255,255,255,0.18)';
  const txtColor = content.textColor || (dark ? '#f8fafc' : '#0f172a');
  const headColor = (headStyle?.color && headStyle.color !== 'var(--site-heading-color)') ? headStyle.color : txtColor;
  const bgH = parseInt(content.height) || 0;
  const bgStyle = content.backgroundImageUrl
    ? { backgroundImage: `url(${content.backgroundImageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center', ...(bgH ? { minHeight: `${bgH}px` } : {}) }
    : { background: content.backgroundColor || `linear-gradient(135deg, ${accent}, #0ea5e9 60%, #8b5cf6)` };
  return (
    <section className={`relative overflow-hidden py-24 px-6 flex items-center ${wrapperClass} ${animationClass}`} style={{ ...finalBlockStyle, ...bgStyle }}>
      <div className="relative max-w-2xl mx-auto rounded-[28px] p-10 text-center"
        style={{
          background: glassBg,
          backdropFilter: 'blur(18px) saturate(160%)',
          WebkitBackdropFilter: 'blur(18px) saturate(160%)',
          border: '1px solid rgba(255,255,255,0.35)',
          boxShadow: `inset 0 1px 1px rgba(255,255,255,0.6), inset 0 -1px 1px rgba(255,255,255,0.15), 0 30px 80px -30px rgba(0,0,0,0.45)`,
        }}>
        {content.eyebrow && <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold mb-5" style={{ backgroundColor: 'rgba(255,255,255,0.25)', color: txtColor, border: '1px solid rgba(255,255,255,0.35)' }}>{content.eyebrow}</span>}
        {content.headline && <h2 style={{ ...headStyle, color: headColor, fontSize: responsiveFontSize(parseInt(content.titleStyle?.fontSize) || 44), lineHeight: 1.1 }}>{content.headline}</h2>}
        {content.subtitle && <p className="mt-4 max-w-lg mx-auto" style={{ color: txtColor, opacity: 0.85, fontSize: responsiveFontSize(18), lineHeight: 1.6 }}>{content.subtitle}</p>}
        {content.buttonText && <a href={heroHref(content.buttonUrl)} target={content.buttonOpenInNewTab ? '_blank' : '_self'} rel={content.buttonOpenInNewTab ? 'noopener noreferrer' : undefined} className={`inline-flex items-center justify-center gap-2 mt-7 px-7 py-3 font-semibold transition-all hover:-translate-y-0.5 ${flatHoverClass(content)}`} style={{ background: 'rgba(255,255,255,0.9)', color: content.buttonTextColor || '#0f172a', boxShadow: '0 8px 24px -8px rgba(0,0,0,0.4)', borderRadius: getButtonRadius(content.buttonRadius || 'pill') }}>{flatHasIcon(content) && flatIconPos(content) === 'left' && flatIcon(content, 18)}{content.buttonText}{flatHasIcon(content) && flatIconPos(content) === 'right' && flatIcon(content, 18)}</a>}
      </div>
    </section>
  );
};

// 21st.dev-inspired (efferd/testimonials-columns-1): auto-scrolling vertical columns
const TestimonialsColumnsBlock = ({ content, accent, headStyle, subColor, finalBlockStyle, wrapperClass, animationClass, scopeId }) => {
  const items = (content.items || []).filter((t) => t);
  const sc = scopeId || 'tc';
  const speed = Math.max(15, parseInt(content.speed) || 40);
  const cols = items.length >= 3 ? 3 : (items.length >= 2 ? 2 : 1);
  const split = [[], [], []];
  items.forEach((t, i) => split[i % cols].push(t));
  const cardText = content.cardTextColor;
  const Card = ({ t, k }) => (
    <div key={k} className="rounded-2xl p-6 mb-4" style={{ background: 'var(--site-card-bg,#fff)', border: `1px solid ${accent}22`, boxShadow: `0 12px 40px -24px ${accent}55` }}>
      <p style={{ color: cardText || 'var(--site-heading-color)', fontSize: responsiveFontSize(15), lineHeight: 1.55 }}>{t.quote}</p>
      <div className="flex items-center gap-3 mt-4">
        {t.avatarUrl ? <img src={t.avatarUrl} alt={t.name || ''} className="h-9 w-9 rounded-full object-cover" /> : <span className="h-9 w-9 rounded-full flex items-center justify-center text-white text-sm font-bold" style={{ backgroundColor: accent }}>{(t.name || '?').charAt(0)}</span>}
        <div>
          <div className="font-semibold leading-tight" style={{ color: cardText || 'var(--site-heading-color)', fontSize: responsiveFontSize(14) }}>{t.name}</div>
          {t.role && <div style={{ color: cardText || subColor, opacity: cardText ? 0.75 : 1, fontSize: responsiveFontSize(12) }}>{t.role}</div>}
        </div>
      </div>
    </div>
  );
  return (
    <section className={`${sc} py-10 px-4 md:py-20 md:px-6 ${wrapperClass} ${animationClass}`} style={finalBlockStyle}>
      <style>{`
        @keyframes ${sc}-scroll{from{transform:translateY(0);}to{transform:translateY(-50%);}}
        .${sc} .tc-col{animation:${sc}-scroll linear infinite;}
        .${sc} .tc-col:hover{animation-play-state:paused;}
      `}</style>
      <div className="max-w-2xl mx-auto text-center mb-14">
        {content.badge && <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold mb-4" style={{ backgroundColor: `${accent}1a`, color: accent, border: `1px solid ${accent}33` }}>{content.badge}</span>}
        {content.heading && <h2 style={{ fontSize: responsiveFontSize(40), lineHeight: 1.15, ...headStyle }}>{content.heading}</h2>}
        {content.subheading && <p className="mt-4" style={{ color: subColor, fontSize: responsiveFontSize(17) }}>{content.subheading}</p>}
      </div>
      <div className="max-w-6xl mx-auto grid gap-6" style={{ gridTemplateColumns: `repeat(${cols},minmax(0,1fr))`, maxHeight: '34rem', overflow: 'hidden', maskImage: 'linear-gradient(to bottom, transparent, #000 12%, #000 88%, transparent)', WebkitMaskImage: 'linear-gradient(to bottom, transparent, #000 12%, #000 88%, transparent)' }}>
        {split.slice(0, cols).map((colItems, ci) => (
          <div key={ci} className={ci > 0 ? 'hidden md:block' : ''}>
            <div className="tc-col" style={{ animationDuration: `${speed + ci * 8}s` }}>
              {[...colItems, ...colItems].map((t, i) => <Card key={`${ci}-${i}`} t={t} k={`${ci}-${i}`} />)}
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="col-span-full rounded-2xl bg-slate-100 border-2 border-dashed border-slate-200 flex items-center justify-center text-slate-400 py-16">Add testimonials</div>}
      </div>
    </section>
  );
};

// 21st.dev-inspired (danielpetho/typewriter): typing + deleting text cycle
const TypewriterBlock = ({ content, accent, headStyle, finalBlockStyle, wrapperClass, animationClass }) => {
  const phrases = (content.phrases || []).filter((p) => p && p.length);
  const speed = parseInt(content.speed) || 70;
  const deleteSpeed = parseInt(content.deleteSpeed) || 40;
  const waitTime = parseInt(content.waitTime) || 1500;
  const loop = content.loop !== false;
  const cursorChar = content.cursorChar || '|';
  const [display, setDisplay] = useState('');
  const [pi, setPi] = useState(0);
  const [deleting, setDeleting] = useState(false);
  useEffect(() => {
    if (phrases.length === 0) return;
    const cur = phrases[pi % phrases.length];
    let t;
    if (!deleting && display.length < cur.length) {
      t = setTimeout(() => setDisplay(cur.slice(0, display.length + 1)), speed);
    } else if (!deleting && display.length === cur.length) {
      if (phrases.length === 1 && !loop) return;
      t = setTimeout(() => setDeleting(true), waitTime);
    } else if (deleting && display.length > 0) {
      t = setTimeout(() => setDisplay(cur.slice(0, display.length - 1)), deleteSpeed);
    } else if (deleting && display.length === 0) {
      setDeleting(false);
      const next = pi + 1;
      if (next >= phrases.length && !loop) return;
      setPi(next % phrases.length);
    }
    return () => clearTimeout(t);
  }, [display, deleting, pi, phrases.length, speed, deleteSpeed, waitTime, loop]);
  const align = (content.titleStyle?.align) || 'left';
  return (
    <section className={`py-10 px-4 md:py-20 md:px-6 ${wrapperClass} ${animationClass}`} style={finalBlockStyle}>
      <div className="max-w-4xl mx-auto" style={{ textAlign: align }}>
        <p className="whitespace-pre-wrap" style={{ ...headStyle, textAlign: align, fontSize: responsiveFontSize(parseInt(content.titleStyle?.fontSize) || 44), lineHeight: 1.2 }}>
          {content.prefixText && <span>{content.prefixText}</span>}
          <span style={{ color: content.wordColor || accent }}>{display}</span>
          <span className="tw-cursor" style={{ color: content.wordColor || accent }}>{cursorChar}</span>
        </p>
      </div>
      <style>{`@keyframes tw-blink{0%,100%{opacity:1}50%{opacity:0}} .tw-cursor{animation:tw-blink 1s step-end infinite;}`}</style>
    </section>
  );
};

// 21st.dev-inspired (victorwelander/gooey-text-morphing): smooth blur crossfade word cycle
const GooeyTextBlock = ({ content, accent, headStyle, finalBlockStyle, wrapperClass, animationClass, scopeId }) => {
  const texts = (content.texts || []).filter((t) => t && t.length);
  const sc = scopeId || 'goo';
  const hold = ((parseFloat(content.morphTime) || 1) + (parseFloat(content.cooldownTime) || 0.25)) * 1000 + 1000;
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (texts.length < 2) return;
    const dwell = Math.max(1600, hold);
    const id = setInterval(() => setIdx((i) => (i + 1) % texts.length), dwell);
    return () => clearInterval(id);
  }, [texts.length, hold]);
  const fs = responsiveFontSize(parseInt(content.titleStyle?.fontSize) || 64);
  const gooeyColor = content.titleStyle?.color || accent;
  return (
    <section className={`${sc} relative py-24 px-6 ${wrapperClass} ${animationClass}`} style={finalBlockStyle}>
      <div className="relative flex items-center justify-center" style={{ minHeight: '8rem' }}>
        {texts.length === 0 && <span className="inline-block text-slate-400" style={{ ...headStyle, color: undefined, fontSize: fs }}>Add words in the editor</span>}
        {texts.map((t, i) => (
          <span key={i} className="absolute inline-block whitespace-nowrap" style={{ ...headStyle, color: gooeyColor, fontSize: fs, lineHeight: 1, opacity: i === idx ? 1 : 0, filter: i === idx ? 'blur(0px)' : 'blur(10px)', transform: i === idx ? 'scale(1)' : 'scale(.96)', transition: 'opacity .55s ease, filter .55s ease, transform .55s ease', pointerEvents: 'none' }}>{t}</span>
        ))}
      </div>
    </section>
  );
};

// 21st.dev-inspired (thimows/animated-text-cycle): single word swap inside a sentence
const TextCycleBlock = ({ content, accent, headStyle, finalBlockStyle, wrapperClass, animationClass }) => {
  const words = (content.words || []).filter((w) => w && w.length);
  const interval = parseInt(content.interval) || 3000;
  const [idx, setIdx] = useState(0);
  const [anim, setAnim] = useState(true);
  useEffect(() => {
    if (words.length < 2) return;
    const id = setInterval(() => {
      setAnim(false);
      setTimeout(() => { setIdx((i) => (i + 1) % words.length); setAnim(true); }, 240);
    }, interval);
    return () => clearInterval(id);
  }, [words.length, interval]);
  const align = (content.titleStyle?.align) || 'center';
  return (
    <section className={`py-10 px-4 md:py-20 md:px-6 ${wrapperClass} ${animationClass}`} style={finalBlockStyle}>
      <div className="max-w-4xl mx-auto" style={{ textAlign: align }}>
        <h2 style={{ ...headStyle, textAlign: align, fontSize: responsiveFontSize(parseInt(content.titleStyle?.fontSize) || 44), lineHeight: 1.25 }}>
          {content.prefix && <span>{content.prefix} </span>}
          <span className="inline-block" style={{ color: content.wordColor || accent, transition: 'opacity .24s ease, transform .24s ease', opacity: anim ? 1 : 0, transform: anim ? 'translateY(0)' : 'translateY(10px)' }}>{words[idx % (words.length || 1)] || ''}</span>
          {content.suffix && <span> {content.suffix}</span>}
        </h2>
      </div>
    </section>
  );
};

// 21st.dev-inspired (Northstrix/circular-testimonials): photo stack + quote carousel
const CircularTestimonialsBlock = ({ content, accent, headStyle, subColor, finalBlockStyle, wrapperClass, animationClass }) => {
  const items = (content.items || []).filter((t) => t);
  const len = items.length;
  const [active, setActive] = useState(0);
  useEffect(() => {
    if (len < 2 || content.autoplay === false) return;
    const id = setInterval(() => setActive((a) => (a + 1) % len), 5000);
    return () => clearInterval(id);
  }, [len, content.autoplay]);
  const cur = items[active] || {};
  const go = (d) => setActive((a) => (a + d + len) % len);
  return (
    <section className={`py-10 px-4 md:py-20 md:px-6 ${wrapperClass} ${animationClass}`} style={finalBlockStyle}>
      <div className="max-w-5xl mx-auto">
        {content.heading && <h2 className="text-center mb-12" style={{ fontSize: responsiveFontSize(38), lineHeight: 1.15, ...headStyle }}>{content.heading}</h2>}
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div className="relative mx-auto" style={{ width: '100%', maxWidth: '22rem', height: '22rem', perspective: '1000px' }}>
            {items.map((t, i) => {
              const offset = (i - active + len) % len;
              const isActive = offset === 0;
              const behind = offset === len - 1;
              const imgStyle = {
                opacity: isActive ? 1 : (behind ? 0.55 : 0),
                transform: `scale(${isActive ? 1 : 0.92}) rotateY(${isActive ? 0 : (behind ? 12 : -12)}deg) translateX(${isActive ? 0 : (behind ? 16 : -16)}px)`,
                zIndex: isActive ? 3 : (behind ? 2 : 1),
                transition: 'opacity .6s, transform .6s',
                boxShadow: '0 30px 70px -30px rgba(0,0,0,.5)',
              };
              return t.imageUrl
                ? <img key={t.id || i} src={t.imageUrl} alt={t.name || ''} className="absolute inset-0 w-full h-full object-cover rounded-3xl" style={imgStyle} />
                : <div key={t.id || i} className="absolute inset-0 rounded-3xl flex items-center justify-center text-white font-bold" style={{ ...imgStyle, backgroundColor: accent, fontSize: responsiveFontSize(64) }}>{(t.name || '?').charAt(0)}</div>;
            })}
            {len === 0 && <div className="absolute inset-0 rounded-3xl bg-slate-100 border-2 border-dashed border-slate-200 flex items-center justify-center text-slate-400">Add testimonials</div>}
          </div>
          <div>
            <p style={{ color: 'var(--site-heading-color)', fontSize: responsiveFontSize(20), lineHeight: 1.55 }}>“{cur.quote}”</p>
            <div className="mt-6">
              <div className="font-bold break-words" style={{ color: 'var(--site-heading-color)', fontSize: responsiveFontSize(18), lineHeight: 1.25 }}>{cur.name}</div>
              {cur.designation && <div className="break-words" style={{ color: subColor, fontSize: responsiveFontSize(14) }}>{cur.designation}</div>}
            </div>
            {len > 1 && (
              <div className="flex gap-3 mt-8">
                <button type="button" onClick={() => go(-1)} aria-label="Previous" className="h-11 w-11 rounded-full flex items-center justify-center transition-transform hover:-translate-y-0.5" style={{ backgroundColor: accent, color: '#fff' }}><ChevronLeft size={20} /></button>
                <button type="button" onClick={() => go(1)} aria-label="Next" className="h-11 w-11 rounded-full flex items-center justify-center transition-transform hover:-translate-y-0.5" style={{ backgroundColor: accent, color: '#fff' }}><ChevronRight size={20} /></button>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// 21st.dev-inspired (vaib215/stagger-testimonials): horizontal fanned card carousel
const StaggerTestimonialsBlock = ({ content, accent, headStyle, subColor, finalBlockStyle, wrapperClass, animationClass }) => {
  const items = (content.items || []).filter((t) => t);
  const len = items.length;
  const [active, setActive] = useState(0);
  useEffect(() => {
    if (len < 2 || content.autoplay === false) return;
    const id = setInterval(() => setActive((a) => (a + 1) % len), 5000);
    return () => clearInterval(id);
  }, [len, content.autoplay]);
  const go = (d) => setActive((a) => (a + d + len) % len);
  const cardColor = content.cardColor || accent;
  return (
    <section className={`py-10 px-4 md:py-20 md:px-6 ${wrapperClass} ${animationClass}`} style={finalBlockStyle}>
      <div className="max-w-5xl mx-auto">
        {content.heading && <h2 className="text-center mb-12" style={{ fontSize: responsiveFontSize(38), lineHeight: 1.15, ...headStyle }}>{content.heading}</h2>}
        <div className="relative mx-auto" style={{ height: '24rem', perspective: '1000px' }}>
          {items.map((t, i) => {
            let diff = i - active;
            if (diff > len / 2) diff -= len;
            if (diff < -len / 2) diff += len;
            const isActive = diff === 0;
            const abs = Math.abs(diff);
            return (
              <div key={t.id || i} onClick={() => isActive ? go(1) : setActive(i)}
                className="absolute top-0 left-1/2 rounded-3xl p-8 flex flex-col cursor-pointer"
                style={{
                  width: 'min(24rem,86vw)', height: '22rem', marginLeft: 'calc(min(24rem, 86vw) / -2)',
                  background: isActive ? cardColor : 'var(--site-card-bg,#fff)',
                  color: isActive ? '#fff' : 'var(--site-heading-color)',
                  border: `1px solid ${cardColor}33`,
                  transform: `translateX(${diff * 55}%) translateZ(${isActive ? 0 : -180}px) rotateY(${diff * -10}deg) scale(${isActive ? 1 : 0.85})`,
                  opacity: abs > 2 ? 0 : 1,
                  zIndex: len - abs,
                  boxShadow: isActive ? `0 30px 70px -28px ${cardColor}aa` : '0 14px 40px -28px rgba(0,0,0,.4)',
                  transition: 'transform .5s cubic-bezier(.2,.8,.2,1), opacity .5s, background .4s',
                  pointerEvents: abs > 2 ? 'none' : 'auto',
                }}>
                {t.avatarUrl && <img src={t.avatarUrl} alt={t.name || ''} className="h-14 w-14 rounded-full object-cover mb-4" />}
                <p className="flex-1 font-medium" style={{ fontSize: responsiveFontSize(19), lineHeight: 1.45 }}>“{t.quote}”</p>
                <p className="mt-4" style={{ fontSize: responsiveFontSize(14), opacity: isActive ? 0.9 : 0.7, color: content.subtitleColor || undefined }}>- {t.name}{t.role ? `, ${t.role}` : ''}</p>
              </div>
            );
          })}
          {len === 0 && <div className="absolute inset-0 rounded-3xl bg-slate-100 border-2 border-dashed border-slate-200 flex items-center justify-center text-slate-400">Add testimonials</div>}
        </div>
        {len > 1 && (
          <div className="flex justify-center gap-3 mt-8">
            <button type="button" onClick={() => go(-1)} aria-label="Previous" className="h-11 w-11 rounded-full flex items-center justify-center transition-transform hover:-translate-y-0.5" style={{ backgroundColor: cardColor, color: '#fff' }}><ChevronLeft size={20} /></button>
            <button type="button" onClick={() => go(1)} aria-label="Next" className="h-11 w-11 rounded-full flex items-center justify-center transition-transform hover:-translate-y-0.5" style={{ backgroundColor: cardColor, color: '#fff' }}><ChevronRight size={20} /></button>
          </div>
        )}
      </div>
    </section>
  );
};

// 21st.dev-inspired (kokonutd/hand-writing-text): SVG draw-on title with circle swoosh
const HandWritingBlock = ({ content, accent, headStyle, subColor, finalBlockStyle, wrapperClass, animationClass, scopeId }) => {
  const sc = scopeId || 'hw';
  const circleColor = content.circleColor || accent;
  const loop = content.loop === true;
  const [inView, setInView] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') { setInView(true); return; }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold: 0.3 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <section ref={ref} className={`${sc} ${inView ? 'hw-in' : ''} relative py-24 px-6 text-center ${wrapperClass} ${animationClass}`} style={finalBlockStyle}>
      <style>{`
        .${sc} .hw-path{stroke-dasharray:1600;stroke-dashoffset:1600;}
        .${sc}.hw-in .hw-path{animation:${sc}-draw ${loop ? '5s' : '2.5s'} ease ${loop ? 'infinite' : 'forwards'} .2s;}
        @keyframes ${sc}-draw{${loop ? '0%{stroke-dashoffset:1600}45%{stroke-dashoffset:0}80%{stroke-dashoffset:0}100%{stroke-dashoffset:1600}' : 'to{stroke-dashoffset:0}'}}
        .${sc} .hw-title{opacity:${loop ? 1 : 0};}
        .${sc}.hw-in .hw-title{${loop ? '' : `animation:${sc}-fade .8s ease forwards .9s;`}}
        @keyframes ${sc}-fade{to{opacity:1;}}
      `}</style>
      <div className="relative inline-block px-24 py-16">
        <svg className="absolute pointer-events-none" style={{ inset: '0', width: '100%', height: '100%', zIndex: 0 }} viewBox="0 0 600 220" fill="none" preserveAspectRatio="none" aria-hidden>
          <path className="hw-path" d="M70 110 C 130 40, 240 30, 320 38 C 470 50, 560 78, 552 118 C 545 160, 450 188, 300 190 C 150 192, 48 168, 52 118 C 55 82, 120 58, 220 54" stroke={circleColor} strokeWidth="5" strokeLinecap="round" />
        </svg>
        <h1 className="hw-title relative" style={{ ...headStyle, fontSize: responsiveFontSize(64), lineHeight: 1.1, zIndex: 1 }}>{content.title || 'Hello'}</h1>
      </div>
      {content.subtitle && <p className="hw-title mt-6 max-w-xl mx-auto" style={{ color: subColor, fontSize: responsiveFontSize(18) }}>{content.subtitle}</p>}
    </section>
  );
};

// 21st.dev-inspired (aceternity/typewriter-effect smooth): word-by-word reveal + cursor + CTAs
const TypewriterSmoothBlock = ({ content, accent, headStyle, subColor, finalBlockStyle, wrapperClass, animationClass, scopeId }) => {
  const sc = scopeId || 'tws';
  const words = (content.words || []).filter((w) => w && w.length);
  const accentIdx = words.length - 1;
  const wordColor = content.wordColor || accent;
  const align = (content.titleStyle?.align) || 'center';
  const loop = content.loop === true;
  const [count, setCount] = useState(0);
  const [inView, setInView] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') { setInView(true); return; }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold: 0.25 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  useEffect(() => {
    if (!inView || words.length === 0) return;
    let t;
    if (count < words.length) t = setTimeout(() => setCount((c) => c + 1), 430);
    else if (loop) t = setTimeout(() => setCount(0), 1800);
    return () => clearTimeout(t);
  }, [inView, count, words.length, loop]);
  const typingDone = count >= words.length;
  return (
    <section ref={ref} className={`${sc} py-10 px-4 md:py-20 md:px-6 ${wrapperClass} ${animationClass}`} style={finalBlockStyle}>
      <style>{`
        .${sc} .tws-cursor{display:inline-block;width:.1em;height:1em;background:${wordColor};margin-left:.12em;vertical-align:-.05em;animation:${sc}-blink 1s step-end infinite;}
        @keyframes ${sc}-blink{0%,100%{opacity:1}50%{opacity:0}}
      `}</style>
      <div className="max-w-4xl mx-auto" style={{ textAlign: align }}>
        {/* Match prefix font size to the rotating words so the whole line
            reads as one continuous phrase (`Build with <word>`), not as a
            small tag above a big title. Users can still override via
            `content.titleStyle.fontSize`. */}
        {(() => {
          const titleSize = parseInt(content.titleStyle?.fontSize) || 56;
          return (
            <>
              {content.prefix && <p className="mb-4" style={{ color: subColor, fontSize: responsiveFontSize(titleSize), lineHeight: 1.15 }}>{content.prefix}</p>}
              <div style={{ ...headStyle, fontSize: responsiveFontSize(titleSize), lineHeight: 1.15 }}>
                {words.map((w, i) => (
                  <span key={i} style={{ display: 'inline-block', marginRight: '0.3em', color: i === accentIdx ? wordColor : undefined, opacity: i < count ? 1 : 0, transform: i < count ? 'translateY(0)' : 'translateY(0.25em)', transition: 'opacity .35s ease, transform .35s ease' }}>{w}</span>
                ))}
                <span className="tws-cursor" style={{ opacity: typingDone && !loop ? 1 : 1 }} />
              </div>
            </>
          );
        })()}
        {(content.button1Text || content.button2Text) && (
          <div className="flex flex-wrap gap-4 mt-9" style={{ justifyContent: align === 'center' ? 'center' : (align === 'right' ? 'flex-end' : 'flex-start') }}>
            {content.button1Text && <a href={heroHref(content.button1Url)} target={content.button1OpenInNewTab ? '_blank' : '_self'} rel={content.button1OpenInNewTab ? 'noopener noreferrer' : undefined} className={`inline-flex items-center justify-center gap-2 px-7 py-3 font-semibold transition-all hover:-translate-y-0.5 ${flatHoverClass(content)}`} style={{ backgroundColor: accent, color: content.buttonTextColor || '#fff', borderRadius: getButtonRadius(content.buttonRadius || 'rounded') }}>{flatHasIcon(content) && flatIconPos(content) === 'left' && flatIcon(content, 18)}{content.button1Text}{flatHasIcon(content) && flatIconPos(content) === 'right' && flatIcon(content, 18)}</a>}
            {content.button2Text && <a href={heroHref(content.button2Url)} target={content.button2OpenInNewTab ? '_blank' : '_self'} rel={content.button2OpenInNewTab ? 'noopener noreferrer' : undefined} className={`inline-flex items-center justify-center gap-2 px-7 py-3 font-semibold transition-all hover:-translate-y-0.5 ${flatHoverClass(content)}`} style={{ border: `1px solid ${accent}66`, color: content.button2TextColor || 'var(--site-heading-color)', borderRadius: getButtonRadius(content.buttonRadius || 'rounded') }}>{content.button2Text}</a>}
          </div>
        )}
      </div>
    </section>
  );
};

// 21st.dev-inspired (uithefactory/interactive-folder-gallery): click-to-open folder fan
const FolderGalleryBlock = ({ content, accent, headStyle, subColor, finalBlockStyle, wrapperClass, animationClass }) => {
  const imgs = (content.items || []).filter((i) => i && i.imageUrl).slice(0, 6);
  const [open, setOpen] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const n = imgs.length;
  const folderColor = content.folderColor || accent;
  const fan = (i) => {
    const center = (n - 1) / 2;
    const rel = i - center;
    return { x: rel * 64, y: -120 - Math.abs(rel) * -6, rot: rel * 9 };
  };
  const openLightbox = (i) => setLightbox(i);
  const step = (d) => setLightbox((v) => (v + d + n) % n);
  return (
    <section className={`py-24 px-6 text-center ${wrapperClass} ${animationClass}`} style={finalBlockStyle}>
      {content.heading && <h2 className="mb-3" style={{ fontSize: responsiveFontSize(34), lineHeight: 1.2, ...headStyle }}>{content.heading}</h2>}
      {content.subheading && <p className="mb-12 max-w-xl mx-auto" style={{ color: subColor, fontSize: responsiveFontSize(16) }}>{content.subheading}</p>}
      <div className="relative mx-auto select-none" style={{ width: '20rem', height: '18rem' }}>
        {/* photos */}
        {imgs.map((it, i) => {
          const f = fan(i);
          return (
            <button type="button" key={it.id || i} onClick={() => open && openLightbox(i)} aria-label={`View photo ${i + 1}`}
              className="absolute left-1/2 rounded-xl overflow-hidden p-0 border-0"
              style={{
                width: '8rem', height: '10rem', bottom: '3.5rem', marginLeft: '-4rem',
                transform: open ? `translate(${f.x}px, ${f.y}px) rotate(${f.rot}deg)` : 'translate(0,0) rotate(0deg) scale(.8)',
                opacity: open ? 1 : 0,
                zIndex: open ? 5 + i : 1,
                boxShadow: '0 18px 40px -18px rgba(0,0,0,.5)',
                transition: `transform .55s cubic-bezier(.2,1.2,.4,1) ${i * 40}ms, opacity .4s ${i * 40}ms`,
                cursor: open ? 'zoom-in' : 'default',
                pointerEvents: open ? 'auto' : 'none',
                background: 'transparent',
              }}>
              <img src={it.imageUrl} alt="" className="w-full h-full object-cover" draggable={false} />
            </button>
          );
        })}
        {/* folder back */}
        <div className="absolute bottom-0 left-1/2 rounded-2xl" style={{ width: '18rem', height: '11rem', marginLeft: '-9rem', background: folderColor, opacity: 0.55, zIndex: 4 }} />
        {/* folder front (clickable) */}
        <button type="button" onClick={() => setOpen((o) => !o)} aria-label="Toggle folder" className="absolute bottom-0 left-1/2 rounded-2xl rounded-tl-none flex items-end justify-center pb-4 cursor-pointer"
          style={{ width: '18rem', height: '8.5rem', marginLeft: '-9rem', background: folderColor, zIndex: 10, transform: open ? 'rotateX(-18deg)' : 'rotateX(0)', transformOrigin: 'bottom', transition: 'transform .5s', boxShadow: `0 20px 50px -20px ${folderColor}cc` }}>
          <span className="text-white font-semibold" style={{ fontSize: responsiveFontSize(15) }}>{content.folderLabel || 'Photos'}</span>
        </button>
      </div>
      <p className="mt-8 text-sm" style={{ color: subColor }}>{open ? (content.openHint || 'Click a photo to view it — or the folder to close') : (content.closedHint || 'Click the folder to open')}</p>
      {/* Lightbox */}
      {lightbox !== null && imgs[lightbox] && (
        <div onClick={() => setLightbox(null)} className="fixed inset-0 z-[9999] flex items-center justify-center p-6" style={{ background: 'rgba(0,0,0,0.85)' }}>
          <button type="button" onClick={(e) => { e.stopPropagation(); setLightbox(null); }} aria-label="Close" className="absolute top-5 right-5 h-11 w-11 rounded-full flex items-center justify-center text-white" style={{ background: 'rgba(255,255,255,0.15)' }}><X size={22} /></button>
          {n > 1 && <button type="button" onClick={(e) => { e.stopPropagation(); step(-1); }} aria-label="Previous" className="absolute left-5 h-12 w-12 rounded-full flex items-center justify-center text-white" style={{ background: 'rgba(255,255,255,0.15)' }}><ChevronLeft size={26} /></button>}
          <img src={imgs[lightbox].imageUrl} alt="" onClick={(e) => e.stopPropagation()} className="max-h-[85vh] max-w-[85vw] rounded-2xl object-contain" style={{ boxShadow: '0 30px 80px -20px rgba(0,0,0,0.8)' }} />
          {n > 1 && <button type="button" onClick={(e) => { e.stopPropagation(); step(1); }} aria-label="Next" className="absolute right-5 h-12 w-12 rounded-full flex items-center justify-center text-white" style={{ background: 'rgba(255,255,255,0.15)' }}><ChevronRight size={26} /></button>}
        </div>
      )}
    </section>
  );
};






const CountdownBlock = ({ content, accentColor, headStyle, finalBlockStyle, wrapperClass, animationClass }) => {
  const calc = () => {
    const target = content.targetDate ? new Date(content.targetDate).getTime() : 0;
    const diff = target - Date.now();
    if (!target || isNaN(target) || diff <= 0) return null;
    const s = Math.floor(diff / 1000);
    return { days: Math.floor(s / 86400), hours: Math.floor((s % 86400) / 3600), minutes: Math.floor((s % 3600) / 60), seconds: s % 60 };
  };
  const [remaining, setRemaining] = useState(calc());
  useEffect(() => {
    setRemaining(calc());
    const id = setInterval(() => setRemaining(calc()), 1000);
    return () => clearInterval(id);
  }, [content.targetDate]);

  const accent = accentColor || 'var(--site-accent)';
  const boxBg = content.boxBgColor || accent;
  const boxText = content.boxTextColor || '#fff';
  const alignClass = content.align === 'left' ? 'items-start text-left' : (content.align === 'right' ? 'items-end text-right' : 'items-center text-center');
  const expired = remaining === null;

  const Box = ({ val, label }) => (
    <div className="flex flex-col items-center">
      <div className="rounded-xl font-extrabold tabular-nums shadow-sm" style={{ minWidth: '78px', padding: '16px 10px', backgroundColor: boxBg, color: boxText, fontSize: responsiveFontSize(36), lineHeight: 1 }}>
        {String(val).padStart(2, '0')}
      </div>
      <span className="mt-2 text-xs uppercase tracking-wider" style={{ color: 'var(--site-body-color)', opacity: 0.7 }}>{label}</span>
    </div>
  );

  return (
    <section className={`pt-16 pb-20 px-6 ${wrapperClass} ${animationClass}`} style={finalBlockStyle}>
      <div className={`max-w-3xl mx-auto flex flex-col ${alignClass}`}>
        {content.heading && (
          <h2 style={{ color: 'var(--site-heading-color)', fontFamily: 'var(--site-heading-font)', fontSize: responsiveFontSize(36), fontWeight: 800, lineHeight: 1.2, ...(headStyle || {}) }}>{content.heading}</h2>
        )}
        {content.subheading && (
          <p className="mt-3 max-w-xl" style={{ color: 'var(--site-body-color)', opacity: 0.8, fontSize: responsiveFontSize(17) }}>{content.subheading}</p>
        )}
        {expired ? (
          <div className="mt-8 inline-block px-6 py-4 rounded-xl font-bold" style={{ backgroundColor: accent, color: '#fff', fontSize: responsiveFontSize(22) }}>
            {content.expiredText || "We're live!"}
          </div>
        ) : (
          <div className="mt-8 flex gap-3 sm:gap-5">
            <Box val={remaining.days} label="Days" />
            <Box val={remaining.hours} label="Hours" />
            <Box val={remaining.minutes} label="Minutes" />
            <Box val={remaining.seconds} label="Seconds" />
          </div>
        )}
        {!expired && content.buttonText && (
          <a href={content.buttonUrl && /^(https?:|\/|#|mailto:|tel:)/i.test(content.buttonUrl) ? content.buttonUrl : (content.buttonUrl ? `https://${content.buttonUrl}` : '#')} className={`mt-8 inline-flex items-center justify-center gap-2 px-6 py-3 font-semibold transition-all hover:-translate-y-0.5 ${flatHoverClass(content)}`} style={{ backgroundColor: accent, color: content.buttonTextColor || '#fff', borderRadius: getButtonRadius(content.buttonRadius) }}>
            {flatHasIcon(content) && flatIconPos(content) === 'left' && flatIcon(content, 18)}
            {content.buttonText}
            {flatHasIcon(content) && flatIconPos(content) === 'right' && flatIcon(content, 18)}
          </a>
        )}
      </div>
    </section>
  );
};

// Kometa-inspired: Steps with icon circles + connecting arrows
const StepsIconsBlock = ({ content, accent, headStyle, subColor, finalBlockStyle, wrapperClass, animationClass }) => {
  const steps = (content.steps || []).filter((s) => s);
  const ac = content.accentColor || accent;
  const badgeColor = content.badgeColor || '#10b981';
  return (
    <section className={`py-10 px-4 md:py-20 md:px-6 ${wrapperClass} ${animationClass}`} style={{ ...finalBlockStyle, ...heroLayoutStyle(content) }}>
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-14">
          {content.badge && <span className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide mb-5" style={{ backgroundColor: `${badgeColor}22`, color: badgeColor }}>{content.badge}</span>}
          {content.heading && <h2 className="max-w-2xl mx-auto" style={{ fontSize: responsiveFontSize(44), lineHeight: 1.12, ...headStyle }}>{content.heading}</h2>}
          {content.subheading && <p className="mt-5 max-w-xl mx-auto" style={{ color: subColor, fontSize: responsiveFontSize(17), lineHeight: 1.6 }}>{content.subheading}</p>}
        </div>
        {content.layout === 'vertical' ? (
          <div className="max-w-2xl mx-auto flex flex-col">
            {steps.length === 0 && <div className="w-full rounded-2xl bg-slate-100 border-2 border-dashed border-slate-200 flex items-center justify-center text-slate-400 py-16">Add steps in the editor</div>}
            {steps.map((s, i) => (
              <div key={s.id || i} className="flex gap-5">
                <div className="flex flex-col items-center">
                  <div className="flex items-center justify-center rounded-full shrink-0" style={{ width: '64px', height: '64px', backgroundColor: `${ac}1a`, color: ac }}>
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={EXP_TAB_ICONS[s.icon] || EXP_TAB_ICONS.zap} /></svg>
                  </div>
                  {i < steps.length - 1 && <div className="w-0.5 flex-1 my-2" style={{ backgroundColor: `${ac}33`, minHeight: '28px' }} aria-hidden />}
                </div>
                <div className="text-left pb-9 pt-2">
                  {s.title && <h3 className="font-bold mb-2" style={{ color: 'var(--site-heading-color)', fontSize: responsiveFontSize(21) }}>{s.title}</h3>}
                  {s.description && <p style={{ color: subColor, fontSize: responsiveFontSize(15), lineHeight: 1.6 }}>{s.description}</p>}
                  {s.linkText && <a href={heroHref(s.linkUrl)} className="inline-flex items-center gap-1 mt-4 font-semibold" style={{ color: ac }}>{s.linkText}<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6" /></svg></a>}
                </div>
              </div>
            ))}
          </div>
        ) : (
        <div className="flex flex-col md:flex-row md:items-start justify-center gap-10 md:gap-4">
          {steps.length === 0 && <div className="w-full rounded-2xl bg-slate-100 border-2 border-dashed border-slate-200 flex items-center justify-center text-slate-400 py-16">Add steps in the editor</div>}
          {steps.map((s, i) => (
            <React.Fragment key={s.id || i}>
              <div className="flex-1 md:max-w-xs mx-auto text-center">
                <div className="mx-auto mb-6 flex items-center justify-center rounded-full" style={{ width: '84px', height: '84px', backgroundColor: `${ac}1a`, color: ac }}>
                  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={EXP_TAB_ICONS[s.icon] || EXP_TAB_ICONS.zap} /></svg>
                </div>
                {s.title && <h3 className="font-bold mb-3" style={{ color: 'var(--site-heading-color)', fontSize: responsiveFontSize(22) }}>{s.title}</h3>}
                {s.description && <p style={{ color: subColor, fontSize: responsiveFontSize(15), lineHeight: 1.6 }}>{s.description}</p>}
                {s.linkText && <a href={heroHref(s.linkUrl)} className="inline-flex items-center gap-1 mt-5 font-semibold" style={{ color: ac }}>{s.linkText}<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6" /></svg></a>}
              </div>
              {i < steps.length - 1 && (
                <div className="hidden md:flex items-center justify-center" style={{ paddingTop: '28px', color: `${ac}80` }} aria-hidden>
                  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
        )}
      </div>
    </section>
  );
};

// Kometa-inspired: numbered process cards connected by a line + single CTA
const StepsCardsBlock = ({ content, accent, headStyle, subColor, finalBlockStyle, wrapperClass, animationClass }) => {
  const steps = (content.steps || []).filter((s) => s);
  const ac = content.accentColor || accent;
  const badgeColor = content.badgeColor || '#10b981';
  const btnColor = content.buttonColor || ac;
  return (
    <section className={`py-10 px-4 md:py-20 md:px-6 ${wrapperClass} ${animationClass}`} style={{ ...finalBlockStyle, ...heroLayoutStyle(content) }}>
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-14">
          {content.badge && <span className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide mb-5" style={{ backgroundColor: `${badgeColor}22`, color: badgeColor }}>{content.badge}</span>}
          {content.heading && <h2 className="max-w-2xl mx-auto" style={{ fontSize: responsiveFontSize(44), lineHeight: 1.12, ...headStyle }}>{content.heading}</h2>}
          {content.subheading && <p className="mt-5 max-w-xl mx-auto" style={{ color: subColor, fontSize: responsiveFontSize(17), lineHeight: 1.6 }}>{content.subheading}</p>}
        </div>
        {content.layout === 'vertical' ? (
          <div className="max-w-2xl mx-auto flex flex-col">
            {steps.length === 0 && <div className="w-full rounded-2xl bg-slate-100 border-2 border-dashed border-slate-200 flex items-center justify-center text-slate-400 py-16">Add steps in the editor</div>}
            {steps.map((s, i) => (
              <div key={s.id || i} className="flex gap-5">
                <div className="flex flex-col items-center">
                  <span className="shrink-0 h-9 w-9 rounded-lg flex items-center justify-center text-sm font-bold" style={{ backgroundColor: `${ac}1a`, color: ac }}>{i + 1}</span>
                  {i < steps.length - 1 && <div className="w-0.5 flex-1 my-2" style={{ backgroundColor: `${ac}33`, minHeight: '24px' }} aria-hidden />}
                </div>
                <div className="flex-1 rounded-2xl p-5 mb-4" style={{ background: 'var(--site-card-bg,#fff)', border: `1px solid ${ac}22`, boxShadow: `0 14px 44px -30px ${ac}88` }}>
                  {s.title && <h3 className="font-bold mb-2" style={{ color: 'var(--site-heading-color)', fontSize: responsiveFontSize(19) }}>{s.title}</h3>}
                  {s.description && <p style={{ color: subColor, fontSize: responsiveFontSize(14), lineHeight: 1.6 }}>{s.description}</p>}
                </div>
              </div>
            ))}
          </div>
        ) : (
        <div className="flex flex-col md:flex-row items-stretch justify-center">
          {steps.length === 0 && <div className="w-full rounded-2xl bg-slate-100 border-2 border-dashed border-slate-200 flex items-center justify-center text-slate-400 py-16">Add steps in the editor</div>}
          {steps.map((s, i) => (
            <React.Fragment key={s.id || i}>
              <div className="flex-1 rounded-2xl p-6" style={{ background: 'var(--site-card-bg,#fff)', border: `1px solid ${ac}22`, boxShadow: `0 14px 44px -30px ${ac}88` }}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  {s.title && <h3 className="font-bold" style={{ color: 'var(--site-heading-color)', fontSize: responsiveFontSize(19) }}>{s.title}</h3>}
                  <span className="shrink-0 h-7 w-7 rounded-md flex items-center justify-center text-sm font-bold" style={{ backgroundColor: `${ac}1a`, color: ac }}>{i + 1}</span>
                </div>
                {s.description && <p style={{ color: subColor, fontSize: responsiveFontSize(14), lineHeight: 1.6 }}>{s.description}</p>}
              </div>
              {i < steps.length - 1 && <div className="hidden md:block self-center shrink-0" style={{ width: '28px', height: '2px', backgroundColor: `${ac}44` }} aria-hidden />}
            </React.Fragment>
          ))}
        </div>
        )}
        {content.buttonText && (
          <div className="text-center mt-12">
            <a href={heroHref(content.buttonUrl)} target={content.buttonOpenInNewTab ? '_blank' : '_self'} rel={content.buttonOpenInNewTab ? 'noopener noreferrer' : undefined} className={`inline-flex items-center justify-center gap-2 px-8 py-3 font-semibold transition-all hover:-translate-y-0.5 ${flatHoverClass(content)}`} style={{ backgroundColor: btnColor, color: content.buttonTextColor || '#fff', borderRadius: getButtonRadius(content.buttonRadius) }}>{flatHasIcon(content) && flatIconPos(content) === 'left' && flatIcon(content, 18)}{content.buttonText}{flatHasIcon(content) && flatIconPos(content) === 'right' && flatIcon(content, 18)}</a>
          </div>
        )}
      </div>
    </section>
  );
};


// Kometa-inspired: numbered steps grid beside a portrait image
const StepsGridImageBlock = ({ content, accent, headStyle, subColor, finalBlockStyle, wrapperClass, animationClass }) => {
  const steps = (content.steps || []).filter((s) => s);
  const ac = content.accentColor || accent;
  const imgRight = content.imagePosition !== 'left';
  const StepsCol = (
    <div key="steps">
      {content.heading && <h2 className="mb-8" style={{ fontSize: responsiveFontSize(36), lineHeight: 1.15, ...headStyle }}>{content.heading}</h2>}
      {content.subheading && <p className="mb-8 -mt-4" style={{ color: subColor, fontSize: responsiveFontSize(16), lineHeight: 1.6 }}>{content.subheading}</p>}
      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
        {steps.length === 0 && <div className="col-span-2 rounded-xl bg-slate-100 border-2 border-dashed border-slate-200 flex items-center justify-center text-slate-400 py-12">Add steps</div>}
        {steps.map((s, i) => (
          <div key={s.id || i} className="rounded-xl p-4" style={{ background: s.highlight ? `${ac}12` : 'transparent' }}>
            <div className="flex items-center gap-2 mb-2">
              <span className="h-6 w-6 rounded-md flex items-center justify-center text-xs font-bold text-white shrink-0" style={{ backgroundColor: s.badgeColor || ac }}>{i + 1}</span>
              {s.title && <h3 className="font-bold" style={{ color: 'var(--site-heading-color)', fontSize: responsiveFontSize(18) }}>{s.title}</h3>}
            </div>
            {s.description && <p style={{ color: subColor, fontSize: responsiveFontSize(14), lineHeight: 1.65 }}>{s.description}</p>}
          </div>
        ))}
      </div>
    </div>
  );
  const ImgCol = content.imageUrl
    ? <img key="img" src={content.imageUrl} alt="" className="w-full object-cover rounded-2xl" style={{ maxHeight: '560px' }} />
    : <div key="img" className="rounded-2xl bg-slate-100 border-2 border-dashed border-slate-200 flex items-center justify-center text-slate-400" style={{ minHeight: '340px' }}>Add an image</div>;
  return (
    <section className={`py-10 px-4 md:py-20 md:px-6 ${wrapperClass} ${animationClass}`} style={{ ...finalBlockStyle, ...heroLayoutStyle(content) }}>
      <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
        {imgRight ? [StepsCol, ImgCol] : [ImgCol, StepsCol]}
      </div>
    </section>
  );
};

// Kometa-inspired: vertical timeline steps (with Success node) beside a portrait image
const StepsTimelineImageBlock = ({ content, accent, headStyle, subColor, finalBlockStyle, wrapperClass, animationClass }) => {
  const steps = (content.steps || []).filter((s) => s);
  const ac = content.accentColor || accent;
  const imgRight = content.imagePosition !== 'left';
  const down = <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M6 13l6 6 6-6" /></svg>;
  const check = <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>;
  const node = (icon, title, desc, isLast, key) => (
    <div key={key} className="flex gap-4">
      <div className="flex flex-col items-center">
        <div className="h-9 w-9 rounded-full border flex items-center justify-center shrink-0" style={{ borderColor: `${ac}55`, color: ac, background: 'var(--site-card-bg,#fff)' }}>{icon}</div>
        {!isLast && <div className="w-0.5 flex-1 my-1" style={{ backgroundColor: `${ac}33`, minHeight: '38px' }} aria-hidden />}
      </div>
      <div className={isLast ? 'pb-1 pt-1' : 'pb-8 pt-1'}>
        <h3 className="font-bold mb-1" style={{ color: 'var(--site-heading-color)', fontSize: responsiveFontSize(18) }}>{title}</h3>
        {desc && <p style={{ color: subColor, fontSize: responsiveFontSize(14), lineHeight: 1.65 }}>{desc}</p>}
      </div>
    </div>
  );
  const StepsCol = (
    <div key="steps">
      {content.heading && <h2 className="mb-8" style={{ fontSize: responsiveFontSize(36), lineHeight: 1.15, ...headStyle }}>{content.heading}</h2>}
      {steps.length === 0 && !content.showSuccess && <div className="rounded-xl bg-slate-100 border-2 border-dashed border-slate-200 flex items-center justify-center text-slate-400 py-12">Add steps</div>}
      {steps.map((s, i) => node(down, s.title, s.description, !content.showSuccess && i === steps.length - 1, s.id || i))}
      {content.showSuccess && node(check, content.successLabel || 'Success', '', true, 'success')}
    </div>
  );
  const ImgCol = content.imageUrl
    ? <img key="img" src={content.imageUrl} alt="" className="w-full object-cover rounded-2xl" style={{ maxHeight: '620px' }} />
    : <div key="img" className="rounded-2xl bg-slate-100 border-2 border-dashed border-slate-200 flex items-center justify-center text-slate-400" style={{ minHeight: '340px' }}>Add an image</div>;
  return (
    <section className={`py-10 px-4 md:py-20 md:px-6 ${wrapperClass} ${animationClass}`} style={{ ...finalBlockStyle, ...heroLayoutStyle(content) }}>
      <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
        {imgRight ? [StepsCol, ImgCol] : [ImgCol, StepsCol]}
      </div>
    </section>
  );
};


const BeforeAfterSlider = ({ content, finalBlockStyle, wrapperClass, animationClass }) => {
  const [pos, setPos] = useState(content.startPosition != null ? Number(content.startPosition) : 50);
  useEffect(() => { if (content.startPosition != null && content.startPosition !== '') setPos(Number(content.startPosition)); }, [content.startPosition]);
  const move = (clientX, el) => {
    const rect = el.getBoundingClientRect();
    let p = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, p)));
  };
  const height = content.height || 420;
  const before = content.beforeImage;
  const after = content.afterImage;

  return (
    <section className={`py-6 px-4 md:py-12 md:px-6 ${wrapperClass} ${animationClass}`} style={finalBlockStyle}>
      <div className="max-w-4xl mx-auto">
        {content.heading && (
          <h2 className="mb-6" style={{ color: 'var(--site-heading-color)', fontFamily: 'var(--site-heading-font)', fontSize: responsiveFontSize(32), fontWeight: 700, textAlign: 'center', lineHeight: 1.2 }}>{content.heading}</h2>
        )}
        {(!before && !after) ? (
          <div className="p-10 bg-slate-50 border-2 border-dashed border-slate-200 rounded-xl text-center"><p className="text-slate-400">Add a “before” and “after” image to enable the slider</p></div>
        ) : (
          <div
            className="relative w-full overflow-hidden rounded-xl select-none"
            style={{ height: `${height}px`, cursor: 'ew-resize', backgroundColor: '#0f172a' }}
            onMouseMove={(e) => { if (e.buttons === 1) move(e.clientX, e.currentTarget); }}
            onMouseDown={(e) => move(e.clientX, e.currentTarget)}
            onTouchMove={(e) => move(e.touches[0].clientX, e.currentTarget)}
          >
            {after && <img src={after} alt={content.afterLabel || 'After'} className="absolute inset-0 w-full h-full object-cover" draggable={false} />}
            {before && <img src={before} alt={content.beforeLabel || 'Before'} className="absolute inset-0 w-full h-full object-cover" draggable={false} style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }} />}
            {content.beforeLabel && <span className="absolute bottom-3 left-3 px-2.5 py-1 rounded-md text-xs font-semibold" style={{ backgroundColor: 'rgba(0,0,0,.6)', color: '#fff' }}>{content.beforeLabel}</span>}
            {content.afterLabel && <span className="absolute bottom-3 right-3 px-2.5 py-1 rounded-md text-xs font-semibold" style={{ backgroundColor: 'rgba(0,0,0,.6)', color: '#fff' }}>{content.afterLabel}</span>}
            <div className="absolute top-0 bottom-0" style={{ left: `${pos}%`, width: '2px', backgroundColor: '#fff', transform: 'translateX(-1px)' }}>
              <div className="absolute top-1/2 left-1/2 flex items-center justify-center rounded-full shadow-lg" style={{ width: '40px', height: '40px', transform: 'translate(-50%,-50%)', backgroundColor: '#fff', color: '#0f172a' }}>
                <ChevronLeft size={16} /><ChevronRight size={16} />
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

const EventsCalendarBlock = ({ content = {}, styles = {}, wrapperClass = '', animationClass = '', blockStyle = {}, tenantId: propTenantId, subdomain }) => {
  const slug = subdomain || propTenantId;
  const accent = styles.accentColor || '#6366f1';
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(content.defaultView || 'calendar');
  const [viewDate, setViewDate] = useState(new Date());
  const [selected, setSelected] = useState(null);
  const [rsvpForm, setRsvpForm] = useState({ name: '', email: '', party_size: 1 });
  const [rsvpResult, setRsvpResult] = useState(null);
  const [rsvpError, setRsvpError] = useState(null);
  const [rsvpSubmitting, setRsvpSubmitting] = useState(false);
  const [wlForm, setWlForm] = useState({ name: '', email: '', party_size: 1 });
  const [wlResult, setWlResult] = useState(null);
  const [wlError, setWlError] = useState(null);
  const [wlSubmitting, setWlSubmitting] = useState(false);

  const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  useEffect(() => {
    if (!slug) { setLoading(false); return; }
    let active = true;
    const q = content.groupId ? `?group_id=${encodeURIComponent(content.groupId)}` : '';
    fetch(`${API_URL}/api/public/events/${slug}${q}`)
      .then((r) => r.ok ? r.json() : Promise.reject())
      .then((data) => {
        if (!active) return;
        const evs = data.events || [];
        setEvents(evs);
        const now = Date.now();
        const upcoming = evs.filter((e) => e.start && new Date(e.start).getTime() >= now)
          .sort((a, b) => new Date(a.start) - new Date(b.start))[0];
        const target = upcoming || evs[evs.length - 1];
        if (target?.start) setViewDate(new Date(target.start));
      })
      .catch(() => {})
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [slug, content.groupId]);

  useEffect(() => {
    if (!selected) return;
    const onKey = (e) => { if (e.key === 'Escape') setSelected(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected]);

  const openEvent = (ev) => {
    setSelected(ev);
    setRsvpForm({ name: '', email: '', party_size: 1 });
    setRsvpResult(null);
    setRsvpError(null);
    setWlForm({ name: '', email: '', party_size: 1 });
    setWlResult(null);
    setWlError(null);
  };

  const submitWaitlist = async () => {
    if (!wlForm.name.trim() || !wlForm.email.trim()) { setWlError('Please enter your name and email.'); return; }
    setWlSubmitting(true); setWlError(null);
    try {
      const res = await fetch(`${API_URL}/api/public/events/${slug}/${selected.event_id}/waitlist`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...wlForm, party_size: parseInt(wlForm.party_size, 10) || 1 }),
      });
      const json = await res.json();
      if (!res.ok) { setWlError(json.detail || 'Could not join the waitlist'); return; }
      setWlResult(json);
    } catch (e) {
      setWlError('Something went wrong. Please try again.');
    } finally {
      setWlSubmitting(false);
    }
  };

  const submitRsvp = async () => {
    if (!rsvpForm.name.trim() || !rsvpForm.email.trim()) { setRsvpError('Please enter your name and email.'); return; }
    setRsvpSubmitting(true); setRsvpError(null);
    try {
      const res = await fetch(`${API_URL}/api/public/events/${slug}/${selected.event_id}/rsvp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...rsvpForm, party_size: parseInt(rsvpForm.party_size, 10) || 1 }),
      });
      const json = await res.json();
      if (!res.ok) { setRsvpError(json.detail || 'Could not register'); return; }
      setRsvpResult(json);
      // refresh spots
      const q = content.groupId ? `?group_id=${encodeURIComponent(content.groupId)}` : '';
      fetch(`${API_URL}/api/public/events/${slug}${q}`).then(r => r.json()).then(d => {
        const evs = d.events || [];
        setEvents(evs);
        const fresh = evs.find(e => e.event_id === selected.event_id);
        if (fresh) setSelected(fresh);
      }).catch(() => {});
    } catch (e) {
      setRsvpError('Registration failed. Please try again.');
    } finally {
      setRsvpSubmitting(false);
    }
  };

  // calendar grid
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const startDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);
  const byDay = {};
  events.forEach((ev) => { if (ev.start) { const k = new Date(ev.start).toDateString(); (byDay[k] = byDay[k] || []).push(ev); } });
  const monthLabel = viewDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  const startOfToday = new Date(); startOfToday.setHours(0, 0, 0, 0);
  const listEvents = events
    .filter((e) => e.start && new Date(e.start) >= startOfToday)
    .sort((a, b) => new Date(a.start) - new Date(b.start))
    .slice(0, content.limit || 10);
  const icalUrl = `${API_URL}/api/public/events/${slug}/calendar.ics`;

  return (
    <div className={`${wrapperClass} ${animationClass}`} style={blockStyle} data-testid="events-calendar-block">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          {content.title ? <h2 className="text-2xl font-bold" style={{ color: styles.headingColor || 'inherit' }}>{content.title}</h2> : <span />}
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-white">
              <button onClick={() => setView('calendar')} className="px-3 py-1.5 text-sm rounded-md" style={view === 'calendar' ? { background: accent, color: '#fff' } : { color: '#475569' }} data-testid="events-block-view-calendar">Calendar</button>
              <button onClick={() => setView('list')} className="px-3 py-1.5 text-sm rounded-md" style={view === 'list' ? { background: accent, color: '#fff' } : { color: '#475569' }} data-testid="events-block-view-list">List</button>
            </div>
            {content.showIcal !== false && slug && (
              <a href={icalUrl} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 hover:bg-slate-50" data-testid="events-block-ical-link">
                <Download className="w-4 h-4" /> iCal
              </a>
            )}
          </div>
        </div>

        {loading ? (
          <div className="py-16 text-center text-slate-400 text-sm">Loading events…</div>
        ) : events.length === 0 ? (
          <div className="py-16 text-center text-slate-400 bg-slate-50 rounded-xl border border-slate-200">
            <CalendarIcon className="w-10 h-10 mx-auto mb-3 opacity-40" /> No upcoming events.
          </div>
        ) : view === 'calendar' ? (
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center justify-center gap-2 mb-4">
              <button onClick={() => setViewDate(new Date(year, month - 1, 1))} className="p-2 rounded-lg hover:bg-slate-100" data-testid="events-block-prev"><ChevronLeft className="w-5 h-5" /></button>
              <h3 className="text-lg font-semibold w-48 text-center">{monthLabel}</h3>
              <button onClick={() => setViewDate(new Date(year, month + 1, 1))} className="p-2 rounded-lg hover:bg-slate-100" data-testid="events-block-next"><ChevronRight className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-7 gap-px bg-slate-200 rounded-lg overflow-hidden border border-slate-200">
              {WEEKDAYS.map((w) => <div key={w} className="bg-slate-50 text-center text-xs font-semibold text-slate-500 py-2">{w}</div>)}
              {cells.map((cell, i) => {
                const dayEvents = cell ? (byDay[cell.toDateString()] || []) : [];
                const isToday = cell && cell.toDateString() === new Date().toDateString();
                return (
                  <div key={i} className={`bg-white min-h-[92px] p-1.5 ${!cell ? 'bg-slate-50/40' : ''}`}>
                    {cell && (
                      <>
                        <div className="text-xs font-medium mb-1" style={isToday ? { background: accent, color: '#fff', borderRadius: '9999px', width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' } : { color: '#64748b' }}>{cell.getDate()}</div>
                        <div className="space-y-1">
                          {dayEvents.map((ev) => (
                            <button key={ev.event_id} onClick={() => openEvent(ev)} className="w-full text-left text-[11px] leading-tight px-1.5 py-0.5 rounded truncate text-white hover:opacity-90" style={{ backgroundColor: ev.color || accent }} title={ev.title} data-testid={`events-block-day-event-${ev.event_id}`}>{ev.title}</button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {listEvents.map((ev) => (
              <button key={ev.event_id} onClick={() => openEvent(ev)} className="w-full text-left bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-4 hover:shadow-md transition-shadow" data-testid={`events-block-list-event-${ev.event_id}`}>
                <div className="flex flex-col items-center justify-center w-14 shrink-0">
                  <span className="text-xs uppercase text-slate-400">{new Date(ev.start).toLocaleDateString(undefined, { month: 'short' })}</span>
                  <span className="text-2xl font-bold text-slate-900">{new Date(ev.start).getDate()}</span>
                </div>
                <div className="w-1 self-stretch rounded-full" style={{ backgroundColor: ev.color || accent }} />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-900">{ev.title}</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 mt-1">
                    <span className="flex items-center gap-1"><ClockIcon className="w-3 h-3" />{ev.all_day ? 'All day' : new Date(ev.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                    {ev.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{ev.location}</span>}
                    {ev.rsvp_enabled && <span style={{ color: accent }}>{ev.rsvp_full ? 'Fully booked' : 'RSVP'}</span>}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {selected && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={selected.title} data-testid="events-block-modal">
            <div className="h-2" style={{ backgroundColor: selected.color || accent }} />
            <div className="p-6">
              <div className="flex items-start justify-between gap-3">
                <h3 className="text-2xl font-bold text-slate-900">{selected.title}</h3>
                <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-slate-600" aria-label="Close" data-testid="events-block-modal-close"><X className="w-5 h-5" /></button>
              </div>
              <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-slate-600 mt-3">
                <span className="flex items-center gap-2"><ClockIcon className="w-4 h-4 text-slate-400" />{selected.all_day ? new Date(selected.start).toLocaleDateString() : new Date(selected.start).toLocaleString()}</span>
                {selected.location && <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-slate-400" />{selected.location}</span>}
              </div>
              {selected.description_html && <div className="prose prose-sm max-w-none mt-4" dangerouslySetInnerHTML={{ __html: selected.description_html }} />}
              <div className="mt-5">
                <a href={`${API_URL}/api/public/events/${slug}/${selected.event_id}.ics`} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-medium" style={{ background: accent }} data-testid="events-block-modal-ics"><Download className="w-4 h-4" /> Add to calendar</a>
              </div>

              {content.showRsvp !== false && selected.rsvp_enabled && (
                <div className="mt-6 border-t border-slate-100 pt-5" data-testid="events-block-rsvp-section">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-base font-semibold text-slate-900">Register / RSVP</h4>
                    {selected.spots_left != null && (
                      <span className={`text-sm font-medium ${selected.rsvp_full ? 'text-red-600' : 'text-emerald-600'}`} data-testid="events-block-spots">{selected.rsvp_full ? 'Fully booked' : `${selected.spots_left} spot${selected.spots_left === 1 ? '' : 's'} left`}</span>
                    )}
                  </div>
                  {rsvpResult ? (
                    <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4 text-center" data-testid="events-block-rsvp-success">
                      <p className="font-semibold text-emerald-800">You're registered! 🎉</p>
                      <p className="text-sm text-emerald-700 mt-1">A confirmation has been emailed to {rsvpForm.email}.</p>
                      {rsvpResult.cancel_token && (
                        <p className="text-sm text-emerald-700 mt-2">Can't make it later? <a href={`/site/${subdomain || slug}/events/rsvp/${rsvpResult.cancel_token}`} className="underline font-medium">Cancel your RSVP</a>.</p>
                      )}
                    </div>
                  ) : selected.rsvp_full ? (
                    selected.waitlist_enabled ? (
                      wlResult ? (
                        <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 text-center" data-testid="events-block-waitlist-success">
                          <p className="font-semibold text-amber-800">You're on the waitlist ✓</p>
                          <p className="text-sm text-amber-700 mt-1">We'll email you a claim link if a spot opens up{wlResult.position ? ` — you're #${wlResult.position} in line` : ''}.</p>
                        </div>
                      ) : selected.waitlist_full ? (
                        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center text-slate-500 text-sm">Event and waitlist are both full.</div>
                      ) : (
                        <div className="bg-amber-50/60 border border-amber-100 rounded-xl p-4 space-y-3" data-testid="events-block-waitlist-form">
                          <p className="text-sm font-medium text-amber-900">This event is full — join the waitlist.</p>
                          {wlError && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2" data-testid="events-block-waitlist-error">{wlError}</div>}
                          <div className="grid sm:grid-cols-2 gap-3">
                            <input value={wlForm.name} onChange={(e) => setWlForm((f) => ({ ...f, name: e.target.value }))} placeholder="Your name" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="events-block-waitlist-name" />
                            <input type="email" value={wlForm.email} onChange={(e) => setWlForm((f) => ({ ...f, email: e.target.value }))} placeholder="Email" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="events-block-waitlist-email" />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1">Party size</label>
                            <input type="number" min="1" value={wlForm.party_size} onChange={(e) => setWlForm((f) => ({ ...f, party_size: e.target.value }))} className="w-24 border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="events-block-waitlist-party" />
                          </div>
                          <button onClick={submitWaitlist} disabled={wlSubmitting} className="px-5 py-2.5 rounded-lg text-white text-sm font-medium disabled:opacity-60" style={{ background: '#d97706' }} data-testid="events-block-waitlist-submit">{wlSubmitting ? 'Joining…' : 'Join the waitlist'}</button>
                        </div>
                      )
                    ) : (
                      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center text-slate-500 text-sm">This event is fully booked.</div>
                    )
                  ) : (
                    <div className="space-y-3">
                      {rsvpError && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2" data-testid="events-block-rsvp-error">{rsvpError}</div>}
                      <div className="grid sm:grid-cols-2 gap-3">
                        <input value={rsvpForm.name} onChange={(e) => setRsvpForm((f) => ({ ...f, name: e.target.value }))} placeholder="Your name" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="events-block-rsvp-name" />
                        <input type="email" value={rsvpForm.email} onChange={(e) => setRsvpForm((f) => ({ ...f, email: e.target.value }))} placeholder="Email" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="events-block-rsvp-email" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Party size</label>
                        <input type="number" min="1" max={selected.spots_left != null ? selected.spots_left : undefined} value={rsvpForm.party_size} onChange={(e) => setRsvpForm((f) => ({ ...f, party_size: e.target.value }))} className="w-24 border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="events-block-rsvp-party" />
                      </div>
                      <button onClick={submitRsvp} disabled={rsvpSubmitting} className="block px-5 py-2.5 rounded-lg text-white text-sm font-medium disabled:opacity-60" style={{ background: accent }} data-testid="events-block-rsvp-submit">{rsvpSubmitting ? 'Registering…' : 'Register'}</button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ArticleFeedPreview = ({ content, styles = {}, wrapperClass, animationClass, blockStyle, tenantId: propTenantId, currentLanguage, defaultLanguage = 'en' }) => {
  const [articles, setArticles] = useState([]);
  const [translatedArticles, setTranslatedArticles] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isTranslating, setIsTranslating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategoryFilter, setActiveCategoryFilter] = useState('all');
  const [publicCategories, setPublicCategories] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [translatedArticleContent, setTranslatedArticleContent] = useState(null);
  const [isTranslatingArticle, setIsTranslatingArticle] = useState(false);
  const [tenantId, setTenantId] = useState(propTenantId || null);
  const [tenantSubdomain, setTenantSubdomain] = useState(null);
  const [copiedUrl, setCopiedUrl] = useState(false);
  
  // Translate article popup content when selected and language differs
  React.useEffect(() => {
    if (!selectedArticle || !currentLanguage || currentLanguage === defaultLanguage) {
      setTranslatedArticleContent(null);
      return;
    }
    
    const translateArticleContent = async () => {
      setIsTranslatingArticle(true);
      try {
        // Prioritize content_html over blocks (matches rendering order)
        if (selectedArticle.content_html) {
          // Translate HTML content
          const response = await fetch(`${API_URL}/api/translation/translate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              text: selectedArticle.content_html,
              target_language: currentLanguage,
              source_language: defaultLanguage,
              preserve_html: true
            })
          });
          
          if (response.ok) {
            const data = await response.json();
            if (data.translated_text) {
              setTranslatedArticleContent({ type: 'html', content: data.translated_text });
              return; // Success - exit early
            }
          }
        }
        
        // Fall back to blocks translation if no content_html or HTML translation failed
        if (selectedArticle.blocks && selectedArticle.blocks.length > 0) {
          const response = await fetch(`${API_URL}/api/translation/translate-blocks`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              blocks: selectedArticle.blocks,
              target_language: currentLanguage,
              tenant_id: tenantId,
              source_language: defaultLanguage
            })
          });
          
          if (response.ok) {
            const data = await response.json();
            if (data.translated_blocks) {
              setTranslatedArticleContent({ type: 'blocks', content: data.translated_blocks });
            }
          }
        }
      } catch (err) {
        console.error('[ArticleFeed] Article translation error:', err);
      } finally {
        setIsTranslatingArticle(false);
      }
    };
    
    translateArticleContent();
  }, [selectedArticle, currentLanguage, defaultLanguage, tenantId]);

  // Get public article URL
  const getArticleUrl = (article) => {
    const identifier = article?.slug || article?.article_id;
    if (!identifier) return null;
    const currentHost = window.location.hostname;
    const isPreviewOrLocal = currentHost.includes('emergentagent.com') || currentHost.includes('localhost') || /^\d+\.\d+\.\d+\.\d+$/.test(currentHost);
    const isCustomDomain = !currentHost.includes('nexuscms') && !isPreviewOrLocal;
    
    if (isPreviewOrLocal && tenantSubdomain) {
      // Preview/localhost: use /site/ prefix route (React Router handles it)
      return `${window.location.origin}/site/${tenantSubdomain}/article/${identifier}`;
    } else if (isCustomDomain) {
      // Custom domain: SubdomainRouter handles /article/:slug
      return `${window.location.origin}/article/${identifier}`;
    } else if (tenantSubdomain) {
      // Subdomain (e.g., school.nexuscms.ai): SubdomainRouter handles /article/:slug
      return `${window.location.origin}/article/${identifier}`;
    }
    return null;
  };

  // Copy URL to clipboard
  const copyArticleUrl = (url) => {
    navigator.clipboard.writeText(url);
    setCopiedUrl(true);
    setTimeout(() => setCopiedUrl(false), 2000);
  };

  // Social sharing functions
  const shareOnFacebook = (url, title) => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank', 'width=600,height=400');
  };

  const shareOnTwitter = (url, title) => {
    window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`, '_blank', 'width=600,height=400');
  };

  const shareOnLinkedIn = (url, title) => {
    window.open(`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`, '_blank', 'width=600,height=400');
  };

  const shareByEmail = (url, title) => {
    // Create a temporary link and click it to avoid Chrome's account picker
    const mailtoLink = document.createElement('a');
    mailtoLink.href = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`Check out this article: ${url}`)}`;
    mailtoLink.target = '_blank';
    document.body.appendChild(mailtoLink);
    mailtoLink.click();
    document.body.removeChild(mailtoLink);
  };

  // Get tenant ID from auth context or page context
  React.useEffect(() => {
    // If propTenantId was provided, use it
    if (propTenantId) {
      setTenantId(propTenantId);
      return;
    }
    
    const fetchTenantId = async () => {
      try {
        // Try to get from localStorage (user info)
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const user = JSON.parse(userStr);
          if (user.tenant_id) {
            setTenantId(user.tenant_id);
            return;
          }
        }
      } catch (e) {
        console.error('Failed to get tenant ID:', e);
      }
    };
    fetchTenantId();
  }, [propTenantId]);

  // Fetch public categories for the filter bar
  React.useEffect(() => {
    if (!tenantId || !content.showCategoryFilter) return;
    const fetchPublicCategories = async () => {
      try {
        const response = await fetch(`${API_URL}/api/public/articles/${tenantId}/categories`);
        if (response.ok) {
          const data = await response.json();
          // API returns [{name: "News", count: 5}, ...] — extract names
          setPublicCategories(Array.isArray(data) ? data.map(c => c.name || c) : []);
        }
      } catch (e) {
        console.error('Failed to fetch public categories:', e);
      }
    };
    fetchPublicCategories();
  }, [tenantId, content.showCategoryFilter]);

  React.useEffect(() => {
    if (!tenantId) return;
    
    const fetchArticles = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams({
          limit: String(content.enablePagination ? (content.perPage || 6) : (content.limit || 6)),
          offset: String((currentPage - 1) * (content.perPage || 6)),
        });
        
        // Apply CMS-configured category filters first
        if (activeCategoryFilter && activeCategoryFilter !== 'all') {
          // Public visitor selected a specific category
          params.append('category', activeCategoryFilter);
        } else if (content.categoryFilters?.length > 0) {
          params.append('category', content.categoryFilters.join(','));
        } else if (content.categoryFilter) {
          params.append('category', content.categoryFilter);
        }
        if (searchQuery) {
          params.append('search', searchQuery);
        }
        
        const response = await fetch(`${API_URL}/api/public/articles/${tenantId}?${params}`);
        const data = await response.json();
        
        setArticles(data.articles || []);
        setTotal(data.total || 0);
        
        // Also get tenant subdomain for URL generation
        if (data.subdomain) {
          setTenantSubdomain(data.subdomain);
        } else {
          // Try to fetch tenant info for subdomain
          try {
            const tenantResponse = await fetch(`${API_URL}/api/public/tenant/${tenantId}/info`);
            if (tenantResponse.ok) {
              const tenantData = await tenantResponse.json();
              setTenantSubdomain(tenantData.subdomain || tenantData.site_slug);
            }
          } catch (e) {
            console.log('Could not fetch tenant info for URL generation');
          }
        }
      } catch (error) {
        console.error('Failed to fetch articles:', error);
        setArticles([]);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, [tenantId, content.categoryFilter, content.categoryFilters, content.limit, content.perPage, content.enablePagination, currentPage, searchQuery, activeCategoryFilter]);

  // Translate articles when language changes
  React.useEffect(() => {
    const translateArticles = async () => {
      // Skip if no translation needed
      if (!currentLanguage || currentLanguage === defaultLanguage || articles.length === 0) {
        setTranslatedArticles(null);
        return;
      }
      
      setIsTranslating(true);
      try {
        
        // Translate each article's title and excerpt
        const translationPromises = articles.map(async (article) => {
          try {
            const response = await fetch(`${API_URL}/api/translation/translate-blocks`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                blocks: [
                  { type: 'text', content: { text: article.title || '' } },
                  { type: 'text', content: { text: article.excerpt || '' } }
                ],
                target_language: currentLanguage,
                tenant_id: tenantId
              })
            });
            
            if (response.ok) {
              const data = await response.json();
              if (data.translated_blocks && data.translated_blocks.length >= 2) {
                return {
                  ...article,
                  title: data.translated_blocks[0]?.content?.text || article.title,
                  excerpt: data.translated_blocks[1]?.content?.text || article.excerpt
                };
              }
            }
            return article;
          } catch (e) {
            console.error('Article translation failed:', e);
            return article;
          }
        });
        
        const translated = await Promise.all(translationPromises);
        setTranslatedArticles(translated);
      } catch (error) {
        console.error('Failed to translate articles:', error);
        setTranslatedArticles(null);
      } finally {
        setIsTranslating(false);
      }
    };
    
    translateArticles();
  }, [articles, currentLanguage, defaultLanguage, tenantId]);

  // Use translated articles if available
  const displayArticles = translatedArticles || articles;

  const layout = content.layout || 'grid';
  const columns = content.columns || 3;
  const showImage = content.showImage !== false;
  const showDate = content.showDate !== false;
  const showExcerpt = content.showExcerpt !== false;
  const showCategory = content.showCategory !== false;
  const openAs = content.openAs || 'popup';  // 'popup' or 'newpage'
  
  // Image height from styles (BlockStyleControls stores as string like '300px')
  const imageHeightStr = styles.imageHeight || '192px';
  const imageHeight = parseInt(imageHeightStr) || 192;

  // Handle article click based on openAs setting
  const handleArticleClick = (article) => {
    setTranslatedArticleContent(null); // Clear previous translation
    if (openAs === 'newpage') {
      // Navigate to article page in a new tab
      const articleUrl = getArticleUrl(article);
      if (articleUrl) {
        window.open(articleUrl, '_blank', 'noopener noreferrer');
      } else {
        // Fallback: use slug-based URL
        const identifier = article.slug || article.article_id;
        if (!identifier) { setSelectedArticle(article); return; }
        const currentHost = window.location.hostname;
        const isPreviewOrLocal = currentHost.includes('emergentagent.com') || currentHost.includes('localhost') || /^\d+\.\d+\.\d+\.\d+$/.test(currentHost);
        const isCustomDomain = !currentHost.includes('nexuscms') && !isPreviewOrLocal;
        if (isPreviewOrLocal && tenantSubdomain) {
          window.open(`/site/${tenantSubdomain}/article/${identifier}`, '_blank', 'noopener noreferrer');
        } else if (isCustomDomain) {
          window.open(`/article/${identifier}`, '_blank', 'noopener noreferrer');
        } else if (tenantSubdomain) {
          window.open(`/article/${identifier}`, '_blank', 'noopener noreferrer');
        } else {
          // Last fallback - open popup
          setSelectedArticle(article);
        }
      }
    } else {
      // Open as popup (default)
      setSelectedArticle(article);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const totalPages = content.enablePagination ? Math.ceil(total / (content.perPage || 6)) : 1;

  // Bug 4 fix: when CMS admin has pre-selected specific categories, only show
  // those in the public filter bar — not all available categories.
  const displayCategories = (content.categoryFilters && content.categoryFilters.length > 0)
    ? content.categoryFilters
    : publicCategories;

  if (!tenantId) {
    return (
      <div className={`mb-8 p-8 bg-slate-50 border-2 border-dashed border-slate-200 rounded-lg text-center ${wrapperClass}`} style={blockStyle}>
        <p className="text-slate-500">Article Feed - Preview available after login</p>
      </div>
    );
  }

  // Initial load (no articles yet) shows full spinner; re-fetches (search/filter) show inline spinner
  const isInitialLoading = loading && articles.length === 0 && !searchQuery && activeCategoryFilter === 'all';

  return (
    <div className={`mb-8 ${wrapperClass} ${animationClass}`} style={blockStyle}>
      {/* Heading */}
      {content.heading && (
        <h2 className="text-2xl font-bold text-slate-800 mb-6">{content.heading}</h2>
      )}

      {/* Search Bar - always mounted to preserve focus */}
      {content.showSearch && (
        <div className="mb-6">
          <input
            type="text"
            placeholder="Search articles..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full max-w-md px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            data-testid="article-search-input"
          />
        </div>
      )}

      {/* Public Category Filter */}
      {content.showCategoryFilter && displayCategories.length > 0 && (
        <div className="mb-6 flex flex-wrap gap-2" data-testid="public-category-filter">
          <button
            onClick={() => { setActiveCategoryFilter('all'); setCurrentPage(1); }}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              activeCategoryFilter === 'all'
                ? 'bg-slate-800 text-white shadow-sm'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
            data-testid="category-filter-all"
          >
            All
          </button>
          {displayCategories.map(cat => (
            <button
              key={cat}
              onClick={() => { setActiveCategoryFilter(cat); setCurrentPage(1); }}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                activeCategoryFilter === cat
                  ? 'bg-slate-800 text-white shadow-sm'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              data-testid={`category-filter-${cat.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {cat}
            </button>
          ))}
        </div>
      )}

      {/* Articles section - spinner on initial load, inline spinner on re-fetch */}
      {isInitialLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      ) : (
        <>
          {/* Inline loading / translating indicator */}
          {(loading || isTranslating) && (
            <div className="flex items-center gap-2 text-slate-500 mb-4">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
              <span>{isTranslating ? 'Translating...' : 'Loading...'}</span>
            </div>
          )}
      {displayArticles.length === 0 ? (
        <div className="p-8 bg-slate-50 border-2 border-dashed border-slate-200 rounded-lg text-center">
          <p className="text-slate-500">No articles found</p>
        </div>
      ) : layout === 'grid' ? (
        <div className={`grid gap-6 ${
          columns === 2 ? 'grid-cols-1 md:grid-cols-2' :
          columns === 4 ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4' :
          'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
        }`}>
          {displayArticles.map((article) => (
            <div
              key={article.article_id}
              onClick={() => handleArticleClick(article)}
              className="bg-white border border-slate-200 rounded-lg overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
            >
              {showImage && article.image_url && article.show_image_in_feed && (
                <img 
                  src={article.image_url} 
                  alt={article.image_alt || article.title} 
                  className={`w-full object-top ${article.image_fit === 'contain' ? 'object-contain bg-slate-100' : 'object-cover'}`}
                  style={{ height: `${imageHeight}px` }}
                />
              )}
              <div className="p-4">
                {showCategory && (article.categories?.length > 0 || article.category) && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {(article.categories || (article.category ? [article.category] : [])).map((cat, idx) => (
                      <span key={idx} className="inline-block px-2 py-1 text-xs font-medium bg-indigo-100 text-indigo-700 rounded">
                        {cat}
                      </span>
                    ))}
                  </div>
                )}
                <h3 className="font-semibold text-slate-800 mb-2 line-clamp-2">{article.title}</h3>
                {showDate && article.show_date && article.date && (
                  <p className="text-xs text-slate-400 mb-2">{formatDate(article.date)}</p>
                )}
                {showExcerpt && article.excerpt && (
                  <p className="text-sm text-slate-600 line-clamp-3">{article.excerpt}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {displayArticles.map((article) => (
            <div
              key={article.article_id}
              onClick={() => handleArticleClick(article)}
              className="flex gap-4 bg-white border border-slate-200 rounded-lg overflow-hidden cursor-pointer hover:shadow-lg transition-shadow p-4"
            >
              {showImage && article.image_url && article.show_image_in_feed && (
                <img 
                  src={article.image_url} 
                  alt={article.image_alt || article.title} 
                  className={`w-32 h-24 rounded flex-shrink-0 ${article.image_fit === 'contain' ? 'object-contain bg-slate-100' : 'object-cover object-top'}`}
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-start gap-2 mb-1 flex-wrap">
                  {showCategory && (article.categories?.length > 0 || article.category) && (
                    (article.categories || (article.category ? [article.category] : [])).map((cat, idx) => (
                      <span key={idx} className="inline-block px-2 py-0.5 text-xs font-medium bg-indigo-100 text-indigo-700 rounded flex-shrink-0">
                        {cat}
                      </span>
                    ))
                  )}
                  {showDate && article.show_date && article.date && (
                    <span className="text-xs text-slate-400">{formatDate(article.date)}</span>
                  )}
                </div>
                <h3 className="font-semibold text-slate-800 mb-1 line-clamp-1">{article.title}</h3>
                {showExcerpt && article.excerpt && (
                  <p className="text-sm text-slate-600 line-clamp-2">{article.excerpt}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {content.enablePagination && totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 text-sm border border-slate-300 rounded disabled:opacity-50 hover:bg-slate-50"
          >
            Previous
          </button>
          <span className="text-sm text-slate-600">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 text-sm border border-slate-300 rounded disabled:opacity-50 hover:bg-slate-50"
          >
            Next
          </button>
        </div>
      )}
        </>
      )}

      {/* Article Modal */}
      {selectedArticle && (
        <div 
          className="fixed inset-0 bg-black/50 z-[70] flex items-center justify-center p-4"
          onClick={() => { setSelectedArticle(null); setTranslatedArticleContent(null); }}
        >
          <div 
            className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between shadow-sm">
              <h2 className="text-xl font-bold text-slate-800 line-clamp-1">{selectedArticle.title}</h2>
              <button
                onClick={() => { setSelectedArticle(null); setTranslatedArticleContent(null); }}
                className="p-2 hover:bg-slate-100 rounded-full transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="p-6">
              {selectedArticle.image_url && (
                <img 
                  src={selectedArticle.image_url} 
                  alt={selectedArticle.image_alt || selectedArticle.title} 
                  className="w-full max-h-96 object-contain rounded-lg mb-6"
                />
              )}
              
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                {(selectedArticle.categories?.length > 0 || selectedArticle.category) && (
                  (selectedArticle.categories || (selectedArticle.category ? [selectedArticle.category] : [])).map((cat, idx) => (
                    <span key={idx} className="px-3 py-1 text-sm font-medium bg-indigo-100 text-indigo-700 rounded-full">
                      {cat}
                    </span>
                  ))
                )}
                {selectedArticle.show_date && selectedArticle.date && (
                  <span className="text-sm text-slate-500">{formatDate(selectedArticle.date)}</span>
                )}
              </div>

              {/* Translation loading indicator */}
              {isTranslatingArticle && (
                <div className="flex items-center gap-2 text-slate-500 mb-4">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                  <span>Translating article...</span>
                </div>
              )}
              
              {/* Article Content - use translated content if available */}
              {translatedArticleContent?.type === 'html' ? (
                <div 
                  className="prose prose-slate max-w-none article-content-links"
                  dangerouslySetInnerHTML={{ __html: translatedArticleContent.content }}
                />
              ) : translatedArticleContent?.type === 'blocks' ? (
                <div className="article-blocks-content">
                  <PreviewRenderer 
                    blocks={translatedArticleContent.content}
                    isQuickPreview={true}
                    showNavigation={false}
                    siteTheme={{}}
                  />
                </div>
              ) : selectedArticle.content_html ? (
                <div 
                  className="prose prose-slate max-w-none article-content-links"
                  dangerouslySetInnerHTML={{ __html: selectedArticle.content_html }}
                />
              ) : selectedArticle.blocks && selectedArticle.blocks.length > 0 ? (
                <div className="article-blocks-content">
                  <PreviewRenderer 
                    blocks={selectedArticle.blocks}
                    isQuickPreview={true}
                    showNavigation={false}
                    siteTheme={{}}
                  />
                </div>
              ) : (
                <p className="text-slate-500 italic">No content available for this article.</p>
              )}
              
              {/* Share & Copy URL Section */}
              {getArticleUrl(selectedArticle) && (
                <div className="mt-8 pt-6 border-t border-slate-200">
                  <h4 className="text-sm font-medium text-slate-700 mb-3">Share this article</h4>
                  
                  {/* Copy URL */}
                  <div className="flex items-center gap-2 mb-4 p-3 bg-slate-50 rounded-lg">
                    <input
                      type="text"
                      readOnly
                      value={getArticleUrl(selectedArticle)}
                      className="flex-1 bg-transparent text-sm text-slate-600 outline-none"
                    />
                    <button
                      onClick={() => copyArticleUrl(getArticleUrl(selectedArticle))}
                      className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                        copiedUrl 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-slate-200 hover:bg-slate-300 text-slate-700'
                      }`}
                    >
                      {copiedUrl ? '✓ Copied!' : 'Copy Link'}
                    </button>
                  </div>
                  
                  {/* Social Share Buttons */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500 mr-2">Share on:</span>
                    <button
                      onClick={() => shareOnFacebook(getArticleUrl(selectedArticle), selectedArticle.title)}
                      className="p-2 rounded-full bg-blue-600 hover:bg-blue-700 text-white transition-colors"
                      title="Share on Facebook"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                    </button>
                    <button
                      onClick={() => shareOnTwitter(getArticleUrl(selectedArticle), selectedArticle.title)}
                      className="p-2 rounded-full bg-sky-500 hover:bg-sky-600 text-white transition-colors"
                      title="Share on X (Twitter)"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                      </svg>
                    </button>
                    <button
                      onClick={() => shareOnLinkedIn(getArticleUrl(selectedArticle), selectedArticle.title)}
                      className="p-2 rounded-full bg-blue-700 hover:bg-blue-800 text-white transition-colors"
                      title="Share on LinkedIn"
                    >
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                    </button>
                    <button
                      onClick={() => shareByEmail(getArticleUrl(selectedArticle), selectedArticle.title)}
                      className="p-2 rounded-full bg-slate-600 hover:bg-slate-700 text-white transition-colors"
                      title="Share via Email"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
              <style>{`
                .article-content-links a,
                .article-blocks-content a {
                  color: #2563eb;
                  text-decoration: underline;
                }
                .article-content-links a:hover,
                .article-blocks-content a:hover {
                  color: #1d4ed8;
                }
              `}</style>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const PreviewRenderer = ({ 
  blocks, 
  title, 
  pageWidth = 'standard', 
  theme, 
  isQuickPreview = true, 
  pageId = null,
  tenantId: propTenantId = null,
  showNavigation = true, 
  refreshKey = 0,
  // New props for pre-fetched data (used by public preview)
  siteTheme: propSiteTheme = null,
  headerMenu: propHeaderMenu = null,
  footerMenu: propFooterMenu = null,
  // Props for navigation link generation
  previewToken = null,
  pages = [],
  // For public site popup access
  subdomain = null,
  // Translation props
  currentLanguage = null,
  defaultLanguage = 'en',
  enabledLanguages = null,
  // Children to render between nav header and footer (e.g. article body)
  children = null,
}) => {
  const [fetchedSiteTheme, setFetchedSiteTheme] = useState(null);
  const [fetchedNavigationMenus, setFetchedNavigationMenus] = useState({ header: null, footer: null });
  
  // Popup page modal state
  const [popupPage, setPopupPage] = useState({ isOpen: false, pageSlug: null, previewToken: null });

  // Intersection Observer for scroll-triggered animations
  useEffect(() => {
    let observer = null;
    
    // Small delay to ensure DOM is fully rendered
    const timeoutId = setTimeout(() => {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('in-view');
              // Once animated, stop observing
              if (observer) observer.unobserve(entry.target);
            }
          });
        },
        {
          threshold: 0.1, // Trigger when 10% of element is visible
          rootMargin: '0px 0px -30px 0px' // Trigger slightly before element enters viewport
        }
      );

      // Observe all elements with animate-on-scroll class that haven't animated yet
      const animatedElements = document.querySelectorAll('.animate-on-scroll:not(.in-view)');
      animatedElements.forEach((el) => observer.observe(el));
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      if (observer) {
        observer.disconnect();
      }
    };
  }, [blocks, refreshKey]); // Re-run when blocks change

  // Use props if provided, otherwise use fetched data
  const siteTheme = propSiteTheme !== null ? propSiteTheme : fetchedSiteTheme;
  const navigationMenus = (propHeaderMenu !== null || propFooterMenu !== null) 
    ? { header: propHeaderMenu, footer: propFooterMenu }
    : fetchedNavigationMenus;

  useEffect(() => {
    // Skip fetching if data was pre-provided (public preview mode)
    if (propSiteTheme !== null) {
      return;
    }
    
    let isMounted = true;
    
    const fetchData = async () => {
      try {
        const [themeRes, navRes] = await Promise.all([
          api.get('/theme'),
          showNavigation ? api.get('/navigation') : Promise.resolve({ data: [] })
        ]);
        
        if (isMounted) {
          setFetchedSiteTheme(themeRes.data);
          
          // Find applicable menus (global or assigned to this page)
          const menus = navRes.data || [];
          const headerMenu = menus.find(m => 
            m.location === 'header' && (m.is_global || (pageId && m.page_ids?.includes(pageId)))
          );
          const footerMenu = menus.find(m => 
            m.location === 'footer' && (m.is_global || (pageId && m.page_ids?.includes(pageId)))
          );
          
          setFetchedNavigationMenus({ header: headerMenu, footer: footerMenu });
        }
      } catch (error) {
        console.error('Failed to fetch theme/navigation');
      }
    };
    
    fetchData();
    
    return () => {
      isMounted = false;
    };
  }, [pageId, showNavigation, refreshKey, propSiteTheme]);

  // Listen for custom openPopupPage events from Custom HTML blocks
  useEffect(() => {
    const handleOpenPopupPage = (event) => {
      const { slug } = event.detail || {};
      if (slug) {
        setPopupPage({ isOpen: true, pageSlug: slug, previewToken: null });
      }
    };

    window.addEventListener('openPopupPage', handleOpenPopupPage);
    
    return () => {
      window.removeEventListener('openPopupPage', handleOpenPopupPage);
    };
  }, []);

  // Get effective theme value (page override or site default)
  const getThemeValue = (key) => {
    // When the page has explicitly turned OFF the site theme, page-level
    // settings (theme[key]) win — including when the value is undefined, so
    // the renderer falls back to block defaults instead of silently
    // restoring the site theme value (which made "off" feel like "no-op").
    if (theme && theme.use_site_theme === false) {
      return theme[key];
    }
    return siteTheme?.[key];
  };

  // Check if site theme is being used (strip inline colors when true)
  const usingSiteTheme = !theme || theme.use_site_theme !== false;
  
  // Helper to process HTML content - strips inline colors when using site theme
  const processHtml = (html) => stripInlineColorStyles(html, usingSiteTheme);

  const getPageWidthClass = () => {
    // In Quick Preview, cap full-width pages to wide (1280px) to prevent overflow
    if (isQuickPreview && pageWidth === 'full') return 'max-w-7xl';
    switch (pageWidth) {
      case 'narrow': return 'max-w-3xl'; // 720px
      case 'standard': return 'max-w-5xl'; // 1024px
      case 'wide': return 'max-w-7xl'; // 1280px
      case 'full': return 'max-w-full';
      default: return 'max-w-5xl';
    }
  };

  // Get page background style
  const getPageBackground = () => {
    const bgType = getThemeValue('background_type') || 'solid';
    if (bgType === 'linear-gradient') {
      return `linear-gradient(${getThemeValue('gradient_direction') || 'to bottom'}, ${getThemeValue('gradient_start') || '#6366f1'}, ${getThemeValue('gradient_end') || '#8b5cf6'})`;
    } else if (bgType === 'radial-gradient') {
      return `radial-gradient(circle, ${getThemeValue('gradient_start') || '#6366f1'}, ${getThemeValue('gradient_end') || '#8b5cf6'})`;
    }
    return getThemeValue('background_color') || '#ffffff';
  };

  // ============================================================================
  // SITE THEME → CSS VARIABLES (Option B foundation)
  // Emits the entire resolved theme as CSS custom properties so every block
  // can inherit via `var(--site-*)` without each block re-running getThemeValue().
  // Per-block style overrides still win via CSS specificity (inline style).
  // ============================================================================
  const buildSiteThemeCSSVars = () => {
    const v = (key, fb) => getThemeValue(key) || fb;

    // Map button_style → border-radius value for buttons
    const buttonStyleRadius = {
      pill: '999px',
      sharp: '0px',
      rounded: '8px',
      // default fallback
      default: '8px',
    };
    const btnStyle = v('button_style', 'rounded');
    const btnRadius = buttonStyleRadius[btnStyle] || buttonStyleRadius.default;

    // Map border_radius → numeric pixel for cards
    const radiusMap = { none: '0px', sm: '6px', md: '12px', lg: '20px', xl: '28px' };
    const themeRadius = radiusMap[v('border_radius', 'md')] || '12px';

    // Map shadow_preset → CSS shadow
    const shadowMap = {
      none: 'none',
      subtle: '0 1px 2px rgba(0,0,0,0.05), 0 1px 4px rgba(0,0,0,0.04)',
      medium: '0 4px 6px -1px rgba(0,0,0,0.07), 0 2px 4px -1px rgba(0,0,0,0.05)',
      dramatic: '0 20px 25px -5px rgba(0,0,0,0.10), 0 10px 10px -5px rgba(0,0,0,0.04)',
    };
    const themeShadow = shadowMap[v('shadow_preset', 'medium')] || shadowMap.medium;

    return `
      .preview-theme-scope {
        --site-primary: ${v('primary_color', '#4f46e5')};
        --site-secondary: ${v('secondary_color', '#8b5cf6')};
        --site-accent: ${v('accent_color', v('primary_color', '#4f46e5'))};
        --site-heading-color: ${v('heading_color', '#1e293b')};
        --site-body-color: ${v('body_color', '#475569')};
        --site-muted-color: ${v('muted_color', '#94a3b8')};
        --site-border-color: ${v('border_color', '#e2e8f0')};
        --site-link-color: ${v('link_color', '#2563eb')};
        --site-link-hover-color: ${v('link_hover_color', v('link_color', '#1d4ed8'))};
        --site-bg: ${v('background_color', '#ffffff')};
        --site-heading-font: ${v('heading_font', 'Manrope, sans-serif')};
        --site-body-font: ${v('body_font', 'Inter, sans-serif')};
        --site-h1-size: ${v('h1_size', 48)}px;
        --site-h2-size: ${v('h2_size', 36)}px;
        --site-h3-size: ${v('h3_size', 28)}px;
        --site-h4-size: ${v('h4_size', 22)}px;
        --site-body-size: ${v('body_size', 16)}px;
        --site-radius: ${themeRadius};
        --site-button-radius: ${btnRadius};
        --site-shadow: ${themeShadow};
      }

      /* Shape & Style — apply site radius + shadow to common block elements.
         The :not([style*="border-radius"]) (and similar) means an inline style
         set by the user on a specific block ALWAYS wins. This rule only
         affects elements that haven't explicitly overridden these props. */
      .preview-theme-scope img:not([style*="border-radius"]) {
        border-radius: var(--site-radius);
      }
      .preview-theme-scope .block-card:not([style*="border-radius"]),
      .preview-theme-scope .pricing-card:not([style*="border-radius"]),
      .preview-theme-scope .testimonial-card:not([style*="border-radius"]) {
        border-radius: var(--site-radius);
        box-shadow: var(--site-shadow);
      }
      /* Form inputs + textareas inside blocks also follow the site radius */
      .preview-theme-scope input:not([style*="border-radius"]),
      .preview-theme-scope textarea:not([style*="border-radius"]),
      .preview-theme-scope select:not([style*="border-radius"]) {
        border-radius: var(--site-radius);
      }
    `;
  };

  // Animation CSS keyframes
  const animationStyles = `
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    @keyframes fadeInUp {
      from { opacity: 0; transform: translateY(30px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes fadeInDown {
      from { opacity: 0; transform: translateY(-30px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes slideLeft {
      from { opacity: 0; transform: translateX(50px); }
      to { opacity: 1; transform: translateX(0); }
    }
    @keyframes slideRight {
      from { opacity: 0; transform: translateX(-50px); }
      to { opacity: 1; transform: translateX(0); }
    }
    @keyframes zoomIn {
      from { opacity: 0; transform: scale(0.8); }
      to { opacity: 1; transform: scale(1); }
    }
    @keyframes bounce {
      0%, 20%, 53%, 100% { transform: translateY(0); }
      40%, 43% { transform: translateY(-20px); }
      70% { transform: translateY(-10px); }
      90% { transform: translateY(-4px); }
    }
    @keyframes shake {
      0%, 100% { transform: translateX(0); }
      10%, 30%, 50%, 70%, 90% { transform: translateX(-2px); }
      20%, 40%, 60%, 80% { transform: translateX(2px); }
    }
    .full-width-banner {
      margin-left: calc(-50vw + 50%);
      margin-right: calc(-50vw + 50%);
      width: 100vw;
    }
    .quick-preview-mode .full-width-banner {
      margin-left: 0;
      margin-right: 0;
      width: 100%;
    }

    /* ─────────────────────────────────────────────────────────────
       Mobile-responsive block spacing overrides (< 768px).
       Blocks in the CMS often have desktop-tuned Tailwind margin
       classes (mb-8, mb-12, mb-16) plus inline styles like
       minHeight: 500px, padding: 80px. On phones this leaves
       huge white gaps between blocks. These rules shrink those
       to sane mobile values ONLY on narrow viewports — desktop
       is unaffected.
       Scope with .preview-theme-scope so we don't touch the
       admin chrome or unrelated site regions.
       ───────────────────────────────────────────────────────── */
    @media (max-width: 767px) {
      /* Tighten between-block gaps */
      .preview-theme-scope .mb-16 { margin-bottom: 0.75rem !important; }
      .preview-theme-scope .mb-12 { margin-bottom: 0.75rem !important; }
      .preview-theme-scope .mb-8  { margin-bottom: 0.5rem  !important; }
      .preview-theme-scope .mb-6  { margin-bottom: 0.5rem  !important; }
      /* Cap the mobile height of hero-style blocks so a
         "Height: 600px" desktop hero doesn't consume most of the
         mobile viewport. Uses attribute matcher on inline style
         to only target elements that actually set minHeight in px. */
      .preview-theme-scope [style*="min-height:"].hero-responsive,
      .preview-theme-scope [style*="minHeight:"].hero-responsive {
        min-height: 320px !important;
      }
      /* Columns Block on mobile:
         The Columns block uses grid-auto-rows: 1fr +
         align-items: stretch (inline) so that all cards in a row
         have the same visual height on desktop. On mobile the grid
         collapses to 1 column (5 stacked rows) but the 1fr rule
         still forces every row to the SAME height as the tallest
         one — so a "Coming soon" card gets padded out by ~120px of
         empty white space to match a longer neighbour. Override
         both properties on mobile so each card uses its natural
         content height. Desktop stretching stays intact. */
      .preview-theme-scope .grid[class*="grid-cols-1"] {
        grid-auto-rows: auto !important;
        align-items: start !important;
      }
      .preview-theme-scope .grid[class*="grid-cols-1"] > *,
      .preview-theme-scope .grid[class*="grid-cols-1"] > * > * {
        height: auto !important;
        min-height: 0 !important;
      }
    }
  `;

  const renderBlock = (block, index) => {
    if (!block) return null;
    const { type, content = {}, styles } = block;
    const safeStyles = styles || {};
    
    // Get preset styles if applied
    const presetStyles = content?.presetStyles || {};
    
    // Helper to resolve any URL - handles internal page links in both preview and live mode
    const resolveUrl = (url) => {
      if (!url || url === '#') return '#';
      
      // Download links (starts with download:)
      if (url.startsWith('download:')) {
        return url.substring(9);
      }
      
      // mailto: and tel: are native browser protocols — pass through untouched.
      if (url.startsWith('mailto:') || url.startsWith('tel:')) {
        return url;
      }
      
      // External URLs (starts with http:// or https://)
      if (url.startsWith('http://') || url.startsWith('https://')) {
        return url;
      }
      
      // Anchor links (starts with #)
      if (url.startsWith('#')) {
        return url;
      }
      
      // Handle anchor links with page slug (e.g., /page-slug#anchor or page-slug#anchor)
      if (url.includes('#')) {
        const [pathPart, anchorPart] = url.split('#');
        const slug = pathPart.startsWith('/') ? pathPart.substring(1) : pathPart;
        
        if (slug && pages && pages.length > 0) {
          const page = pages.find(p => p.slug === slug);
          if (page && page.preview_token && isInPreviewMode()) {
            return `/preview/${page.preview_token}#${anchorPart}`;
          }
        }
        // Fallback - return with proper formatting
        return pathPart.startsWith('/') ? url : `/${url}`;
      }
      
      // Internal page links (starts with / or is a slug without /)
      // Get the slug - remove leading / if present
      const slug = url.startsWith('/') ? url.substring(1) : url;
      
      // In preview mode, try to find the page by slug and use its preview token
      if (pages && pages.length > 0) {
        const page = pages.find(p => p.slug === slug);
        if (page && page.preview_token && isInPreviewMode()) {
          return `/preview/${page.preview_token}`;
        }
      }
      
      // In live mode or if page not found, return with leading slash for proper routing
      return url.startsWith('/') ? url : `/${url}`;
    };
    
    // Check if a URL points to a popup page and return click handler if so
    const getPopupClickHandler = (url) => {
      if (!url || url === '#' || url.startsWith('download:') || url.startsWith('http://') || url.startsWith('https://') || url.startsWith('#')) {
        return null;
      }
      
      // Strip leading slash and any /preview/ prefix for matching
      let needle = url.startsWith('/') ? url.substring(1) : url;
      if (needle.startsWith('preview/')) {
        needle = needle.substring('preview/'.length);
      }
      // Drop query string / hash fragments
      needle = needle.split('?')[0].split('#')[0];
      
      if (pages && pages.length > 0 && needle) {
        // Match by slug, full_path, page_id, or preview_token
        const page = pages.find(p => 
          p.slug === needle ||
          p.full_path === needle ||
          p.page_id === needle ||
          p.preview_token === needle
        );
        if (page && page.isPopup) {
          return (e) => {
            e.preventDefault();
            e.stopPropagation();
            setPopupPage({ 
              isOpen: true, 
              pageSlug: page.slug, 
              previewToken: page.preview_token 
            });
          };
        }
      }
      
      return null;
    };
    
    const getHoverClass = () => {
      switch (safeStyles.hoverEffect) {
        case 'shadow':
          return 'hover:shadow-xl transition-shadow duration-300';
        case 'lift':
          return 'hover:-translate-y-1 hover:shadow-lg transition-all duration-300';
        case 'glow':
          return 'glow-effect transition-shadow duration-300';
        case 'darken':
          return 'hover:brightness-95 transition-all duration-300';
        case 'scale':
          return 'hover:scale-[1.02] transition-transform duration-300';
        case 'border-highlight':
          // Use a class for the hover state, but actual color is applied via inline style
          return 'border-highlight-effect transition-colors duration-300';
        default:
          return '';
      }
    };
    
    // Generate inline style for effects that need custom colors
    const getHoverStyle = () => {
      if (safeStyles.hoverEffect === 'border-highlight') {
        return {
          border: '2px solid transparent',
          '--highlight-color': safeStyles.highlightColor || '#6366f1',
        };
      }
      if (safeStyles.hoverEffect === 'glow') {
        return {
          '--glow-color': safeStyles.glowColor || '#6366f1',
        };
      }
      return {};
    };

    const getClickClass = () => {
      switch (safeStyles.clickEffect) {
        case 'scale-down':
          return 'active:scale-[0.98] transition-transform';
        case 'flash':
          return 'active:opacity-70 transition-opacity';
        case 'shake':
          return 'active:animate-[shake_0.3s_ease-in-out]';
        case 'ripple':
          return 'ripple-effect';
        default:
          return '';
      }
    };

    const getShadowClass = () => {
      switch (safeStyles.shadow) {
        case 'sm': return 'shadow-sm';
        case 'md': return 'shadow-md';
        case 'lg': return 'shadow-lg';
        case 'xl': return 'shadow-xl';
        case 'inner': return 'shadow-inner';
        default: return '';
      }
    };

    const getAnimationStyle = () => {
      if (!safeStyles.animation || safeStyles.animation === 'none') return {};
      return {
        animation: `${safeStyles.animation} ${safeStyles.animationDuration || '0.5s'} ease-out ${safeStyles.animationDelay || '0s'} both`,
      };
    };

    // Generate unique class name for custom CSS
    const customCSSClass = safeStyles.customCSS ? `custom-block-${index}` : '';
    let customCSSContent = safeStyles.customCSS ? `
      .${customCSSClass} { ${safeStyles.customCSS} }
    ` : '';

    // Mobile responsive override: when a block has a container-width % or
    // custom px width set, or a Max Width preset, the raw inline
    // `width: 50%` / `max-width: 768px` squashes text on mobile screens.
    // Emit a scoped mobile media-query that forces the block back to 100%
    // width when the viewport is narrow (< 768px).
    const containerWidthClass = (() => {
      const w = safeStyles?.containerWidth;
      const cw = safeStyles?.containerWidthPx;
      const mw = safeStyles?.maxWidth;
      // Full-bleed (`100vw`) must keep its negative-margin breakout on
      // mobile too, otherwise the coloured band shrinks back into the
      // container's padding. Skip the mobile-shrink override in that case.
      if (mw === '100vw') return '';
      const hasCustomWidth =
        (w && w !== '100%') ||
        (w === 'custom' && cw) ||
        (mw && mw !== 'none' && mw !== '100%');
      if (!hasCustomWidth) return '';
      const cls = `cw-mobile-${index}`;
      customCSSContent += `
        @media (max-width: 767px) {
          .${cls} {
            width: 100% !important;
            max-width: 100% !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
          }
        }
      `;
      return cls;
    })();

    // Check if block should be displayed as a card
    const isCard = safeStyles.showAsCard === true;
    
    // Handle container width
    const getContainerWidthStyle = () => {
      const width = safeStyles.containerWidth;
      const customWidth = safeStyles.containerWidthPx;
      const maxWidth = safeStyles.maxWidth;
      const align = safeStyles.containerAlign || 'center';

      // Full-bleed edge-to-edge: escape the page's max-w-* container using
      // negative-margin breakout (matches the technique used by the hero &
      // full-width image blocks). Only kick in outside Quick Preview
      // because the editor iframe doesn't span the real viewport width, so
      // 100vw would visibly overflow the panel. In Quick Preview we fall
      // back to 100% of the panel so the layout remains sensible.
      if (maxWidth === '100vw') {
        if (isQuickPreview) {
          return { width: '100%', maxWidth: 'none' };
        }
        return {
          width: '100vw',
          maxWidth: 'none',
          marginLeft: 'calc(-50vw + 50%)',
          marginRight: 'calc(-50vw + 50%)',
        };
      }

      let widthStyle = {};

      if (width === 'custom' && customWidth) {
        widthStyle.width = customWidth;
      } else if (width && width !== '100%') {
        widthStyle.width = width;
      }

      if (maxWidth && maxWidth !== 'none') {
        widthStyle.maxWidth = maxWidth;
      }

      // Apply alignment whenever the block has any width constraint —
      // either an explicit containerWidth (<100%) or a maxWidth preset.
      // Without this, picking "Max Width: Medium (768px)" alone would leave
      // the block flush-left inside the page wrapper because no margin auto
      // was ever emitted. Also apply block-level display so margin: auto
      // actually centers (block-level items shrink to width + maxWidth cap).
      const hasWidthConstraint =
        (width && width !== '100%') ||
        (maxWidth && maxWidth !== 'none' && maxWidth !== '100%');
      if (hasWidthConstraint) {
        // Ensure display is block-level so margins center the box even when
        // an outer wrapper uses flex layout.
        widthStyle.display = widthStyle.display || 'block';
        if (align === 'center') {
          widthStyle.marginLeft = 'auto';
          widthStyle.marginRight = 'auto';
        } else if (align === 'flex-end') {
          widthStyle.marginLeft = 'auto';
          widthStyle.marginRight = '0';
        } else {
          widthStyle.marginLeft = '0';
          widthStyle.marginRight = 'auto';
        }
      }
      
      return widthStyle;
    };
    
    // Build preset style object (only apply relevant CSS properties)
    const getPresetStyleObject = () => {
      if (!presetStyles || !presetStyles.preset) return {};
      const { preset, icon, layout, ...cssProps } = presetStyles;
      return cssProps;
    };
    
    const blockStyle = {
      backgroundColor: isCard ? (safeStyles.backgroundColor || '#ffffff') : (safeStyles.backgroundColor || 'transparent'),
      borderColor: safeStyles.borderColor || (isCard ? '#e2e8f0' : 'transparent'),
      borderWidth: safeStyles.borderWidth || (isCard ? '1px' : '0px'),
      borderStyle: (safeStyles.borderWidth && safeStyles.borderWidth !== '0px') || isCard ? 'solid' : 'none',
      borderRadius: safeStyles.borderRadius || (isCard ? '12px' : undefined),
      padding: safeStyles.padding || (isCard ? '24px' : '0px'),
      transition: `all ${safeStyles.transitionDuration || '0.2s'} ease`,
      ...(isCard && !safeStyles.shadow ? { boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)' } : {}),
      ...getAnimationStyle(),
      ...getContainerWidthStyle(),
      // Apply preset styles last so they override defaults
      ...getPresetStyleObject(),
    };
    
    // Get continuous animation style for block-level animations
    // This is computed here so it can be used by all blocks
    const getContinuousAnimationStyleForBlock = () => {
      if (!content || !content.animation) return {};
      const anim = content.animation;
      
      if (anim.attention && anim.attention !== 'none') {
        const animationDurations = {
          pulse: '2s',
          bounce: '1s',
          shake: '0.8s',
          swing: '1.5s',
          float: '3s',
          glowPulse: '2s',
        };
        
        return {
          animation: `${anim.attention} ${animationDurations[anim.attention] || '2s'} ease-in-out infinite`,
        };
      }
      
      return {};
    };
    
    // Compute optional spacing overrides — these become inline marginTop /
    // marginBottom which override the default Tailwind `mb-6` / `mb-8` classes
    // baked into every block wrapper.
    const SPACING_PRESETS = {
      none: '0px',
      small: '0.5rem',
      medium: '2rem',
      large: '4rem',
    };
    const getSpacingStyle = () => {
      const out = {};
      if (safeStyles.spaceAbove && safeStyles.spaceAbove !== 'auto') {
        out.marginTop = SPACING_PRESETS[safeStyles.spaceAbove] ?? '';
      }
      if (safeStyles.spaceBelow && safeStyles.spaceBelow !== 'auto') {
        out.marginBottom = SPACING_PRESETS[safeStyles.spaceBelow] ?? '';
      }
      return out;
    };

    // Merge continuous animation into block style for all blocks
    const finalBlockStyle = {
      ...blockStyle,
      ...getContinuousAnimationStyleForBlock(),
      ...getHoverStyle(),
      // Spacing must apply LAST so it overrides Tailwind margin classes.
      ...getSpacingStyle(),
    };

    const wrapperClass = `${getHoverClass()} ${getClickClass()} ${getShadowClass()} ${customCSSClass} ${containerWidthClass} transition-all cursor-default`;

    // Render block button if enabled
    const renderBlockButton = () => {
      if (!content?.blockButton?.enabled) return null;
      const btn = content.blockButton;
      const btnStyle = getButtonVariantStyle(btn);
      const sizeClasses = getButtonSizeClass(btn.size);
      const hoverClass = getButtonHoverClass(btn.hover);
      const iconEl = renderButtonIcon(btn.icon, btn.size === 'lg' ? 20 : 16, btn.iconColor);
      const iconPos = btn.iconPosition || 'right';
      
      const isDownload = isDownloadLink(btn.url);
      // If openInNewTab is set, respect that — disable popup handler so the
      // user gets a new tab rather than an in-page overlay.
      const btnPopupHandler = btn.openInNewTab ? null : getPopupClickHandler(btn.url);
      
      return (
        <div style={{ textAlign: btn.align || 'center', marginTop: `${btn.marginTop || 16}px` }}>
          <a
            href={resolveUrl(btn.url) || '#'}
            onClick={btnPopupHandler || undefined}
            download={isDownload ? getFilenameFromUrl(btn.url) : undefined}
            target={btnPopupHandler ? '_self' : (btn.openInNewTab ? '_blank' : undefined)}
            rel={!btnPopupHandler && btn.openInNewTab ? 'noopener noreferrer' : undefined}
            className={`inline-flex items-center justify-center gap-2 ${sizeClasses} ${btn.fullWidth ? 'w-full' : ''} font-medium transition-colors transition-transform ${hoverClass}`}
            style={{ ...btnStyle, display: 'inline-flex', cursor: 'pointer', WebkitAppearance: 'none' }}
          >
            {iconEl && iconPos === 'left' && iconEl}
            {btn.text || 'Click Me'}
            {iconEl && iconPos === 'right' && iconEl}
          </a>
        </div>
      );
    };

    // Get animation class from block content - with null check
    const getBlockAnimationClass = () => {
      if (!content || !content.animation) return '';
      const anim = content.animation;
      
      // Only apply animation classes if there's actually an entrance animation
      if (!anim.entrance || anim.entrance === 'none') {
        return '';
      }
      
      const classes = ['animate', `animate-${anim.entrance}`];
      
      // Speed
      classes.push(`animate-${anim.speed || 'normal'}`);
      
      // Delay
      if (anim.delay && anim.delay !== '0') {
        classes.push(`animate-delay-${anim.delay}`);
      }
      
      // Scroll trigger - only if entrance animation is set
      if (anim.animateOnScroll) {
        classes.push('animate-on-scroll');
      }
      
      return classes.join(' ');
    };
    
    const animationClass = getBlockAnimationClass();

    // Render Section Header if enabled — this may append to customCSSContent
    // so it MUST run before we create customStyleTag below.
    const renderSectionHeader = () => {
      const sh = content?.sectionHeader;
      if (!sh) return null;
      const hasHeaderText = sh.text && String(sh.text).trim();
      // Render when explicitly enabled, or when text exists and enabled was never set
      // (covers legacy/imported blocks that carry text without the enabled flag).
      const isEnabled = sh.enabled === true || (sh.enabled === undefined && hasHeaderText);
      if (!isEnabled) return null;
      const HeadingTag = sh.headingTag || 'h2';
      const shTop = Number(sh.marginTop) || 0;
      const shBottom = Number(sh.marginBottom ?? 16);
      const shClass = `sh-mobile-${index}`;
      // Mobile-responsive margins: cap large pixel values so they don't leave
      // huge white gaps on 375–430px screens. Desktop keeps configured values.
      // Mobile top/bottom cap at 32/24px respectively (0.4x scale, capped).
      const mobileTop = Math.min(Math.round(shTop * 0.4), 32);
      const mobileBottom = Math.min(Math.round(shBottom * 0.4), 24);
      customCSSContent += `
        @media (max-width: 767px) {
          .${shClass} {
            margin-top: ${mobileTop}px !important;
            margin-bottom: ${mobileBottom}px !important;
          }
        }
      `;
      const headingStyle = {
        fontSize: responsiveFontSize(sh.fontSize || 28),
        fontFamily: sh.fontFamily || 'inherit',
        fontWeight: sh.fontWeight || 'bold',
        color: sh.color || '#1e293b',
        textAlign: sh.align || 'left',
        marginTop: `${shTop}px`,
        marginBottom: `${shBottom}px`,
        // If no text, act as spacer with minimum height
        minHeight: sh.text ? 'auto' : `${sh.spacerHeight || 20}px`,
      };
      return React.createElement(HeadingTag, { style: headingStyle, className: shClass }, sh.text || '');
    };
    // Pre-render section header so its CSS is baked into customCSSContent
    // before we create the <style> tag.
    const sectionHeaderEl = renderSectionHeader();

    // Inject custom CSS if present (customCSSContent may have been extended
    // by containerWidthClass and renderSectionHeader above).
    const customStyleTag = customCSSContent ? <style key={`style-${index}`}>{customCSSContent}</style> : null;

    // Helper to wrap block with custom CSS style tag and section header
    const wrapWithCustomCSS = (blockElement) => {
      if (customStyleTag || sectionHeaderEl) {
        return (
          <React.Fragment key={index}>
            {customStyleTag}
            {sectionHeaderEl}
            {blockElement}
          </React.Fragment>
        );
      }
      return blockElement;
    };

    switch (type) {
      case 'heading':
        return wrapWithCustomCSS(
          <div key={`block-${index}`} className={`mb-6 ${wrapperClass} ${animationClass}`} style={finalBlockStyle}>
            <div
              dangerouslySetInnerHTML={{ __html: clampInlineFontSizes(processHtml(content.html || `<h2>${content.text || 'Heading'}</h2>`)) }}
              className="heading-text-content"
              style={{ 
                fontFamily: content.fontFamily || 'Manrope, sans-serif',
                fontSize: content.fontSize ? responsiveFontSize(content.fontSize) : undefined,
                color: content.textColor || undefined,
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
              }}
            />
            <style>{`
              .heading-text-content h1 { font-size: ${content.fontSize ? responsiveFontSize(content.fontSize) : responsiveFontSize(getThemeValue('h1_size') || 48)}; font-weight: 700; line-height: 1.2; margin: 0; }
              .heading-text-content h2 { font-size: ${content.fontSize ? responsiveFontSize(content.fontSize) : responsiveFontSize(getThemeValue('h2_size') || 36)}; font-weight: 700; line-height: 1.3; margin: 0; }
              .heading-text-content h3 { font-size: ${content.fontSize ? responsiveFontSize(content.fontSize) : responsiveFontSize(getThemeValue('h3_size') || 28)}; font-weight: 600; line-height: 1.3; margin: 0; }
              .heading-text-content h4 { font-size: ${content.fontSize ? responsiveFontSize(content.fontSize) : responsiveFontSize(getThemeValue('h4_size') || 22)}; font-weight: 600; line-height: 1.4; margin: 0; }
              .heading-text-content h5 { font-size: ${content.fontSize ? responsiveFontSize(content.fontSize) : '1.25rem'}; font-weight: 600; line-height: 1.4; margin: 0; }
              .heading-text-content h6 { font-size: ${content.fontSize ? responsiveFontSize(content.fontSize) : '1.125rem'}; font-weight: 600; line-height: 1.4; margin: 0; }
              .heading-text-content p { font-size: ${content.fontSize ? responsiveFontSize(content.fontSize) : responsiveFontSize(getThemeValue('h2_size') || 36)}; font-weight: 700; margin: 0; }
            `}</style>
            {renderBlockButton()}
          </div>
        );

      case 'paragraph':
        // Render callout icon if preset has one
        const renderCalloutIcon = () => {
          const iconType = presetStyles?.icon;
          if (!iconType) return null;
          
          const iconConfig = {
            info: { color: '#3b82f6', bgColor: '#eff6ff', svg: '<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/></svg>' },
            warning: { color: '#f59e0b', bgColor: '#fffbeb', svg: '<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/></svg>' },
            success: { color: '#22c55e', bgColor: '#f0fdf4', svg: '<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>' },
            error: { color: '#ef4444', bgColor: '#fef2f2', svg: '<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>' },
            quote: { color: '#6366f1', bgColor: '#eef2ff', svg: '<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M6 17h3l2-4V7H5v6h3zm8 0h3l2-4V7h-6v6h3z"/></svg>' },
          };
          
          const config = iconConfig[iconType];
          if (!config) return null;
          
          return (
            <div 
              style={{ 
                color: config.color,
                marginRight: '12px',
                flexShrink: 0,
              }}
              dangerouslySetInnerHTML={{ __html: config.svg }}
            />
          );
        };
        
        const hasCalloutIcon = presetStyles?.icon;
        
        return wrapWithCustomCSS(
          <div key={`block-${index}`} className={`mb-4 ${wrapperClass} ${animationClass}`} style={{
            ...finalBlockStyle,
            display: hasCalloutIcon ? 'flex' : 'block',
            alignItems: hasCalloutIcon ? 'flex-start' : undefined,
          }}>
            {renderCalloutIcon()}
            <div style={{ flex: hasCalloutIcon ? 1 : undefined }}>
              <div
                dangerouslySetInnerHTML={{ __html: clampInlineFontSizes(processHtml(content.html || '<p>Start typing...</p>')) }}
                className="leading-relaxed paragraph-text-content"
                style={{
                  fontFamily: content.fontFamily || getThemeValue('body_font') || undefined,
                  fontSize: content.fontSize ? responsiveFontSize(content.fontSize) : responsiveFontSize(getThemeValue('body_size') || 16),
                  color: content.textColor || getThemeValue('body_color') || '#334155',
                  overflowWrap: 'anywhere',
                  wordBreak: 'break-word',
                  '--theme-h1-size': `${getThemeValue('h1_size') || 48}px`,
                  '--theme-h2-size': `${getThemeValue('h2_size') || 36}px`,
                  '--theme-h3-size': `${getThemeValue('h3_size') || 28}px`,
                  '--theme-h4-size': `${getThemeValue('h4_size') || 22}px`,
                  '--theme-body-size': `${getThemeValue('body_size') || 16}px`,
                }}
              />
              <style>{`
                .paragraph-text-content h1 { font-size: ${content.fontSize ? responsiveFontSize(content.fontSize) : responsiveFontSize(getThemeValue('h1_size') || 48)}; font-weight: 700; line-height: 1.2; margin: 0.75rem 0; }
                .paragraph-text-content h2 { font-size: ${content.fontSize ? responsiveFontSize(content.fontSize) : responsiveFontSize(getThemeValue('h2_size') || 36)}; font-weight: 700; line-height: 1.3; margin: 0.75rem 0; }
                .paragraph-text-content h3 { font-size: ${content.fontSize ? responsiveFontSize(content.fontSize) : responsiveFontSize(getThemeValue('h3_size') || 28)}; font-weight: 600; line-height: 1.3; margin: 0.75rem 0; }
                .paragraph-text-content h4 { font-size: ${content.fontSize ? responsiveFontSize(content.fontSize) : responsiveFontSize(getThemeValue('h4_size') || 22)}; font-weight: 600; line-height: 1.4; margin: 0.5rem 0; }
                .paragraph-text-content h5 { font-size: ${content.fontSize ? responsiveFontSize(content.fontSize) : '1.125rem'}; font-weight: 600; line-height: 1.4; margin: 0.5rem 0; }
                .paragraph-text-content h6 { font-size: ${content.fontSize ? responsiveFontSize(content.fontSize) : '1rem'}; font-weight: 600; line-height: 1.4; margin: 0.5rem 0; }
                .paragraph-text-content p { font-size: ${content.fontSize ? responsiveFontSize(content.fontSize) : responsiveFontSize(getThemeValue('body_size') || 16)}; margin: 0.5rem 0; }
                .paragraph-text-content ul { list-style-type: disc; padding-left: 1.5rem; margin: 0.5rem 0; }
                .paragraph-text-content ol { list-style-type: decimal; padding-left: 1.5rem; margin: 0.5rem 0; }
                .paragraph-text-content li { font-size: ${content.fontSize ? responsiveFontSize(content.fontSize) : responsiveFontSize(getThemeValue('body_size') || 16)}; margin: 0.25rem 0; display: list-item; }
                .paragraph-text-content ul ul { list-style-type: circle; }
                .paragraph-text-content ul ul ul { list-style-type: square; }
                .paragraph-text-content a { color: ${getThemeValue('link_color') || '#2563eb'}; text-decoration: underline; }
                .paragraph-text-content a:hover { color: ${getThemeValue('link_hover_color') || '#1d4ed8'}; }
              `}</style>
              {renderBlockButton()}
            </div>
          </div>
        );

      case 'image':
        if (!content.url) return null;
        const imageSizeMode = safeStyles.imageSizeMode || 'width';
        const imageWidth = safeStyles.imageWidth || '100%';
        const imageHeight = safeStyles.imageHeight || '300px';
        const imageAlign = safeStyles.imageAlign || 'center';
        const isFullWidth = content.fullWidth === true;
        // In Quick Preview, don't let full-width banners break out of container
        const applyFullWidth = isFullWidth && !isQuickPreview;
        const imgShapeMask = content.shapeMask || 'none';
        const imgShapeSize = content.shapeSize || 300;
        
        // Shape mask container style for image block
        const imgShapeContainerStyle = imgShapeMask !== 'none' ? {
          width: `${imgShapeSize}px`,
          height: imgShapeMask === 'ellipse' ? `${imgShapeSize * 1.25}px` : `${imgShapeSize}px`,
          maxWidth: '100%',
          borderRadius: imgShapeMask === 'circle' ? '50%' : 
                       imgShapeMask === 'ellipse' ? '50%' :
                       imgShapeMask === 'rounded' ? '20px' : '0',
          clipPath: imgShapeMask === 'diamond' ? 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)' :
                   imgShapeMask === 'hexagon' ? 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)' :
                   imgShapeMask === 'octagon' ? 'polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%)' :
                   imgShapeMask === 'star' ? 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)' :
                   imgShapeMask === 'arch' ? 'ellipse(50% 100% at 50% 100%)' : 'none',
          border: content.shapeBorderWidth ? `${content.shapeBorderWidth}px solid ${content.shapeBorderColor || '#ffffff'}` : 'none',
          overflow: 'hidden',
          ...(content.paddingSize ? {
            paddingTop: (content.paddingSide === 'all' || content.paddingSide === 'top' || !content.paddingSide) ? `${content.paddingSize}px` : '0',
            paddingRight: (content.paddingSide === 'all' || content.paddingSide === 'right' || !content.paddingSide) ? `${content.paddingSize}px` : '0',
            paddingBottom: (content.paddingSide === 'all' || content.paddingSide === 'bottom' || !content.paddingSide) ? `${content.paddingSize}px` : '0',
            paddingLeft: (content.paddingSide === 'all' || content.paddingSide === 'left' || !content.paddingSide) ? `${content.paddingSize}px` : '0',
          } : {}),
        } : null;
        
        const imgLinkUrl = content.linkUrl || null;
        const imgLinkTarget = content.linkNewTab !== false ? '_blank' : '_self';
        const imgLinkRel = content.linkNewTab !== false ? 'noopener noreferrer' : undefined;
        
        const imgElement = imgShapeMask !== 'none' ? (
          <div className="inline-block" style={imgShapeContainerStyle}>
            <img
              src={content.url}
              alt={content.alt || ''}
              className="w-full h-full object-cover"
            />
          </div>
        ) : (
          <img
            src={content.url}
            alt={content.alt || ''}
            className={`${isFullWidth ? '' : 'rounded-lg'} ${isFullWidth ? 'w-full' : 'inline-block'}`}
            style={isFullWidth ? { width: '100%', height: 'auto', maxHeight: '600px', objectFit: 'cover' } : { 
              width: imageSizeMode === 'height' ? 'auto' : imageWidth,
              height: imageSizeMode === 'height' ? imageHeight : 'auto',
              maxWidth: '100%',
            }}
          />
        );

        return wrapWithCustomCSS(
          <div 
            key={`block-${index}`} 
            className={`mb-8 ${wrapperClass} ${animationClass}`} 
            style={{ 
              ...finalBlockStyle, 
              textAlign: applyFullWidth ? 'center' : (isFullWidth ? 'center' : imageAlign),
              marginLeft: applyFullWidth ? 'calc(-50vw + 50%)' : undefined,
              marginRight: applyFullWidth ? 'calc(-50vw + 50%)' : undefined,
              width: applyFullWidth ? '100vw' : (isFullWidth ? '100%' : undefined),
            }}
          >
            {imgLinkUrl ? (
              <a href={imgLinkUrl} target={imgLinkTarget} rel={imgLinkRel} className="inline-block">
                {imgElement}
              </a>
            ) : imgElement}
            {content.caption && (
              <p className="text-sm text-slate-500 mt-3 text-center italic">{content.caption}</p>
            )}
            {renderBlockButton()}
          </div>
        );

      case 'button':
        // Render the button's icon using the shared curated icon set
        // (40 lucide icons, same list as every other block that uses
        // ButtonEnhancements). Returns a React element or null.
        const buttonIcon = renderButtonIcon(
          content.icon,
          content.size === 'lg' || content.size === 'xl' ? 20 : 16,
          content.iconColor,
        );
        const buttonId = `btn-${block.id || index}`;
        const buttonIsDownload = isDownloadLink(content.url);
        // If the target URL points to a popup page, render the button so it
        // opens that page in the PopupPageModal rather than navigating.
        // BUT: if openInNewTab is explicitly set, respect that over the popup behaviour
        // — the user intentionally wants a new browser tab, not an in-page overlay.
        const buttonPopupHandler = content.openInNewTab ? null : getPopupClickHandler(content.url);

        // Theme fallbacks — when a Button block does not set explicit
        // colours, follow the current site theme (primary / link-hover /
        // secondary). Applying a Theme preset or changing the Colors tab
        // in Site Theme instantly repaints every unstyled button.
        const btnThemeColor = siteTheme?.primary_color || '#6366f1';
        const btnThemeHover = siteTheme?.link_hover_color || siteTheme?.primary_color || '#4f46e5';
        const btnThemeGradient = siteTheme?.secondary_color || '#8b5cf6';
        const btnColor = content.color || btnThemeColor;
        const btnHover = content.hoverColor || btnThemeHover;
        const btnGradient = content.gradientColor || btnThemeGradient;

        return wrapWithCustomCSS(
          <div 
            key={`block-${index}`}
            className={`mb-6 ${wrapperClass} ${animationClass}`}
            style={{ ...finalBlockStyle, textAlign: content.align || 'left' }}
          >
            <style>{`
              #${buttonId}:hover {
                ${content.style === 'solid' || !content.style ? `background-color: ${btnHover} !important;` : ''}
                ${content.style === 'outline' ? `border-color: ${btnHover} !important; color: ${btnHover} !important;` : ''}
                ${content.style === 'ghost' ? `background-color: ${btnColor}15 !important;` : ''}
                transform: translateY(-2px);
                box-shadow: 0 4px 12px ${btnColor}40;
              }
            `}</style>
            <a
              id={buttonId}
              href={resolveUrl(content.url) || '#'}
              onClick={buttonPopupHandler || undefined}
              download={buttonIsDownload ? getFilenameFromUrl(content.url) : undefined}
              target={buttonPopupHandler ? '_self' : (content.openInNewTab ? '_blank' : '_self')}
              rel={content.openInNewTab && !buttonPopupHandler ? 'noopener noreferrer' : ''}
              className={`
                inline-flex items-center gap-2 font-semibold transition-all duration-200
                ${content.size === 'sm' ? 'px-4 py-2 text-sm' : ''}
                ${content.size === 'md' || !content.size ? 'px-6 py-3 text-base' : ''}
                ${content.size === 'lg' ? 'px-8 py-4 text-lg' : ''}
                ${content.size === 'xl' ? 'px-10 py-5 text-xl' : ''}
                ${content.fullWidth ? 'w-full justify-center' : ''}
                ${content.borderRadius === 'square' ? 'rounded-none' : ''}
                ${content.borderRadius === 'rounded' || !content.borderRadius ? 'rounded-lg' : ''}
                ${content.borderRadius === 'pill' ? 'rounded-full' : ''}
              `}
              style={{
                ...(content.style === 'gradient'
                  ? { background: `linear-gradient(135deg, ${btnColor}, ${btnGradient})`, color: content.textColor || '#ffffff' }
                  : content.style === 'soft'
                    ? { backgroundColor: `${btnColor}26`, color: btnColor }
                    : {
                        backgroundColor: content.style === 'solid' || !content.style ? btnColor : 'transparent',
                        color: content.style === 'solid' || !content.style ? (content.textColor || '#ffffff') : btnColor,
                        border: content.style === 'outline' ? `2px solid ${btnColor}` : 'none',
                      }),
                textDecoration: 'none',
              }}
            >
              {buttonIcon && content.iconPosition === 'left' && (
                <span className="inline-flex items-center">{buttonIcon}</span>
              )}
              {content.text || 'Click Here'}
              {buttonIcon && (content.iconPosition === 'right' || !content.iconPosition) && (
                <span className="inline-flex items-center">{buttonIcon}</span>
              )}
            </a>
          </div>
        );

      case 'quote': {
        // IMPORTANT: `finalBlockStyle` includes an inline `padding: 0px` for
        // non-card blocks, which silently overrides any Tailwind padding
        // utilities (inline style beats class). So we must set the quote's
        // padding via inline style here. If the user has explicitly set
        // their own padding via Style controls, that wins (because
        // finalBlockStyle is spread AFTER our default below).
        const quoteDefaultPadding = '20px 24px';
        // The decorative quote mark is rendered INLINE so it follows the
        // text alignment (left/center/right). With an absolute mark it would
        // sit stuck in the top-left even when text is centered.
        return wrapWithCustomCSS(
          <blockquote
            key={`block-${index}`}
            className={`border-l-4 border-indigo-600 my-6 ${wrapperClass} ${animationClass}`}
            style={{
              padding: quoteDefaultPadding,
              fontFamily: content.fontFamily || 'Merriweather, serif',
              fontSize: content.fontSize ? `${content.fontSize}px` : undefined,
              fontStyle: content.fontStyle || 'italic',
              fontWeight: content.fontWeight || 'normal',
              color: content.color || '#334155',
              textAlign: content.align || 'left',
              ...finalBlockStyle,
              // If user hasn't set custom padding in Style controls, force
              // our quote-friendly padding back on (finalBlockStyle would
              // otherwise reset it to 0).
              ...(!safeStyles.padding ? { padding: quoteDefaultPadding } : {}),
            }}
          >
            <span
              aria-hidden="true"
              style={{
                fontFamily: 'Georgia, serif',
                fontSize: '1.6em',
                lineHeight: 0,
                verticalAlign: '-0.35em',
                marginRight: '0.18em',
                fontStyle: 'normal',
                color: content.color || '#6366f1',
                opacity: 0.45,
                userSelect: 'none',
              }}
            >&ldquo;</span>
            {content.text || ''}
            {content.author && (
              <footer className="mt-3 text-sm not-italic" style={{ opacity: 0.7 }}>
                &mdash; {content.author}
              </footer>
            )}
          </blockquote>
        );
      }

      case 'list':
        return wrapWithCustomCSS(
          <ul key={`block-${index}`} className={`mb-6 space-y-2 list-disc list-inside text-lg text-slate-700 ${wrapperClass} ${animationClass}`} style={finalBlockStyle}>
            {content.items?.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        );

      case 'code':
        return wrapWithCustomCSS(
          <div key={`block-${index}`} className={`mb-6 ${wrapperClass} ${animationClass}`} style={finalBlockStyle}>
            <CodeBlock 
              code={content.code || ''} 
              language={content.language || 'javascript'}
              theme={content.theme || 'github-dark'}
              showLineNumbers={content.showLineNumbers !== false}
            />
          </div>
        );

      case 'custom-html':
        return wrapWithCustomCSS(
          <div 
            key={`block-${index}`} 
            className={`mb-6 ${wrapperClass} ${animationClass}`} 
            style={finalBlockStyle}
            dangerouslySetInnerHTML={{ __html: content.html || '' }}
          />
        );

      case 'hero':
        const slides = content.slides || [];
        const hasSlides = slides.length > 0;
        
        // For backwards compatibility
        const singleSlide = {
          imageUrl: content.imageUrl || '',
          headline: content.headline || '',
          subheadline: content.subheadline || '',
          buttonText: content.buttonText || '',
          buttonUrl: content.buttonUrl || '',
          headlineAnimation: content.headlineAnimation,
          subheadlineAnimation: content.subheadlineAnimation,
          buttonAnimation: content.buttonAnimation,
          // Include color properties for vision import
          useBackgroundColor: content.useBackgroundColor,
          backgroundColor: content.backgroundColor,
          textColor: content.textColor,
        };
        
        const effectiveSlides = hasSlides ? slides : [singleSlide];
        const overlayStyle = getCustomOverlayStyle(content);

        // Legacy presets still supported. Anything else (incl. hex) falls
        // through to the inline-style branch in HeroCarousel.
        const isLegacyPreset = (c) => c === 'white' || c === 'black' || c === 'primary';
        const textColorClass = content.textColor === 'black' ? 'text-slate-900' :
                               content.textColor === 'primary' ? 'text-indigo-600' :
                               isLegacyPreset(content.textColor) ? 'text-white' :
                               (typeof content.textColor === 'string' && content.textColor.startsWith('#') ? '' : 'text-white');
        
        // Hero block width logic
        const heroWidth = content.width || 'container';
        const isFullWidthHero = heroWidth === 'full' && !isQuickPreview;
        
        const getHeroWidthStyle = () => {
          switch (heroWidth) {
            case 'full': return { width: '100%' };
            case 'large': return { width: '100%', maxWidth: '1200px', marginLeft: 'auto', marginRight: 'auto' };
            case 'medium': return { width: '100%', maxWidth: '900px', marginLeft: 'auto', marginRight: 'auto' };
            case 'small': return { width: '100%', maxWidth: '600px', marginLeft: 'auto', marginRight: 'auto' };
            default: return { width: '100%', maxWidth: '1280px', marginLeft: 'auto', marginRight: 'auto' };
          }
        };
        
        // Use carousel component if multiple slides
        if (effectiveSlides.length > 1) {
          return (
            <HeroCarousel
              key={`block-${index}`}
              slides={effectiveSlides}
              overlay={content.overlay}
              overlayStyle={overlayStyle}
              textColorClass={textColorClass}
              defaultTextColor={content.textColor}
              textPositionH={content.textPositionH || 'center'}
              textPositionV={content.textPositionV || 'middle'}
              height={content.height || 500}
              widthStyle={getHeroWidthStyle()}
              isFullWidth={isFullWidthHero}
              autoPlay={content.autoPlay !== false}
              autoPlaySpeed={content.autoPlaySpeed || 5000}
              transitionEffect={content.transitionEffect || 'fade'}
              wrapperClass={wrapperClass}
              blockStyle={finalBlockStyle}
              pages={pages}
            />
          );
        }
        
        // Single slide hero
        const slide = effectiveSlides[0];
        const getTextAnimClass = (anim) => {
          if (!anim || anim.entrance === 'none') return '';
          return `animate animate-${anim.entrance} animate-${anim.speed || 'normal'} ${anim.delay !== '0' ? `animate-delay-${anim.delay}` : ''}`;
        };
        
        return (
          <div 
            key={`block-${index}`}
            className={`relative overflow-hidden ${isFullWidthHero ? '' : 'mb-8'} ${wrapperClass} ${animationClass} hero-responsive`}
            style={{ 
              ...finalBlockStyle,
              borderRadius: safeStyles.borderRadius || (isFullWidthHero ? undefined : '12px'),
              overflow: 'hidden',
              minHeight: `${content.height || 500}px`,
              ...getHeroWidthStyle()
            }}
          >
            {/* Background - Video, Color or Image */}
            {slide.backgroundType === 'video' && slide.videoUrl ? (
              <div className="absolute inset-0">
                {slide.videoUrl.includes('youtube.com') || slide.videoUrl.includes('youtu.be') || slide.videoUrl.includes('youtube-nocookie.com') ? (
                  <iframe
                    src={`https://www.youtube.com/embed/${getYouTubeVideoId(slide.videoUrl)}?autoplay=1&mute=${slide.videoMuted !== false ? 1 : 0}&loop=1&playlist=${getYouTubeVideoId(slide.videoUrl)}&controls=1&showinfo=0&modestbranding=1&rel=0`}
                    className="absolute inset-0 w-full h-full"
                    frameBorder="0"
                    allow="autoplay; encrypted-media; fullscreen"
                    allowFullScreen
                    title="Video"
                  />
                ) : slide.videoUrl.includes('vimeo.com') ? (
                  <iframe
                    src={`https://player.vimeo.com/video/${getVimeoVideoId(slide.videoUrl)}?autoplay=1&muted=${slide.videoMuted !== false ? 1 : 0}&loop=1`}
                    className="absolute inset-0 w-full h-full"
                    frameBorder="0"
                    allow="autoplay; fullscreen"
                    allowFullScreen
                    title="Video"
                  />
                ) : (
                  <video
                    src={slide.videoUrl}
                    autoPlay
                    muted={slide.videoMuted !== false}
                    loop={slide.videoLoop !== false}
                    playsInline
                    controls
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                )}
              </div>
            ) : slide.backgroundType === 'color' || slide.useBackgroundColor ? (
              <div 
                className="absolute inset-0"
                style={{ backgroundColor: slide.backgroundColor || '#6366f1' }}
              />
            ) : slide.imageUrl ? (
              <img 
                src={slide.imageUrl} 
                alt="Hero background"
                className="absolute inset-0 w-full h-full object-cover"
              />
            ) : (
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 to-purple-700" />
            )}
            
            {/* Overlay */}
            {content.overlay !== 'none' && (
              <div className="absolute inset-0" style={overlayStyle} />
            )}
            
            {/* Content */}
            {(() => {
              // Per-slide override for the single-slide hero — same logic as carousel.
              const isHexColor = (v) => typeof v === 'string' && /^#[0-9a-fA-F]{3,8}$/.test(v.trim());
              const slideHex = isHexColor(slide.textColor)
                ? slide.textColor
                : (isHexColor(content.textColor) ? content.textColor : null);
              const colourClass = slideHex ? '' : textColorClass;
              const colourStyle = slideHex ? { color: slideHex } : {};
              return (
            <div
              className={`
                absolute inset-0 p-4 sm:p-8 md:p-12 flex flex-col
                ${content.textPositionH === 'left' ? 'items-start text-left' : ''}
                ${content.textPositionH === 'center' ? 'items-center text-center' : ''}
                ${content.textPositionH === 'right' ? 'items-end text-right' : ''}
                ${content.textPositionV === 'top' ? 'justify-start' : ''}
                ${content.textPositionV === 'middle' ? 'justify-center' : ''}
                ${content.textPositionV === 'bottom' ? 'justify-end' : ''}
                ${colourClass}
              `}
              style={colourStyle}
            >
              {slide.useHtml && slide.htmlContent ? (
                /* Custom HTML Content */
                <div 
                  className="hero-html-content w-full max-w-4xl"
                  dangerouslySetInnerHTML={{ __html: slide.htmlContent }}
                />
              ) : (
                /* Standard Content */
                <>
                  {slide.headline && (
                    <h2 
                      className={`text-4xl md:text-5xl lg:text-6xl font-bold mb-4 w-full max-w-3xl ${getTextAnimClass(slide.headlineAnimation)}`}
                      style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.3)', ...(slide.headlineStyle || {}) }}
                    >
                      {slide.headline}
                    </h2>
                  )}
                  {slide.subheadline && (
                    <p 
                      className={`text-xl md:text-2xl mb-8 w-full max-w-2xl opacity-90 ${getTextAnimClass(slide.subheadlineAnimation)}`}
                      style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.3)', ...(slide.subheadlineStyle || {}) }}
                    >
                      {slide.subheadline}
                    </p>
                  )}
                  {slide.buttonText && (
                    <a 
                      href={resolveUrl(slide.buttonUrl) || '#'}
                      onClick={getPopupClickHandler(slide.buttonUrl)}
                      className={`inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-slate-900 rounded-lg font-semibold text-lg hover:bg-slate-100 transition-all transform hover:scale-105 shadow-lg ${getTextAnimClass(slide.buttonAnimation)}`}
                      style={flatRadiusStyle(slide)}
                    >
                      {flatHasIcon(slide) && flatIconPos(slide) === 'left' && flatIcon(slide, 20)}
                      {slide.buttonText}
                      {flatHasIcon(slide) && flatIconPos(slide) === 'right' && flatIcon(slide, 20)}
                    </a>
                  )}
                </>
              )}
            </div>
              );
            })()}
          </div>
        );

      case 'two-column':
        return wrapWithCustomCSS(
          <div key={`block-${index}`} className={`grid grid-cols-1 md:grid-cols-2 gap-8 mb-8 ${wrapperClass} ${animationClass}`} style={finalBlockStyle}>
            <div className="space-y-4 min-w-0">
              <div
                dangerouslySetInnerHTML={{ __html: clampInlineFontSizes(processHtml(content.leftHtml || '<p>Left column</p>'), { tighter: true }) }}
                className="text-lg leading-relaxed text-slate-700"
                style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
              />
            </div>
            <div className="space-y-4 min-w-0">
              <div
                dangerouslySetInnerHTML={{ __html: clampInlineFontSizes(processHtml(content.rightHtml || '<p>Right column</p>'), { tighter: true }) }}
                className="text-lg leading-relaxed text-slate-700"
                style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
              />
            </div>
          </div>
        );

      case 'card-grid':
      case 'columns':
        const columnCount = content.cardsPerRow || content.columnCount || 2;
        const columns = content.columns || [];
        const showAsCards = content.showAsCards || false;
        const cardStyle = content.cardStyle || {};
        
        // Block width styling for columns
        const getColumnsWidthStyle = () => {
          switch (content.blockWidth) {
            case 'full': return { 
              width: '100vw', 
              maxWidth: 'none',
              marginLeft: 'calc(-50vw + 50%)',
              marginRight: 'calc(-50vw + 50%)',
              paddingLeft: '1rem',
              paddingRight: '1rem',
            };
            case 'large': return { width: '100%', maxWidth: '1200px', margin: '0 auto' };
            case 'medium': return { width: '100%', maxWidth: '900px', margin: '0 auto' };
            case 'small': return { width: '100%', maxWidth: '600px', margin: '0 auto' };
            default: return { width: '100%', maxWidth: 'none' }; // container
          }
        };
        const columnsWidthStyle = getColumnsWidthStyle();
        
        // Build grid template columns based on custom widths
        const getGridTemplateColumns = () => {
          const columnWidths = content.columnWidths;
          // Handle array of widths (legacy format)
          if (Array.isArray(columnWidths) && columnWidths.length === columnCount) {
            return columnWidths.map(w => `${w}fr`).join(' ');
          }
          // Handle string presets
          if (columnWidths === '1-2') return '1fr 2fr';
          if (columnWidths === '2-1') return '2fr 1fr';
          if (columnWidths === '1-1-2') return '1fr 1fr 2fr';
          if (columnWidths === '2-1-1') return '2fr 1fr 1fr';
          if (columnWidths === '1-2-1') return '1fr 2fr 1fr';
          // Handle custom widths
          if (columnWidths === 'custom' && Array.isArray(content.customWidths)) {
            return content.customWidths.map(w => `${w}fr`).join(' ');
          }
          // Default to equal columns
          return `repeat(${columnCount}, 1fr)`;
        };
        
        // Column height styling
        const getColumnHeightStyle = () => {
          const columnHeight = content.columnHeight || 'auto';
          if (columnHeight === 'auto') {
            return {}; // Let content determine height
          } else if (columnHeight === 'match') {
            // Use 1fr to make all rows equal height within the grid
            return { alignItems: 'stretch', gridAutoRows: '1fr' };
          } else {
            // Fixed pixel height
            return { alignItems: 'stretch', gridAutoRows: `${columnHeight}px` };
          }
        };
        
        const getIndividualColumnHeight = () => {
          const columnHeight = content.columnHeight || 'auto';
          if (columnHeight === 'auto') {
            return {}; // Auto - let content determine height
          } else if (columnHeight === 'match') {
            return { height: '100%', minHeight: '100%' }; // Stretch to match tallest
          }
          return { minHeight: `${columnHeight}px`, height: `${columnHeight}px` };
        };
        
        // Map vertical alignment to CSS for per-column text alignment
        const getVerticalAlignStyle = (vAlign) => {
          switch (vAlign) {
            case 'center': return { display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' };
            case 'bottom': return { display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: '100%' };
            default: return {}; // top is default
          }
        };
        
        const cardClasses = showAsCards 
          ? `bg-white ${cardStyle.shadow !== false ? 'shadow-md' : ''}`
          : '';
        const cardInlineStyle = showAsCards 
          ? { 
              backgroundColor: cardStyle.backgroundColor || '#ffffff',
              borderRadius: cardStyle.borderRadius || safeStyles.borderRadius || '8px',
              padding: cardStyle.padding || '16px'
            }
          : {};
        
        // Get card animation settings
        const defaultCardAnimation = showAsCards ? (cardStyle.animation || 'none') : 'none';
        const cardStagger = showAsCards && cardStyle.stagger !== false;
        
        // Continuous animations that should loop infinitely
        const continuousAnimations = ['pulse', 'float', 'bounce', 'glowPulse', 'shake', 'swing'];
        
        // Helper function to get column/card animation classes and styles
        const getColumnAnimationProps = (colIndex, col) => {
          const animation = col.animation || defaultCardAnimation;
          if (animation === 'none') return { className: '', style: {} };
          
          const isContinuous = continuousAnimations.includes(animation);
          const delay = cardStagger ? `${colIndex * 0.15}s` : '0s';
          
          // For scroll-triggered animations, use CSS classes
          if (col.animateOnScroll && !isContinuous) {
            return {
              className: `animate animate-${animation} animate-on-scroll`,
              style: { animationDelay: delay }
            };
          }
          
          // For immediate or continuous animations, use inline styles
          const duration = isContinuous ? '2s' : '0.5s';
          const iteration = isContinuous ? 'infinite' : '1';
          return {
            className: '',
            style: {
              animation: `${animation} ${duration} ease-out ${delay} ${iteration} both`,
            }
          };
        };
        
        // Helper to get button classes
        const getButtonClasses = (col) => {
          const sizeClasses = {
            sm: 'px-3 py-1.5 text-sm',
            md: 'px-4 py-2',
            lg: 'px-6 py-3 text-lg'
          };
          const hoverClass = getButtonHoverClass(col.buttonHover || 'fade');
          // inline-flex so icon + label align and gap works; w-full still applies.
          return `inline-flex items-center justify-center gap-2 ${sizeClasses[col.buttonSize] || sizeClasses.md} ${col.buttonFullWidth ? 'w-full' : ''} font-medium transition-all ${hoverClass}`;
        };
        
        // Helper to get button styles (full variant model incl. gradient/soft + radius)
        const getButtonStyle = (col) => getFlatButtonVariantStyle(col);
        
        // Columns block padding - supports multi-select sides
        const colPaddingSize = content.paddingSize || 0;
        const colPaddingSides = content.paddingSides || ['top', 'right', 'bottom', 'left'];
        const colPaddingStyle = colPaddingSize ? {
          paddingTop: colPaddingSides.includes('top') ? `${colPaddingSize}px` : '0',
          paddingRight: colPaddingSides.includes('right') ? `${colPaddingSize}px` : '0',
          paddingBottom: colPaddingSides.includes('bottom') ? `${colPaddingSize}px` : '0',
          paddingLeft: colPaddingSides.includes('left') ? `${colPaddingSize}px` : '0',
        } : {};

        // Mobile-responsive columns padding: cap large desktop padding
        // (e.g. paddingSize: 80px) so it doesn't leave huge white space
        // above/below the Columns block on 375-430px screens. Scale
        // 0.4x with a hard cap of 24px on mobile — desktop unchanged.
        const colPadMobileClass = colPaddingSize > 24 ? `col-pad-mobile-${index}` : '';
        if (colPadMobileClass) {
          const scale = 0.4;
          const cap = 24;
          const mobilePad = Math.min(Math.round(colPaddingSize * scale), cap);
          customCSSContent += `
            @media (max-width: 767px) {
              .${colPadMobileClass} {
                ${colPaddingSides.includes('top') ? `padding-top: ${mobilePad}px !important;` : ''}
                ${colPaddingSides.includes('right') ? `padding-right: ${mobilePad}px !important;` : ''}
                ${colPaddingSides.includes('bottom') ? `padding-bottom: ${mobilePad}px !important;` : ''}
                ${colPaddingSides.includes('left') ? `padding-left: ${mobilePad}px !important;` : ''}
              }
            }
          `;
        }
        
        return wrapWithCustomCSS(
          <div 
            key={`block-${index}`} 
            className={`grid gap-6 mb-8 ${wrapperClass} ${animationClass} ${colPadMobileClass} ${
              // Always collapse to 1 column on mobile, use responsive classes for larger screens
              columnCount === 2 ? 'grid-cols-1 sm:grid-cols-2' :
              columnCount === 3 ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' :
              columnCount === 4 ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4' :
              columnCount === 5 ? 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5' :
              columnCount === 6 ? 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6' :
              'grid-cols-1 sm:grid-cols-2'
            } ${content.columnWidths ? 'columns-custom-widths' : ''}`}
            style={{ 
              ...finalBlockStyle,
              ...colPaddingStyle,
              ...columnsWidthStyle,
              ...getColumnHeightStyle(),
              // Apply custom column widths via grid-template-columns
              ...(content.columnWidths ? { gridTemplateColumns: getGridTemplateColumns() } : {}),
            }}
          >
            {columns.map((col, colIndex) => {
              // Per-column hover classes
              const getColumnHoverClass = () => {
                switch (col.hoverEffect) {
                  case 'shadow': return 'hover:shadow-xl';
                  case 'lift': return 'hover:-translate-y-1 hover:shadow-lg';
                  case 'scale': return 'hover:scale-[1.02]';
                  case 'darken': return 'hover:brightness-95';
                  case 'glow': return '';
                  default: return '';
                }
              };
              
              // Per-column shadow classes
              const getColumnShadowClass = () => {
                switch (col.shadow) {
                  case 'sm': return 'shadow-sm';
                  case 'md': return 'shadow-md';
                  case 'lg': return 'shadow-lg';
                  default: return '';
                }
              };
              
              const columnStyleClasses = `${getColumnHoverClass()} ${getColumnShadowClass()} transition-all duration-300`;
              
              // Get column preset styles if applied
              const colPresetStyles = col.presetStyles || {};
              const getColumnPresetStyleObject = () => {
                if (!colPresetStyles || !colPresetStyles.preset) return {};
                const { preset, icon, layout, ...cssProps } = colPresetStyles;
                return cssProps;
              };
              
              // Determine if button should be outside the card
              const buttonOutsideCard = showAsCards && col.hasButton && col.buttonCardPosition === 'outside';
              
              // Render the button element
              const renderColumnButton = () => {
                if (!col.hasButton) return null;
                // Clamp buttonMarginTop to a sensible range so legacy data
                // (e.g. a fat-fingered "1616" instead of "16") can't push the
                // column to thousands of pixels tall and ruin the grid layout.
                const rawMt = Number(col.buttonMarginTop);
                const safeMt = Number.isFinite(rawMt) ? Math.min(200, Math.max(0, rawMt)) : 16;
                // When the column wrapper is an <a> tag, nesting another <a> is invalid HTML.
                // iOS Safari strips inner anchors entirely — the button becomes invisible.
                // Fix: render as a <span> (styled identically) when already inside a column link.
                const colHasLink = !!(col.hasColumnLink && col.columnUrl);
                const isInsideAnchor = colHasLink && !buttonOutsideCard;
                const ButtonTag = isInsideAnchor ? 'span' : 'a';
                const popupHandler = getPopupClickHandler(col.buttonUrl);
                const buttonLinkProps = isInsideAnchor ? {} : {
                  href: resolveUrl(col.buttonUrl) || '#',
                  download: isDownloadLink(col.buttonUrl) ? getFilenameFromUrl(col.buttonUrl) : undefined,
                  onClick: popupHandler,
                  target: col.buttonOpenInNewTab && !popupHandler ? '_blank' : undefined,
                  rel: col.buttonOpenInNewTab && !popupHandler ? 'noopener noreferrer' : undefined,
                };
                return (
                  <div style={{ 
                    textAlign: col.buttonAlign || 'center', 
                    marginTop: col.buttonVerticalAlign === 'bottom' && !buttonOutsideCard ? 'auto' : `${safeMt}px`,
                    paddingTop: col.buttonVerticalAlign === 'bottom' && !buttonOutsideCard ? `${safeMt}px` : '0',
                  }}>
                    <ButtonTag
                      {...buttonLinkProps}
                      className={getButtonClasses(col)}
                      style={{ ...getButtonStyle(col), display: 'inline-flex', cursor: 'pointer', WebkitAppearance: 'none' }}
                    >
                      {flatHasIcon(col) && flatIconPos(col) === 'left' && flatIcon(col, col.buttonSize === 'lg' ? 20 : 16)}
                      {col.buttonText || 'Click Me'}
                      {flatHasIcon(col) && flatIconPos(col) === 'right' && flatIcon(col, col.buttonSize === 'lg' ? 20 : 16)}
                    </ButtonTag>
                  </div>
                );
              };
              
              // Check if column should be clickable
              const columnLinkUrl = col.hasColumnLink && col.columnUrl ? resolveUrl(col.columnUrl) : null;
              const columnIsDownload = col.hasColumnLink && isDownloadLink(col.columnUrl);
              const columnPopupHandler = col.hasColumnLink ? getPopupClickHandler(col.columnUrl) : null;
              const columnLinkProps = columnLinkUrl ? {
                href: columnLinkUrl,
                download: columnIsDownload ? getFilenameFromUrl(col.columnUrl) : undefined,
                onClick: columnPopupHandler,
                target: col.columnLinkNewTab && !columnPopupHandler ? '_blank' : undefined,
                rel: col.columnLinkNewTab && !columnPopupHandler ? 'noopener noreferrer' : undefined,
              } : {};
              
              // Wrapper component - either an anchor or a div
              const ColumnWrapper = columnLinkUrl ? 'a' : 'div';
              
              // Get animation properties for this column
              const colAnimProps = getColumnAnimationProps(colIndex, col);
              
              return (
              <div key={colIndex} className={`flex flex-col min-w-0 ${content.columnHeight === 'match' ? 'h-full' : ''}`} style={content.columnHeight === 'match' || (content.columnHeight && content.columnHeight !== 'auto') ? { height: '100%', minHeight: '100%' } : {}}>
                <ColumnWrapper 
                  {...columnLinkProps}
                  className={`${cardClasses} ${columnStyleClasses} ${colAnimProps.className} ${col.hasButton && col.buttonVerticalAlign && !buttonOutsideCard ? 'flex flex-col' : ''} ${buttonOutsideCard ? 'flex-1' : ''} ${content.columnHeight === 'match' || (content.columnHeight && content.columnHeight !== 'auto') ? 'h-full flex-1' : ''} ${columnLinkUrl ? 'block cursor-pointer no-underline' : ''}`}
                  style={{ 
                    ...cardInlineStyle,
                    ...colAnimProps.style,
                    ...getIndividualColumnHeight(),
                    textAlign: col.textAlign || 'left',
                    ...(col.bgTransparent ? { backgroundColor: 'transparent' } : col.backgroundColor ? { backgroundColor: col.backgroundColor } : {}),
                    ...(col.borderRadius > 0 ? { borderRadius: `${col.borderRadius}px` } : {}),
                    ...(col.columnPadding ? { padding: `${col.columnPadding}px` } : {}),
                    overflow: 'hidden',
                    ...(col.hasButton && col.buttonVerticalAlign && !buttonOutsideCard ? { justifyContent: col.buttonVerticalAlign === 'top' ? 'flex-start' : col.buttonVerticalAlign === 'middle' ? 'center' : 'space-between' } : {}),
                    // Apply column preset styles last to override
                    ...getColumnPresetStyleObject(),
                  }}
                >
                {col.type === 'avatar' && (() => {
                  const initials = (col.avatarText && col.avatarText.trim())
                    ? col.avatarText.trim().toUpperCase()
                    : ((col.avatarTitle || '?').trim().charAt(0).toUpperCase() || '?');
                  const circleColor = col.avatarColor || 'var(--site-accent)';
                  return (
                    <div className="text-center w-full">
                      <div
                        className="mx-auto mb-4 flex items-center justify-center font-bold text-white"
                        style={{ width: 80, height: 80, borderRadius: '9999px', backgroundColor: circleColor, fontSize: initials.length > 1 ? '1.5rem' : '2rem' }}
                      >
                        {initials}
                      </div>
                      {col.avatarTitle && (
                        <h3 className="font-semibold text-lg" style={{ color: 'var(--site-heading-color)' }}>{col.avatarTitle}</h3>
                      )}
                      {col.avatarSubtitle && (
                        <p className="opacity-70 text-sm mt-1" style={{ color: 'var(--site-body-color)' }}>{col.avatarSubtitle}</p>
                      )}
                    </div>
                  );
                })()}


                {col.type === 'text' && (
                  <div
                    style={{ 
                      ...getVerticalAlignStyle(col.verticalAlign),
                      // Don't force minHeight - let content flow naturally
                    }}
                  >
                    {/* Render HTML content - TipTapEditor outputs heading tags directly in HTML */}
                    <div
                      dangerouslySetInnerHTML={{ __html: clampInlineFontSizes(processHtml(col.html || '<p>Column content</p>'), { tighter: true }) }}
                      className="leading-relaxed column-text-content"
                      style={{ 
                        color: col.textColor || getThemeValue('body_color') || '#475569',
                        textAlign: col.textAlign || 'left',
                        fontFamily: col.fontFamily || getThemeValue('body_font') || 'inherit',
                        // Apply fontSize when explicitly set, otherwise use theme body size
                        fontSize: col.fontSize ? `${col.fontSize}px` : `${getThemeValue('body_size') || 16}px`,
                        fontWeight: col.isBold ? 'bold' : 'inherit',
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-word',
                        minWidth: 0,
                      }}
                    />
                    <style>{`
                      .column-text-content h1 { font-size: ${getThemeValue('h1_size') || 48}px; font-weight: 700; line-height: 1.2; margin: 0.5rem 0; color: inherit; }
                      .column-text-content h2 { font-size: ${getThemeValue('h2_size') || 36}px; font-weight: 700; line-height: 1.3; margin: 0.5rem 0; color: inherit; }
                      .column-text-content h3 { font-size: ${getThemeValue('h3_size') || 28}px; font-weight: 600; line-height: 1.3; margin: 0.5rem 0; color: inherit; }
                      .column-text-content h4 { font-size: ${getThemeValue('h4_size') || 22}px; font-weight: 600; line-height: 1.4; margin: 0.5rem 0; color: inherit; }
                      .column-text-content h5 { font-size: 1.125rem; font-weight: 600; line-height: 1.4; margin: 0.5rem 0; color: inherit; }
                      .column-text-content h6 { font-size: 1rem; font-weight: 600; line-height: 1.4; margin: 0.5rem 0; color: inherit; }
                      .column-text-content p { font-size: ${getThemeValue('body_size') || 16}px; margin: 0.5rem 0; color: inherit; }
                      .column-text-content ul { list-style-type: disc; padding-left: 1.5rem; margin: 0.5rem 0; color: inherit; }
                      .column-text-content ol { list-style-type: decimal; padding-left: 1.5rem; margin: 0.5rem 0; color: inherit; }
                      .column-text-content li { font-size: ${getThemeValue('body_size') || 16}px; margin: 0.25rem 0; display: list-item; color: inherit; }
                      .column-text-content ul ul { list-style-type: circle; }
                      .column-text-content ul ul ul { list-style-type: square; }
                      .column-text-content span:not([style*="color"]) { color: inherit; }
                      .column-text-content a { color: ${getThemeValue('link_color') || '#2563eb'}; text-decoration: underline; }
                      .column-text-content a:hover { color: ${getThemeValue('link_hover_color') || '#1d4ed8'}; }
                    `}</style>
                  </div>
                )}
                {col.type === 'image' && col.imageUrl && (() => {
                  const imageCorners = col.imageCorners || 'rounded';
                  const getCornerRadius = () => {
                    if (col.shapeMask === 'circle' || col.shapeMask === 'ellipse') return '50%';
                    if (col.shapeMask === 'rounded') return '20px';
                    if (col.shapeMask && col.shapeMask !== 'none') return '0';
                    switch (imageCorners) {
                      case 'square': return '0';
                      case 'pill': return '9999px';
                      case 'rounded':
                      default: return '8px';
                    }
                  };
                  const imgBorderRadius = getCornerRadius();
                  return (
                  <div style={{ textAlign: col.imageAlign || 'center' }}>
                    <div 
                      className="inline-block overflow-hidden"
                      style={{
                        width: col.shapeMask && col.shapeMask !== 'none'
                          ? `${col.shapeSize || 200}px`
                          : (col.imageSizeMode === 'custom' ? `${col.imageWidth || 200}px` :
                             col.imageSizeMode === 'height' ? 'auto' : `${col.imageWidth || 200}px`),
                        height: col.shapeMask && col.shapeMask !== 'none'
                          ? (col.shapeMask === 'ellipse' ? `${(col.shapeSize || 200) * 1.25}px` : `${col.shapeSize || 200}px`)
                          : (col.imageSizeMode === 'custom' ? `${col.imageHeight || 200}px` :
                             col.imageSizeMode === 'height' ? `${col.imageHeight || 200}px` : 'auto'),
                        maxWidth: '100%',
                        borderRadius: imgBorderRadius,
                        clipPath: col.shapeMask === 'diamond' ? 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)' :
                                 col.shapeMask === 'hexagon' ? 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)' :
                                 col.shapeMask === 'octagon' ? 'polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%)' :
                                 col.shapeMask === 'star' ? 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)' :
                                 col.shapeMask === 'arch' ? 'ellipse(50% 100% at 50% 100%)' :
                                 'none',
                        border: col.shapeBorderWidth && col.shapeMask && col.shapeMask !== 'none'
                          ? `${col.shapeBorderWidth}px solid ${col.shapeBorderColor || '#ffffff'}` 
                          : 'none',
                        ...(col.paddingSize ? {
                          paddingTop: (col.paddingSide === 'all' || col.paddingSide === 'top' || !col.paddingSide) ? `${col.paddingSize}px` : '0',
                          paddingRight: (col.paddingSide === 'all' || col.paddingSide === 'right' || !col.paddingSide) ? `${col.paddingSize}px` : '0',
                          paddingBottom: (col.paddingSide === 'all' || col.paddingSide === 'bottom' || !col.paddingSide) ? `${col.paddingSize}px` : '0',
                          paddingLeft: (col.paddingSide === 'all' || col.paddingSide === 'left' || !col.paddingSide) ? `${col.paddingSize}px` : '0',
                        } : {}),
                      }}
                    >
                      <img 
                        src={col.imageUrl} 
                        alt={col.imageAlt || ''} 
                        className="w-full h-full object-cover"
                        style={{ borderRadius: imgBorderRadius }}
                      />
                    </div>
                    {col.imageCaption && (
                      <p className="text-sm text-slate-500 mt-2 italic" style={{ textAlign: col.imageAlign || 'center' }}>{col.imageCaption}</p>
                    )}
                  </div>
                  );
                })()}
                {/* Video Only type */}
                {col.type === 'video' && col.videoUrl && (
                  <div className="rounded-lg overflow-hidden" style={{ height: `${col.videoHeight || 250}px` }}>
                    {(col.videoUrl.includes('youtube.com') || col.videoUrl.includes('youtu.be') || col.videoUrl.includes('youtube-nocookie.com')) ? (
                      <iframe
                        src={`https://www.youtube.com/embed/${getYouTubeVideoId(col.videoUrl)}?autoplay=${col.videoAutoplay !== false ? 1 : 0}&mute=${col.videoMuted !== false ? 1 : 0}&loop=${col.videoLoop !== false ? 1 : 0}&playlist=${getYouTubeVideoId(col.videoUrl)}&controls=${col.videoControls !== false ? 1 : 0}`}
                        className="w-full h-full"
                        frameBorder="0"
                        allow="autoplay; encrypted-media"
                        allowFullScreen
                      />
                    ) : col.videoUrl.includes('vimeo.com') ? (
                      <iframe
                        src={`https://player.vimeo.com/video/${getVimeoVideoId(col.videoUrl)}?autoplay=${col.videoAutoplay !== false ? 1 : 0}&muted=${col.videoMuted !== false ? 1 : 0}&loop=${col.videoLoop !== false ? 1 : 0}&controls=${col.videoControls !== false ? 1 : 0}`}
                        className="w-full h-full"
                        frameBorder="0"
                        allow="autoplay; encrypted-media"
                        allowFullScreen
                      />
                    ) : (
                      <video
                        src={col.videoUrl}
                        autoPlay={col.videoAutoplay !== false}
                        muted={col.videoMuted !== false}
                        loop={col.videoLoop !== false}
                        controls={col.videoControls !== false}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>
                )}
                {/* Card type - Image/Video + Text combined */}
                {col.type === 'card' && (
                  <div className="flex flex-col h-full">
                    {/* Media above text */}
                    {(col.imagePosition || 'top') === 'top' && (
                      <>
                        {/* Image */}
                        {(col.mediaType || 'image') === 'image' && col.imageUrl && (() => {
                          const hasShape = col.shapeMask && col.shapeMask !== 'none';
                          const cardImgW = col.cardImageSizeMode === 'custom' || col.cardImageSizeMode === 'width'
                            ? `${col.cardImageWidth || 200}px`
                            : '100%';
                          const cardImgH = col.cardImageSizeMode === 'custom' || col.cardImageSizeMode === 'height' || !col.cardImageSizeMode
                            ? (col.cardImageHeight === 'auto' ? 'auto' : `${col.cardImageHeight || 200}px`)
                            : 'auto';
                          const shapeRadius = col.shapeMask === 'circle' || col.shapeMask === 'ellipse' ? '50%' :
                                             col.shapeMask === 'rounded' ? '20px' :
                                             hasShape ? '0' : '8px';
                          const shapeClip = col.shapeMask === 'diamond' ? 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)' :
                                           col.shapeMask === 'hexagon' ? 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)' :
                                           col.shapeMask === 'octagon' ? 'polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%)' :
                                           col.shapeMask === 'star' ? 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)' :
                                           col.shapeMask === 'arch' ? 'ellipse(50% 100% at 50% 100%)' :
                                           'none';
                          return (
                          <div className="mb-4" style={{ textAlign: col.textAlign || 'left' }}>
                            <div
                              className="inline-block overflow-hidden"
                              style={{
                                width: cardImgW,
                                height: cardImgH,
                                maxWidth: '100%',
                                borderRadius: shapeRadius,
                                clipPath: shapeClip,
                                border: hasShape && col.shapeBorderWidth
                                  ? `${col.shapeBorderWidth}px solid ${col.shapeBorderColor || '#ffffff'}`
                                  : 'none',
                              }}
                            >
                              <img
                                src={col.imageUrl}
                                alt={col.imageAlt || ''}
                                className="w-full h-full"
                                style={{ objectFit: col.cardImageFit || 'contain', borderRadius: shapeRadius }}
                              />
                            </div>
                          </div>
                          );
                        })()}
                        {/* Video */}
                        {col.mediaType === 'video' && col.videoUrl && (
                          <div className="mb-4 rounded-lg overflow-hidden" style={{ height: `${col.videoHeight || 200}px`, textAlign: col.textAlign || 'left' }}>
                            {(col.videoUrl.includes('youtube.com') || col.videoUrl.includes('youtu.be') || col.videoUrl.includes('youtube-nocookie.com')) ? (
                              <iframe
                                src={`https://www.youtube.com/embed/${getYouTubeVideoId(col.videoUrl)}?autoplay=${col.videoAutoplay !== false ? 1 : 0}&mute=${col.videoMuted !== false ? 1 : 0}&loop=${col.videoLoop !== false ? 1 : 0}&playlist=${getYouTubeVideoId(col.videoUrl)}&controls=${col.videoControls !== false ? 1 : 0}`}
                                className="w-full h-full"
                                frameBorder="0"
                                allow="autoplay; encrypted-media"
                                allowFullScreen
                              />
                            ) : col.videoUrl.includes('vimeo.com') ? (
                              <iframe
                                src={`https://player.vimeo.com/video/${getVimeoVideoId(col.videoUrl)}?autoplay=${col.videoAutoplay !== false ? 1 : 0}&muted=${col.videoMuted !== false ? 1 : 0}&loop=${col.videoLoop !== false ? 1 : 0}&controls=${col.videoControls !== false ? 1 : 0}`}
                                className="w-full h-full"
                                frameBorder="0"
                                allow="autoplay; encrypted-media"
                                allowFullScreen
                              />
                            ) : (
                              <video
                                src={col.videoUrl}
                                autoPlay={col.videoAutoplay !== false}
                                muted={col.videoMuted !== false}
                                loop={col.videoLoop !== false}
                                controls={col.videoControls !== false}
                                className="w-full h-full object-cover"
                              />
                            )}
                          </div>
                        )}
                      </>
                    )}
                    
                    {/* Text content */}
                    <div 
                      className="flex-grow card-text-content"
                      dangerouslySetInnerHTML={{ __html: clampInlineFontSizes(processHtml(col.html || '<p>Card content</p>'), { tighter: true }) }}
                      style={{ 
                        color: col.textColor || getThemeValue('body_color') || '#475569',
                        textAlign: col.textAlign || 'left',
                        // Only apply fontSize if explicitly set (not default)
                        ...(col.fontSize && col.fontSize !== '16' && col.fontSize !== 16 ? { fontSize: responsiveFontSize(col.fontSize) } : {}),
                        fontFamily: col.fontFamily || 'inherit',
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-word',
                        minWidth: 0,
                      }}
                    />
                    <style>{`
                      .card-text-content h1 { font-size: 2.25rem; font-weight: 700; line-height: 1.2; margin: 0.5rem 0; color: inherit; }
                      .card-text-content h2 { font-size: 1.875rem; font-weight: 700; line-height: 1.3; margin: 0.5rem 0; color: inherit; }
                      .card-text-content h3 { font-size: 1.5rem; font-weight: 600; line-height: 1.3; margin: 0.5rem 0; color: inherit; }
                      .card-text-content h4 { font-size: 1.25rem; font-weight: 600; line-height: 1.4; margin: 0.5rem 0; color: inherit; }
                      .card-text-content h5 { font-size: 1.125rem; font-weight: 600; line-height: 1.4; margin: 0.5rem 0; color: inherit; }
                      .card-text-content h6 { font-size: 1rem; font-weight: 600; line-height: 1.4; margin: 0.5rem 0; color: inherit; }
                      .card-text-content p { margin: 0.5rem 0; color: inherit; }
                      .card-text-content ul { list-style-type: disc; padding-left: 1.5rem; margin: 0.5rem 0; color: inherit; }
                      .card-text-content ol { list-style-type: decimal; padding-left: 1.5rem; margin: 0.5rem 0; color: inherit; }
                      .card-text-content li { margin: 0.25rem 0; display: list-item; color: inherit; }
                      .card-text-content ul ul { list-style-type: circle; }
                      .card-text-content ul ul ul { list-style-type: square; }
                      .card-text-content span:not([style*="color"]) { color: inherit; }
                    `}</style>
                    
                    {/* Media below text */}
                    {col.imagePosition === 'bottom' && (
                      <>
                        {/* Image */}
                        {(col.mediaType || 'image') === 'image' && col.imageUrl && (() => {
                          const hasShape = col.shapeMask && col.shapeMask !== 'none';
                          const cardImgW = col.cardImageSizeMode === 'custom' || col.cardImageSizeMode === 'width'
                            ? `${col.cardImageWidth || 200}px`
                            : '100%';
                          const cardImgH = col.cardImageSizeMode === 'custom' || col.cardImageSizeMode === 'height' || !col.cardImageSizeMode
                            ? (col.cardImageHeight === 'auto' ? 'auto' : `${col.cardImageHeight || 200}px`)
                            : 'auto';
                          const shapeRadius = col.shapeMask === 'circle' || col.shapeMask === 'ellipse' ? '50%' :
                                             col.shapeMask === 'rounded' ? '20px' :
                                             hasShape ? '0' : '8px';
                          const shapeClip = col.shapeMask === 'diamond' ? 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)' :
                                           col.shapeMask === 'hexagon' ? 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)' :
                                           col.shapeMask === 'octagon' ? 'polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%)' :
                                           col.shapeMask === 'star' ? 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)' :
                                           col.shapeMask === 'arch' ? 'ellipse(50% 100% at 50% 100%)' :
                                           'none';
                          return (
                          <div className="mt-4" style={{ textAlign: col.textAlign || 'left' }}>
                            <div
                              className="inline-block overflow-hidden"
                              style={{
                                width: cardImgW,
                                height: cardImgH,
                                maxWidth: '100%',
                                borderRadius: shapeRadius,
                                clipPath: shapeClip,
                                border: hasShape && col.shapeBorderWidth
                                  ? `${col.shapeBorderWidth}px solid ${col.shapeBorderColor || '#ffffff'}`
                                  : 'none',
                              }}
                            >
                              <img
                                src={col.imageUrl}
                                alt={col.imageAlt || ''}
                                className="w-full h-full"
                                style={{ objectFit: col.cardImageFit || 'contain', borderRadius: shapeRadius }}
                              />
                            </div>
                          </div>
                          );
                        })()}
                        {/* Video */}
                        {col.mediaType === 'video' && col.videoUrl && (
                          <div className="mt-4 rounded-lg overflow-hidden" style={{ height: `${col.videoHeight || 200}px`, textAlign: col.textAlign || 'left' }}>
                            {(col.videoUrl.includes('youtube.com') || col.videoUrl.includes('youtu.be') || col.videoUrl.includes('youtube-nocookie.com')) ? (
                              <iframe
                                src={`https://www.youtube.com/embed/${getYouTubeVideoId(col.videoUrl)}?autoplay=${col.videoAutoplay !== false ? 1 : 0}&mute=${col.videoMuted !== false ? 1 : 0}&loop=${col.videoLoop !== false ? 1 : 0}&playlist=${getYouTubeVideoId(col.videoUrl)}&controls=${col.videoControls !== false ? 1 : 0}`}
                                className="w-full h-full"
                                frameBorder="0"
                                allow="autoplay; encrypted-media"
                                allowFullScreen
                              />
                            ) : col.videoUrl.includes('vimeo.com') ? (
                              <iframe
                                src={`https://player.vimeo.com/video/${getVimeoVideoId(col.videoUrl)}?autoplay=${col.videoAutoplay !== false ? 1 : 0}&muted=${col.videoMuted !== false ? 1 : 0}&loop=${col.videoLoop !== false ? 1 : 0}&controls=${col.videoControls !== false ? 1 : 0}`}
                                className="w-full h-full"
                                frameBorder="0"
                                allow="autoplay; encrypted-media"
                                allowFullScreen
                              />
                            ) : (
                              <video
                                src={col.videoUrl}
                                autoPlay={col.videoAutoplay !== false}
                                muted={col.videoMuted !== false}
                                loop={col.videoLoop !== false}
                                controls={col.videoControls !== false}
                                className="w-full h-full object-cover"
                              />
                            )}
                          </div>
                        )}
                      </>
                    )}
                    {/* Button rendered INSIDE the card flex container so it isn't
                        clipped by the ColumnWrapper's overflow:hidden */}
                    {col.hasButton && !buttonOutsideCard && renderColumnButton()}
                  </div>
                )}
                {/* Render button for non-card column types (text / image / video / avatar).
                    For 'card' type the button is already rendered inside the card div above. */}
                {col.type !== 'card' && col.hasButton && !buttonOutsideCard && renderColumnButton()}
              </ColumnWrapper>
              {/* Render button outside card when buttonCardPosition is 'outside' */}
              {buttonOutsideCard && renderColumnButton()}
              </div>
            );
            })}
          </div>
        );

      case 'image-text-row':
        // Support for carousel mode with multiple slides
        const rowSlides = content.slides || [];
        const hasCarousel = rowSlides.length > 1;
        
        // Use slides if available, otherwise use legacy single content
        const effectiveRowSlides = rowSlides.length > 0 ? rowSlides : [{
          imageUrl: content.imageUrl || '',
          imageAlt: content.imageAlt || '',
          textHtml: content.textHtml || '<p>Text content</p>',
          imagePosition: content.imagePosition || 'left',
          imageSizeMode: content.imageSizeMode || 'width',
          imageWidth: content.imageWidth || 300,
          imageHeight: content.imageHeight || 200,
          imageAlign: content.imageAlign || 'center',
          imageCorners: content.imageCorners || 'rounded',
          shapeMask: content.shapeMask || 'none',
          shapeSize: content.shapeSize || 250,
          shapeBorderWidth: content.shapeBorderWidth || 0,
          shapeBorderColor: content.shapeBorderColor || '#ffffff',
          paddingSide: content.paddingSide || 'all',
          paddingSize: content.paddingSize || 0,
        }];
        
        // Render carousel if multiple slides
        if (hasCarousel) {
          return wrapWithCustomCSS(
            <ImageTextCarousel
              key={`block-${index}`}
              slides={effectiveRowSlides}
              autoPlay={content.autoPlay !== false}
              autoPlaySpeed={content.autoPlaySpeed || 5000}
              showDots={content.showDots !== false}
              showArrows={content.showArrows !== false}
              wrapperClass={wrapperClass}
              animationClass={animationClass}
              blockStyle={blockStyle}
              processHtml={processHtml}
              blockWidth={content.blockWidth || 'container'}
              pages={pages}
              isQuickPreview={isQuickPreview}
            />
          );
        }
        
        // Single slide rendering (legacy)
        const rowSingleSlide = effectiveRowSlides[0];
        const rowImageOnLeft = rowSingleSlide.imagePosition === 'left';
        const rowShapeMask = rowSingleSlide.shapeMask || 'none';
        const rowShapeSize = rowSingleSlide.shapeSize || 250;
        
        // Block width styling
        const getBlockWidthStyle = () => {
          switch (content.blockWidth) {
            case 'full': return isQuickPreview ? { width: '100%', maxWidth: 'none' } : { 
              width: '100vw', 
              maxWidth: 'none',
              marginLeft: 'calc(-50vw + 50%)',
              marginRight: 'calc(-50vw + 50%)',
              paddingLeft: '1rem',
              paddingRight: '1rem',
            };
            case 'large': return { width: '100%', maxWidth: '1200px', margin: '0 auto' };
            case 'medium': return { width: '100%', maxWidth: '900px', margin: '0 auto' };
            case 'small': return { width: '100%', maxWidth: '600px', margin: '0 auto' };
            default: return { width: '100%', maxWidth: 'none' }; // container
          }
        };
        const blockWidthStyle = getBlockWidthStyle();
        const rowImageCorners = rowSingleSlide.imageCorners || 'rounded';
        const getRowCornerRadius = () => {
          if (rowShapeMask === 'circle' || rowShapeMask === 'ellipse') return '50%';
          if (rowShapeMask === 'rounded') return '20px';
          if (rowShapeMask !== 'none') return '0';
          switch (rowImageCorners) {
            case 'square': return '0';
            case 'pill': return '9999px';
            case 'rounded':
            default: return '8px';
          }
        };
        const rowImageContainerStyle = {
          width: rowShapeMask !== 'none'
            ? `${rowShapeSize}px`
            : (rowSingleSlide.imageSizeMode === 'custom' ? `${rowSingleSlide.imageWidth || 300}px` :
               rowSingleSlide.imageSizeMode === 'height' ? 'auto' : `${rowSingleSlide.imageWidth || 300}px`),
          height: rowShapeMask !== 'none'
            ? (rowShapeMask === 'ellipse' ? `${rowShapeSize * 1.25}px` : `${rowShapeSize}px`)
            : (rowSingleSlide.imageSizeMode === 'custom' ? `${rowSingleSlide.imageHeight || 200}px` :
               rowSingleSlide.imageSizeMode === 'height' ? `${rowSingleSlide.imageHeight || 200}px` : 'auto'),
          maxWidth: '100%',
          borderRadius: getRowCornerRadius(),
          clipPath: rowShapeMask === 'diamond' ? 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)' :
                   rowShapeMask === 'hexagon' ? 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)' :
                   rowShapeMask === 'octagon' ? 'polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%)' :
                   rowShapeMask === 'star' ? 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)' :
                   rowShapeMask === 'arch' ? 'ellipse(50% 100% at 50% 100%)' : 'none',
          border: rowSingleSlide.shapeBorderWidth && rowShapeMask !== 'none'
            ? `${rowSingleSlide.shapeBorderWidth}px solid ${rowSingleSlide.shapeBorderColor || '#ffffff'}` : 'none',
          overflow: 'hidden',
          ...(rowSingleSlide.paddingSize ? {
            paddingTop: (rowSingleSlide.paddingSide === 'all' || rowSingleSlide.paddingSide === 'top' || !rowSingleSlide.paddingSide) ? `${rowSingleSlide.paddingSize}px` : '0',
            paddingRight: (rowSingleSlide.paddingSide === 'all' || rowSingleSlide.paddingSide === 'right' || !rowSingleSlide.paddingSide) ? `${rowSingleSlide.paddingSize}px` : '0',
            paddingBottom: (rowSingleSlide.paddingSide === 'all' || rowSingleSlide.paddingSide === 'bottom' || !rowSingleSlide.paddingSide) ? `${rowSingleSlide.paddingSize}px` : '0',
            paddingLeft: (rowSingleSlide.paddingSide === 'all' || rowSingleSlide.paddingSide === 'left' || !rowSingleSlide.paddingSide) ? `${rowSingleSlide.paddingSize}px` : '0',
          } : {}),
        };
        
        const renderRowImage = () => (
          <div style={{ textAlign: rowSingleSlide.imageAlign || 'center', minWidth: 0, maxWidth: '100%' }}>
            <div className="inline-block" style={{ ...rowImageContainerStyle, maxWidth: '100%' }}>
              <img 
                src={rowSingleSlide.imageUrl} 
                alt={rowSingleSlide.imageAlt || ''} 
                className="w-full h-full object-cover"
                style={{ borderRadius: getRowCornerRadius(), maxWidth: '100%' }}
              />
            </div>
          </div>
        );
        
        return wrapWithCustomCSS(
          <div key={`block-${index}`} className={`grid grid-cols-1 md:grid-cols-2 gap-8 mb-8 items-center overflow-hidden ${wrapperClass} ${animationClass}`} style={{ ...blockStyle, ...blockWidthStyle }}>
            {rowImageOnLeft && rowSingleSlide.imageUrl && renderRowImage()}
            <div className="space-y-4 min-w-0">
              <div
                dangerouslySetInnerHTML={{ __html: clampInlineFontSizes(processHtml(rowSingleSlide.textHtml || '<p>Text content</p>'), { tighter: true }) }}
                className="leading-relaxed image-text-slide-content"
                style={{
                  textAlign: rowSingleSlide.textAlign || 'left',
                  color: rowSingleSlide.textColor || '#475569',
                  fontSize: responsiveFontSize(rowSingleSlide.textSize || 18),
                  fontFamily: rowSingleSlide.fontFamily || 'inherit',
                  overflowWrap: 'anywhere',
                  wordBreak: 'break-word',
                  minWidth: 0,
                }}
              />
              <style>{slideContentTypographyCss(getThemeValue)}</style>
              {/* Button for single slide */}
              {rowSingleSlide.hasButton && (
                <div style={{ textAlign: rowSingleSlide.buttonAlign || 'left', marginTop: '1rem' }}>
                  <a
                    href={resolveUrl(rowSingleSlide.buttonUrl) || '#'}
                    download={isDownloadLink(rowSingleSlide.buttonUrl) ? getFilenameFromUrl(rowSingleSlide.buttonUrl) : undefined}
                    onClick={getPopupClickHandler(rowSingleSlide.buttonUrl)}
                    className={`inline-flex items-center justify-center gap-2 ${rowSingleSlide.buttonSize === 'sm' ? 'px-3 py-1 text-sm' : rowSingleSlide.buttonSize === 'lg' ? 'px-6 py-3 text-lg' : 'px-4 py-2'} ${rowSingleSlide.buttonFullWidth ? 'w-full' : ''} font-medium transition-all ${getButtonHoverClass(rowSingleSlide.buttonHover || 'fade')}`}
                    style={getFlatButtonVariantStyle(rowSingleSlide)}
                  >
                    {flatHasIcon(rowSingleSlide) && flatIconPos(rowSingleSlide) === 'left' && flatIcon(rowSingleSlide, rowSingleSlide.buttonSize === 'lg' ? 20 : 16)}
                    {rowSingleSlide.buttonText || 'Learn More'}
                    {flatHasIcon(rowSingleSlide) && flatIconPos(rowSingleSlide) === 'right' && flatIcon(rowSingleSlide, rowSingleSlide.buttonSize === 'lg' ? 20 : 16)}
                  </a>
                </div>
              )}
            </div>
            {!rowImageOnLeft && rowSingleSlide.imageUrl && renderRowImage()}
          </div>
        );

      case 'image-text-stack':
        const imageOnTop = content.imagePosition === 'top';
        const stackShapeMask = content.shapeMask || 'none';
        const stackShapeSize = content.shapeSize || 250;
        
        // Helper to render stack image with optional shape mask
        const renderStackImage = (position) => {
          const marginClass = position === 'top' ? 'mb-6' : 'mt-6';
          
          if (stackShapeMask !== 'none') {
            const stackShapeContainerStyle = {
              width: `${stackShapeSize}px`,
              height: stackShapeMask === 'ellipse' ? `${stackShapeSize * 1.25}px` : `${stackShapeSize}px`,
              maxWidth: '100%',
              borderRadius: stackShapeMask === 'circle' ? '50%' : 
                           stackShapeMask === 'ellipse' ? '50%' :
                           stackShapeMask === 'rounded' ? '20px' : '0',
              clipPath: stackShapeMask === 'diamond' ? 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)' :
                       stackShapeMask === 'hexagon' ? 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)' :
                       stackShapeMask === 'octagon' ? 'polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%)' :
                       stackShapeMask === 'star' ? 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)' :
                       stackShapeMask === 'arch' ? 'ellipse(50% 100% at 50% 100%)' : 'none',
              border: content.shapeBorderWidth ? `${content.shapeBorderWidth}px solid ${content.shapeBorderColor || '#ffffff'}` : 'none',
              overflow: 'hidden',
              ...(content.paddingSize ? {
                paddingTop: (content.paddingSide === 'all' || content.paddingSide === 'top' || !content.paddingSide) ? `${content.paddingSize}px` : '0',
                paddingRight: (content.paddingSide === 'all' || content.paddingSide === 'right' || !content.paddingSide) ? `${content.paddingSize}px` : '0',
                paddingBottom: (content.paddingSide === 'all' || content.paddingSide === 'bottom' || !content.paddingSide) ? `${content.paddingSize}px` : '0',
                paddingLeft: (content.paddingSide === 'all' || content.paddingSide === 'left' || !content.paddingSide) ? `${content.paddingSize}px` : '0',
              } : {}),
            };
            return (
              <div className={marginClass} style={{ textAlign: content.imageAlign || 'center' }}>
                <div className="inline-block" style={stackShapeContainerStyle}>
                  <img src={content.imageUrl} alt={content.imageAlt || ''} className="w-full h-full object-cover" />
                </div>
              </div>
            );
          } else {
            const stackImageStyle = {
              width: content.imageSizeMode === 'custom' ? `${content.imageWidth || 300}px` :
                     content.imageSizeMode === 'height' ? 'auto' : `${content.imageWidth || 300}px`,
              height: content.imageSizeMode === 'custom' ? `${content.imageHeight || 200}px` :
                      content.imageSizeMode === 'height' ? `${content.imageHeight || 200}px` : 'auto',
              maxWidth: '100%',
            };
            return (
              <div className={marginClass} style={{ textAlign: content.imageAlign || 'center' }}>
                <img src={content.imageUrl} alt={content.imageAlt || ''} className="rounded-lg inline-block" style={stackImageStyle} />
              </div>
            );
          }
        };
        
        return wrapWithCustomCSS(
          <div key={`block-${index}`} className={`mb-8 ${wrapperClass} ${animationClass}`} style={finalBlockStyle}>
            {imageOnTop && content.imageUrl && renderStackImage('top')}
            <div className="space-y-4">
              <div
                dangerouslySetInnerHTML={{ __html: clampInlineFontSizes(processHtml(content.textHtml || '<p>Text content</p>')) }}
                className="text-lg leading-relaxed text-slate-700 image-text-slide-content"
                style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
              />
              <style>{slideContentTypographyCss(getThemeValue)}</style>
            </div>
            {!imageOnTop && content.imageUrl && renderStackImage('bottom')}
          </div>
        );

      case 'form':
        return wrapWithCustomCSS(
          <FormPreviewBlock 
            key={`block-${index}`} 
            formId={content.formId}
            formName={content.formName}
            blockStyle={finalBlockStyle}
            wrapperClass={wrapperClass}
            animationClass={animationClass}
            customStyle={content.style || {}}
            enabledLanguages={enabledLanguages}
            defaultLanguage={defaultLanguage}
          />
        );

      case 'video': {
        const videoUrl = content.videoUrl || '';
        const videoWidth = content.width || 'large';
        const videoAspect = content.aspectRatio || '16:9';
        const videoAutoplay = content.autoplay || false;
        const videoMuted = content.muted || false;
        const videoLoop = content.loop || false;
        const videoCaption = content.caption || '';
        
        const widthClass = {
          full: 'w-full',
          large: 'max-w-4xl mx-auto',
          medium: 'max-w-2xl mx-auto',
          small: 'max-w-lg mx-auto',
        }[videoWidth] || 'max-w-4xl mx-auto';

        const aspectPadding = {
          '16:9': '56.25%',
          '4:3': '75%',
          '21:9': '42.85%',
          '1:1': '100%',
        }[videoAspect] || '56.25%';

        let videoEmbed = null;
        if (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be') || videoUrl.includes('youtube-nocookie.com')) {
          const ytId = getYouTubeVideoId(videoUrl);
          if (ytId) {
            const params = new URLSearchParams({
              rel: '0',
              modestbranding: '1',
              autoplay: videoAutoplay ? '1' : '0',
              mute: videoMuted ? '1' : '0',
              loop: videoLoop ? '1' : '0',
              controls: '1',
            });
            if (videoLoop) params.set('playlist', ytId);
            videoEmbed = (
              <iframe
                src={`https://www.youtube.com/embed/${ytId}?${params.toString()}`}
                className="absolute inset-0 w-full h-full"
                frameBorder="0"
                allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
                allowFullScreen
                title="Video"
              />
            );
          }
        } else if (videoUrl.includes('vimeo.com')) {
          const vimeoId = getVimeoVideoId(videoUrl);
          if (vimeoId) {
            videoEmbed = (
              <iframe
                src={`https://player.vimeo.com/video/${vimeoId}?autoplay=${videoAutoplay ? 1 : 0}&muted=${videoMuted ? 1 : 0}&loop=${videoLoop ? 1 : 0}`}
                className="absolute inset-0 w-full h-full"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title="Video"
              />
            );
          }
        } else if (videoUrl) {
          videoEmbed = (
            <video
              src={videoUrl}
              controls
              autoPlay={videoAutoplay}
              muted={videoMuted}
              loop={videoLoop}
              playsInline
              className="absolute inset-0 w-full h-full object-contain"
            />
          );
        }

        if (!videoUrl) {
          return wrapWithCustomCSS(
            <div key={block.id || index} className={`${widthClass} px-4`} style={finalBlockStyle}>
              <div className="border-2 border-dashed border-slate-300 rounded-xl p-12 text-center text-slate-400">
                No video URL provided
              </div>
            </div>
          );
        }

        return wrapWithCustomCSS(
          <div key={block.id || index} className={`${widthClass} px-4`} style={finalBlockStyle} data-testid="video-embed-block">
            <figure>
              <div className="relative rounded-xl overflow-hidden" style={{ paddingBottom: aspectPadding }}>
                {videoEmbed}
              </div>
              {videoCaption && (
                <figcaption className="text-xs text-slate-500 mt-2 text-center italic">
                  {videoCaption}
                </figcaption>
              )}
            </figure>
          </div>
        );
      }

      case 'gallery':
        const galleryImages = content.images || [];
        const galleryLayout = content.layout || 'grid-3';
        const galleryGap = content.gap || '16';
        const galleryRadius = content.imageRadius || '8';
        const galleryAspect = content.aspectRatio || 'auto';
        const enableLightbox = content.enableLightbox !== false;
        const imageAnimation = content.imageAnimation || 'none';
        const animSpeed = content.animationSpeed || '0.3';
        const hoverEffect = content.hoverEffect || 'zoom';
        const staggerAnim = content.staggerAnimation !== false;
        
        // Determine grid columns based on layout
        const getGalleryColumns = () => {
          switch (galleryLayout) {
            case 'grid-2': return 2;
            case 'grid-3': return 3;
            case 'grid-4': return 4;
            case 'masonry': return 3;
            case 'featured': return 4;
            default: return 3;
          }
        };
        
        // Get hover effect class
        const getHoverEffectClass = () => {
          switch (hoverEffect) {
            case 'zoom': return 'hover:scale-105';
            case 'brighten': return 'hover:brightness-110';
            case 'darken': return 'hover:brightness-75';
            case 'grayscale': return 'grayscale hover:grayscale-0';
            default: return '';
          }
        };
        
        // Get image animation style
        const getImageAnimStyle = (imgIdx) => {
          if (imageAnimation === 'none') return {};
          const delay = staggerAnim ? `${imgIdx * 0.1}s` : '0s';
          return {
            animation: `${imageAnimation} ${animSpeed}s ease-out ${delay} both`,
          };
        };
        
        // Constrained aspect ratio for 2 and 3 column grids
        const constrainedAspect = (galleryLayout === 'grid-2' || galleryLayout === 'grid-3') 
          ? (galleryAspect === 'auto' ? '4/3' : galleryAspect)
          : galleryAspect;
        
        if (galleryImages.length === 0) {
          return wrapWithCustomCSS(
            <div key={`block-${index}`} className={`mb-8 p-8 bg-slate-50 border-2 border-dashed border-slate-200 rounded-lg text-center ${wrapperClass}`} style={finalBlockStyle}>
              <p className="text-slate-400">No images in gallery</p>
            </div>
          );
        }
        
        // Carousel layout
        if (galleryLayout === 'carousel') {
          return wrapWithCustomCSS(
            <GalleryWithLightbox
              key={`block-${index}`}
              images={galleryImages}
              enableLightbox={enableLightbox}
              blockStyle={blockStyle}
              wrapperClass={wrapperClass}
              renderGallery={(onImageClick) => (
                <div className="overflow-x-auto pb-4">
                  <div className="flex gap-4" style={{ gap: `${galleryGap}px` }}>
                    {galleryImages.map((img, imgIdx) => (
                      <div 
                        key={img.id || imgIdx} 
                        className={`flex-shrink-0 w-80 cursor-pointer overflow-hidden transition-transform duration-300 ${getHoverEffectClass()}`}
                        style={{ borderRadius: `${galleryRadius}px`, ...getImageAnimStyle(imgIdx) }}
                        onClick={() => onImageClick(imgIdx)}
                      >
                        <img 
                          src={img.url} 
                          alt={img.alt || ''} 
                          className="w-full h-48 object-cover transition-all duration-300"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            />
          );
        }
        
        // Featured layout (interactive thumbnails)
        if (galleryLayout === 'featured' && galleryImages.length > 0) {
          const thumbnailPosition = content.thumbnailPosition || 'side';
          return wrapWithCustomCSS(
            <FeaturedGallery
              key={`block-${index}`}
              images={galleryImages}
              gap={galleryGap}
              radius={galleryRadius}
              thumbnailPosition={thumbnailPosition}
              blockStyle={blockStyle}
              wrapperClass={wrapperClass}
              animationClass=""
              enableLightbox={enableLightbox}
            />
          );
        }
        
        // Grid and Masonry layouts
        const gridCols = getGalleryColumns();
        const isMasonry = galleryLayout === 'masonry';
        
        return wrapWithCustomCSS(
          <GalleryWithLightbox
            key={`block-${index}`}
            images={galleryImages}
            enableLightbox={enableLightbox}
            blockStyle={blockStyle}
            wrapperClass={wrapperClass}
            renderGallery={(onImageClick) => (
              <div 
                className={isMasonry 
                  ? 'columns-1 sm:columns-2 lg:columns-3 gap-4' 
                  : `grid grid-cols-1 sm:grid-cols-2 ${
                      gridCols === 2 ? 'lg:grid-cols-2' :
                      gridCols === 3 ? 'lg:grid-cols-3' :
                      gridCols === 4 ? 'lg:grid-cols-4' :
                      'lg:grid-cols-3'
                    }`
                }
                style={isMasonry ? { columnGap: `${galleryGap}px` } : { 
                  gap: `${galleryGap}px`
                }}
              >
                {galleryImages.map((img, imgIdx) => (
                  <div 
                    key={img.id || imgIdx}
                    className={`${isMasonry ? 'mb-4 break-inside-avoid' : ''} cursor-pointer overflow-hidden transition-transform duration-300 ${getHoverEffectClass()}`}
                    style={{ 
                      borderRadius: `${galleryRadius}px`, 
                      overflow: 'hidden',
                      aspectRatio: constrainedAspect !== 'auto' && !isMasonry ? constrainedAspect : undefined,
                      ...getImageAnimStyle(imgIdx)
                    }}
                    onClick={() => onImageClick(imgIdx)}
                  >
                    <img 
                      src={img.url} 
                      alt={img.alt || ''} 
                      className={`w-full transition-all duration-300 ${constrainedAspect !== 'auto' && !isMasonry ? 'h-full object-cover' : ''}`}
                    />
                  </div>
                ))}
              </div>
            )}
          />
        );

      case 'ticker':
        const tickerItems = content.items || [];
        const tickerDirection = content.direction || 'left';
        const tickerSpeed = content.speed || 'medium';
        const tickerAnimStyle = content.animationStyle || 'continuous';
        const tickerPauseOnHover = content.pauseOnHover !== false;
        const tickerBg = content.transparentBg ? 'transparent' : (content.backgroundColor || '#f8fafc');
        const tickerSpacing = content.itemSpacing || 48;
        const tickerItemHeight = content.itemHeight || 48;
        const tickerPadding = content.verticalPadding || 16;
        const tickerFilter = content.logoFilter || 'none';
        
        // Calculate animation duration based on speed
        const getTickerDuration = () => {
          // Duration is based on how many items are in one group (half of total rendered)
          // This keeps the scroll speed consistent regardless of duplication count
          const groupSize = tickerAnimStyle === 'continuous' 
            ? Math.max(tickerItems.length, Math.ceil(2000 / (150 + tickerSpacing))) 
            : tickerItems.length;
          const baseSpeed = groupSize * 3; // 3 seconds per item for smooth speed
          switch (tickerSpeed) {
            case 'slow': return baseSpeed * 1.5;
            case 'fast': return baseSpeed * 0.5;
            default: return baseSpeed;
          }
        };
        
        // Step animation timing
        const getStepDuration = () => {
          switch (tickerSpeed) {
            case 'slow': return 4000;
            case 'fast': return 1500;
            default: return 2500;
          }
        };
        
        if (tickerItems.length === 0) {
          return wrapWithCustomCSS(
            <div key={`block-${index}`} className={`mb-8 p-8 bg-slate-50 border-2 border-dashed border-slate-200 rounded-lg text-center ${wrapperClass}`} style={finalBlockStyle}>
              <p className="text-slate-400">No items in ticker tape</p>
            </div>
          );
        }
        
        // CSS for ticker animation
        const tickerKeyframes = `
          @keyframes ticker-scroll-left {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          @keyframes ticker-scroll-right {
            0% { transform: translateX(-50%); }
            100% { transform: translateX(0); }
          }
        `;
        
        // Filter classes
        const getFilterClass = () => {
          switch (tickerFilter) {
            case 'grayscale': return 'grayscale';
            case 'grayscale-hover': return 'grayscale hover:grayscale-0 transition-all duration-300';
            default: return '';
          }
        };
        
        // Render a single ticker item
        const renderTickerItem = (item, idx) => {
          const itemContent = item.type === 'image' ? (
            item.imageUrl ? (
              <img 
                src={item.imageUrl} 
                alt={item.imageAlt || ''} 
                style={{ height: `${tickerItemHeight}px`, maxWidth: '150px', objectFit: 'contain' }}
                className={getFilterClass()}
              />
            ) : null
          ) : (
            <span className="text-slate-700 font-medium whitespace-nowrap text-lg">
              {item.text || ''}
            </span>
          );
          
          if (!itemContent) return null;
          
          return item.link ? (
            <a 
              key={idx} 
              href={item.link} 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex-shrink-0 hover:opacity-80 transition-opacity"
              style={{ marginRight: `${tickerSpacing}px` }}
            >
              {itemContent}
            </a>
          ) : (
            <div key={idx} className="flex-shrink-0" style={{ marginRight: `${tickerSpacing}px` }}>
              {itemContent}
            </div>
          );
        };
        
        // For continuous scroll: repeat items enough times so one "group" fills the viewport
        // then duplicate that group for seamless looping (translateX 0 to -50%)
        const estimatedItemWidth = 150 + tickerSpacing;
        const minItemsPerGroup = Math.max(tickerItems.length, Math.ceil(2000 / estimatedItemWidth));
        const repeatCount = Math.ceil(minItemsPerGroup / tickerItems.length);
        const oneGroup = [];
        for (let r = 0; r < repeatCount; r++) {
          oneGroup.push(...tickerItems);
        }
        // Two identical groups = seamless loop with translateX(-50%)
        const duplicatedItems = tickerAnimStyle === 'continuous' 
          ? [...oneGroup, ...oneGroup] 
          : tickerItems;
        
        return wrapWithCustomCSS(
          <div 
            key={`block-${index}`} 
            className={`mb-8 overflow-hidden ${wrapperClass} ${animationClass}`} 
            style={{ 
              ...blockStyle, 
              backgroundColor: tickerBg,
              padding: `${tickerPadding}px 0`,
            }}
          >
            <style>{tickerKeyframes}</style>
            {tickerAnimStyle === 'continuous' ? (
              <div 
                className="flex items-center"
                style={{ 
                  animation: `ticker-scroll-${tickerDirection} ${getTickerDuration()}s linear infinite`,
                  animationPlayState: 'running',
                  width: 'max-content',
                }}
                onMouseEnter={(e) => tickerPauseOnHover && (e.currentTarget.style.animationPlayState = 'paused')}
                onMouseLeave={(e) => tickerPauseOnHover && (e.currentTarget.style.animationPlayState = 'running')}
              >
                {duplicatedItems.map((item, idx) => renderTickerItem(item, idx))}
              </div>
            ) : (
              // Step-by-step animation using React state
              <TickerStepAnimation 
                items={tickerItems}
                direction={tickerDirection}
                stepDuration={getStepDuration()}
                pauseOnHover={tickerPauseOnHover}
                spacing={tickerSpacing}
                renderItem={renderTickerItem}
              />
            )}
          </div>
        );

      case 'article-feed':
        return wrapWithCustomCSS(
          <ArticleFeedPreview
            key={`block-${index}`}
            content={content}
            styles={safeStyles}
            tenantId={propTenantId}
            wrapperClass={wrapperClass}
            animationClass={animationClass}
            blockStyle={finalBlockStyle}
            currentLanguage={currentLanguage}
            defaultLanguage={defaultLanguage}
          />
        );

      case 'search':
        return (
          <SiteSearchBlock
            key={`block-${index}`}
            content={content}
            siteTheme={siteTheme}
            subdomain={subdomain}
            wrapperClass={wrapperClass}
            animationClass={animationClass}
            blockStyle={finalBlockStyle}
          />
        );

      case 'events-calendar':
        return wrapWithCustomCSS(
          <EventsCalendarBlock
            key={`block-${index}`}
            content={content}
            styles={safeStyles}
            tenantId={propTenantId}
            subdomain={subdomain}
            wrapperClass={wrapperClass}
            animationClass={animationClass}
            blockStyle={finalBlockStyle}
          />
        );

      case 'article-card':
        // Render an article card for newsletters / Insert Article block
        const articleIdentifier = content.articleSlug || content.articleId;
        let articleUrl;
        if (!articleIdentifier) {
          articleUrl = '#';
        } else {
          const currentHost = window.location.hostname;
          const isPreviewOrLocal = currentHost.includes('emergentagent.com') || currentHost.includes('localhost') || /^\d+\.\d+\.\d+\.\d+$/.test(currentHost);
          if (isPreviewOrLocal && subdomain) {
            articleUrl = `/site/${subdomain}/article/${articleIdentifier}`;
          } else if (subdomain) {
            articleUrl = `/article/${articleIdentifier}`;
          } else {
            articleUrl = '#';
          }
        }
        
        if (content.displayStyle === 'headline') {
          // Headline only style
          return (
            <div key={`block-${index}`} className={`${wrapperClass} ${animationClass}`} style={finalBlockStyle}>
              <a 
                href={articleUrl}
                className="text-lg font-semibold hover:underline"
                style={{ color: getThemeValue('heading_color') || '#1e293b' }}
              >
                {content.articleTitle || 'Article Title'}
                {content.showReadMore !== false && <span className="ml-2">→</span>}
              </a>
            </div>
          );
        }
        
        // Card style (default)
        return (
          <div key={`block-${index}`} className={`${wrapperClass} ${animationClass}`} style={finalBlockStyle}>
            <div className="bg-white rounded-lg overflow-hidden border border-slate-200 shadow-sm max-w-2xl mx-auto">
              {content.showImage !== false && content.articleImage && (
                <a href={articleUrl}>
                  <img 
                    src={content.articleImage} 
                    alt={content.articleTitle || ''} 
                    className="w-full h-48 object-cover hover:opacity-90 transition-opacity"
                  />
                </a>
              )}
              <div className="p-5">
                <a href={articleUrl}>
                  <h3 
                    className="font-bold text-xl hover:underline"
                    style={{ 
                      color: getThemeValue('heading_color') || '#1e293b',
                      fontFamily: getThemeValue('heading_font') || 'inherit'
                    }}
                  >
                    {content.articleTitle || 'Article Title'}
                  </h3>
                </a>
                {content.showDate !== false && content.articleDate && (
                  <p className="text-sm text-slate-500 mt-1">
                    {new Date(content.articleDate).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </p>
                )}
                {content.showExcerpt !== false && content.articleExcerpt && (
                  <p 
                    className="mt-3 line-clamp-3"
                    style={{ 
                      color: getThemeValue('body_color') || '#475569',
                      fontFamily: getThemeValue('body_font') || 'inherit'
                    }}
                  >
                    {content.articleExcerpt}
                  </p>
                )}
                {content.showReadMore !== false && (
                  <a 
                    href={articleUrl}
                    className="inline-block mt-4 font-medium hover:underline"
                    style={{ color: getThemeValue('accent_color') || '#4f46e5' }}
                  >
                    {content.readMoreText || 'Read Full Article'} →
                  </a>
                )}
              </div>
            </div>
          </div>
        );

      case 'testimonials': {
        const tItems = content.items || [];
        const ts = content.titleStyle || {};
        const accentColor = safeStyles.borderColor && safeStyles.borderColor !== 'transparent'
          ? safeStyles.borderColor
          : 'var(--site-accent)';
        return wrapWithCustomCSS(
          <section
            key={`block-${index}`}
            className={`py-8 px-4 md:py-16 md:px-6 ${wrapperClass} ${animationClass}`}
            style={finalBlockStyle}
          >
            <div className="max-w-6xl mx-auto">
              {content.title && (
                <h2
                  className="mb-10"
                  style={{
                    color: ts.color || 'var(--site-heading-color)',
                    fontFamily: ts.fontFamily || 'var(--site-heading-font)',
                    fontSize: responsiveFontSize(ts.fontSize || 36),
                    fontWeight: ts.fontWeight || 'bold',
                    textAlign: ts.align || 'center',
                    lineHeight: 1.2,
                  }}
                >
                  {content.title}
                </h2>
              )}
              {tItems.length === 0 ? (
                <div className="p-8 bg-slate-50 border-2 border-dashed border-slate-200 rounded-lg text-center">
                  <p className="text-slate-400">No testimonials yet</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {tItems.map((item) => (
                    <div
                      key={item.id}
                      className="p-6"
                      style={{
                        backgroundColor: 'var(--site-bg, #f8fafc)',
                        color: 'var(--site-body-color)',
                        border: '1px solid var(--site-border-color)',
                        borderRadius: 'var(--site-radius)',
                        boxShadow: 'var(--site-shadow)',
                      }}
                    >
                      <Quote size={32} className="mb-4 opacity-20" style={{ color: accentColor }} />
                      <p className="text-lg mb-4 italic">&ldquo;{item.quote}&rdquo;</p>
                      <div className="flex items-center gap-3">
                        {item.avatar && (
                          <img
                            src={item.avatar}
                            alt={item.author || ''}
                            className="w-12 h-12 rounded-full object-cover"
                          />
                        )}
                        <div>
                          {item.author && <p className="font-semibold" style={{ color: 'var(--site-heading-color)' }}>{item.author}</p>}
                          {item.role && <p className="text-sm opacity-60">{item.role}</p>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        );
      }

      case 'pricing': {
        const pItems = content.items || [];
        const ts = content.titleStyle || {};
        const accentColor = safeStyles.borderColor && safeStyles.borderColor !== 'transparent'
          ? safeStyles.borderColor
          : 'var(--site-accent)';
        const cardRadius = safeStyles.borderRadius || 'var(--site-radius)';
        // Respect an explicit columnCount when set, otherwise auto-fit up to 4.
        const requestedCols = parseInt(content.columnCount, 10);
        const maxCols = Number.isFinite(requestedCols) && requestedCols >= 1 && requestedCols <= 4
          ? requestedCols
          : 4;
        const cols = Math.min(pItems.length || 1, maxCols);
        const colsClass = cols === 1 ? 'md:grid-cols-1'
          : cols === 2 ? 'md:grid-cols-2'
          : cols === 3 ? 'md:grid-cols-3'
          : 'md:grid-cols-4';
        // Use accent with 10% alpha for highlighted bg (works whether accent is a hex or CSS var)
        const accentTint = (safeStyles.borderColor && safeStyles.borderColor.startsWith('#'))
          ? `${safeStyles.borderColor}1A`
          : 'color-mix(in srgb, var(--site-accent) 10%, transparent)';
        return wrapWithCustomCSS(
          <section
            key={`block-${index}`}
            className={`py-8 px-4 md:py-16 md:px-6 ${wrapperClass} ${animationClass}`}
            style={finalBlockStyle}
          >
            <div className="max-w-6xl mx-auto">
              {content.title && (
                <h2
                  className="mb-10"
                  style={{
                    color: ts.color || 'var(--site-heading-color)',
                    fontFamily: ts.fontFamily || 'var(--site-heading-font)',
                    fontSize: responsiveFontSize(ts.fontSize || 36),
                    fontWeight: ts.fontWeight || 'bold',
                    textAlign: ts.align || 'center',
                    lineHeight: 1.2,
                  }}
                >
                  {content.title}
                </h2>
              )}
              {pItems.length === 0 ? (
                <div className="p-8 bg-slate-50 border-2 border-dashed border-slate-200 rounded-lg text-center">
                  <p className="text-slate-400">No pricing plans yet</p>
                </div>
              ) : (
                <div className={`grid ${colsClass} gap-6`}>
                  {pItems.map((plan) => {
                    const planUrl = plan.buttonUrl ? resolveUrl(plan.buttonUrl) : '#';
                    const planClickHandler = plan.buttonUrl ? getPopupClickHandler(plan.buttonUrl) : null;
                    return (
                      <div
                        key={plan.id}
                        className={`p-6 flex flex-col ${plan.highlighted ? 'md:scale-105' : ''}`}
                        style={{
                          backgroundColor: plan.highlighted ? accentTint : 'var(--site-bg, #f8fafc)',
                          color: 'var(--site-body-color)',
                          border: `1px solid var(--site-border-color)`,
                          borderRadius: cardRadius,
                          boxShadow: 'var(--site-shadow)',
                          ...(plan.highlighted ? { boxShadow: `0 0 0 2px ${accentColor}, var(--site-shadow)` } : {}),
                        }}
                      >
                        {plan.highlighted && (
                          <div
                            className="text-xs font-semibold text-white px-3 py-1 rounded-full mb-4 inline-block self-start"
                            style={{ backgroundColor: accentColor }}
                          >
                            Most Popular
                          </div>
                        )}
                        <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--site-heading-color)' }}>{plan.name}</h3>
                        <div className="mb-4">
                          <span className="text-4xl font-bold" style={{ color: accentColor }}>{plan.price}</span>
                          <span className="opacity-60">{plan.period}</span>
                        </div>
                        <ul className="space-y-2 mb-6 flex-1">
                          {(plan.features || []).filter(f => f && f.trim()).map((feature, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <Check size={16} className="mt-1 flex-shrink-0" style={{ color: accentColor }} />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                        {plan.buttonText && (
                          <a
                            href={planUrl}
                            onClick={planClickHandler || undefined}
                            className={`w-full py-3 font-semibold text-white transition-all hover:opacity-90 text-center inline-flex items-center justify-center gap-2 ${flatHoverClass(content)}`}
                            style={{ backgroundColor: accentColor, borderRadius: content.buttonRadius ? getButtonRadius(content.buttonRadius) : 'var(--site-button-radius)' }}
                          >
                            {flatHasIcon(content) && flatIconPos(content) === 'left' && flatIcon(content, 18)}
                            {plan.buttonText}
                            {flatHasIcon(content) && flatIconPos(content) === 'right' && flatIcon(content, 18)}
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </section>
        );
      }

      case 'feature-comparison': {
        const ts = content.titleStyle || {};
        const plans = content.plans || [];
        const categories = content.categories || [];
        const accentColor = safeStyles.borderColor && safeStyles.borderColor !== 'transparent'
          ? safeStyles.borderColor
          : 'var(--site-accent)';
        const renderValue = (raw) => {
          const v = (raw == null ? '' : String(raw)).trim().toLowerCase();
          if (v === 'yes' || v === 'true' || v === '✓' || v === 'y') {
            return (
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full" style={{ backgroundColor: `${accentColor}1A`, color: accentColor }} aria-label="Included">
                <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="4 11 8 15 16 6"/></svg>
              </span>
            );
          }
          if (v === 'no' || v === 'false' || v === '✗' || v === 'x' || v === 'n' || v === '-' || v === '') {
            return (
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-slate-100 text-slate-400" aria-label="Not included">
                <svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="5" y1="5" x2="15" y2="15"/><line x1="15" y1="5" x2="5" y2="15"/></svg>
              </span>
            );
          }
          return <span className="text-sm" style={{ color: 'var(--site-body-color)' }}>{raw}</span>;
        };
        return wrapWithCustomCSS(
          <FeatureComparisonSection
            block={block}
            content={content}
            plans={plans}
            categories={categories}
            accentColor={accentColor}
            ts={ts}
            renderValue={renderValue}
            finalBlockStyle={finalBlockStyle}
            wrapperClass={wrapperClass}
            animationClass={animationClass}
            index={index}
          />,
          block
        );
      }

      case 'plan-comparison': {
        const ts = content.titleStyle || {};
        const accentColor = safeStyles.borderColor && safeStyles.borderColor !== 'transparent'
          ? safeStyles.borderColor
          : 'var(--site-accent)';
        let pcPlans;
        let pcRows;
        if (content.source === 'manual') {
          pcPlans = content.plans || [];
          pcRows = content.rows || [];
        } else {
          const pricingBlock = (blocks || []).find((b) => b.type === 'pricing' && (b.content?.items || []).length > 0);
          const derived = derivePlanComparison(pricingBlock?.content?.items || []);
          pcPlans = derived.plans;
          pcRows = derived.rows;
        }
        return wrapWithCustomCSS(
          <PlanComparisonSection
            block={block}
            content={content}
            plans={pcPlans}
            rows={pcRows}
            accentColor={accentColor}
            ts={ts}
            finalBlockStyle={finalBlockStyle}
            wrapperClass={wrapperClass}
            animationClass={animationClass}
            index={index}
            resolveUrl={resolveUrl}
            getPopupClickHandler={getPopupClickHandler}
          />,
          block
        );
      }


      case 'faq': {
        const ts = content.titleStyle || {};
        const accentColor = safeStyles.borderColor && safeStyles.borderColor !== 'transparent'
          ? safeStyles.borderColor
          : 'var(--site-accent)';
        return wrapWithCustomCSS(
          <section
            key={`block-${index}`}
            className={`py-8 px-4 md:py-16 md:px-6 ${wrapperClass} ${animationClass}`}
            style={finalBlockStyle}
          >
            <div className="max-w-3xl mx-auto">
              {content.title && (
                <h2
                  className="mb-10"
                  style={{
                    color: ts.color || 'var(--site-heading-color)',
                    fontFamily: ts.fontFamily || 'var(--site-heading-font)',
                    fontSize: responsiveFontSize(ts.fontSize || 36),
                    fontWeight: ts.fontWeight || 'bold',
                    textAlign: ts.align || 'center',
                    lineHeight: 1.2,
                  }}
                >
                  {content.title}
                </h2>
              )}
              <FAQAccordion
                items={content.items || []}
                accentColor={accentColor}
                cardBg={'var(--site-bg, #ffffff)'}
                bodyColor={'var(--site-body-color)'}
              />
            </div>
          </section>
        );
      }

      case 'cta': {
        const ts = content.titleStyle || {};
        // Use safeStyles.backgroundColor if user set one, otherwise the style scheme
        const userBg = safeStyles.backgroundColor && safeStyles.backgroundColor !== 'transparent';
        const ctaSchemes = {
          primary: { bg: 'var(--site-primary)', fg: '#ffffff' },
          secondary: { bg: 'var(--site-secondary)', fg: '#ffffff' },
          dark: { bg: '#1e293b', fg: '#ffffff' },
        };
        const scheme = ctaSchemes[content.style] || ctaSchemes.primary;
        const sectionBg = userBg ? safeStyles.backgroundColor : scheme.bg;
        const fg = userBg ? (ts.color || '#ffffff') : scheme.fg;
        const btnUrl = content.buttonUrl ? resolveUrl(content.buttonUrl) : '#';
        // openInNewTab overrides popup — user explicitly wants a new tab
        const btnClick = content.buttonOpenInNewTab ? null : (content.buttonUrl ? getPopupClickHandler(content.buttonUrl) : null);
        // openInNewTab overrides popup — user explicitly wants a new tab
        const btnOpenNewTab = content.buttonOpenInNewTab;
        const effectiveBtnClick = btnOpenNewTab ? null : btnClick;
        // Button uses inverse colors so it pops against the section bg
        const btnBg = fg;
        const btnFg = sectionBg;
        return wrapWithCustomCSS(
          <section
            key={`block-${index}`}
            className={`py-10 px-4 md:py-20 md:px-6 ${wrapperClass} ${animationClass}`}
            style={{ ...finalBlockStyle, backgroundColor: sectionBg, color: fg }}
          >
            <div className="max-w-3xl mx-auto text-center pb-8">
              {content.title && (
                <h2
                  className="mb-4"
                  style={{
                    color: ts.color || fg,
                    fontFamily: ts.fontFamily || 'var(--site-heading-font)',
                    fontSize: responsiveFontSize(ts.fontSize || 36),
                    fontWeight: ts.fontWeight || 'bold',
                    textAlign: ts.align || 'center',
                    lineHeight: 1.2,
                  }}
                >
                  {content.title}
                </h2>
              )}
              {content.subtitle && (
                <p className="text-lg opacity-90 mb-8">{content.subtitle}</p>
              )}
              {content.buttonText && (
                <a
                  href={btnUrl}
                  onClick={btnClick || undefined}
                  target={btnOpenNewTab ? '_blank' : '_self'}
                  rel={btnOpenNewTab ? 'noopener noreferrer' : undefined}
                  className={`inline-flex items-center justify-center gap-2 px-8 py-4 font-semibold transition-all hover:scale-105 ${flatHoverClass(content)}`}
                  style={{ backgroundColor: btnBg, color: btnFg, borderRadius: content.buttonRadius ? getButtonRadius(content.buttonRadius) : 'var(--site-button-radius)' }}
                >
                  {flatHasIcon(content) && flatIconPos(content) === 'left' && flatIcon(content, 18)}
                  {content.buttonText}
                  {flatHasIcon(content) && flatIconPos(content) === 'right' && flatIcon(content, 18)}
                </a>
              )}
            </div>
          </section>
        );
      }

      case 'team': {
        const tMembers = content.members || [];
        const ts = content.titleStyle || {};
        const accentColor = safeStyles.borderColor && safeStyles.borderColor !== 'transparent'
          ? safeStyles.borderColor
          : 'var(--site-accent)';
        const cols = Math.min(tMembers.length || 1, 4);
        const colsClass = cols === 1 ? 'lg:grid-cols-1' : cols === 2 ? 'lg:grid-cols-2' : cols === 3 ? 'lg:grid-cols-3' : 'lg:grid-cols-4';
        return wrapWithCustomCSS(
          <section
            key={`block-${index}`}
            className={`py-8 px-4 md:py-16 md:px-6 ${wrapperClass} ${animationClass}`}
            style={finalBlockStyle}
          >
            <div className="max-w-6xl mx-auto">
              {content.title && (
                <h2
                  className="mb-10"
                  style={{
                    color: ts.color || 'var(--site-heading-color)',
                    fontFamily: ts.fontFamily || 'var(--site-heading-font)',
                    fontSize: responsiveFontSize(ts.fontSize || 36),
                    fontWeight: ts.fontWeight || 'bold',
                    textAlign: ts.align || 'center',
                    lineHeight: 1.2,
                  }}
                >
                  {content.title}
                </h2>
              )}
              {tMembers.length === 0 ? (
                <div className="p-8 bg-slate-50 border-2 border-dashed border-slate-200 rounded-lg text-center">
                  <p className="text-slate-400">No team members yet</p>
                </div>
              ) : (
                <div className={`grid md:grid-cols-2 ${colsClass} gap-6`}>
                  {tMembers.map((member) => (
                    <div
                      key={member.id}
                      className="text-center p-6"
                      style={{
                        backgroundColor: 'var(--site-bg, #ffffff)',
                        color: 'var(--site-body-color)',
                        border: '1px solid var(--site-border-color)',
                        borderRadius: 'var(--site-radius)',
                        boxShadow: 'var(--site-shadow)',
                      }}
                    >
                      {member.image ? (
                        <img
                          src={member.image}
                          alt={member.name || ''}
                          className="w-24 h-24 rounded-full mx-auto mb-4 object-cover shadow-md"
                        />
                      ) : (
                        <div
                          className="w-24 h-24 rounded-full mx-auto mb-4 flex items-center justify-center text-2xl font-bold text-white"
                          style={{ backgroundColor: accentColor }}
                        >
                          {member.name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                      )}
                      {member.name && (
                        <h3 className="font-semibold text-lg" style={{ color: 'var(--site-heading-color)' }}>
                          {member.name}
                        </h3>
                      )}
                      {member.role && <p className="opacity-60">{member.role}</p>}
                      {member.bio && <p className="mt-2 text-sm opacity-80">{member.bio}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        );
      }

      case 'stats': {
        const sItems = content.items || [];
        const ts = content.titleStyle || {};
        const accentColor = safeStyles.borderColor && safeStyles.borderColor !== 'transparent'
          ? safeStyles.borderColor
          : 'var(--site-accent)';
        // Stats default has a primary-color background. If user set their own bg, use it.
        const userBg = safeStyles.backgroundColor && safeStyles.backgroundColor !== 'transparent';
        const sectionBg = userBg ? safeStyles.backgroundColor : accentColor;
        const fg = userBg ? 'var(--site-body-color)' : '#ffffff';
        const cols = Math.min(sItems.length || 1, 4);
        const colsClass = cols === 1 ? 'md:grid-cols-1' : cols === 2 ? 'md:grid-cols-2' : cols === 3 ? 'md:grid-cols-3' : 'md:grid-cols-4';
        return wrapWithCustomCSS(
          <section
            key={`block-${index}`}
            className={`py-8 px-4 md:py-16 md:px-6 ${wrapperClass} ${animationClass}`}
            style={{ ...finalBlockStyle, backgroundColor: sectionBg, color: fg }}
          >
            <div className="max-w-6xl mx-auto">
              {content.title && (
                <h2
                  className="mb-10"
                  style={{
                    color: ts.color || fg,
                    fontFamily: ts.fontFamily || 'var(--site-heading-font)',
                    fontSize: responsiveFontSize(ts.fontSize || 36),
                    fontWeight: ts.fontWeight || 'bold',
                    textAlign: ts.align || 'center',
                    lineHeight: 1.2,
                  }}
                >
                  {content.title}
                </h2>
              )}
              {sItems.length === 0 ? (
                <div className="p-8 bg-white/10 border-2 border-dashed border-white/30 rounded-lg text-center">
                  <p className="opacity-70">No statistics yet</p>
                </div>
              ) : (
                <div className={`grid grid-cols-2 ${colsClass} gap-8`} style={{ fontFamily: content.statFontFamily && content.statFontFamily !== 'inherit' ? content.statFontFamily : undefined }}>
                  {sItems.map((stat) => (
                    <div key={stat.id} className="text-center">
                      <div className="text-4xl md:text-5xl font-bold mb-2" style={{ color: content.statValueColor || fg }}>{stat.value}</div>
                      <div style={{ color: content.statLabelColor || fg, opacity: content.statLabelColor ? 1 : 0.8 }}>{stat.label}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        );
      }

      case 'logo-cloud': {
        const lcLogos = (content.logos || []).filter(l => l && (l.imageUrl || l.name));
        const lts = content.titleStyle || {};
        const lcCols = content.columns || 5;
        const lcColMap = { 3: 'md:grid-cols-3', 4: 'md:grid-cols-4', 5: 'md:grid-cols-5', 6: 'md:grid-cols-6' };
        const lcColsClass = lcColMap[lcCols] || 'md:grid-cols-5';
        const lcId = `lc-${index}`;
        const lcHref = (u) => !u ? '#' : (/^(https?:|\/|#|mailto:|tel:)/i.test(u) ? u : `https://${u}`);
        return wrapWithCustomCSS(
          <section key={`block-${index}`} className={`py-8 px-4 md:py-16 md:px-6 ${wrapperClass} ${animationClass}`} style={finalBlockStyle}>
            <style>{`
              .${lcId} img { transition: filter .3s ease, opacity .3s ease; }
              ${content.grayscale !== false ? `.${lcId} img { filter: grayscale(100%); opacity: .6; } .${lcId} a:hover img, .${lcId} .lc-item:hover img { filter: grayscale(0); opacity: 1; }` : ''}
            `}</style>
            <div className="max-w-6xl mx-auto">
              {content.title && (
                <h2 className="mb-10" style={{ color: lts.color || 'var(--site-heading-color)', fontFamily: lts.fontFamily || 'var(--site-heading-font)', fontSize: responsiveFontSize(lts.fontSize || 30), fontWeight: lts.fontWeight || 'bold', textAlign: lts.align || 'center', lineHeight: 1.2 }}>
                  {content.title}
                </h2>
              )}
              {lcLogos.length === 0 ? (
                <div className="p-8 bg-slate-50 border-2 border-dashed border-slate-200 rounded-lg text-center"><p className="text-slate-400">No logos yet — add client / partner logos</p></div>
              ) : (
                <div className={`${lcId} grid grid-cols-2 ${lcColsClass} gap-x-8 gap-y-10 items-center`}>
                  {lcLogos.map((logo, i) => {
                    const inner = logo.imageUrl
                      ? <div className="flex flex-col items-center gap-1.5"><img src={logo.imageUrl} alt={logo.name || ''} className="max-h-12 w-auto object-contain" />{logo.name && <span className="text-xs font-medium opacity-60" style={{ color: 'var(--site-body-color)' }}>{logo.name}</span>}</div>
                      : <span className="text-lg font-semibold opacity-60" style={{ color: 'var(--site-body-color)' }}>{logo.name}</span>;
                    return logo.link
                      ? <a key={logo.id || i} href={lcHref(logo.link)} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center">{inner}</a>
                      : <div key={logo.id || i} className="lc-item flex items-center justify-center">{inner}</div>;
                  })}
                </div>
              )}
            </div>
          </section>
        );
      }

      case 'hero-spotlight': {
        const ts = content.titleStyle || {};
        const hsAccent = safeStyles.borderColor && safeStyles.borderColor !== 'transparent' ? safeStyles.borderColor : 'var(--site-accent)';
        const hsPrimary = content.primaryButtonColor || hsAccent;
        const hsSecondary = content.secondaryButtonColor || '#ffffff';
        const hsUserBg = safeStyles.backgroundColor && safeStyles.backgroundColor !== 'transparent';
        const hsBaseBg = hsUserBg ? safeStyles.backgroundColor : '#0b1020';
        const hsId = `hs-${index}`;
        const hsMinH = content.height || 560;
        const hsPos = heroPositionClasses(content);
        const hsDefaultText = content.textColor || '#ffffff';
        const hsHeadStyle = heroHeadlineStyle(content, hsDefaultText);
        const hsSubStyle = heroSubheadlineStyle(content, `${hsDefaultText}b8`); // subheadline default ~72% opacity
        const hsHref = (u) => !u ? '#' : (/^(https?:|\/|#|mailto:|tel:)/i.test(u) ? u : `https://${u}`);
        // Overlay style if user set one on this hero
        const hsOverlay = (() => {
          const o = content.overlay;
          switch (o) {
            case 'dark': return 'rgba(0,0,0,0.5)';
            case 'darker': return 'rgba(0,0,0,0.7)';
            case 'light': return 'rgba(255,255,255,0.5)';
            case 'gradient-dark': return 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)';
            case 'gradient-primary': return 'linear-gradient(135deg, rgba(99,102,241,0.8), rgba(168,85,247,0.8))';
            case 'gradient-warm': return 'linear-gradient(135deg, rgba(251,146,60,0.8), rgba(251,113,133,0.8))';
            case 'gradient-cool': return 'linear-gradient(135deg, rgba(56,189,248,0.8), rgba(99,102,241,0.8))';
            default: return null;
          }
        })();
        return wrapWithCustomCSS(
          <section
            key={`block-${index}`}
            className={`${hsId} relative overflow-hidden ${wrapperClass} ${animationClass}`}
            style={{ ...finalBlockStyle, ...heroWidthStyle(content.width), backgroundColor: hsBaseBg, minHeight: `${hsMinH}px` }}
          >
            <style>{`
              @keyframes hsUp-${hsId} { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
              @keyframes hsGlow-${hsId} { 0%, 100% { opacity: .5; } 50% { opacity: .9; } }
              .${hsId}::before { content: ''; position: absolute; top: -25%; left: 50%; width: 900px; height: 900px; transform: translateX(-50%); background: radial-gradient(circle, ${hsAccent}40 0%, transparent 60%); animation: hsGlow-${hsId} 6s ease-in-out infinite; pointer-events: none; }
              .${hsId} .hs-grid { position: absolute; inset: 0; background-image: linear-gradient(${hsAccent}14 1px, transparent 1px), linear-gradient(90deg, ${hsAccent}14 1px, transparent 1px); background-size: 44px 44px; -webkit-mask-image: radial-gradient(circle at 50% 30%, #000 0%, transparent 75%); mask-image: radial-gradient(circle at 50% 30%, #000 0%, transparent 75%); pointer-events: none; }
              .${hsId} .hs-anim { opacity: 0; animation: hsUp-${hsId} .7s cubic-bezier(.16,1,.3,1) forwards; }
            `}</style>
            <div className="hs-grid" />
            {hsOverlay && (
              <div className="absolute inset-0 pointer-events-none" style={{ background: hsOverlay }} />
            )}
            <div
              className={`relative max-w-4xl mx-auto px-6 flex flex-col ${hsPos.items} ${hsPos.text} ${hsPos.justify}`}
              style={{ minHeight: `${hsMinH}px`, paddingTop: '64px', paddingBottom: '64px' }}
            >
              {content.eyebrow && (
                <span className="hs-anim inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-6" style={{ animationDelay: '0ms', color: '#fff', backgroundColor: hsAccent }}>
                  {content.eyebrow}
                </span>
              )}
              {content.headline && (
                <h1 className="hs-anim font-extrabold tracking-tight w-full max-w-3xl" style={{ animationDelay: '120ms', color: hsHeadStyle.color || ts.color || hsDefaultText, fontSize: hsHeadStyle.fontSize || responsiveFontSize(56), lineHeight: 1.1, fontFamily: hsHeadStyle.fontFamily || (ts.fontFamily && ts.fontFamily !== 'inherit' ? ts.fontFamily : 'var(--site-heading-font)'), fontWeight: hsHeadStyle.fontWeight || ts.fontWeight || undefined, textAlign: ts.align || undefined }}>
                  {content.headline}
                </h1>
              )}
              {content.subheadline && (
                <p className="hs-anim mt-5 w-full max-w-2xl" style={{ animationDelay: '260ms', color: hsSubStyle.color || `${hsDefaultText}b8`, fontSize: hsSubStyle.fontSize || responsiveFontSize(18), lineHeight: 1.6, fontFamily: hsSubStyle.fontFamily || undefined, fontWeight: hsSubStyle.fontWeight || undefined }}>
                  {content.subheadline}
                </p>
              )}
              {(content.primaryButtonText || content.secondaryButtonText) && (
                <div className="hs-anim flex flex-wrap gap-3 mt-8" style={{ animationDelay: '400ms', justifyContent: hsPos.alignFlex }}>
                  {content.primaryButtonText && (
                    <a href={hsHref(content.primaryButtonUrl)} className="px-6 py-3 rounded-lg font-semibold transition-transform hover:-translate-y-0.5" style={{ backgroundColor: hsPrimary, color: content.primaryButtonTextColor || '#fff', boxShadow: `0 10px 30px -10px ${hsPrimary}` }}>
                      {content.primaryButtonText}
                    </a>
                  )}
                  {content.secondaryButtonText && (
                    <a href={hsHref(content.secondaryButtonUrl)} className="px-6 py-3 rounded-lg font-semibold transition-colors hover:bg-white/10" style={{ color: hsSecondary, border: `1px solid ${hsSecondary}40` }}>
                      {content.secondaryButtonText}
                    </a>
                  )}
                </div>
              )}
            </div>
          </section>
        );
      }

      case 'countdown': {
        const cdAccent = safeStyles.borderColor && safeStyles.borderColor !== 'transparent' ? safeStyles.borderColor : 'var(--site-accent)';
        return wrapWithCustomCSS(
          <CountdownBlock key={`block-${index}`} content={content} accentColor={cdAccent} headStyle={heroHead(content.titleStyle || {})} finalBlockStyle={finalBlockStyle} wrapperClass={wrapperClass} animationClass={animationClass} />
        );
      }

      case 'before-after': {
        return wrapWithCustomCSS(
          <BeforeAfterSlider key={`block-${index}`} content={content} finalBlockStyle={finalBlockStyle} wrapperClass={wrapperClass} animationClass={animationClass} />
        );
      }

      case 'hero-tabs': {
        const ts = content.titleStyle || {};
        const accent = safeStyles.borderColor && safeStyles.borderColor !== 'transparent' ? safeStyles.borderColor : 'var(--site-accent)';
        const subColor = content.subtitleColor || 'var(--site-body-color)';
        return wrapWithCustomCSS(
          <HeroTabs key={`block-${index}`} content={content} accent={accent} headStyle={heroHead(ts)} subColor={subColor} finalBlockStyle={finalBlockStyle} wrapperClass={wrapperClass} animationClass={animationClass} />
        );
      }

      case 'hero-feature-cards': {
        const ts = content.titleStyle || {};
        const accent = safeStyles.borderColor && safeStyles.borderColor !== 'transparent' ? safeStyles.borderColor : 'var(--site-accent)';
        const subColor = content.subtitleColor || 'var(--site-body-color)';
        const feats = (content.features || []).filter((f) => f);
        const accentColor = content.accentColor || accent;
        const cardText = content.cardTextColor || '#ffffff';
        return wrapWithCustomCSS(
          <section key={`block-${index}`} className={`py-10 px-4 md:py-20 md:px-6 ${wrapperClass} ${animationClass}`} style={{ ...finalBlockStyle, ...heroLayoutStyle(content) }}>
            <div className="max-w-6xl mx-auto">
              {content.headline && (
                <h1 className="max-w-3xl" style={{ fontSize: responsiveFontSize(52), lineHeight: 1.1, ...heroHead(ts), ...heroHeadlineStyle(content) }}>
                  {content.headline}{content.accentWord && <span style={{ color: accentColor }}> {content.accentWord}</span>}
                </h1>
              )}
              {content.subtitle && <p className="mt-5 max-w-2xl" style={{ color: subColor, fontSize: responsiveFontSize(18), lineHeight: 1.6, ...heroSubheadlineStyle(content) }}>{content.subtitle}</p>}
              {content.ctaText && <a href={heroHref(content.ctaUrl)} target={content.ctaOpenInNewTab ? '_blank' : '_self'} rel={content.ctaOpenInNewTab ? 'noopener noreferrer' : undefined} className={`inline-flex items-center justify-center gap-2 mt-7 px-7 py-3 font-semibold transition-all hover:-translate-y-0.5 ${flatHoverClass(content)}`} style={{ backgroundColor: accent, color: content.buttonTextColor || '#fff', borderRadius: getButtonRadius(content.buttonRadius) }}>{flatHasIcon(content) && flatIconPos(content) === 'left' && flatIcon(content, 18)}{content.ctaText}{flatHasIcon(content) && flatIconPos(content) === 'right' && flatIcon(content, 18)}</a>}
              {feats.length > 0 && (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
                  {feats.map((f, i) => (
                    <a key={f.id || i} href={heroHref(f.link)} className="group relative block rounded-2xl overflow-hidden h-56 transition-transform hover:-translate-y-1" style={{ boxShadow: `0 16px 50px -20px ${accent}66` }}>
                      {f.imageUrl ? <img src={f.imageUrl} alt={f.title || ''} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" /> : <div className="absolute inset-0 bg-slate-200" />}
                      <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,.7), transparent 60%)' }} />
                      <span className="absolute bottom-4 left-4 right-4 font-semibold" style={{ color: cardText, fontSize: responsiveFontSize(18), ...(f.titleStyle || {}) }}>{f.title}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>
          </section>
        );
      }

      case 'hero-split-stats': {
        const ts = content.titleStyle || {};
        const accent = safeStyles.borderColor && safeStyles.borderColor !== 'transparent' ? safeStyles.borderColor : 'var(--site-accent)';
        const subColor = content.subtitleColor || 'var(--site-body-color)';
        const imgs = (content.images || []).filter((im) => im && im.imageUrl).map((im) => im.imageUrl);
        const stats = (content.stats || []).filter((s) => s);
        const statColor = content.statColor || accent;
        return wrapWithCustomCSS(
          <section key={`block-${index}`} className={`py-10 px-4 md:py-20 md:px-6 ${wrapperClass} ${animationClass}`} style={{ ...finalBlockStyle, ...heroLayoutStyle(content) }}>
            <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
              <div>
                {content.headline && <h1 style={{ fontSize: responsiveFontSize(50), lineHeight: 1.1, ...heroHead(ts), ...heroHeadlineStyle(content) }}>{content.headline}</h1>}
                {content.subtitle && <p className="mt-5 max-w-xl" style={{ color: subColor, fontSize: responsiveFontSize(18), lineHeight: 1.6, ...heroSubheadlineStyle(content) }}>{content.subtitle}</p>}
                <div className="flex flex-wrap gap-3 mt-7">
                  {content.primaryButtonText && <a href={heroHref(content.primaryButtonUrl)} target={content.primaryButtonOpenInNewTab ? '_blank' : '_self'} rel={content.primaryButtonOpenInNewTab ? 'noopener noreferrer' : undefined} className="px-6 py-3 rounded-lg font-semibold transition-transform hover:-translate-y-0.5" style={{ backgroundColor: accent, color: content.buttonTextColor || '#fff' }}>{content.primaryButtonText}</a>}
                  {content.secondaryButtonText && <a href={heroHref(content.secondaryButtonUrl)} target={content.secondaryButtonOpenInNewTab ? '_blank' : '_self'} rel={content.secondaryButtonOpenInNewTab ? 'noopener noreferrer' : undefined} className="px-6 py-3 rounded-lg font-semibold" style={{ color: content.secondaryButtonTextColor || 'var(--site-heading-color)', border: `1px solid ${accent}55` }}>{content.secondaryButtonText}</a>}
                </div>
                {stats.length > 0 && (
                  <div className="flex flex-wrap gap-8 mt-10">
                    {stats.map((s, i) => (
                      <div key={i}>
                        <div className="font-extrabold" style={{ color: statColor, fontSize: responsiveFontSize(30), ...(s.valueStyle || {}) }}>{s.value}</div>
                        <div className="text-sm" style={{ color: subColor, ...(s.labelStyle || {}) }}>{s.label}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                {imgs.length === 0 ? (
                  isQuickPreview ? <div className="col-span-2 h-64 rounded-2xl bg-slate-100 border-2 border-dashed border-slate-200 flex items-center justify-center text-slate-400">Add up to 3 images</div> : null
                ) : (
                  <>
                    {imgs[0] && <img src={imgs[0]} alt="" className="rounded-2xl object-cover w-full h-64 row-span-2" style={{ height: '100%' }} />}
                    {imgs[1] && <img src={imgs[1]} alt="" className="rounded-2xl object-cover w-full h-[120px]" />}
                    {imgs[2] && <img src={imgs[2]} alt="" className="rounded-2xl object-cover w-full h-[120px]" />}
                  </>
                )}
              </div>
            </div>
          </section>
        );
      }

      case 'hero-split-cards': {
        const ts = content.titleStyle || {};
        const accent = safeStyles.borderColor && safeStyles.borderColor !== 'transparent' ? safeStyles.borderColor : 'var(--site-accent)';
        const subColor = content.subtitleColor || 'var(--site-body-color)';
        const hscId = `hsc-${index}`;
        const hscAccentWord = content.accentColor || accent;
        return wrapWithCustomCSS(
          <section key={`block-${index}`} className={`${hscId} relative overflow-hidden py-10 px-4 md:py-20 md:px-6 ${wrapperClass} ${animationClass}`} style={{ ...finalBlockStyle, ...heroLayoutStyle(content) }}>
            <style>{`.${hscId} .hsc-grid{position:absolute;inset:0;background-image:linear-gradient(${accent}12 1px,transparent 1px),linear-gradient(90deg,${accent}12 1px,transparent 1px);background-size:40px 40px;-webkit-mask-image:radial-gradient(circle at 30% 40%,#000,transparent 80%);mask-image:radial-gradient(circle at 30% 40%,#000,transparent 80%);pointer-events:none;}`}</style>
            <div className="hsc-grid" />
            <div className="relative max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
              <div>
                {content.headline && (
                  <h1 style={{ fontSize: responsiveFontSize(50), lineHeight: 1.1, ...heroHead(ts), ...heroHeadlineStyle(content) }}>
                    {content.headline}{content.accentWord && <span style={{ color: hscAccentWord }}> {content.accentWord}</span>}
                  </h1>
                )}
                {content.subtitle && <p className="mt-5 max-w-xl" style={{ color: subColor, fontSize: responsiveFontSize(18), lineHeight: 1.6, ...heroSubheadlineStyle(content) }}>{content.subtitle}</p>}
                {content.buttonText && <a href={heroHref(content.buttonUrl)} target={content.buttonOpenInNewTab ? '_blank' : '_self'} rel={content.buttonOpenInNewTab ? 'noopener noreferrer' : undefined} className={`inline-flex items-center justify-center gap-2 mt-7 px-7 py-3 font-semibold transition-all hover:-translate-y-0.5 ${flatHoverClass(content)}`} style={{ backgroundColor: accent, color: content.buttonTextColor || '#fff', borderRadius: getButtonRadius(content.buttonRadius) }}>{flatHasIcon(content) && flatIconPos(content) === 'left' && flatIcon(content, 18)}{content.buttonText}{flatHasIcon(content) && flatIconPos(content) === 'right' && flatIcon(content, 18)}</a>}
              </div>
              <div className="relative h-[360px]">
                {content.imageUrl2 && <img src={content.imageUrl2} alt="" className="absolute rounded-2xl object-cover shadow-xl" style={{ width: '62%', height: '70%', right: 0, top: 0, transform: 'rotate(4deg)' }} />}
                {content.imageUrl1 && <img src={content.imageUrl1} alt="" className="absolute rounded-2xl object-cover shadow-2xl" style={{ width: '62%', height: '70%', left: 0, bottom: 0, transform: 'rotate(-4deg)', border: `4px solid ${accent}` }} />}
                {!content.imageUrl1 && !content.imageUrl2 && isQuickPreview && <div className="absolute inset-0 rounded-2xl bg-slate-100 border-2 border-dashed border-slate-200 flex items-center justify-center text-slate-400">Add two layered images</div>}
              </div>
            </div>
          </section>
        );
      }

      case 'expandable-tabs': {
        const ts = content.titleStyle || {};
        const accent = safeStyles.borderColor && safeStyles.borderColor !== 'transparent' ? safeStyles.borderColor : 'var(--site-accent)';
        const subColor = content.subtitleColor || 'var(--site-body-color)';
        return wrapWithCustomCSS(
          <ExpandableTabsBlock key={`block-${index}`} content={content} accent={accent} headStyle={heroHead(ts)} subColor={subColor} finalBlockStyle={finalBlockStyle} wrapperClass={wrapperClass} animationClass={animationClass} />
        );
      }

      case 'sparkles': {
        const ts = content.titleStyle || {};
        const accent = safeStyles.borderColor && safeStyles.borderColor !== 'transparent' ? safeStyles.borderColor : '#6366f1';
        return wrapWithCustomCSS(
          <SparklesBlock key={`block-${index}`} content={content} accent={accent} headStyle={heroHead(ts)} finalBlockStyle={finalBlockStyle} wrapperClass={wrapperClass} animationClass={animationClass} />
        );
      }

      case 'gallery-carousel': {
        const ts = content.titleStyle || {};
        const accent = safeStyles.borderColor && safeStyles.borderColor !== 'transparent' ? safeStyles.borderColor : 'var(--site-accent)';
        const subColor = content.subtitleColor || 'var(--site-body-color)';
        return wrapWithCustomCSS(
          <GalleryCarouselBlock key={`block-${index}`} content={content} accent={accent} headStyle={heroHead(ts)} subColor={subColor} finalBlockStyle={finalBlockStyle} wrapperClass={wrapperClass} animationClass={animationClass} />
        );
      }

      case 'testimonial-deck': {
        const ts = content.titleStyle || {};
        const accent = safeStyles.borderColor && safeStyles.borderColor !== 'transparent' ? safeStyles.borderColor : 'var(--site-accent)';
        const subColor = content.subtitleColor || 'var(--site-body-color)';
        return wrapWithCustomCSS(
          <TestimonialDeckBlock key={`block-${index}`} content={content} accent={accent} headStyle={heroHead(ts)} subColor={subColor} finalBlockStyle={finalBlockStyle} wrapperClass={wrapperClass} animationClass={animationClass} />
        );
      }

      case 'liquid-glass': {
        const ts = content.titleStyle || {};
        const accent = safeStyles.borderColor && safeStyles.borderColor !== 'transparent' ? safeStyles.borderColor : '#6366f1';
        return wrapWithCustomCSS(
          <LiquidGlassBlock key={`block-${index}`} content={content} accent={accent} headStyle={heroHead(ts)} finalBlockStyle={finalBlockStyle} wrapperClass={wrapperClass} animationClass={animationClass} />
        );
      }

      case 'testimonials-columns': {
        const ts = content.titleStyle || {};
        const accent = safeStyles.borderColor && safeStyles.borderColor !== 'transparent' ? safeStyles.borderColor : 'var(--site-accent)';
        const subColor = content.subtitleColor || 'var(--site-body-color)';
        return wrapWithCustomCSS(
          <TestimonialsColumnsBlock key={`block-${index}`} content={content} accent={accent} headStyle={heroHead(ts)} subColor={subColor} finalBlockStyle={finalBlockStyle} wrapperClass={wrapperClass} animationClass={animationClass} scopeId={`tc-${index}`} />
        );
      }

      case 'typewriter': {
        const ts = content.titleStyle || {};
        const accent = safeStyles.borderColor && safeStyles.borderColor !== 'transparent' ? safeStyles.borderColor : 'var(--site-accent)';
        return wrapWithCustomCSS(
          <TypewriterBlock key={`block-${index}`} content={content} accent={accent} headStyle={heroHead(ts)} finalBlockStyle={finalBlockStyle} wrapperClass={wrapperClass} animationClass={animationClass} />
        );
      }

      case 'gooey-text': {
        const ts = content.titleStyle || {};
        const accent = safeStyles.borderColor && safeStyles.borderColor !== 'transparent' ? safeStyles.borderColor : 'var(--site-heading-color)';
        return wrapWithCustomCSS(
          <GooeyTextBlock key={`block-${index}`} content={content} accent={accent} headStyle={heroHead(ts)} finalBlockStyle={finalBlockStyle} wrapperClass={wrapperClass} animationClass={animationClass} scopeId={`goo-${index}`} />
        );
      }

      case 'text-cycle': {
        const ts = content.titleStyle || {};
        const accent = safeStyles.borderColor && safeStyles.borderColor !== 'transparent' ? safeStyles.borderColor : 'var(--site-accent)';
        return wrapWithCustomCSS(
          <TextCycleBlock key={`block-${index}`} content={content} accent={accent} headStyle={heroHead(ts)} finalBlockStyle={finalBlockStyle} wrapperClass={wrapperClass} animationClass={animationClass} />
        );
      }

      case 'circular-testimonials': {
        const ts = content.titleStyle || {};
        const accent = safeStyles.borderColor && safeStyles.borderColor !== 'transparent' ? safeStyles.borderColor : 'var(--site-accent)';
        const subColor = content.subtitleColor || 'var(--site-body-color)';
        return wrapWithCustomCSS(
          <CircularTestimonialsBlock key={`block-${index}`} content={content} accent={accent} headStyle={heroHead(ts)} subColor={subColor} finalBlockStyle={finalBlockStyle} wrapperClass={wrapperClass} animationClass={animationClass} />
        );
      }

      case 'stagger-testimonials': {
        const ts = content.titleStyle || {};
        const accent = safeStyles.borderColor && safeStyles.borderColor !== 'transparent' ? safeStyles.borderColor : 'var(--site-accent)';
        const subColor = content.subtitleColor || 'var(--site-body-color)';
        return wrapWithCustomCSS(
          <StaggerTestimonialsBlock key={`block-${index}`} content={content} accent={accent} headStyle={heroHead(ts)} subColor={subColor} finalBlockStyle={finalBlockStyle} wrapperClass={wrapperClass} animationClass={animationClass} />
        );
      }

      case 'steps-icons': {
        const ts = content.titleStyle || {};
        const accent = safeStyles.borderColor && safeStyles.borderColor !== 'transparent' ? safeStyles.borderColor : 'var(--site-accent)';
        const subColor = content.subtitleColor || 'var(--site-body-color)';
        return wrapWithCustomCSS(
          <StepsIconsBlock key={`block-${index}`} content={content} accent={accent} headStyle={heroHead(ts)} subColor={subColor} finalBlockStyle={finalBlockStyle} wrapperClass={wrapperClass} animationClass={animationClass} />
        );
      }

      case 'steps-cards': {
        const ts = content.titleStyle || {};
        const accent = safeStyles.borderColor && safeStyles.borderColor !== 'transparent' ? safeStyles.borderColor : 'var(--site-accent)';
        const subColor = content.subtitleColor || 'var(--site-body-color)';
        return wrapWithCustomCSS(
          <StepsCardsBlock key={`block-${index}`} content={content} accent={accent} headStyle={heroHead(ts)} subColor={subColor} finalBlockStyle={finalBlockStyle} wrapperClass={wrapperClass} animationClass={animationClass} />
        );
      }

      case 'steps-grid-image': {
        const ts = content.titleStyle || {};
        const accent = safeStyles.borderColor && safeStyles.borderColor !== 'transparent' ? safeStyles.borderColor : 'var(--site-accent)';
        const subColor = content.subtitleColor || 'var(--site-body-color)';
        return wrapWithCustomCSS(
          <StepsGridImageBlock key={`block-${index}`} content={content} accent={accent} headStyle={heroHead(ts)} subColor={subColor} finalBlockStyle={finalBlockStyle} wrapperClass={wrapperClass} animationClass={animationClass} />
        );
      }

      case 'steps-timeline-image': {
        const ts = content.titleStyle || {};
        const accent = safeStyles.borderColor && safeStyles.borderColor !== 'transparent' ? safeStyles.borderColor : 'var(--site-accent)';
        const subColor = content.subtitleColor || 'var(--site-body-color)';
        return wrapWithCustomCSS(
          <StepsTimelineImageBlock key={`block-${index}`} content={content} accent={accent} headStyle={heroHead(ts)} subColor={subColor} finalBlockStyle={finalBlockStyle} wrapperClass={wrapperClass} animationClass={animationClass} />
        );
      }



      case 'handwriting-text': {
        const ts = content.titleStyle || {};
        const accent = safeStyles.borderColor && safeStyles.borderColor !== 'transparent' ? safeStyles.borderColor : 'var(--site-accent)';
        const subColor = content.subtitleColor || 'var(--site-body-color)';
        return wrapWithCustomCSS(
          <HandWritingBlock key={`block-${index}`} content={content} accent={accent} headStyle={heroHead(ts)} subColor={subColor} finalBlockStyle={finalBlockStyle} wrapperClass={wrapperClass} animationClass={animationClass} scopeId={`hw-${index}`} />
        );
      }

      case 'typewriter-smooth': {
        const ts = content.titleStyle || {};
        const accent = safeStyles.borderColor && safeStyles.borderColor !== 'transparent' ? safeStyles.borderColor : 'var(--site-accent)';
        const subColor = content.subtitleColor || 'var(--site-body-color)';
        return wrapWithCustomCSS(
          <TypewriterSmoothBlock key={`block-${index}`} content={content} accent={accent} headStyle={heroHead(ts)} subColor={subColor} finalBlockStyle={finalBlockStyle} wrapperClass={wrapperClass} animationClass={animationClass} scopeId={`tws-${index}`} />
        );
      }

      case 'folder-gallery': {
        const ts = content.titleStyle || {};
        const accent = safeStyles.borderColor && safeStyles.borderColor !== 'transparent' ? safeStyles.borderColor : 'var(--site-accent)';
        const subColor = content.subtitleColor || 'var(--site-body-color)';
        return wrapWithCustomCSS(
          <FolderGalleryBlock key={`block-${index}`} content={content} accent={accent} headStyle={heroHead(ts)} subColor={subColor} finalBlockStyle={finalBlockStyle} wrapperClass={wrapperClass} animationClass={animationClass} />
        );
      }





      case 'unsubscribe':
        // Unsubscribe link block
        return (
          <div key={`block-${index}`} className={`${wrapperClass} ${animationClass} text-center`} style={blockStyle}>
            <a 
              href="{{unsubscribe_url}}"
              className="text-sm text-slate-500 hover:text-slate-700 underline"
            >
              {content.text || 'Unsubscribe from this newsletter'}
            </a>
          </div>
        );

      default:
        return null;
    }
  };

  // For Quick Preview, we want a centered, constrained container that looks like a page
  const previewContainerStyle = {
    background: getPageBackground(),
    fontFamily: getThemeValue('body_font') || 'Inter, sans-serif',
    minHeight: blocks && blocks.length > 0 ? '400px' : undefined,
  };

  // Handler for opening popup pages from navigation
  const handlePopupOpen = (slug, token) => {
    setPopupPage({ isOpen: true, pageSlug: slug, previewToken: token });
  };

  // Show loading state while fetching theme (skip if propSiteTheme was provided, even if null/empty)
  // When propSiteTheme is explicitly passed (even as empty object), we're in public preview mode
  const isPublicPreviewMode = propSiteTheme !== null;
  if (!isPublicPreviewMode && !siteTheme && (!theme || theme.use_site_theme !== false)) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-pulse text-slate-400">Loading theme...</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-full">
      {/* Header Navigation */}
      {showNavigation && navigationMenus.header && (
        <NavigationPreview 
          menu={navigationMenus.header} 
          location="header" 
          siteTheme={siteTheme}
          previewToken={previewToken}
          pages={pages}
          onPopupOpen={handlePopupOpen}
          subdomain={subdomain}
          currentLanguage={currentLanguage}
          defaultLanguage={defaultLanguage}
          tenantId={propTenantId}
        />
      )}
      
      {/* Main Content */}
      <div className={`flex-1 ${isQuickPreview ? 'flex justify-center py-8' : ''}`}>
        <div 
          className={`preview-theme-scope w-full ${isQuickPreview ? `${getPageWidthClass()} py-12 px-8 rounded-xl shadow-lg border border-slate-200 overflow-hidden quick-preview-mode` : 'overflow-x-hidden'}`} 
          data-testid="preview-renderer"
          style={previewContainerStyle}
        >
          <style>{buildSiteThemeCSSVars()}{animationStyles}</style>
          {title && (
            <div className={`${!isQuickPreview ? `${getPageWidthClass()} mx-auto px-6` : ''}`}>
              <h1 
                className="text-5xl font-bold mb-8" 
                style={{ 
                  fontFamily: getThemeValue('heading_font') || 'Manrope, sans-serif',
                  color: getThemeValue('heading_color') || '#1e293b'
                }}
              >
                {title}
              </h1>
            </div>
          )}
          <div 
            key={`blocks-container-${refreshKey}`}
            className="prose prose-lg max-w-none"
            style={{ 
              color: getThemeValue('body_color') || '#475569',
              fontFamily: getThemeValue('body_font') || 'Inter, sans-serif',
              '--tw-prose-body': getThemeValue('body_color') || '#475569',
              '--tw-prose-headings': getThemeValue('heading_color') || '#1e293b',
            }}
          >
            {blocks
              .filter(block => block.isPublished !== false) // Filter out unpublished blocks
              .map((block, index) => {
              // Unified full-width detection: works for the classic 'hero' block
              // (which stores width in content.width), all new hero variants
              // (hero-spotlight/tabs/feature-cards/split-stats/split-cards,
              // also using content.width), and all non-hero blocks that use
              // the generic Block Width control (content.blockWidth). Heading
              // blocks stay excluded because edge-to-edge headings look odd.
              const contentWidth = (block.content?.width || '').toLowerCase();
              const blockWidthGeneric = (block.content?.blockWidth || '').toLowerCase();
              const effectiveWidth = contentWidth || blockWidthGeneric;
              const isFullWidthBlock =
                effectiveWidth === 'full' &&
                block.type !== 'heading';
              // Use stored anchor_id if available, otherwise generate
              const anchorId = block.content?.anchor_id || generateAnchorId(block);

              // Resolve per-block width override. Hero / heading don't get
              // the generic width control; everything else honors blockWidth.
              // - undefined / 'container' (default) -> existing behaviour
              // - 'full' -> edge-to-edge in Full Preview, capped in Quick Preview
              // - 'large' / 'medium' / 'small' -> centered, narrower max-width
              const widthKey = effectiveWidth;
              const widthOverrideClass = (() => {
                switch (widthKey) {
                  // Block-width percentages only kick in from the small
                  // breakpoint (>= 640px) upwards. On mobile the block always
                  // uses full viewport width. Mobile padding is intentionally
                  // tight (px-3) to match the default block wrapper.
                  case 'large':  return 'mx-auto w-full px-3 sm:px-6 max-w-full sm:max-w-[75%]';
                  case 'medium': return 'mx-auto w-full px-3 sm:px-6 max-w-full sm:max-w-[60%]';
                  case 'small':  return 'mx-auto w-full px-3 sm:px-6 max-w-full sm:max-w-[45%]';
                  default:       return null;
                }
              })();
              const wantsEdgeToEdge = isFullWidthBlock && !isQuickPreview;

              // Wrap non-full-width blocks in a constrained container for Full Preview.
              // In Quick Preview, always wrap all blocks in the constrained container.
              // Mobile padding is intentionally tight (px-3 = 12px each side)
              // so paragraphs use the maximum available viewport width — sm:
              // and above go back to the more generous px-6.
              if (isQuickPreview || !wantsEdgeToEdge) {
                const wrapperClass = widthOverrideClass
                  ? widthOverrideClass
                  : `${getPageWidthClass()} mx-auto w-full px-3 sm:px-6`;
                return (
                  <div key={`wrapper-${index}`} id={anchorId} className={wrapperClass}>
                    {renderBlock(block, index)}
                  </div>
                );
              }
              return (
                <div key={`block-${index}`} id={anchorId}>
                  {renderBlock(block, index)}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* Children content (e.g. article body injected between nav header and footer) */}
      {children}
      
      {/* Footer Navigation */}
      {showNavigation && navigationMenus.footer && (
        <NavigationPreview 
          menu={navigationMenus.footer} 
          location="footer" 
          siteTheme={siteTheme}
          previewToken={previewToken}
          pages={pages}
          onPopupOpen={handlePopupOpen}
          subdomain={subdomain}
          currentLanguage={currentLanguage}
          defaultLanguage={defaultLanguage}
          tenantId={propTenantId}
        />
      )}
      
      {/* Popup Page Modal */}
      <PopupPageModal
        pageSlug={popupPage.pageSlug}
        previewToken={popupPage.previewToken}
        isOpen={popupPage.isOpen}
        onClose={() => setPopupPage({ isOpen: false, pageSlug: null, previewToken: null })}
        siteTheme={siteTheme}
        pages={pages}
        subdomain={subdomain}
        currentLanguage={currentLanguage}
        defaultLanguage={defaultLanguage}
        tenantId={propTenantId}
      />

      {/* AI Assistant floating chat widget (live tenant sites only) */}
      {showNavigation && propTenantId && (
        <AiAssistantWidget tenantId={propTenantId} />
      )}
    </div>
  );
};

export default PreviewRenderer;
